import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Camera, Users, Home, Settings, Download, Mail, QrCode, Printer, CheckCircle, XCircle, Clock, Bed, ListChecks, Pencil, Save, X, UserPlus, Trash2, Shield, ChevronDown, ChevronLeft, ChevronRight, Menu, Calendar, FileText, Lock, DollarSign, Bus, ClipboardList } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import './quill.snow.css';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import SignatureCanvas from 'react-signature-canvas';
import { Html5Qrcode } from 'html5-qrcode';

// Initialize Stripe
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_KEY && STRIPE_KEY !== 'pk_test_' 
  ? loadStripe(STRIPE_KEY) 
  : null;

// Log Stripe initialization status
if (!STRIPE_KEY || STRIPE_KEY === 'pk_test_') {
  console.warn('⚠️ Stripe publishable key not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in .env file.');
}

// Role Definitions
export const ROLES = {
  VIEWER: 'viewer',
  ROOM_ADMIN: 'room_admin',
  REGISTRATION_ADMIN: 'registration_admin',
  TRANSPORTATION_ADMIN: 'transportation_admin',
  APPLICATION_ADMIN: 'application_admin'
};

// Role Hierarchy (higher number = more permissions)
const ROLE_HIERARCHY = {
  [ROLES.VIEWER]: 1,
  [ROLES.ROOM_ADMIN]: 2,
  [ROLES.REGISTRATION_ADMIN]: 3,
  [ROLES.TRANSPORTATION_ADMIN]: 3.5,
  [ROLES.APPLICATION_ADMIN]: 4
};

// Permission Helper Functions
export const hasPermission = (userRole, permission) => {
  if (!userRole) return false;
  
  const roleLevel = ROLE_HIERARCHY[userRole] || 0;
  
  // Viewer - can view all data (read-only access)
  // View-only permissions - available to all roles including viewers
  if (permission === 'view_registrations' || 
      permission === 'view_lodges' || 
      permission === 'view_checkin' ||
      permission === 'view_badges' ||
      permission === 'view_export' ||
      permission === 'view_bulk_email' ||
      permission === 'view_schedule' ||
      permission === 'view_users' ||
      permission === 'view_room_assignment' ||
      permission === 'view_deleted_registrations') {
    return roleLevel >= ROLE_HIERARCHY[ROLES.VIEWER];
  }
  
  // Room Admin - can create lodges, add rooms, assign rooms to confirmed registrations
  if (permission === 'create_lodges' || permission === 'add_rooms' || permission === 'assign_rooms') {
    return roleLevel >= ROLE_HIERARCHY[ROLES.ROOM_ADMIN];
  }
  
  // Registration Admin - can approve/reject registrations, edit registrations
  if (permission === 'approve_registrations' || permission === 'edit_registrations' || permission === 'reject_registrations') {
    return roleLevel >= ROLE_HIERARCHY[ROLES.REGISTRATION_ADMIN];
  }
  
  // Transportation Admin - can manage transportation requests
  // Note: Transportation Admin also has view-only access to registrations (via VIEWER-level permissions above)
  if (permission === 'manage_transportation' || permission === 'approve_transportation' || permission === 'edit_transportation_payment') {
    return roleLevel >= ROLE_HIERARCHY[ROLES.TRANSPORTATION_ADMIN];
  }
  
  // Registration Admin and above - dashboard toggles (registrations open, family/adult, transportation, etc.)
  if (permission === 'manage_dashboard_toggles') {
    return roleLevel >= ROLE_HIERARCHY[ROLES.REGISTRATION_ADMIN];
  }
  
  // Application Admin - full access
  if (permission === 'export_data' || permission === 'manage_settings' || permission === 'manage_checkin' || permission === 'print_badges') {
    return roleLevel >= ROLE_HIERARCHY[ROLES.APPLICATION_ADMIN];
  }
  
  // Bulk Email - available to all admin roles (Room Admin, Registration Admin, Application Admin)
  if (permission === 'send_bulk_email') {
    return roleLevel >= ROLE_HIERARCHY[ROLES.ROOM_ADMIN];
  }
  
  return false;
};

// Check if user has at least a certain role level
export const hasRole = (userRole, requiredRole) => {
  if (!userRole || !requiredRole) return false;
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
};

// Get role display name
export const getRoleDisplayName = (role) => {
  const roleNames = {
    [ROLES.VIEWER]: 'Viewer',
    [ROLES.ROOM_ADMIN]: 'Room Admin',
    [ROLES.REGISTRATION_ADMIN]: 'Registration Admin',
    [ROLES.TRANSPORTATION_ADMIN]: 'Transportation Admin',
    [ROLES.APPLICATION_ADMIN]: 'Application Admin'
  };
  return roleNames[role] || 'User';
};

// Note: User management is now handled via API - see usersAPI above

// Landing page / Camp Settings (stored as JSON in settings key `campSettings`)
export const DEFAULT_CAMP_SETTINGS = {
  campTitle: 'ICPF Awake Camp 2026',
  /**
   * Public registration site base (no ?code=). When set, QR codes and “view registration” links in emails
   * use this URL instead of REACT_APP_PUBLIC_URL / the admin’s current browser origin (fixes stale builds).
   * Example: https://camps.example.org or https://example.org/my-app
   */
  registrationPortalBaseUrl: '',
  // Canonical camp date range (ISO YYYY-MM-DD). These drive the landing page
  // hero, the Day registration attendance options, price math, the registration
  // code prefix and the email footer date string.
  campStartDate: '2026-03-19',
  campEndDate: '2026-03-22',
  // Optional manual override for the human-readable date string. When blank
  // (recommended), the value is auto-derived from campStartDate / campEndDate
  // via formatCampDateRange. Existing installs keep whatever string they had
  // saved; clearing the field switches them to the auto-derived format.
  campDates: '',
  conferenceCenterName: 'Trinity Pines Conference Center',
  location: 'Texas',
  campTheme: 'Unshakable',
  bibleVerseReference: 'Hebrews 12:28-29',
  bibleVerseText: 'Since we are receiving a Kingdom that is unshakable, let us be thankful and please God by worshiping him with holy fear and awe. For our God is a devouring fire.',
  customMessage: 'Welcome to the camp registration portal',
  // Yellow callout on registration type selection + registration form (you may use HTML tags like strong, em, a)
  registrationNoticeTitle: 'Important Notice',
  registrationNoticeBodyHtml:
    'All confirmations for room assignments and registrations are based on a <strong>first come, first serve</strong> basis. Seats are limited and will be assigned in the order registrations are received and confirmed.',
  registrationPaymentPolicyTitle: 'Payment Policy:',
  registrationPaymentBullet1: 'Payment deadline: <strong>February 15th</strong>',
  registrationPaymentBullet2:
    'Include your <strong>registration number</strong> and the <strong>camp name</strong> shown below in the payment memo when paying by Zelle, Venmo, or PayPal.',
  registrationPaymentBullet3: 'No on-site payment will be available',
  // Shown only on the registration form when type is Day registration
  dayRegistrationNoticeIntroHtml:
    '<strong>Day Registration Includes:</strong> Meals are included for all selected days. No overnight accommodations are provided.',
  dayRegistrationPaymentTitle: 'Payment:',
  dayRegistrationPaymentBullet1:
    'Payment is accepted via Zelle only. Send payment to <strong>icpfcamp@icpfhouston.com</strong>',
  dayRegistrationPaymentBullet2:
    'Include your <strong>registration number</strong> and the <strong>camp name</strong> shown below in the payment memo when paying by Zelle, Venmo, or PayPal.',
  dayRegistrationPaymentBullet3: 'Your registration will be confirmed upon receipt of full payment',
  dayRegistrationPaymentBullet4: 'No on-site payment is available',
  /** Email wrapper (createEmailTemplate): blank = derive from camp title / theme below */
  emailTemplateHeaderTitle: '',
  emailTemplateHeaderSubtitle: '',
  /** Optional data-URL image: shown as a small centered circle at the top of HTML registration emails */
  emailTemplateBannerDataUrl: '',
  emailFooterTeamName: 'ICPF Awake Camp Team',
  /** One line under team name; blank = conference center + location from above */
  emailFooterVenueLine: '',
  emailFooterContactHeading: 'Questions? Contact Us:',
  /** Help & support inbox (email footers, non-payment contact). Not the Zelle payment address. */
  emailFooterSupportEmail: 'icpfcamp@icpfhouston.com',
  /** Shown after “Zelle - ” / “Venmo - ” / “PayPal - ” on forms and in registration emails */
  paymentZelleRecipient: 'icpfcamp@icpfhouston.com',
  paymentVenmoRecipient: 'ICPF-USA',
  paymentPaypalRecipient: 'icpfusa@gmail.com',
  /** Up to 12 rows; name, role, phone for the “Questions? Contact us” block */
  emailFooterContacts: [
    { name: 'Sajan Joy', role: 'Camp Director', phone: '(919) 943-0797' },
    { name: 'Stafin Jacob', role: 'Associate Camp Director', phone: '(346) 208-0014' },
    { name: 'Pr James Jesudas', role: 'Camp Secretary', phone: '(361) 550-8365' },
    { name: 'Aby Mathew', role: 'Registration Coordinator', phone: '(214) 552-8713' }
  ],
  /** Inclusive max age for Below Elementary family members who pay $0 in the family flat-fee split (0–18). */
  familyFreeChildMaxAge: 4,
  /**
   * ISO YYYY-MM-DD strings (must fall within camp start–end). When non-empty, day registration
   * only offers these dates (plus a bundle option when 2+ are listed). Empty = all days in range.
   */
  dayPassAvailableDates: []
};

/** Stored camper category value (legacy label text; pricing uses familyFreeChildMaxAge in camp settings). */
export const BELOW_ELEMENTARY_CAMPER_CATEGORY = 'Below Elementary (4yrs and below)';

export const CLEAR_REGISTRATIONS_CONFIRMATION = 'DELETE ALL CAMP REGISTRATIONS';

const EMAIL_BANNER_DATA_URL_MAX_LEN = 900000;

function normalizeEmailTemplateBannerInCamp(merged) {
  const s = merged.emailTemplateBannerDataUrl;
  if (typeof s !== 'string' || !s.trim()) {
    merged.emailTemplateBannerDataUrl = '';
    return;
  }
  const t = s.trim().replace(/\s/g, '');
  if (
    t.length > EMAIL_BANNER_DATA_URL_MAX_LEN ||
    !/^data:image\/(?:png|jpeg|jpg|gif|webp);base64,[A-Za-z0-9+/]+=*$/.test(t)
  ) {
    merged.emailTemplateBannerDataUrl = '';
    return;
  }
  merged.emailTemplateBannerDataUrl = t;
}

function normalizeEmailFooterContactsInCamp(merged) {
  const raw = merged.emailFooterContacts;
  const fallback = () =>
    DEFAULT_CAMP_SETTINGS.emailFooterContacts.map((r) => ({ name: r.name, role: r.role, phone: r.phone }));
  if (!Array.isArray(raw) || raw.length === 0) {
    merged.emailFooterContacts = fallback();
    normalizeEmailTemplateBannerInCamp(merged);
    return merged;
  }
  const next = raw
    .filter((row) => row && typeof row === 'object')
    .slice(0, 12)
    .map((row) => ({
      name: String(row.name != null ? row.name : '').trim(),
      role: String(row.role != null ? row.role : '').trim(),
      phone: String(row.phone != null ? row.phone : '').trim()
    }))
    .filter((row) => row.name || row.role || row.phone);
  merged.emailFooterContacts = next.length ? next : fallback();
  normalizeEmailTemplateBannerInCamp(merged);
  return merged;
}

function normalizeFamilyFreeChildMaxAge(merged) {
  let n = merged.familyFreeChildMaxAge;
  n = typeof n === 'number' ? n : parseInt(String(n ?? '').trim(), 10);
  if (!Number.isFinite(n)) n = DEFAULT_CAMP_SETTINGS.familyFreeChildMaxAge;
  merged.familyFreeChildMaxAge = Math.min(18, Math.max(0, Math.floor(n)));
}

/** ISO list for each calendar day in camp range (no mergeCampSettings — avoids recursion during merge). */
function enumerateCampDateRangeISOs(merged) {
  const start = parseISODateLocal(merged.campStartDate);
  const end = parseISODateLocal(merged.campEndDate);
  if (!start || !end || end < start) return [];
  const out = [];
  const cur = new Date(start);
  while (cur <= end) {
    out.push(formatISODateLocal(cur));
    cur.setDate(cur.getDate() + 1);
  }
  if (out.length > 14) return [];
  return out;
}

function normalizeDayPassAvailableDates(merged) {
  const validIso = new Set(enumerateCampDateRangeISOs(merged));
  const raw = merged.dayPassAvailableDates;
  let arr = [];
  if (Array.isArray(raw)) {
    arr = raw.map((x) => String(x || '').trim()).filter(Boolean);
  } else if (raw != null && typeof raw === 'string' && raw.trim().startsWith('[')) {
    try {
      const p = JSON.parse(raw.trim());
      if (Array.isArray(p)) arr = p.map((x) => String(x || '').trim()).filter(Boolean);
    } catch (_) {}
  }
  const filtered = [...new Set(arr)].filter((iso) => /^\d{4}-\d{2}-\d{2}$/.test(iso) && validIso.has(iso));
  filtered.sort();
  merged.dayPassAvailableDates = filtered;
}

export function mergeCampSettings(raw) {
  if (!raw) {
    const merged = normalizeEmailFooterContactsInCamp({ ...DEFAULT_CAMP_SETTINGS });
    normalizeFamilyFreeChildMaxAge(merged);
    normalizeDayPassAvailableDates(merged);
    return merged;
  }
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (parsed && typeof parsed === 'object') {
      const merged = { ...DEFAULT_CAMP_SETTINGS, ...parsed };
      normalizeEmailFooterContactsInCamp(merged);
      normalizeFamilyFreeChildMaxAge(merged);
      normalizeDayPassAvailableDates(merged);
      return merged;
    }
  } catch (_) {
    /* ignore */
  }
  const merged = normalizeEmailFooterContactsInCamp({ ...DEFAULT_CAMP_SETTINGS });
  normalizeFamilyFreeChildMaxAge(merged);
  normalizeDayPassAvailableDates(merged);
  return merged;
}

export function getFamilyFreeChildMaxAge(camp) {
  return mergeCampSettings(camp).familyFreeChildMaxAge;
}

/** True when this family member should receive $0 of the flat family fee (Below Elementary + age rule). */
export function isFamilyBelowElementaryFreeForFlatSplit(memberLike, camp) {
  if (!memberLike || memberLike.camperCategory !== BELOW_ELEMENTARY_CAMPER_CATEGORY) return false;
  const maxAge = getFamilyFreeChildMaxAge(camp);
  const age = parseInt(memberLike.age, 10);
  if (!Number.isFinite(age) || age < 0) return true;
  return age <= maxAge;
}

export function belowElementaryDisplayLabel(camp) {
  return `Below Elementary (${getFamilyFreeChildMaxAge(camp)} yrs & under)`;
}

// ---- Camp-date helpers ------------------------------------------------------
// Parse a YYYY-MM-DD string as a Date in the local timezone. Using `new Date()`
// directly on YYYY-MM-DD is interpreted as UTC, which can shift the day by one
// for users in negative offsets (e.g. America/Chicago). We do it manually.
export function parseISODateLocal(value) {
  if (typeof value !== 'string') return null;
  const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10) - 1;
  const d = parseInt(m[3], 10);
  const dt = new Date(y, mo, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

/** Local calendar date as YYYY-MM-DD (for day-pass allowlists; avoids UTC shift). */
export function formatISODateLocal(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

/** Build a human-readable range like "March 19-22, 2026" or "Dec 30, 2026 - Jan 2, 2027". */
export function formatCampDateRange(startISO, endISO) {
  const start = parseISODateLocal(startISO);
  const end = parseISODateLocal(endISO);
  if (!start && !end) return '';
  if (start && !end) return start.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  if (!start && end) return end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  if (end < start) return formatCampDateRange(endISO, startISO);
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  if (start.getTime() === end.getTime()) {
    return start.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  if (sameMonth) {
    const monthLong = start.toLocaleDateString('en-US', { month: 'long' });
    return `${monthLong} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
  }
  if (sameYear) {
    const a = start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    const b = end.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    return `${a} - ${b}, ${start.getFullYear()}`;
  }
  const a = start.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const b = end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  return `${a} - ${b}`;
}

/** Get the display string for camp dates: explicit override wins; else derived. */
export function getDisplayCampDates(camp) {
  const c = mergeCampSettings(camp);
  if (typeof c.campDates === 'string' && c.campDates.trim()) return c.campDates.trim();
  const derived = formatCampDateRange(c.campStartDate, c.campEndDate);
  return derived || c.campDates || '';
}

/** Each day in the configured camp range, as Date objects. */
function getCampDateList(camp) {
  const c = mergeCampSettings(camp);
  const start = parseISODateLocal(c.campStartDate);
  const end = parseISODateLocal(c.campEndDate);
  if (!start || !end || end < start) return [];
  const out = [];
  const cur = new Date(start);
  while (cur <= end) {
    out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  // Soft cap: refuse insanely long ranges (e.g. someone typed the wrong year)
  // to avoid rendering hundreds of <option>s by accident.
  if (out.length > 14) return [];
  return out;
}

/**
 * Build the attendance-day options shown in the Day registration dropdown.
 * Each option carries the value persisted on the registration, the price for
 * that selection, and a registration-code prefix used by generateDayRegistrationCode.
 */
export function getDayRegistrationOptions(camp, pricing) {
  const c = mergeCampSettings(camp);
  const fullDays = getCampDateList(c);
  const allow = c.dayPassAvailableDates;
  let days = fullDays;
  if (Array.isArray(allow) && allow.length > 0) {
    const allowSet = new Set(
      allow.map((x) => String(x || '').trim()).filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s))
    );
    days = fullDays.filter((d) => allowSet.has(formatISODateLocal(d)));
  }
  const p = { ...DEFAULT_PRICING_SETTINGS, ...(pricing || {}) };
  if (!days.length) return [];

  const dayName = (d) => d.toLocaleDateString('en-US', { weekday: 'long' });
  const codePrefixForDay = (name) => {
    const map = { Monday: 'ICPFDAYM', Tuesday: 'ICPFDAYTU', Wednesday: 'ICPFDAYW', Thursday: 'ICPFDAYTH', Friday: 'ICPFDAYF', Saturday: 'ICPFDAYS', Sunday: 'ICPFDAYSU' };
    return map[name] || `ICPFDAY${name.slice(0, 3).toUpperCase()}`;
  };

  const singleOptions = days.map((d) => {
    const name = dayName(d);
    return {
      value: name, // backwards-compatible: e.g. "Friday", "Saturday"
      label: `${name} only ($${p.dayOneDay})`,
      shortLabel: name,
      days: 1,
      price: p.dayOneDay,
      codePrefix: codePrefixForDay(name)
    };
  });

  if (days.length < 2) return singleOptions;

  const firstName = dayName(days[0]);
  const lastName = dayName(days[days.length - 1]);
  let allValue;
  let allPrice;
  let allLabel;
  if (days.length === 2) {
    allValue = `${firstName} & ${lastName}`; // backwards-compatible: "Friday & Saturday"
    allPrice = p.dayTwoDay;
    allLabel = `${firstName} & ${lastName} ($${allPrice})`;
  } else {
    allValue = `All ${days.length} days (${firstName}-${lastName})`;
    allPrice = p.dayOneDay * days.length;
    allLabel = `All ${days.length} days ($${allPrice})`;
  }
  // Generic prefix combining initials of first/last day, e.g. "ICPFDAYTHFR"
  // for Thursday-Friday. Keeps existing 'ICPFDAYTF' for "Friday & Saturday".
  let allPrefix;
  if (days.length === 2 && firstName === 'Friday' && lastName === 'Saturday') {
    allPrefix = 'ICPFDAYTF';
  } else {
    allPrefix = `ICPFDAY${firstName.slice(0, 2).toUpperCase()}${lastName.slice(0, 2).toUpperCase()}`;
  }
  return [
    ...singleOptions,
    {
      value: allValue,
      label: allLabel,
      shortLabel: allValue,
      days: days.length,
      price: allPrice,
      codePrefix: allPrefix
    }
  ];
}

// Module-level latest-known camp settings, used by the module-scoped
// createEmailTemplate helper so that emails sent from anywhere in the app pick
// up the latest configured dates without having to thread campSettings through
// every email-sending call site.
let LATEST_CAMP_SETTINGS_FOR_EMAILS = { ...DEFAULT_CAMP_SETTINGS };
export function setEmailTemplateCampSettings(camp) {
  LATEST_CAMP_SETTINGS_FOR_EMAILS = mergeCampSettings(camp);
}

/** Yellow informational callout on registration type selection and registration form. */
function RegistrationFlowNoticeCallout({ camp, dayVariant = false }) {
  const c = mergeCampSettings(camp);
  const title = (c.registrationNoticeTitle || '').trim() || DEFAULT_CAMP_SETTINGS.registrationNoticeTitle;

  if (dayVariant) {
    const introHtml =
      (c.dayRegistrationNoticeIntroHtml || '').trim() ||
      DEFAULT_CAMP_SETTINGS.dayRegistrationNoticeIntroHtml;
    const payTitle =
      (c.dayRegistrationPaymentTitle || '').trim() || DEFAULT_CAMP_SETTINGS.dayRegistrationPaymentTitle;
    const dayBullets = [
      c.dayRegistrationPaymentBullet1 || DEFAULT_CAMP_SETTINGS.dayRegistrationPaymentBullet1,
      c.dayRegistrationPaymentBullet2 || DEFAULT_CAMP_SETTINGS.dayRegistrationPaymentBullet2,
      c.dayRegistrationPaymentBullet3 || DEFAULT_CAMP_SETTINGS.dayRegistrationPaymentBullet3,
      c.dayRegistrationPaymentBullet4 || DEFAULT_CAMP_SETTINGS.dayRegistrationPaymentBullet4
    ].filter((s) => typeof s === 'string' && s.trim());

    return (
      <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-yellow-800 mb-2">{title}</h4>
            <div className="text-xs text-yellow-700 space-y-2">
              <p dangerouslySetInnerHTML={{ __html: introHtml }} />
              <div className="border-t border-yellow-200 pt-2 mt-2">
                <p className="font-semibold mb-1">{payTitle}</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  {dayBullets.map((html, i) => (
                    <li key={i} dangerouslySetInnerHTML={{ __html: html }} />
                  ))}
                  <li className="text-yellow-800">
                    <strong>Memo:</strong> include your <strong>registration number</strong> and{' '}
                    <strong>{escapeHtmlForEmail(getCampTitleForPayments(c))}</strong> in Zelle, Venmo, or PayPal notes.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const bodyHtml =
    (c.registrationNoticeBodyHtml || '').trim() || DEFAULT_CAMP_SETTINGS.registrationNoticeBodyHtml;
  const payTitle =
    (c.registrationPaymentPolicyTitle || '').trim() ||
    DEFAULT_CAMP_SETTINGS.registrationPaymentPolicyTitle;
  const bullets = [
    c.registrationPaymentBullet1 || DEFAULT_CAMP_SETTINGS.registrationPaymentBullet1,
    c.registrationPaymentBullet2 || DEFAULT_CAMP_SETTINGS.registrationPaymentBullet2,
    c.registrationPaymentBullet3 || DEFAULT_CAMP_SETTINGS.registrationPaymentBullet3
  ].filter((s) => typeof s === 'string' && s.trim());

  return (
    <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 mb-6">
      <div className="flex items-start gap-3">
        <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-yellow-800 mb-2">{title}</h4>
          <div className="text-xs text-yellow-700 space-y-2">
            <p dangerouslySetInnerHTML={{ __html: bodyHtml }} />
            <div className="border-t border-yellow-200 pt-2 mt-2">
              <p className="font-semibold mb-1">{payTitle}</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                {bullets.map((html, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: html }} />
                ))}
                <li className="text-yellow-800">
                  <strong>Memo:</strong> include your <strong>registration number</strong> and{' '}
                  <strong>{escapeHtmlForEmail(getCampTitleForPayments(c))}</strong> in Zelle, Venmo, or PayPal notes.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Registration pricing (stored as JSON in settings key `pricingSettings`).
// Family pricing is a flat amount per family size (family2 / family3 / family4 / family5).
export const DEFAULT_PRICING_SETTINGS = {
  test: 2,
  student: 195,
  earlyBirdStudent: 150,
  adult: 275,
  dayOneDay: 60,
  dayTwoDay: 120,
  family2: 470,
  family3: 665,
  family4: 860,
  family5: 1055
};

// Sender / Reply-To addresses (stored as JSON in settings key `emailSettings`).
// These override env vars when set; the backend reads them at startup and on save.
export const DEFAULT_EMAIL_SETTINGS = {
  fromEmail: 'icpfsecamp@icpfse.org',
  replyToEmail: 'johanjeson2002@gmail.com',
  /** Shown as the sender display name (inbox “From” label). Blank = use server env defaults. */
  fromDisplayName: '',
  /** Copied on every outbound email from the API (registration, OTP, etc.). Blank = none. */
  alwaysCcEmail: ''
};

export function isValidEmailAddress(value) {
  return typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function mergeEmailSettings(raw) {
  if (!raw) return { ...DEFAULT_EMAIL_SETTINGS };
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (parsed && typeof parsed === 'object') {
      const merged = { ...DEFAULT_EMAIL_SETTINGS };
      if (typeof parsed.fromEmail === 'string' && parsed.fromEmail.trim()) {
        merged.fromEmail = parsed.fromEmail.trim();
      }
      if (typeof parsed.replyToEmail === 'string' && parsed.replyToEmail.trim()) {
        merged.replyToEmail = parsed.replyToEmail.trim();
      }
      if (typeof parsed.fromDisplayName === 'string') {
        merged.fromDisplayName = parsed.fromDisplayName
          .trim()
          .replace(/[\r\n\x00]/g, '')
          .slice(0, 150);
      }
      if (typeof parsed.alwaysCcEmail === 'string') {
        const t = parsed.alwaysCcEmail.trim();
        merged.alwaysCcEmail = isValidEmailAddress(t) ? t : '';
      }
      return merged;
    }
  } catch (_) {
    /* ignore */
  }
  return { ...DEFAULT_EMAIL_SETTINGS };
}

/**
 * Help / support contact email from camp settings (email footers, check-in footer, non-payment UI).
 */
export function getHelpSupportEmail(camp) {
  const c = mergeCampSettings(camp);
  const v = (c.emailFooterSupportEmail || '').trim();
  if (v && isValidEmailAddress(v)) return v;
  return DEFAULT_CAMP_SETTINGS.emailFooterSupportEmail;
}

export function getPaymentZelleRecipient(camp) {
  const v = (mergeCampSettings(camp).paymentZelleRecipient || '').trim();
  return v || DEFAULT_CAMP_SETTINGS.paymentZelleRecipient;
}

export function getPaymentVenmoRecipient(camp) {
  const v = (mergeCampSettings(camp).paymentVenmoRecipient || '').trim();
  return v || DEFAULT_CAMP_SETTINGS.paymentVenmoRecipient;
}

export function getPaymentPaypalRecipient(camp) {
  const v = (mergeCampSettings(camp).paymentPaypalRecipient || '').trim();
  return v || DEFAULT_CAMP_SETTINGS.paymentPaypalRecipient;
}

/** Human-readable payment method for emails and admin UI (uses camp payment recipient fields). */
export function paymentMethodDisplayLabel(method, camp) {
  if (method == null || method === '') return 'Not specified';
  const m = String(method).toLowerCase();
  if (m === 'card') return 'Credit/Debit Card';
  if (m === 'zelle') return `Zelle - ${getPaymentZelleRecipient(camp)}`;
  if (m === 'venmo') return `Venmo - ${getPaymentVenmoRecipient(camp)}`;
  if (m === 'paypal') return `PayPal - ${getPaymentPaypalRecipient(camp)}`;
  if (m === 'waived') return 'Waived';
  return String(method);
}

/** Camp title for payment memos (Camp Settings → Camp title). */
export function getCampTitleForPayments(camp) {
  const t = (mergeCampSettings(camp).campTitle || '').trim();
  return t || (DEFAULT_CAMP_SETTINGS.campTitle || '').trim() || 'Camp';
}

/**
 * HTML block for Zelle / Venmo / PayPal when payment is still expected.
 * @param {string} memoRegistrationCode — registration or family code to cite in memo instructions
 */
export function buildNonCardPaymentMemoInstructionsHtml(camp, paymentMethod, memoRegistrationCode) {
  const m = String(paymentMethod || '').toLowerCase();
  if (m === 'card' || m === 'waived') return '';
  const titleEsc = escapeHtmlForEmail(getCampTitleForPayments(camp));
  const codeRaw = String(memoRegistrationCode || '').trim();
  const codeEsc = escapeHtmlForEmail(codeRaw);
  const memoCore = codeEsc
    ? `In the memo or notes field, include your <strong>registration number</strong> (<strong>${codeEsc}</strong>) and the camp name <strong>${titleEsc}</strong> so we can match your payment.`
    : `In the memo or notes field, include your <strong>registration number</strong> and the camp name <strong>${titleEsc}</strong> so we can match your payment.`;

  const box = (heading, innerHtml) => `
        <div style="background-color: #ecfdf5; border-left: 4px solid #10b981; padding: 15px; margin: 12px 0; border-radius: 4px;">
          <p style="margin: 0 0 8px 0; color: #065f46; font-size: 15px; font-weight: 600;">${heading}</p>
          <p style="margin: 0; color: #047857; font-size: 14px; line-height: 1.6;">
            ${innerHtml}
          </p>
        </div>`;

  const zelleTo = escapeHtmlForEmail(getPaymentZelleRecipient(camp));
  const venmoTo = escapeHtmlForEmail(getPaymentVenmoRecipient(camp));
  const paypalTo = escapeHtmlForEmail(getPaymentPaypalRecipient(camp));

  if (m === 'zelle') {
    return box('Zelle payment', `Send payment to <strong>${zelleTo}</strong> (Zelle).<br>${memoCore}`);
  }
  if (m === 'venmo') {
    return box('Venmo payment', `Send payment to <strong>${venmoTo}</strong> (Venmo).<br>${memoCore}`);
  }
  if (m === 'paypal') {
    return box('PayPal payment', `Send payment to <strong>${paypalTo}</strong> (PayPal).<br>${memoCore}`);
  }
  return box(
    'Zelle, Venmo, or PayPal payment',
    `<strong>Zelle:</strong> ${zelleTo}<br><strong>Venmo:</strong> ${venmoTo}<br><strong>PayPal:</strong> ${paypalTo}<br>${memoCore}`
  );
}

/** Show bank-app payment memo instructions when balance may still be due via Zelle/Venmo/PayPal. */
export function shouldAppendNonCardPaymentMemo(paymentMethod, paymentStatus) {
  const m = String(paymentMethod || '').toLowerCase();
  if (m === 'card' || m === 'waived') return false;
  if (m !== 'zelle' && m !== 'venmo' && m !== 'paypal' && m !== '') return false;
  const st = String(paymentStatus || '').toLowerCase();
  return st !== 'paid' && st !== 'waived';
}

/** True when no balance is owed (waiver/discount, guest-style waived, or $0 amount). */
export function registrationHasNoPaymentDue(reg) {
  if (!reg) return false;
  const m = String(reg.paymentMethod || '').toLowerCase();
  if (m === 'waived') return true;
  const st = String(reg.paymentStatus || '').toLowerCase();
  if (st === 'waived') return true;
  const amt = parseFloat(reg.amount);
  if (Number.isFinite(amt) && amt <= 0) return true;
  return false;
}

export function mergePricingSettings(raw) {
  if (!raw) return { ...DEFAULT_PRICING_SETTINGS };
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (parsed && typeof parsed === 'object') {
      const merged = { ...DEFAULT_PRICING_SETTINGS };
      Object.keys(DEFAULT_PRICING_SETTINGS).forEach((key) => {
        const v = parsed[key];
        const num = typeof v === 'number' ? v : parseFloat(v);
        if (Number.isFinite(num) && num >= 0) merged[key] = num;
      });
      // Backward compatibility: if a legacy per-camper family rate is present
      // (familyStudent / familyAdult) but the new flat keys are missing, derive
      // sensible per-size defaults assuming 1 adult + (N-1) students.
      const legacyStudent = parseFloat(parsed.familyStudent);
      const legacyAdult = parseFloat(parsed.familyAdult);
      if (Number.isFinite(legacyStudent) && Number.isFinite(legacyAdult)) {
        ['family2', 'family3', 'family4', 'family5'].forEach((key) => {
          if (parsed[key] === undefined || parsed[key] === null) {
            const n = parseInt(key.replace('family', ''), 10);
            merged[key] = legacyAdult + legacyStudent * (n - 1);
          }
        });
      }
      return merged;
    }
  } catch (_) {
    /* ignore */
  }
  return { ...DEFAULT_PRICING_SETTINGS };
}

export const DEFAULT_FEE_WAIVER_SETTINGS = { feeWaivers: [], discounts: [] };

export function mergeFeeWaiverSettings(raw) {
  if (!raw) return { ...DEFAULT_FEE_WAIVER_SETTINGS };
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (parsed && typeof parsed === 'object') {
      return {
        feeWaivers: Array.isArray(parsed.feeWaivers) ? parsed.feeWaivers : [],
        discounts: Array.isArray(parsed.discounts) ? parsed.discounts : []
      };
    }
  } catch (_) {
    /* ignore */
  }
  return { ...DEFAULT_FEE_WAIVER_SETTINGS };
}

// Stripe Payment Form Component
function StripePaymentForm({ amount, onSuccess, onError, onProcessingChange, cardDetails, setCardDetails, payDisabled = false }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    if (!stripe || !elements) {
      console.error('Stripe not loaded:', { stripe: !!stripe, elements: !!elements });
      setError('Payment system is not ready. Please wait a moment and try again.');
      return;
    }

    if (cardDetails.paymentSucceeded) {
      // Payment already completed
      return;
    }

    setError(null);
    setIsProcessing(true);
    onProcessingChange?.(true);

    try {
      // Step 1: Create Payment Intent on backend
      const response = await fetch(`${API_BASE_URL}/payments/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amount,
          currency: 'usd',
          metadata: {
            description: `Camp Registration Payment - $${amount}`,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.details || 'Failed to create payment intent');
      }

      const { clientSecret } = await response.json();

      if (!clientSecret) {
        throw new Error('Failed to create payment intent - no client secret received');
      }

      // Step 2: Confirm payment with Stripe
      const cardElement = elements.getElement(CardElement);
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (paymentIntent.status === 'succeeded') {
        // Step 3: Confirm with backend
        const confirmResponse = await fetch(`${API_BASE_URL}/payments/confirm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            paymentIntentId: paymentIntent.id,
          }),
        });

        if (!confirmResponse.ok) {
          const errorData = await confirmResponse.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.details || 'Payment confirmation failed');
        }

        const confirmResult = await confirmResponse.json();

        if (confirmResult.success) {
          onSuccess({
            success: true,
            transactionId: confirmResult.transactionId,
            paymentIntentId: paymentIntent.id,
          });
        } else {
          throw new Error(confirmResult.error || 'Payment confirmation failed');
        }
      } else {
        throw new Error(`Payment status: ${paymentIntent.status}`);
      }
    } catch (err) {
      const errorMessage = err.message || 'Payment processing failed';
      setError(errorMessage);
      onError?.(err);
    } finally {
      setIsProcessing(false);
      onProcessingChange?.(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="border border-gray-300 rounded-lg p-4 bg-white">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Information
        </label>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      
      {error && (
        <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      )}

      {cardDetails.paymentSucceeded && (
        <div className="bg-green-50 border border-green-200 rounded p-3 text-green-800">
          ✅ Payment successful! Transaction ID: {cardDetails.transactionId?.substring(0, 20)}...
        </div>
      )}

      <button
        type="button"
        onClick={(e) => {
          try {
            if (e) {
              if (typeof e.preventDefault === 'function') {
                e.preventDefault();
              }
              if (typeof e.stopPropagation === 'function') {
                e.stopPropagation();
              }
              if (typeof e.stopImmediatePropagation === 'function') {
                e.stopImmediatePropagation();
              }
            }
          } catch (err) {
            // Ignore event handling errors
            console.warn('Event handling error:', err);
          }
          
          // Additional guard - don't proceed if Stripe not ready
          if (!stripe || !elements) {
            setError('Payment system is not ready. Please wait a moment and try again.');
            return;
          }
          
          // Don't process if already succeeded
          if (cardDetails?.paymentSucceeded) {
            return;
          }
          
          handleSubmit(e);
        }}
        disabled={!stripe || !elements || isProcessing || (cardDetails?.paymentSucceeded) || payDisabled}
        className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors"
      >
        {cardDetails.paymentSucceeded 
          ? `Payment Complete (${cardDetails.transactionId?.substring(0, 20)}...)` 
          : isProcessing 
            ? 'Processing Payment...' 
            : `Pay $${amount.toFixed(2)}`}
      </button>
    </div>
  );
}

// Legacy payment function - now handled by StripePaymentForm
// Keeping for backward compatibility with non-card payment methods
// eslint-disable-next-line no-unused-vars
const processStripePayment = async (amount, cardDetails) => {
  throw new Error('Card payments must use StripePaymentForm component. This function is deprecated.');
};

// API Configuration — when served from Azure Static Web Apps, use App Service API (not baked .env production Houston URL)
function resolveApiBaseForHost() {
  if (typeof window !== 'undefined' && window.location.hostname.includes('azurestaticapps.net')) {
    return (
      process.env.REACT_APP_AZURE_STATIC_API_URL ||
      'https://icpfawakeapi20260113050440.azurewebsites.net/api'
    );
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
}
function resolveEmailApiForHost() {
  if (typeof window !== 'undefined' && window.location.hostname.includes('azurestaticapps.net')) {
    return (
      process.env.REACT_APP_AZURE_STATIC_API_URL ||
      'https://icpfawakeapi20260113050440.azurewebsites.net/api'
    );
  }
  // Local CRA dev: use the same machine’s API so `.env` Resend keys apply (production URL in .env would skip local backend).
  const forceRemote =
    (process.env.REACT_APP_USE_REMOTE_EMAIL_API || '').trim().toLowerCase() === 'true' ||
    (process.env.REACT_APP_USE_REMOTE_EMAIL_API || '').trim() === '1';
  if (
    typeof window !== 'undefined' &&
    !forceRemote &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ) {
    const port = (process.env.REACT_APP_LOCAL_API_PORT || '3001').replace(/^:/, '');
    return `http://localhost:${port}/api`;
  }
  return process.env.REACT_APP_EMAIL_API_URL || 'https://emailapi.icpfhouston.com/api';
}
const API_BASE_URL = resolveApiBaseForHost();
const EMAIL_API_URL = resolveEmailApiForHost();

/**
 * Canonical origin + path for the public SPA (QR codes and “view registration” links in emails).
 * - If Camp Settings → **Registration portal URL** is set, that value is used (recommended when the admin build has a stale REACT_APP_PUBLIC_URL).
 * - Else uses REACT_APP_PUBLIC_URL + CRA `PUBLIC_URL` when set.
 * - Else uses the current browser origin + `PUBLIC_URL` (local/dev).
 */
function resolvePublicSiteBaseUrl() {
  const publicPath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const campBase = (
    mergeCampSettings(LATEST_CAMP_SETTINGS_FOR_EMAILS).registrationPortalBaseUrl || ''
  )
    .trim()
    .replace(/\/+$/, '');
  if (campBase && /^https?:\/\//i.test(campBase)) {
    return campBase;
  }
  const envOrigin = (process.env.REACT_APP_PUBLIC_URL || '').trim().replace(/\/+$/, '');
  if (envOrigin) {
    return `${envOrigin}${publicPath}`;
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${publicPath}`;
  }
  return publicPath || '';
}

/** Opens the public registration details view for the given registration / QR code. */
function buildPublicRegistrationViewUrl(registrationCode) {
  const code = registrationCode == null ? '' : String(registrationCode);
  const base = resolvePublicSiteBaseUrl();
  if (!code) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}code=${encodeURIComponent(code)}`;
}

// API Helper Functions
const apiRequest = async (endpoint, options = {}) => {
  try {
    const { headers: optionHeaders, ...restOptions } = options;
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...restOptions,
      headers: {
        'Content-Type': 'application/json',
        ...(optionHeaders || {}),
      },
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (jsonError) {
        // If response is not JSON, try to get text
        const text = await response.text().catch(() => 'Unknown error');
        errorData = { error: text || `HTTP ${response.status}: ${response.statusText}` };
      }
      
      // Log full error response for debugging
      console.error(`API Error Response (${endpoint}):`, {
        status: response.status,
        statusText: response.statusText,
        errorData: errorData
      });
      
      const errorMessage = errorData.error || errorData.details || `HTTP ${response.status}: ${response.statusText}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.response = errorData;
      error.fullError = errorData; // Store full error for debugging
      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
};

function formatApiErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
  if (!error) return fallback;
  const fromBody = error.response && (error.response.details || error.response.error);
  return (fromBody || error.message || fallback).trim() || fallback;
}

// Registrations API
const registrationsAPI = {
  getAll: () => apiRequest('/registrations'),
  getById: (id) => apiRequest(`/registrations/${id}`),
  search: (email, registrationCode) => apiRequest('/registrations/search', {
    method: 'POST',
    body: JSON.stringify({ email, registrationCode }),
  }),
  create: (registration) => apiRequest('/registrations', {
    method: 'POST',
    body: JSON.stringify(registration),
  }),
  update: (id, updates) => apiRequest(`/registrations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id) => apiRequest(`/registrations/${id}`, {
    method: 'DELETE',
  }),
  getDeleted: () => apiRequest('/registrations/deleted/all'),
  restore: (id) => apiRequest(`/registrations/${id}/restore`, {
    method: 'POST',
  }),
  permanentDelete: (id) => apiRequest(`/registrations/${id}/permanent`, {
    method: 'DELETE',
  }),
  clearAll: (confirmation, purgeKeyHeader) => apiRequest('/admin/registrations/clear-all', {
    method: 'POST',
    body: JSON.stringify({ confirmation }),
    headers: purgeKeyHeader ? { 'X-Admin-Purge-Key': purgeKeyHeader } : {},
  }),
};

// Lodges API
const lodgesAPI = {
  getAll: () => apiRequest('/lodges'),
  getById: (id) => apiRequest(`/lodges/${id}`),
  create: (lodge) => apiRequest('/lodges', {
    method: 'POST',
    body: JSON.stringify(lodge),
  }),
  update: (id, updates) => apiRequest(`/lodges/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id) => apiRequest(`/lodges/${id}`, {
    method: 'DELETE',
  }),
};

// Users API
const usersAPI = {
  getAll: () => apiRequest('/users'),
  getById: (id) => apiRequest(`/users/${id}`),
  create: (user) => apiRequest('/users', {
    method: 'POST',
    body: JSON.stringify(user),
  }),
  update: (id, updates) => apiRequest(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id) => apiRequest(`/users/${id}`, {
    method: 'DELETE',
  }),
  login: (username, password) => apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  }),
  changePassword: (userId, currentPassword, newPassword) => apiRequest(`/users/${userId}/change-password`, {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  }),
  requestPasswordReset: (email) => apiRequest('/auth/request-password-reset', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),
  verifyResetCode: (email, code) => apiRequest('/auth/verify-reset-code', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  }),
  resetPassword: (email, code, newPassword) => apiRequest('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ email, code, newPassword }),
  }),
};

// Settings API
const settingsAPI = {
  get: (key) => apiRequest(`/settings/${key}`),
  set: (key, value) => apiRequest(`/settings/${key}`, {
    method: 'PUT',
    body: JSON.stringify({ value }),
  }),
};

// Welcome Package documents API (admin-managed PDFs shown on the public Welcome Package page)
const welcomePackageDocsAPI = {
  list: () => apiRequest('/welcome-package-docs'),
  upload: ({ label, fileName, contentBase64 }) => apiRequest('/welcome-package-docs', {
    method: 'POST',
    body: JSON.stringify({ label, fileName, contentBase64 }),
  }),
  rename: (id, label) => apiRequest(`/welcome-package-docs/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ label }),
  }),
  remove: (id) => apiRequest(`/welcome-package-docs/${id}`, {
    method: 'DELETE',
  }),
};

/** Public URL for a welcome package doc: builtin docs are static frontend files, uploads come from the API. */
function welcomePackageDocUrl(doc) {
  if (doc.builtin && doc.file) {
    const baseUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '') || '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}${baseUrl}/welcome-package-docs/${doc.file}`;
  }
  return `${API_BASE_URL}/welcome-package-docs/${doc.id}/file`;
}

const feeWaiversAPI = {
  preview: (lines) =>
    apiRequest('/fee-waivers/preview', {
      method: 'POST',
      body: JSON.stringify({ lines }),
    }),
};

// Transportation API
const transportationAPI = {
  getAll: () => apiRequest('/transportation/requests'),
  getById: (id) => apiRequest(`/transportation/requests/${id}`),
  create: (request) => apiRequest('/transportation/requests', {
    method: 'POST',
    body: JSON.stringify(request),
  }),
  update: (id, updates) => apiRequest(`/transportation/requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id) => apiRequest(`/transportation/requests/${id}`, {
    method: 'DELETE',
  }),
};

// Schedules API
const schedulesAPI = {
  getAll: () => apiRequest('/schedules'),
  getById: (id) => apiRequest(`/schedules/${id}`),
  getByCategory: async (category) => {
    // Handle both old array format and new structured format for backward compatibility
    const response = await apiRequest(`/schedules/category/${category}`);
    // If response is an array (old format), return it directly
    if (Array.isArray(response)) {
      return response;
    }
    // If response is structured (new format), return the schedules array
    if (response && response.schedules) {
      return response.schedules;
    }
    return response;
  },
  getByCategoryFull: (category) => apiRequest(`/schedules/category/${category}`), // Returns full response with metadata
  getByCategoryGrouped: () => apiRequest('/schedules/by-category'), // Get all schedules grouped by category
  getCategories: () => apiRequest('/schedules/categories'), // Get available categories
  create: (schedule) => apiRequest('/schedules', {
    method: 'POST',
    body: JSON.stringify(schedule),
  }),
  update: (id, updates) => apiRequest(`/schedules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  delete: (id) => apiRequest(`/schedules/${id}`, {
    method: 'DELETE',
  }),
};

// Email function - calls backend API to send via Office 365
function escapeHtmlForEmail(s) {
  if (s == null || s === undefined) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function telHrefFromDisplayPhone(phone) {
  const d = String(phone || '').replace(/\D/g, '');
  return d ? `tel:${d}` : '#';
}

// Helper function to create professional HTML email template (header/footer from Camp Settings → email template fields)
const createEmailTemplate = (content, title = '') => {
  const c = mergeCampSettings(LATEST_CAMP_SETTINGS_FOR_EMAILS);
  const pageTitle =
    (typeof title === 'string' && title.trim()) || (c.campTitle || '').trim() || DEFAULT_CAMP_SETTINGS.campTitle;
  const headerTitle = (c.emailTemplateHeaderTitle || '').trim() || (c.campTitle || DEFAULT_CAMP_SETTINGS.campTitle);
  const headerSubtitle =
    (c.emailTemplateHeaderSubtitle || '').trim() ||
    [c.campTheme, c.bibleVerseReference].filter(Boolean).join(' - ') ||
    'ICPF Awake Camp';
  const bannerRaw = (c.emailTemplateBannerDataUrl || '').trim().replace(/\s/g, '');
  const bannerOk =
    bannerRaw.length > 0 &&
    bannerRaw.length <= EMAIL_BANNER_DATA_URL_MAX_LEN &&
    /^data:image\/(?:png|jpeg|jpg|gif|webp);base64,[A-Za-z0-9+/]+=*$/.test(bannerRaw);
  const headerBarTopRadius = '8px';
  // Small centered logo: fixed square box + overflow clip (avoids wide “oval” stretch from width:100% layouts).
  // Note: Outlook desktop often ignores object-fit on <img>; square artwork (~200×200+) looks best there.
  const EMAIL_LOGO_DIAM_PX = 88;
  const logoRadius = EMAIL_LOGO_DIAM_PX / 2;
  // Logo sits inside the blue header (no separate white banner row) so there is no huge white gap before the theme line.
  const logoBlock = bannerOk
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto 6px;border-collapse:collapse;">
                <tr>
                  <td align="center" valign="middle" width="${EMAIL_LOGO_DIAM_PX}" height="${EMAIL_LOGO_DIAM_PX}" style="width:${EMAIL_LOGO_DIAM_PX}px;height:${EMAIL_LOGO_DIAM_PX}px;max-width:${EMAIL_LOGO_DIAM_PX}px;padding:0;line-height:0;mso-line-height-rule:exactly;border-radius:${logoRadius}px;overflow:hidden;background:#1e3a8a;">
                    <img src="${bannerRaw}" width="${EMAIL_LOGO_DIAM_PX}" height="${EMAIL_LOGO_DIAM_PX}" alt="" style="display:block;width:${EMAIL_LOGO_DIAM_PX}px;height:${EMAIL_LOGO_DIAM_PX}px;border:0;outline:none;text-decoration:none;object-fit:cover;object-position:center center;-ms-interpolation-mode:bicubic;" />
                  </td>
                </tr>
              </table>`
    : '';
  const footerDate = getDisplayCampDates(c);
  const venueLine =
    (c.emailFooterVenueLine || '').trim() ||
    [c.conferenceCenterName, c.location].filter(Boolean).join(', ');
  const teamName = (c.emailFooterTeamName || '').trim() || 'ICPF Awake Camp Team';
  const contactHeading = (c.emailFooterContactHeading || '').trim() || 'Questions? Contact Us:';
  const supportEmail = getHelpSupportEmail(LATEST_CAMP_SETTINGS_FOR_EMAILS);
  const supportEsc = escapeHtmlForEmail(supportEmail);
  const mailtoHref = `mailto:${encodeURIComponent(supportEmail)}`;
  const contacts = Array.isArray(c.emailFooterContacts) ? c.emailFooterContacts : [];
  const contactsHtml = contacts
    .filter((row) => row && (row.name || row.role || row.phone))
    .map((row) => {
      const bits = [row.name, row.role].filter(Boolean).map((x) => escapeHtmlForEmail(x));
      const left = bits.join(' - ');
      const phoneDisp = escapeHtmlForEmail(row.phone);
      const tel = telHrefFromDisplayPhone(row.phone);
      const prefix = left ? `${left}: ` : '';
      return `<div style="margin-bottom: 6px; word-break: keep-all;">${prefix}<a href="${tel}" style="color: #3b82f6; text-decoration: none; white-space: nowrap;">${phoneDisp}</a></div>`;
    })
    .join('');
  const titleEsc = escapeHtmlForEmail(pageTitle);
  const headerTitleEsc = escapeHtmlForEmail(headerTitle);
  const headerSubtitleEsc = escapeHtmlForEmail(headerSubtitle);
  const teamNameEsc = escapeHtmlForEmail(teamName);
  const footerDateEsc = escapeHtmlForEmail(footerDate);
  const venueLineEsc = escapeHtmlForEmail(venueLine);
  const contactHeadingEsc = escapeHtmlForEmail(contactHeading);

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${titleEsc}</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table role="presentation" style="width: 100%; border-collapse: collapse; background-color: #f5f5f5; padding: 12px;">
    <tr>
      <td align="center" style="padding: 12px 0;">
        <table role="presentation" style="width: 100%; max-width: 600px; border-collapse: collapse; background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <!-- Header: logo (optional) + title on blue; theme/verse in a compact high-contrast strip (some clients drop td backgrounds, which made white/light text invisible on white). -->
          <tr>
            <td bgcolor="#2563eb" align="center" style="background-color: #2563eb; background-image: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); padding: ${bannerOk ? '10px 16px 12px' : '14px 16px 14px'}; text-align: center; border-radius: ${headerBarTopRadius} ${headerBarTopRadius} 0 0;">
              ${logoBlock}
              <h1 style="margin: 0; ${bannerOk ? 'margin-top: 4px; ' : ''}color: #ffffff; font-size: 24px; font-weight: bold; line-height: 1.25;"><font color="#ffffff" style="color: #ffffff;">${headerTitleEsc}</font></h1>
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin: 6px auto 0; border-collapse: collapse; width: 100%; max-width: 560px;">
                <tr>
                  <td bgcolor="#f1f5f9" align="center" style="background-color: #f1f5f9; padding: 8px 12px; border-radius: 6px; mso-padding-alt: 8px 12px;">
                    <span style="color: #0f172a; font-size: 15px; font-weight: 700; line-height: 1.45; font-family: Arial, Helvetica, sans-serif;"><font color="#0f172a" style="color: #0f172a;">${headerSubtitleEsc}</font></span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Content -->
          <tr>
            <td style="padding: 22px 22px 24px;">
              ${content}
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="background-color: #f9fafb; padding: 16px 16px; text-align: center; border-radius: 0 0 8px 8px; border-top: 1px solid #e5e7eb;">
              <p style="margin: 0 0 10px 0; color: #6b7280; font-size: 14px; line-height: 1.6;">
                <strong style="color: #374151;">${teamNameEsc}</strong><br>
                ${footerDateEsc ? `${footerDateEsc}<br>` : ''}
                ${venueLineEsc}
              </p>
              <div style="margin: 14px 0; padding: 14px 16px; background-color: #ffffff; border-radius: 6px; border: 1px solid #e5e7eb;">
                <p style="margin: 0 0 12px 0; color: #374151; font-size: 14px; font-weight: 600;">
                  ${contactHeadingEsc}
                </p>
                <p style="margin: 0 0 8px 0; color: #6b7280; font-size: 13px; line-height: 1.8;">
                  <strong style="color: #3b82f6;">Email:</strong> <a href="${mailtoHref}" style="color: #3b82f6; text-decoration: none;">${supportEsc}</a><br>
                  <strong style="color: #3b82f6;">Phone:</strong>
                </p>
                <div style="margin: 8px 0 0 0; color: #6b7280; font-size: 13px; line-height: 1.8; text-align: left;">
                  ${contactsHtml}
                </div>
              </div>
              <p style="margin: 15px 0 0 0; color: #9ca3af; font-size: 12px;">
                This is an automated email. Please do not reply to this message.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
};

// Helper function to format email content sections
const formatEmailSection = (title, items) => {
  const itemsHtml = items.map(item => 
    `<tr><td style="padding: 6px 0; border-bottom: 1px solid #e5e7eb;"><strong style="color: #374151; min-width: 150px; display: inline-block;">${item.label}:</strong> <span style="color: #6b7280;">${item.value}</span></td></tr>`
  ).join('');
  
  return `
    <h2 style="margin: 0 0 12px 0; color: #1f2937; font-size: 18px; font-weight: 600; border-bottom: 2px solid #3b82f6; padding-bottom: 6px;">${title}</h2>
    <table role="presentation" style="width: 100%; border-collapse: collapse; margin-bottom: 16px;">
      ${itemsHtml}
    </table>
  `;
};

const sendEmail = async (to, subject, body, qrCode = null, cc = null) => {
  try {
    console.log('📧 Attempting to send email via:', EMAIL_API_URL);
    console.log('📧 Email parameters:', {
      to: to || '(empty)',
      cc: cc || '(none)',
      subject: subject || '(empty)',
      bodyLength: body ? body.length : 0,
      hasQrCode: !!qrCode
    });
    
    // Validate required fields
    if (!to || !subject || !body) {
      const missing = [];
      if (!to) missing.push('to');
      if (!subject) missing.push('subject');
      if (!body) missing.push('body');
      throw new Error(`Missing required email fields: ${missing.join(', ')}`);
    }
    
    // Build email payload - only include qrCode if it has a value
    const emailPayload = {
        to,
        subject,
        body,
        // Optional: HTML body support
        htmlBody: body, // Use HTML body directly (already formatted)
    };
    
    // Only add qrCode field if it has a value (not null/undefined)
    if (qrCode) {
      emailPayload.qrCode = qrCode;
    }
    
    // Add CC field if provided
    if (cc) {
      emailPayload.cc = cc;
    }
    
    console.log('📧 Sending email payload:', JSON.stringify(emailPayload).substring(0, 200) + '...');
    
    const response = await fetch(`${EMAIL_API_URL}/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailPayload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const base = errorData.error || errorData.details || `HTTP ${response.status}: Failed to send email`;
      const transportHint =
        errorData.transport && typeof errorData.transport === 'string'
          ? ` (API transport: ${errorData.transport})`
          : '';
      const errorMessage = `${base}${transportHint}`;
      console.error('❌ Email API error:', errorMessage, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('✅ Email sent successfully:', to, 'Subject:', subject);
    if (result.transport || result.fromAddress) {
      console.log('   API transport / from:', result.transport || '(unspecified)', '/', result.fromAddress || '(unspecified)');
    }
    if (result.qrCodeIncluded) {
      console.log('   QR code included in email:', result.qrCodeIncluded);
    }
    return { success: true, ...result };
  } catch (error) {
    // Check if it's a network error (CORS, connection refused, etc.)
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      console.error('❌ Network error - Is the backend server running?');
      console.error('   Make sure to run: node server.js (in backend directory)');
    }
    console.error('❌ Error sending email:', error);
    console.error('Error details:', error.message);
    
    // If response has error details, log them
    if (error.response) {
      error.response.json().then(data => {
        console.error('Backend error response:', data);
      }).catch(() => {});
    }
    
    // Don't fail silently - log the error for debugging
    return { success: false, error: error.message };
  }
};

// Main App Component
export default function CampManagementApp() {
  const [currentView, setCurrentView] = useState('landing');
  const [userRole, setUserRole] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [lodges, setLodges] = useState([]);
  const [selectedRegistrationType, setSelectedRegistrationType] = useState(null);
  const [selfCheckInEnabled, setSelfCheckInEnabled] = useState(false);
  const [cardPaymentEnabled, setCardPaymentEnabled] = useState(true); // Default to enabled
  const [paypalEnabled, setPaypalEnabled] = useState(true); // Default to enabled
  const [venmoEnabled, setVenmoEnabled] = useState(true); // Default to enabled
  const [zelleEnabled, setZelleEnabled] = useState(true); // Default to enabled
  const [registrationsOpen, setRegistrationsOpen] = useState(true);
  const [familyRegistrationsOpen, setFamilyRegistrationsOpen] = useState(true);
  const [adultRegistrationsOpen, setAdultRegistrationsOpen] = useState(true);
  const [dayRegistrationsOpen, setDayRegistrationsOpen] = useState(true);
  const [transportationEnabled, setTransportationEnabled] = useState(false);
  const [welcomePackageEnabled, setWelcomePackageEnabled] = useState(true);
  const [roomAttendanceEnabled, setRoomAttendanceEnabled] = useState(false);
  const [campSettings, setCampSettings] = useState(() => ({ ...DEFAULT_CAMP_SETTINGS }));
  const [campSettingsLoaded, setCampSettingsLoaded] = useState(false);
  const [pricingSettings, setPricingSettings] = useState(() => ({ ...DEFAULT_PRICING_SETTINGS }));
  const [pricingSettingsLoaded, setPricingSettingsLoaded] = useState(false);
  const [emailSettings, setEmailSettings] = useState(() => ({ ...DEFAULT_EMAIL_SETTINGS }));
  const [emailSettingsLoaded, setEmailSettingsLoaded] = useState(false);
  const [feeWaiverSettings, setFeeWaiverSettings] = useState(() => ({ ...DEFAULT_FEE_WAIVER_SETTINGS }));
  const [feeWaiverSettingsLoaded, setFeeWaiverSettingsLoaded] = useState(false);
  const [blockInitialRegistrationReceivedEmail, setBlockInitialRegistrationReceivedEmail] = useState(false);
  const [earlyBirdStudentEnabled, setEarlyBirdStudentEnabled] = useState(false);
  const [maxGeneralSeats, setMaxGeneralSeats] = useState(500);
  const [maxFamilySeats, setMaxFamilySeats] = useState(100);
  const [maxMiddleSchoolSeats, setMaxMiddleSchoolSeats] = useState(200);
  const [maxHighSchoolSeats, setMaxHighSchoolSeats] = useState(200);
  const [maxCollegeSeats, setMaxCollegeSeats] = useState(100);
  const [maxFamilyRoomsSeats, setMaxFamilyRoomsSeats] = useState(100);
  const [settingLoaded, setSettingLoaded] = useState(false); // Track if setting has been loaded from API
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [showCampsiteFormFor, setShowCampsiteFormFor] = useState(null); // Registration ID for campsite (Trinity Pines) form before check-in
  const [adminCampsiteFormFor, setAdminCampsiteFormFor] = useState(null); // Registration ID for campsite form in admin check-in view
  const [adminIcpfWaiverFor, setAdminIcpfWaiverFor] = useState(null); // Registration ID for ICPF waiver form in admin check-in view
  const [checkRegistrationCampsiteFormFor, setCheckRegistrationCampsiteFormFor] = useState(null); // For "Check Registration Status" view
  const [checkRegistrationIcpfWaiverFor, setCheckRegistrationIcpfWaiverFor] = useState(null);
  const [showIcpfWaiverFor, setShowIcpfWaiverFor] = useState(null); // ICPF waiver after campsite in Self Check-In
  const [campsiteWizardQueue, setCampsiteWizardQueue] = useState(null); // [regId, ...] for sequential family forms
  const [campsiteWizardPhase, setCampsiteWizardPhase] = useState('campsite'); // 'campsite' | 'icpf'
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Base path for URL routing (supports deployments at subpaths)
  const basePath = (process.env.PUBLIC_URL || '').replace(/\/$/, '') || '';
  const welcomePackagePath = basePath + '/welcome-package';
  const selfCheckInPath = basePath + '/self-check-in';
  const transportationPath = basePath + '/transportation';
  const roomAttendancePath = basePath + '/room-attendance';

  // Check URL on initial load and on popstate (browser back/forward)
  useEffect(() => {
    const syncViewFromUrl = () => {
      const pathname = window.location.pathname;
      if (pathname === welcomePackagePath || pathname.endsWith('/welcome-package')) {
        if (welcomePackageEnabled) {
          setCurrentView('welcomePackage');
        } else {
          setCurrentView('landing');
          window.history.replaceState({}, '', basePath || '/');
        }
        return;
      }
      // PDF URL that got SPA fallback (404): show Welcome Package view instead of landing
      if (pathname.includes('/welcome-package-docs/')) {
        if (welcomePackageEnabled) {
          setCurrentView('welcomePackage');
        } else {
          setCurrentView('landing');
          window.history.replaceState({}, '', basePath || '/');
        }
        return;
      }
      if (pathname === selfCheckInPath || pathname.endsWith('/self-check-in')) {
        setCurrentView('selfCheckIn');
        return;
      }
      if (pathname === transportationPath || pathname.endsWith('/transportation')) {
        setCurrentView('transportationForm');
        return;
      }
      if (pathname === roomAttendancePath || pathname.endsWith('/room-attendance')) {
        setCurrentView('roomAttendance');
        return;
      }
      const urlParams = new URLSearchParams(window.location.search);
      const registrationCode = urlParams.get('code');
      if (registrationCode) {
        setCurrentView('publicRegistration');
        setSelectedRegistrationType(registrationCode);
      } else {
        setCurrentView('landing');
      }
    };

    syncViewFromUrl();
    window.addEventListener('popstate', syncViewFromUrl);
    return () => window.removeEventListener('popstate', syncViewFromUrl);
  }, [welcomePackagePath, selfCheckInPath, transportationPath, roomAttendancePath, welcomePackageEnabled, basePath]);

  // If Welcome Package is turned off, leave the standalone route and return to home
  useEffect(() => {
    if (!settingLoaded) return;
    if (!welcomePackageEnabled && currentView === 'welcomePackage') {
      setCurrentView('landing');
      const p = window.location.pathname;
      if (p === welcomePackagePath || p.endsWith('/welcome-package') || p.includes('/welcome-package-docs/')) {
        window.history.replaceState({}, '', basePath || '/');
      }
    }
  }, [welcomePackageEnabled, settingLoaded, currentView, welcomePackagePath, basePath]);

  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [currentView]);

  // Load data from API on mount
  // Settings load separately so Camp Registration Form can enable even if registrations/lodges fail
  useEffect(() => {
    const loadSettings = async () => {
      try {
          // Load self check-in setting
          console.log('🔍 [Main App] Loading selfCheckInEnabled from API...');
          const selfCheckInSetting = await settingsAPI.get('selfCheckInEnabled');
          console.log('📥 [Main App] Received setting:', selfCheckInSetting);
          const isEnabled = selfCheckInSetting.value === 'true' || selfCheckInSetting.value === true || selfCheckInSetting.value === 'True' || selfCheckInSetting.value === '1' || selfCheckInSetting.value === 1;
          console.log('✅ [Main App] Loaded selfCheckInEnabled:', isEnabled, 'raw value:', selfCheckInSetting.value, 'type:', typeof selfCheckInSetting.value);
          setSelfCheckInEnabled(isEnabled);
          
          // Load registrations open setting
          try {
            const regOpenSetting = await settingsAPI.get('registrationsOpen');
            const isOpen = regOpenSetting.value === 'true' || regOpenSetting.value === true || regOpenSetting.value === 'True' || regOpenSetting.value === '1' || regOpenSetting.value === 1;
            setRegistrationsOpen(isOpen);
          } catch (e) {
            setRegistrationsOpen(true); // Default to open
          }
          
          // Load early bird student enabled setting
          try {
            const earlyBirdSetting = await settingsAPI.get('earlyBirdStudentEnabled');
            const isEnabled = earlyBirdSetting.value === 'true' || earlyBirdSetting.value === true || earlyBirdSetting.value === 'True' || earlyBirdSetting.value === '1' || earlyBirdSetting.value === 1;
            setEarlyBirdStudentEnabled(isEnabled);
          } catch (e) {
            setEarlyBirdStudentEnabled(false); // Default to disabled
          }
          
          // Load card payment enabled setting
          try {
            const cardPaymentSetting = await settingsAPI.get('cardPaymentEnabled');
            const isEnabled = cardPaymentSetting.value === 'true' || cardPaymentSetting.value === true || cardPaymentSetting.value === 'True' || cardPaymentSetting.value === '1' || cardPaymentSetting.value === 1;
            setCardPaymentEnabled(isEnabled);
          } catch (e) {
            setCardPaymentEnabled(true); // Default to enabled
          }
          
          // Load PayPal enabled setting
          try {
            const paypalSetting = await settingsAPI.get('paypalEnabled');
            const isEnabled = paypalSetting.value === 'true' || paypalSetting.value === true || paypalSetting.value === 'True' || paypalSetting.value === '1' || paypalSetting.value === 1;
            setPaypalEnabled(isEnabled);
          } catch (e) {
            setPaypalEnabled(true); // Default to enabled
          }
          
          // Load Venmo enabled setting
          try {
            const venmoSetting = await settingsAPI.get('venmoEnabled');
            const isEnabled = venmoSetting.value === 'true' || venmoSetting.value === true || venmoSetting.value === 'True' || venmoSetting.value === '1' || venmoSetting.value === 1;
            setVenmoEnabled(isEnabled);
          } catch (e) {
            setVenmoEnabled(true); // Default to enabled
          }

          try {
            const zelleSetting = await settingsAPI.get('zelleEnabled');
            const isZelleOn =
              zelleSetting.value === 'true' ||
              zelleSetting.value === true ||
              zelleSetting.value === 'True' ||
              zelleSetting.value === '1' ||
              zelleSetting.value === 1;
            setZelleEnabled(isZelleOn);
          } catch (e) {
            setZelleEnabled(true);
          }
          
          // Load transportation enabled setting (used on landing page and dashboard)
          try {
            const transportationSetting = await settingsAPI.get('transportationEnabled');
            const isTransportEnabled = transportationSetting.value === 'true' || transportationSetting.value === true || transportationSetting.value === 'True' || transportationSetting.value === '1' || transportationSetting.value === 1;
            setTransportationEnabled(isTransportEnabled);
          } catch (e) {
            setTransportationEnabled(false); // Default to disabled
          }

          try {
            const wpSetting = await settingsAPI.get('welcomePackageEnabled');
            const rawWp = wpSetting?.value;
            const isWpOff = rawWp === 'false' || rawWp === false || rawWp === 'False' || rawWp === '0' || rawWp === 0;
            setWelcomePackageEnabled(!isWpOff);
          } catch (e) {
            setWelcomePackageEnabled(true);
          }

          // Load room attendance enabled (Lodging section toggle)
          try {
            const roomAttSetting = await settingsAPI.get('roomAttendanceEnabled');
            const isRoomAttEnabled = roomAttSetting.value === 'true' || roomAttSetting.value === true || roomAttSetting.value === 'True' || roomAttSetting.value === '1' || roomAttSetting.value === 1;
            setRoomAttendanceEnabled(isRoomAttEnabled);
          } catch (e) {
            setRoomAttendanceEnabled(false);
          }

          // For JSON-object settings we deliberately do NOT reset state to
          // defaults on a transient API failure: a stale defaults state could
          // be persisted back if an admin then clicks Save. We only update
          // state on a successful response, and surface a "loaded" flag so the
          // Camp Settings page can refuse to save until the real persisted
          // values are in hand.
          try {
            const campSetting = await settingsAPI.get('campSettings');
            const merged = mergeCampSettings(campSetting.value);
            setCampSettings(merged);
            setCampSettingsLoaded(true);
            setEmailTemplateCampSettings(merged);
          } catch (e) {
            console.error('Failed to load campSettings (keeping previous state):', e);
          }

          try {
            const pricingSetting = await settingsAPI.get('pricingSettings');
            setPricingSettings(mergePricingSettings(pricingSetting.value));
            setPricingSettingsLoaded(true);
          } catch (e) {
            console.error('Failed to load pricingSettings (keeping previous state):', e);
          }

          try {
            const emailSetting = await settingsAPI.get('emailSettings');
            setEmailSettings(mergeEmailSettings(emailSetting.value));
            setEmailSettingsLoaded(true);
          } catch (e) {
            console.error('Failed to load emailSettings (keeping previous state):', e);
          }

          try {
            const fwSetting = await settingsAPI.get('feeWaiverSettings');
            setFeeWaiverSettings(mergeFeeWaiverSettings(fwSetting.value));
            setFeeWaiverSettingsLoaded(true);
          } catch (e) {
            console.error('Failed to load feeWaiverSettings (keeping previous state):', e);
          }

          // Load family registrations open setting
          try {
            const familyRegSetting = await settingsAPI.get('familyRegistrationsOpen');
            const isFamilyOpen = familyRegSetting.value === 'true' || familyRegSetting.value === true || familyRegSetting.value === 'True' || familyRegSetting.value === '1' || familyRegSetting.value === 1;
            setFamilyRegistrationsOpen(isFamilyOpen);
          } catch (e) {
            setFamilyRegistrationsOpen(true); // Default to open
          }
          
          // Load adult registrations open setting
          try {
            const adultRegSetting = await settingsAPI.get('adultRegistrationsOpen');
            const isAdultOpen = adultRegSetting.value === 'true' || adultRegSetting.value === true || adultRegSetting.value === 'True' || adultRegSetting.value === '1' || adultRegSetting.value === 1;
            setAdultRegistrationsOpen(isAdultOpen);
          } catch (e) {
            setAdultRegistrationsOpen(true); // Default to open
          }
          
          // Load day registrations open setting (default: on - only treat as closed when explicitly false)
          try {
            const dayRegSetting = await settingsAPI.get('dayRegistrationsOpen');
            const raw = dayRegSetting?.value;
            const isDayClosed = raw === 'false' || raw === false || raw === 'False' || raw === '0' || raw === 0;
            setDayRegistrationsOpen(!isDayClosed);
          } catch (e) {
            setDayRegistrationsOpen(true); // Default to on
          }
          
          // Load block initial registration received email (temporary: avoid sending wrong code if duplicate assigned)
          try {
            const blockEmailSetting = await settingsAPI.get('blockInitialRegistrationReceivedEmail');
            const isBlocked = blockEmailSetting.value === 'true' || blockEmailSetting.value === true || blockEmailSetting.value === 'True' || blockEmailSetting.value === '1' || blockEmailSetting.value === 1;
            setBlockInitialRegistrationReceivedEmail(isBlocked);
          } catch (e) {
            setBlockInitialRegistrationReceivedEmail(false); // Default: send email as before
          }
          
          // Load capacity settings
          try {
            const maxGeneralSetting = await settingsAPI.get('maxGeneralSeats');
            if (maxGeneralSetting && maxGeneralSetting.value) {
              setMaxGeneralSeats(parseInt(maxGeneralSetting.value) || 500);
            }
          } catch (e) {
            // Use default
          }
          
          try {
            const maxFamilySetting = await settingsAPI.get('maxFamilySeats');
            if (maxFamilySetting && maxFamilySetting.value) {
              setMaxFamilySeats(parseInt(maxFamilySetting.value) || 100);
            }
          } catch (e) {
            // Use default
          }
          
          // Load category seat limits
          try {
            const maxMiddleSchoolSetting = await settingsAPI.get('maxMiddleSchoolSeats');
            if (maxMiddleSchoolSetting && maxMiddleSchoolSetting.value) {
              setMaxMiddleSchoolSeats(parseInt(maxMiddleSchoolSetting.value) || 200);
            }
          } catch (e) {
            // Use default
          }
          
          try {
            const maxHighSchoolSetting = await settingsAPI.get('maxHighSchoolSeats');
            if (maxHighSchoolSetting && maxHighSchoolSetting.value) {
              setMaxHighSchoolSeats(parseInt(maxHighSchoolSetting.value) || 200);
            }
          } catch (e) {
            // Use default
          }
          
          try {
            const maxCollegeSetting = await settingsAPI.get('maxCollegeSeats');
            if (maxCollegeSetting && maxCollegeSetting.value) {
              setMaxCollegeSeats(parseInt(maxCollegeSetting.value) || 100);
            }
          } catch (e) {
            // Use default
          }
          
          try {
            const maxFamilyRoomsSetting = await settingsAPI.get('maxFamilyRoomsSeats');
            if (maxFamilyRoomsSetting && maxFamilyRoomsSetting.value) {
              setMaxFamilyRoomsSeats(parseInt(maxFamilyRoomsSetting.value) || 100);
            }
          } catch (e) {
            // Use default
          }
          
          setSettingLoaded(true); // Mark as loaded so future changes will be saved
        } catch (error) {
          // Setting doesn't exist yet, use default
          console.log('⚠️ [Main App] Settings not found, using defaults');
          console.error('Error loading settings:', error);
          setSelfCheckInEnabled(false); // Explicitly set to false if not found
          setRegistrationsOpen(true); // Default to open
          setDayRegistrationsOpen(true); // Default to on for Day registration
          setSettingLoaded(true); // Mark as loaded even if setting doesn't exist
        }
      };

    const loadMainData = async () => {
      try {
        const [registrationsData, lodgesData] = await Promise.all([
          registrationsAPI.getAll(),
          lodgesAPI.getAll()
        ]);
        setRegistrations(registrationsData);
        setLodges(lodgesData);
      } catch (error) {
        console.error('Error loading registrations/lodges:', error);
        alert('Failed to load data. Please check if the backend server is running.');
      }
    };

    // Load settings first (enables Camp Registration Form for users); then main data
    loadSettings().then(() => loadMainData());
  }, []);

  // Save settings to API when they change — but never on the hydration pass.
  //
  // Each save effect below has `settingLoaded` in its dependency array, so all
  // of them fire once when `settingLoaded` flips true after the initial load.
  // That first run only echoes whatever the load left in state; writing it back
  // meant every page view (by ANY visitor, not just admins) re-persisted all
  // toggles, and if a settings GET failed transiently the fallback default
  // (e.g. cardPaymentEnabled=true, welcomePackageEnabled=true) was written
  // over the admin's saved value — toggles appeared to "turn on by themselves".
  // persistSetting skips that first post-load echo per key and only writes on
  // real changes (i.e. an admin actually flipping a toggle).
  const hydratedSettingKeysRef = useRef(new Set());
  const persistSetting = (key, value, failureAlert) => {
    if (!settingLoaded || value === undefined) return;
    if (!hydratedSettingKeysRef.current.has(key)) {
      hydratedSettingKeysRef.current.add(key);
      return;
    }
    console.log(`💾 [Main App] Saving ${key} to API:`, value);
    settingsAPI.set(key, value.toString()).catch((error) => {
      console.error(`❌ [Main App] Error saving ${key}:`, error);
      if (failureAlert) alert(failureAlert);
    });
  };

  useEffect(() => {
    persistSetting('selfCheckInEnabled', selfCheckInEnabled, 'Failed to save check-in setting. Please try again.');
  }, [selfCheckInEnabled, settingLoaded]);

  useEffect(() => {
    persistSetting('registrationsOpen', registrationsOpen, 'Failed to save "Registrations open" setting. It may revert after refresh. Please try again or check the server.');
  }, [registrationsOpen, settingLoaded]);

  useEffect(() => {
    persistSetting('maxGeneralSeats', maxGeneralSeats);
  }, [maxGeneralSeats, settingLoaded]);

  useEffect(() => {
    persistSetting('maxFamilySeats', maxFamilySeats);
  }, [maxFamilySeats, settingLoaded]);

  useEffect(() => {
    persistSetting('earlyBirdStudentEnabled', earlyBirdStudentEnabled);
  }, [earlyBirdStudentEnabled, settingLoaded]);

  useEffect(() => {
    persistSetting('maxMiddleSchoolSeats', maxMiddleSchoolSeats);
  }, [maxMiddleSchoolSeats, settingLoaded]);

  useEffect(() => {
    persistSetting('maxHighSchoolSeats', maxHighSchoolSeats);
  }, [maxHighSchoolSeats, settingLoaded]);

  useEffect(() => {
    persistSetting('maxCollegeSeats', maxCollegeSeats);
  }, [maxCollegeSeats, settingLoaded]);

  useEffect(() => {
    persistSetting('maxFamilyRoomsSeats', maxFamilyRoomsSeats);
  }, [maxFamilyRoomsSeats, settingLoaded]);

  useEffect(() => {
    persistSetting('cardPaymentEnabled', cardPaymentEnabled);
  }, [cardPaymentEnabled, settingLoaded]);

  useEffect(() => {
    persistSetting('paypalEnabled', paypalEnabled);
  }, [paypalEnabled, settingLoaded]);

  useEffect(() => {
    persistSetting('venmoEnabled', venmoEnabled);
  }, [venmoEnabled, settingLoaded]);

  useEffect(() => {
    persistSetting('zelleEnabled', zelleEnabled);
  }, [zelleEnabled, settingLoaded]);

  useEffect(() => {
    persistSetting('transportationEnabled', transportationEnabled, 'Failed to save "Transportation" setting. It may revert after refresh. Please try again or check the server.');
  }, [transportationEnabled, settingLoaded]);

  useEffect(() => {
    persistSetting('welcomePackageEnabled', welcomePackageEnabled, 'Failed to save Welcome Package setting. It may revert after refresh.');
  }, [welcomePackageEnabled, settingLoaded]);

  useEffect(() => {
    persistSetting('roomAttendanceEnabled', roomAttendanceEnabled);
  }, [roomAttendanceEnabled, settingLoaded]);

  useEffect(() => {
    persistSetting('familyRegistrationsOpen', familyRegistrationsOpen);
  }, [familyRegistrationsOpen, settingLoaded]);

  useEffect(() => {
    persistSetting('adultRegistrationsOpen', adultRegistrationsOpen);
  }, [adultRegistrationsOpen, settingLoaded]);

  useEffect(() => {
    persistSetting('dayRegistrationsOpen', dayRegistrationsOpen);
  }, [dayRegistrationsOpen, settingLoaded]);

  useEffect(() => {
    persistSetting('blockInitialRegistrationReceivedEmail', blockInitialRegistrationReceivedEmail);
  }, [blockInitialRegistrationReceivedEmail, settingLoaded]);

  const navigation = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, requiredRole: ROLES.VIEWER },
    { id: 'register', label: 'Register', icon: Users },
    { id: 'checkin', label: 'Check-In', icon: QrCode, requiredRole: ROLES.VIEWER },
    { 
      id: 'allRegistrations', 
      label: 'All Registrations', 
      icon: ListChecks, 
      requiredRole: ROLES.VIEWER,
      children: [
        { id: 'registrations', label: 'Registrations', icon: ListChecks, requiredRole: ROLES.VIEWER },
        { id: 'deletedRegistrations', label: 'Deleted Registrations', icon: Trash2, requiredRole: ROLES.VIEWER }
      ]
    },
    { 
      id: 'lodging', 
      label: 'Lodging', 
      icon: Bed, 
      requiredRole: ROLES.VIEWER,
      children: [
        { id: 'lodges', label: 'Lodges', icon: Bed, requiredRole: ROLES.VIEWER },
        { id: 'roomAssignment', label: 'Room Assignment', icon: Bed, requiredRole: ROLES.VIEWER },
        { id: 'roomAttendanceReport', label: 'Room Attendance', icon: ClipboardList, requiredRole: ROLES.ROOM_ADMIN }
      ]
    },
    { id: 'badges', label: 'Badges', icon: Printer, requiredRole: ROLES.VIEWER },
    { id: 'export', label: 'Export', icon: Download, requiredRole: ROLES.VIEWER },
    { id: 'bulkEmail', label: 'Bulk Email', icon: Mail, requiredRole: ROLES.ROOM_ADMIN },
    { id: 'scheduleBuilder', label: 'Schedule Builder', icon: Calendar, requiredRole: ROLES.VIEWER },
    { id: 'transportation', label: 'Transportation', icon: Bus, requiredRole: ROLES.TRANSPORTATION_ADMIN },
    { id: 'campSettings', label: 'Camp Settings', icon: Settings, requiredRole: ROLES.REGISTRATION_ADMIN },
    { id: 'users', label: 'User Management', icon: Shield, requiredRole: ROLES.APPLICATION_ADMIN },
  ];

  const handleAdminLogin = async (username, password) => {
    try {
      const result = await usersAPI.login(username, password);
      console.log('Login result:', result);
      if (result.success && result.user) {
        setUserRole(result.user.role);
        
        // Check if user has temporary password - check both in user object and top-level
        const hasTemporaryPassword = result.user.temporaryPassword === true || result.temporaryPassword === true;
        console.log('Temporary password check:', {
          userTemporaryPassword: result.user.temporaryPassword,
          resultTemporaryPassword: result.temporaryPassword,
          hasTemporaryPassword: hasTemporaryPassword
        });
        
        if (hasTemporaryPassword) {
          // Store user info temporarily to show password change modal
          // DO NOT set isLoggedIn to true yet - user must change password first
          // DO NOT navigate to dashboard - keep user on login page with modal
          setCurrentUser(result.user);
          setShowPasswordChangeModal(true);
          // Keep user on login view - modal will block everything
          // Clear the login form by returning success but not setting isLoggedIn
          return { success: true, requiresPasswordChange: true };
        }
        
        // Only set logged in and navigate to dashboard if password is not temporary
        setIsLoggedIn(true);
        setCurrentView('dashboard');
        return { success: true, requiresPasswordChange: false };
      }
      return { success: false, requiresPasswordChange: false };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, requiresPasswordChange: false };
    }
  };

  const handleUserUpdate = () => {
    // Trigger re-render by updating a dummy state
    // This ensures UserManagement component reloads users from localStorage
  };

  const handleLogout = () => {
    setUserRole(null);
    setIsLoggedIn(false);
    setCurrentView('landing');
  };

  const handleUpdateRegistrationStatus = async (registrationId, newStatus) => {
    try {
      await registrationsAPI.update(registrationId, { status: newStatus });
      // Reload registrations to get latest data
      const updatedRegistrations = await registrationsAPI.getAll();
      setRegistrations(updatedRegistrations);
    } catch (error) {
      console.error('Error updating registration status:', error);
      alert('Failed to update registration status. Please try again.');
    }
  };

  const handleUpdateRegistration = async (registrationId, updatedData) => {
    try {
      // Calculate full name
      const registration = registrations.find(r => r.id === registrationId);
      const fullName = `${updatedData.firstName || registration?.firstName || ''} ${updatedData.middleName ? (updatedData.middleName + ' ') : (registration?.middleName ? registration.middleName + ' ' : '')}${updatedData.lastName || registration?.lastName || ''}`.trim();
      
      // Ensure partialPayment and remainingPayment are numbers, not strings
      const dataToUpdate = {
        ...updatedData,
        name: fullName,
        partialPayment: updatedData.partialPayment !== undefined && updatedData.partialPayment !== null 
          ? (typeof updatedData.partialPayment === 'string' ? parseFloat(updatedData.partialPayment) : updatedData.partialPayment) || 0 
          : (registration?.partialPayment !== undefined ? registration.partialPayment : 0),
        remainingPayment: updatedData.remainingPayment !== undefined && updatedData.remainingPayment !== null 
          ? (typeof updatedData.remainingPayment === 'string' ? parseFloat(updatedData.remainingPayment) : updatedData.remainingPayment) || 0 
          : (registration?.remainingPayment !== undefined ? registration.remainingPayment : 0),
      };
      
      console.log('🔄 Updating registration:', registrationId, 'with data:', dataToUpdate);
      
      await registrationsAPI.update(registrationId, dataToUpdate);
      
      // If this is a primary family member (familyPosition === 1), sync payment info to all family members
      if (registration?.familyCode && registration?.familyPosition === 1) {
        const familyMembers = registrations.filter(r => 
          r.familyCode === registration.familyCode && 
          r.id !== registrationId && 
          r.familyPosition !== 1
        );
        
        if (familyMembers.length > 0) {
          // Sync payment status, method, date, and confirmation to all family members
          // Partial payment is only tracked on primary member
          const paymentDataToSync = {
            paymentStatus: dataToUpdate.paymentStatus,
            paymentMethod: dataToUpdate.paymentMethod,
            paymentDate: dataToUpdate.paymentDate,
            paymentConfirmation: dataToUpdate.paymentConfirmation,
            // Non-primary members don't have their own partial payment - it's tracked on primary only
            partialPayment: 0,
          };
          
          // For each family member, set remaining payment to their full amount
          // (since partial payment is only tracked on primary)
          for (const member of familyMembers) {
            const memberAmount = parseFloat(member.amount) || 0;
            await registrationsAPI.update(member.id, {
              ...paymentDataToSync,
              remainingPayment: memberAmount,
            });
          }
        }
      }
      
      // Reload registrations to get latest data
      const updatedRegistrations = await registrationsAPI.getAll();
      console.log('✅ Reloaded registrations. Sample registration partialPayment:', updatedRegistrations.find(r => r.id === registrationId)?.partialPayment);
      setRegistrations(updatedRegistrations);
    } catch (error) {
      console.error('Error updating registration:', error);
      alert('Failed to update registration. Please try again.');
    }
  };

  const handleDeleteRegistration = async (registrationId) => {
    try {
      await registrationsAPI.delete(registrationId);
      // Reload registrations to get latest data (deleted ones won't show)
      const updatedRegistrations = await registrationsAPI.getAll();
      setRegistrations(updatedRegistrations);
      alert('Registration deleted successfully. It has been moved to the deleted container.');
    } catch (error) {
      console.error('Error deleting registration:', error);
      alert('Failed to delete registration. Please try again.');
    }
  };

  // Navigate helper: updates URL when going to shareable routes so direct links work
  const handleNavigate = (view) => {
    if (view === 'welcomePackage' && !welcomePackageEnabled) {
      return;
    }
    setCurrentView(view);
    if (view === 'welcomePackage') {
      window.history.pushState({}, '', welcomePackagePath);
    } else if (view === 'selfCheckIn') {
      window.history.pushState({}, '', selfCheckInPath);
    } else if (view === 'transportationForm') {
      window.history.pushState({}, '', transportationPath);
    } else if (view === 'roomAttendance') {
      window.history.pushState({}, '', roomAttendancePath);
    }
  };

  // Landing page view
  if (currentView === 'landing') {
    return (
      <LandingPage
        onNavigate={handleNavigate}
        selfCheckInEnabled={selfCheckInEnabled}
        registrationsOpen={registrationsOpen}
        dayRegistrationsOpen={dayRegistrationsOpen}
        settingLoaded={settingLoaded}
        registrations={registrations}
        maxGeneralSeats={maxGeneralSeats}
        maxFamilySeats={maxFamilySeats}
        transportationEnabled={transportationEnabled}
        roomAttendanceEnabled={roomAttendanceEnabled}
        welcomePackageEnabled={welcomePackageEnabled}
        campSettings={campSettings}
      />
    );
  }

  // Admin login view
  if (currentView === 'adminLogin' && !isLoggedIn) {
    // If password change modal is shown, don't show login form - only show modal
    if (showPasswordChangeModal && currentUser) {
    return (
          <PasswordChangeModal
            user={currentUser}
            onPasswordChanged={async () => {
              // Password changed successfully, now complete login
              setShowPasswordChangeModal(false);
              // Now set logged in and navigate to dashboard
              setIsLoggedIn(true);
              setCurrentView('dashboard');
              setCurrentUser(null);
            }}
            onCancel={() => {
            // User cancelled, reset everything and go back to login
              setShowPasswordChangeModal(false);
              setCurrentUser(null);
              setIsLoggedIn(false);
              setUserRole(null);
            // Stay on login page - will show login form again
          }}
        />
      );
    }
    
    // Show password reset modal if needed
    if (showPasswordResetModal) {
      return (
        <PasswordResetModal
          onClose={() => setShowPasswordResetModal(false)}
          onSuccess={() => {
            setShowPasswordResetModal(false);
            // Optionally show success message or redirect
          }}
        />
      );
    }
    
    // Show login form only when password change modal is not shown
    return (
      <AdminLogin 
        onLogin={handleAdminLogin} 
        onBack={() => setCurrentView('landing')}
        onForgotPassword={() => setShowPasswordResetModal(true)}
      />
    );
  }

  // Registration type selection view (guard: block only when no registration type is open; Day has its own toggle)
  if (currentView === 'selectRegistrationType') {
    const isAdmin = isLoggedIn && userRole !== null;
    const anyRegistrationOpen = registrationsOpen || dayRegistrationsOpen || familyRegistrationsOpen || adultRegistrationsOpen;
    if (!anyRegistrationOpen && !isAdmin) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Registrations Are Closed</h2>
            <p className="text-gray-600 mb-6">
              We are no longer accepting new registrations at this time.
            </p>
            <button
              onClick={() => setCurrentView('landing')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg"
            >
              Back to Home
            </button>
          </div>
        </div>
      );
    }
    return (
      <RegistrationTypeSelection
        onSelectType={(type) => {
          setSelectedRegistrationType(type);
          setCurrentView('register');
        }}
        onBack={() => setCurrentView('landing')}
        registrations={registrations}
        maxGeneralSeats={maxGeneralSeats}
        maxFamilySeats={maxFamilySeats}
        maxMiddleSchoolSeats={maxMiddleSchoolSeats}
        maxHighSchoolSeats={maxHighSchoolSeats}
        maxCollegeSeats={maxCollegeSeats}
        maxFamilyRoomsSeats={maxFamilyRoomsSeats}
        earlyBirdStudentEnabled={earlyBirdStudentEnabled}
        familyRegistrationsOpen={familyRegistrationsOpen}
        adultRegistrationsOpen={adultRegistrationsOpen}
        dayRegistrationsOpen={dayRegistrationsOpen}
        registrationsOpen={registrationsOpen}
        isAdmin={isAdmin}
        pricingSettings={pricingSettings}
        campSettings={campSettings}
      />
    );
  }

  // User registration lookup view
  if (currentView === 'checkRegistration') {
    if (checkRegistrationIcpfWaiverFor) {
      return (
        <ICPFWaiverForm
          registration={registrations.find(r => r.id === checkRegistrationIcpfWaiverFor)}
          onComplete={async () => {
            const id = checkRegistrationIcpfWaiverFor;
            setCheckRegistrationIcpfWaiverFor(null);
            try {
              await registrationsAPI.update(id, { waiverSigned: true, waiverSignedAt: new Date().toISOString(), checkedIn: true, checkInTime: new Date().toISOString() });
              const updatedRegistrations = await registrationsAPI.getAll();
              setRegistrations(updatedRegistrations);
              alert('Both forms submitted and check-in completed successfully!');
            } catch (error) {
              console.error('Error completing check-in:', error);
              alert('Failed to complete check-in. Please try again.');
            }
          }}
          onCancel={() => setCheckRegistrationIcpfWaiverFor(null)}
        />
      );
    }
    if (checkRegistrationCampsiteFormFor) {
      return (
        <CampsiteParticipantForm
          registration={registrations.find(r => r.id === checkRegistrationCampsiteFormFor)}
          onComplete={(id) => {
            setCheckRegistrationCampsiteFormFor(null);
            if (id) setCheckRegistrationIcpfWaiverFor(id);
          }}
          onCancel={() => setCheckRegistrationCampsiteFormFor(null)}
        />
      );
    }
    return (
      <CheckRegistrationStatus
        registrations={registrations}
        lodges={lodges}
        selfCheckInEnabled={selfCheckInEnabled}
        onStartCheckIn={(id) => setCheckRegistrationCampsiteFormFor(id)}
        onCheckIn={async (id) => {
          try {
            await registrationsAPI.update(id, { checkedIn: true, checkInTime: new Date().toISOString() });
            const updatedRegistrations = await registrationsAPI.getAll();
            setRegistrations(updatedRegistrations);
          } catch (error) {
            console.error('Error updating check-in:', error);
            alert('Failed to update check-in. Please try again.');
          }
        }}
        onBack={() => setCurrentView('landing')}
      />
    );
  }

  // Self Check-In view
  if (currentView === 'selfCheckIn') {
    // Sequential family wizard: Campsite -> ICPF -> check-in per person, then next
    if (campsiteWizardQueue && campsiteWizardQueue.length > 0) {
      const currentId = campsiteWizardQueue[0];
      const currentRegistration = registrations.find(r => r.id === currentId);
      const queueLength = campsiteWizardQueue.length;

      if (campsiteWizardPhase === 'campsite') {
        return (
          <div className="min-h-screen bg-gradient-to-br from-purple-500 to-purple-700 flex flex-col items-center p-4">
            <div className="w-full max-w-2xl mb-4 flex items-center justify-between text-white">
              <span className="text-sm font-medium">Family check-in: {queueLength} {queueLength === 1 ? 'person' : 'people'} remaining</span>
            </div>
            <div className="w-full max-w-2xl flex-1">
              <CampsiteParticipantForm
                registration={currentRegistration}
                onComplete={(id) => {
                  if (id) {
                    setCampsiteWizardPhase('icpf');
                  }
                }}
                onCancel={() => {
                  setCampsiteWizardQueue(null);
                  setCampsiteWizardPhase('campsite');
                }}
              />
            </div>
          </div>
        );
      }

      if (campsiteWizardPhase === 'icpf') {
        return (
          <div className="min-h-screen bg-gradient-to-br from-purple-500 to-purple-700 flex flex-col items-center p-4">
            <div className="w-full max-w-2xl mb-4 flex items-center justify-between text-white">
              <span className="text-sm font-medium">Family check-in: complete waiver for {currentRegistration?.name}</span>
            </div>
            <div className="w-full max-w-2xl flex-1">
              <ICPFWaiverForm
                registration={currentRegistration}
                onComplete={async () => {
                  try {
                    await registrationsAPI.update(currentId, {
                      waiverSigned: true,
                      waiverSignedAt: new Date().toISOString(),
                      checkedIn: true,
                      checkInTime: new Date().toISOString(),
                    });
                    const updatedRegistrations = await registrationsAPI.getAll();
                    setRegistrations(updatedRegistrations);
                    const nextQueue = campsiteWizardQueue.slice(1);
                    setCampsiteWizardQueue(nextQueue.length > 0 ? nextQueue : null);
                    setCampsiteWizardPhase('campsite');
                    if (nextQueue.length === 0) {
                      alert(`All ${queueLength} family member(s) have been checked in successfully!`);
                    }
                  } catch (error) {
                    console.error('Error completing check-in:', error);
                    alert('Failed to complete check-in. Please try again.');
                  }
                }}
                onCancel={() => {
                  setCampsiteWizardQueue(null);
                  setCampsiteWizardPhase('campsite');
                }}
              />
            </div>
          </div>
        );
      }
    }

    if (showIcpfWaiverFor) {
      return (
        <ICPFWaiverForm
          registration={registrations.find(r => r.id === showIcpfWaiverFor)}
          onComplete={async () => {
            const id = showIcpfWaiverFor;
            setShowIcpfWaiverFor(null);
            try {
              await registrationsAPI.update(id, { waiverSigned: true, waiverSignedAt: new Date().toISOString(), checkedIn: true, checkInTime: new Date().toISOString() });
              const updatedRegistrations = await registrationsAPI.getAll();
              setRegistrations(updatedRegistrations);
              alert('Both forms submitted and check-in completed successfully!');
              setCurrentView('landing');
            } catch (error) {
              console.error('Error completing check-in:', error);
              alert('Failed to complete check-in. Please try again.');
            }
          }}
          onCancel={() => setShowIcpfWaiverFor(null)}
        />
      );
    }
    if (showCampsiteFormFor) {
      return (
        <CampsiteParticipantForm
          registration={registrations.find(r => r.id === showCampsiteFormFor)}
          onComplete={(id) => {
            setShowCampsiteFormFor(null);
            if (id) setShowIcpfWaiverFor(id);
          }}
          onCancel={() => setShowCampsiteFormFor(null)}
        />
      );
    }
    
    return (
      <SelfCheckIn
        registrations={registrations}
        selfCheckInEnabled={selfCheckInEnabled}
        onStartCheckIn={(id) => setShowCampsiteFormFor(id)}
        onStartCheckInWizard={(registrationIds) => {
          if (registrationIds && registrationIds.length > 0) {
            setCampsiteWizardQueue(registrationIds);
            setCampsiteWizardPhase('campsite');
          }
        }}
        onCheckIn={async (id) => {
          try {
            await registrationsAPI.update(id, { checkedIn: true, checkInTime: new Date().toISOString() });
            const updatedRegistrations = await registrationsAPI.getAll();
            setRegistrations(updatedRegistrations);
            alert('Check-in completed successfully!');
            setCurrentView('landing');
          } catch (error) {
            console.error('Error updating check-in:', error);
            alert('Failed to update check-in. Please try again.');
          }
        }}
        onBack={() => window.history.back()}
      />
    );
  }

  // Transportation Form view
  if (currentView === 'transportationForm') {
    return (
      <TransportationForm
        campSettings={campSettings}
        onBack={() => window.history.back()}
        onSuccess={async () => {
          alert('Transportation request submitted successfully! You will receive a confirmation email once your request is approved.');
          setCurrentView('landing');
          window.history.replaceState({}, '', basePath || '/');
        }}
      />
    );
  }

  // Room Attendance view (public - room leaders enter code and submit nightly attendance)
  if (currentView === 'roomAttendance') {
    return (
      <RoomAttendancePage
        onBack={() => {
          setCurrentView('landing');
          window.history.replaceState({}, '', basePath || '/');
        }}
      />
    );
  }

  // Welcome Package view (open, no auth - static PDF downloads)
  if (currentView === 'welcomePackage') {
    if (!welcomePackageEnabled) {
      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Welcome Package unavailable</h1>
            <p className="text-gray-600 mb-6">This resource is not available right now.</p>
            <button
              type="button"
              onClick={() => {
                setCurrentView('landing');
                window.history.replaceState({}, '', basePath || '/');
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to home
            </button>
          </div>
        </div>
      );
    }
    return (
      <WelcomePackageView onBack={() => window.history.back()} />
    );
  }

  // Public Registration Details View (accessed via QR code)
  if (currentView === 'publicRegistration') {
    return (
      <PublicRegistrationDetails
        registrationCode={selectedRegistrationType}
        registrations={registrations}
        lodges={lodges}
        selfCheckInEnabled={selfCheckInEnabled}
        onCheckIn={async (id) => {
          try {
            await registrationsAPI.update(id, { checkedIn: true, checkInTime: new Date().toISOString() });
            // Reload registrations to get latest data
            const updatedRegistrations = await registrationsAPI.getAll();
            setRegistrations(updatedRegistrations);
          } catch (error) {
            console.error('Error updating check-in:', error);
            alert('Failed to update check-in. Please try again.');
          }
        }}
        onBack={() => {
          setCurrentView('landing');
          // Clear URL parameter
          window.history.replaceState({}, document.title, window.location.pathname);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-600 text-white shadow-lg sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <h1 className="text-2xl font-bold truncate">ICPF USA Camp Portal</h1>
              {currentView === 'dashboard' && (
                <span className="hidden sm:inline-block text-lg font-semibold">Dashboard</span>
              )}
              <span className="hidden sm:inline-block text-sm bg-blue-700 px-3 py-1 rounded">
                {userRole ? getRoleDisplayName(userRole) : 'User'}
              </span>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-end">
              {/* Logo in header */}
              <img
                src={process.env.PUBLIC_URL + '/logo.png'}
                alt="ICPF Logo"
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  // Hide logo if not found
                  e.target.style.display = 'none';
                }}
              />
              {currentView === 'dashboard' && (
                <span className="sm:hidden text-base font-semibold">Dashboard</span>
              )}
              <span className="sm:hidden text-xs bg-blue-700 px-3 py-1 rounded">
                {userRole ? getRoleDisplayName(userRole) : 'User'}
              </span>
              {userRole && (
                <button
                  onClick={handleLogout}
                  className="text-xs bg-red-500 hover:bg-red-400 px-3 py-1 rounded"
                >
                  Logout
                </button>
              )}
              <button
                onClick={() => setCurrentView('landing')}
                className="text-xs bg-blue-500 hover:bg-blue-400 px-3 py-1 rounded"
              >
                Home
              </button>
              <button
                onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
                className="lg:hidden inline-flex items-center gap-2 text-xs bg-blue-500/80 hover:bg-blue-500 px-3 py-2 rounded transition"
                aria-label="Toggle navigation menu"
                aria-expanded={isMobileNavOpen}
              >
                {isMobileNavOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                <span>{isMobileNavOpen ? 'Close Menu' : 'Menu'}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Navigation */}
          <nav
            className={`${isMobileNavOpen ? 'block' : 'hidden'} lg:block w-full lg:w-64 bg-white rounded-lg shadow p-4 flex-shrink-0`}
          >
            <ul className="space-y-2">
              {navigation.map(item => {
                // Hide navigation items if they require a role and user doesn't have it
                if (item.requiredRole && (!userRole || !hasRole(userRole, item.requiredRole))) {
                  return null;
                }
                
                // Check if any child item should be shown
                const hasVisibleChildren = item.children && item.children.some(child => 
                  !child.requiredRole || (userRole && hasRole(userRole, child.requiredRole))
                );
                
                // Hide parent if no children are visible and parent itself doesn't have a view
                if (item.children && !hasVisibleChildren && !['allRegistrations', 'lodging'].includes(item.id)) {
                  return null;
                }
                
                const Icon = item.icon;
                const isParent = item.children && item.children.length > 0;
                const isExpanded = isParent && (
                  currentView === item.id || 
                  (item.children && item.children.some(child => currentView === child.id))
                );
                
                return (
                  <li key={item.id}>
                    {isParent ? (
                      <>
                        <button
                          onClick={() => {
                            // For "All Registrations", navigate to "registrations" sub-section
                            // For other parents, navigate to first visible child
                            if (item.id === 'allRegistrations') {
                              setCurrentView('registrations');
                            } else if (item.children && item.children.length > 0) {
                              const firstVisibleChild = item.children.find(child => 
                                !child.requiredRole || (userRole && hasRole(userRole, child.requiredRole))
                              );
                              if (firstVisibleChild) {
                                setCurrentView(firstVisibleChild.id);
                              }
                            }
                          }}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition ${
                            isExpanded
                              ? 'bg-blue-100 text-blue-600'
                              : 'hover:bg-gray-100 text-gray-700'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5" />
                            <span className="font-medium">{item.label}</span>
                          </div>
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                        {isExpanded && item.children && (
                          <ul className="ml-4 mt-1 space-y-1">
                            {item.children.map(child => {
                              // Hide child items if they require a role and user doesn't have it
                              if (child.requiredRole && (!userRole || !hasRole(userRole, child.requiredRole))) {
                                return null;
                              }
                              const ChildIcon = child.icon;
                              return (
                                <li key={child.id}>
                                  <button
                                    onClick={() => setCurrentView(child.id)}
                                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition text-sm ${
                                      currentView === child.id
                                        ? 'bg-blue-100 text-blue-600'
                                        : 'hover:bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    <ChildIcon className="w-4 h-4" />
                                    <span>{child.label}</span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </>
                    ) : (
                    <button
                      onClick={() => {
                        // Register: go to type selection first (same flow as Camp Registration Form on home)
                        setCurrentView(item.id === 'register' ? 'selectRegistrationType' : item.id);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${
                        currentView === item.id
                          ? 'bg-blue-100 text-blue-600'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {currentView === 'dashboard' && hasPermission(userRole, 'view_registrations') && (
              <Dashboard 
                registrations={registrations} 
                lodges={lodges} 
                userRole={userRole}
                maxGeneralSeats={maxGeneralSeats}
                maxFamilySeats={maxFamilySeats}
                maxMiddleSchoolSeats={maxMiddleSchoolSeats}
                maxHighSchoolSeats={maxHighSchoolSeats}
                maxCollegeSeats={maxCollegeSeats}
                maxFamilyRoomsSeats={maxFamilyRoomsSeats}
                onUpdateMaxGeneralSeats={(value) => {
                  setMaxGeneralSeats(value);
                }}
                onUpdateMaxFamilySeats={(value) => {
                  setMaxFamilySeats(value);
                }}
                onUpdateMaxMiddleSchoolSeats={(value) => {
                  setMaxMiddleSchoolSeats(value);
                }}
                onUpdateMaxHighSchoolSeats={(value) => {
                  setMaxHighSchoolSeats(value);
                }}
                onUpdateMaxCollegeSeats={(value) => {
                  setMaxCollegeSeats(value);
                }}
                onUpdateMaxFamilyRoomsSeats={(value) => {
                  setMaxFamilyRoomsSeats(value);
                }}
              />
            )}
            {currentView === 'register' && !(registrationsOpen || dayRegistrationsOpen || familyRegistrationsOpen || adultRegistrationsOpen) && !(isLoggedIn && userRole !== null) && (
              <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Registrations Are Closed</h2>
                <p className="text-gray-600 mb-6">
                  We are no longer accepting new registrations. You cannot submit this form.
                </p>
                <button
                  onClick={() => { setSelectedRegistrationType(null); setCurrentView('landing'); }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg"
                >
                  Back to Home
                </button>
              </div>
            )}
            {currentView === 'register' && (registrationsOpen || dayRegistrationsOpen || familyRegistrationsOpen || adultRegistrationsOpen || (isLoggedIn && userRole !== null)) && (
              <RegistrationForm
                registrationType={selectedRegistrationType}
                registrations={registrations}
                cardPaymentEnabled={cardPaymentEnabled}
                paypalEnabled={paypalEnabled}
                venmoEnabled={venmoEnabled}
                zelleEnabled={zelleEnabled}
                blockInitialRegistrationReceivedEmail={blockInitialRegistrationReceivedEmail}
                userRole={userRole}
                pricingSettings={pricingSettings}
                campSettings={campSettings}
                onRegister={async (registration) => {
                  try {
                    // Map qrCode to registrationCode for backend compatibility
                    const registrationToCreate = {
                      ...registration,
                      registrationCode: registration.registrationCode || registration.qrCode,
                      phone: registration.phone || registration.mobileNumber || registration.homePhone,
                      totalAmount: registration.totalAmount || registration.amount
                    };
                    const created = await registrationsAPI.create(registrationToCreate);
                    // Reload registrations to get latest data
                    const updatedRegistrations = await registrationsAPI.getAll();
                    setRegistrations(updatedRegistrations);
                    return created; // Return so family flow can apply backend-assigned familyCode
                  } catch (error) {
                    console.error('Error creating registration:', error);
                    throw error;
                  }
                }}
                onBack={() => {
                  setSelectedRegistrationType(null);
                  setCurrentView('selectRegistrationType');
                }}
              />
            )}
            {currentView === 'checkin' && hasPermission(userRole, 'view_checkin') && !adminCampsiteFormFor && !adminIcpfWaiverFor && (
              <CheckInView
                registrations={registrations}
                userRole={userRole}
                onStartCampsiteForm={(id) => setAdminCampsiteFormFor(id)}
                onCheckIn={userRole !== ROLES.VIEWER ? async (id) => {
                  try {
                    await registrationsAPI.update(id, { checkedIn: true, checkInTime: new Date().toISOString() });
                    const updatedRegistrations = await registrationsAPI.getAll();
                    setRegistrations(updatedRegistrations);
                  } catch (error) {
                    console.error('Error updating check-in:', error);
                    alert('Failed to update check-in. Please try again.');
                  }
                } : null}
                onUncheckIn={userRole !== ROLES.VIEWER ? async (id) => {
                  try {
                    await registrationsAPI.update(id, { 
                      checkedIn: false, 
                      checkInTime: null 
                    });
                    const updatedRegistrations = await registrationsAPI.getAll();
                    setRegistrations(updatedRegistrations);
                  } catch (error) {
                    console.error('Error unchecking in:', error);
                    alert('Failed to uncheck-in. Please try again.');
                  }
                } : null}
                onResubmitWaiver={userRole !== ROLES.VIEWER ? async (id) => {
                  try {
                    await registrationsAPI.update(id, { waiverSigned: false, waiverSignedAt: null });
                    const updatedRegistrations = await registrationsAPI.getAll();
                    setRegistrations(updatedRegistrations);
                    setAdminCampsiteFormFor(id);
                  } catch (error) {
                    console.error('Error resetting waiver for resubmit:', error);
                    alert('Failed to initiate resubmit. Please try again.');
                  }
                } : null}
                onWaiverRequired={(id) => {
                  setAdminCampsiteFormFor(id);
                }}
                onReloadRegistrations={async () => {
                  const updatedRegistrations = await registrationsAPI.getAll();
                  setRegistrations(updatedRegistrations);
                }}
              />
            )}
            {currentView === 'checkin' && adminCampsiteFormFor && (
              <CampsiteParticipantForm
                registration={registrations.find(r => r.id === adminCampsiteFormFor)}
                onComplete={(id) => {
                  setAdminCampsiteFormFor(null);
                  if (id) setAdminIcpfWaiverFor(id);
                }}
                onCancel={() => setAdminCampsiteFormFor(null)}
              />
            )}
            {currentView === 'checkin' && adminIcpfWaiverFor && (
              <ICPFWaiverForm
                registration={registrations.find(r => r.id === adminIcpfWaiverFor)}
                onComplete={async () => {
                  const id = adminIcpfWaiverFor;
                  setAdminIcpfWaiverFor(null);
                  try {
                    await registrationsAPI.update(id, {
                      waiverSigned: true,
                      waiverSignedAt: new Date().toISOString(),
                      checkedIn: true,
                      checkInTime: new Date().toISOString(),
                    });
                    const updatedRegistrations = await registrationsAPI.getAll();
                    setRegistrations(updatedRegistrations);
                    alert('Both forms submitted and check-in completed!');
                  } catch (error) {
                    console.error('Error completing check-in:', error);
                    alert('Failed to complete check-in. Please try again.');
                  }
                }}
                onCancel={() => setAdminIcpfWaiverFor(null)}
              />
            )}
            {currentView === 'registrations' && hasPermission(userRole, 'view_registrations') && (
              <AdminRegistrations
                registrations={registrations}
                lodges={lodges}
                userRole={userRole}
                onUpdateStatus={handleUpdateRegistrationStatus}
                onUpdateRegistration={handleUpdateRegistration}
                onDelete={handleDeleteRegistration}
                cardPaymentEnabled={cardPaymentEnabled}
                paypalEnabled={paypalEnabled}
                venmoEnabled={venmoEnabled}
                zelleEnabled={zelleEnabled}
                campSettings={campSettings}
                pricingSettings={pricingSettings}
                feeWaiverSettings={feeWaiverSettings}
              />
            )}
            {currentView === 'deletedRegistrations' && hasPermission(userRole, 'view_deleted_registrations') && (
              <DeletedRegistrations
                userRole={userRole}
                onReload={() => {
                  // Reload registrations when coming back from deleted view
                  registrationsAPI.getAll().then(setRegistrations);
                }}
              />
            )}
            {currentView === 'lodges' && hasPermission(userRole, 'view_registrations') && (
              <LodgeManagement
                lodges={lodges}
                setLodges={setLodges}
                userRole={userRole}
                registrations={registrations.filter(r => r.status === 'confirmed')}
                campSettings={campSettings}
                roomAttendanceEnabled={roomAttendanceEnabled}
                onToggleRoomAttendance={() => setRoomAttendanceEnabled(prev => !prev)}
                {...(hasPermission(userRole, 'assign_rooms') ? {
                  onRemoveRoom: async (registrationId, lodgeId) => {
                    try {
                      // Remove assignment from registration
                      const registration = registrations.find(r => r.id === registrationId);
                      if (registration) {
                        const updatedData = {
                          ...registration?.data || {},
                          lodgeId: null,
                          roomNumber: null,
                          bedNumber: null
                        };
                        // Remove lodgeId, roomNumber, and bedNumber from data
                        delete updatedData.lodgeId;
                        delete updatedData.roomNumber;
                        delete updatedData.bedNumber;
                        await registrationsAPI.update(registrationId, { data: updatedData });
                      }
                      
                      // Remove assignment from lodge
                      const lodge = lodges.find(l => l.id === lodgeId);
                      if (!lodge) return;
                      
                      // Support both new structure (rooms array) and old structure (assignedRooms)
                      const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
                      
                      if (hasNewStructure) {
                        // New structure: remove from rooms.assignedBeds
                        const updatedRooms = lodge.rooms.map(room => {
                          if (room.assignedBeds) {
                      return {
                              ...room,
                              assignedBeds: room.assignedBeds.filter(bed => 
                                bed.registrationId !== registrationId
                              )
                            };
                          }
                          return room;
                        });
                        await lodgesAPI.update(lodgeId, { rooms: updatedRooms });
                      } else if (lodge.assignedRooms) {
                        // Old structure: remove from assignedRooms
                        const filteredAssignments = lodge.assignedRooms.filter(assignment => 
                          assignment.registrationId !== registrationId
                        );
                        await lodgesAPI.update(lodgeId, { assignedRooms: filteredAssignments });
                      }
                      
                      // Reload data
                      const [updatedRegistrations, updatedLodges] = await Promise.all([
                        registrationsAPI.getAll(),
                        lodgesAPI.getAll()
                      ]);
                      setRegistrations(updatedRegistrations);
                      setLodges(updatedLodges);
                    } catch (error) {
                      console.error('Error removing room assignment:', error);
                      alert('Failed to remove room assignment. Please try again.');
                    }
                  },
                  onAssignRoom: async (registrationId, lodgeId, roomNumber, bedNumber = null) => {
                  try {
                    // First, check if this registration is already assigned anywhere
                    const registration = registrations.find(r => r.id === registrationId);
                    
                    if (!registration) {
                      alert('Registration not found.');
                      return;
                    }
                    
                    // Get target lodge
                    const targetLodge = lodges.find(l => l.id === lodgeId);
                    if (!targetLodge) {
                      alert('Lodge not found.');
                      return;
                    }
                    
                    // Validate lodge restrictions before assigning
                    const checkRestrictions = (reg, lodge) => {
                      if (lodge.lodgeMainCategory === 'Family Lodge') {
                        const regData = reg.data || {};
                        const category = (regData.camperCategory || reg.camperCategory || '').trim();
                        const stayPreference = regData.stayPreference || reg.stayPreference || '';
                        const isAdult = category === 'Adults' || category === 'Adult' || (category && category.includes('Adult'));
                        const isGuestSpeaker = category === 'Guest Speaker';
                        const isElementary = category === 'Elementary';
                        const isBelowElementary = category === BELOW_ELEMENTARY_CAMPER_CATEGORY;
                        const isMiddleWithParents = category === 'Middle School' && stayPreference === 'withParents';
                        return isAdult || isGuestSpeaker || isElementary || isBelowElementary || isMiddleWithParents;
                      }
                      if (lodge.lodgeMainCategory === 'Student Lodge') {
                        const hasCategoryRestriction = (lodge.camperCategories && lodge.camperCategories.length > 0) || lodge.camperCategory;
                        if (!hasCategoryRestriction && !lodge.gender) {
                          return true; // No restrictions defined
                        }
                        const regType = reg.type || '';
                        // Exclude family-type only when lodge does NOT allow Adults (students-only)
                        const lodgeAllowsAdults = (lodge.camperCategories || []).some(c => {
                          const cc = (c || '').trim().toLowerCase();
                          return cc === 'adult' || cc === 'adults' || cc.includes('adult');
                        });
                        if (regType && regType.startsWith('family') && !lodgeAllowsAdults) {
                          return false; // Family registrations can't go to students-only Student Lodges
                        }
                        const regData = reg.data || {};
                        const regCamperCategory = (regData.camperCategory || reg.camperCategory || '').trim();
                        const regGender = (regData.gender || reg.gender || '').trim();
                        if (lodge.gender && regGender && regGender.toLowerCase() !== (lodge.gender || '').toLowerCase()) return false;
                        if (hasCategoryRestriction) {
                          if (lodge.camperCategories && lodge.camperCategories.length > 0) {
                            const belowElem = BELOW_ELEMENTARY_CAMPER_CATEGORY.toLowerCase();
                            return lodge.camperCategories.some(c => {
                              const cc = (c || '').trim().toLowerCase();
                              const rcc = regCamperCategory.toLowerCase();
                              if (cc === rcc) return true;
                              if (cc === 'adult' && (rcc === 'adults' || rcc.includes('adult'))) return true;
                              if (cc === 'elementary' && rcc === belowElem) return true;
                              if (cc === belowElem && rcc === belowElem) return true;
                              return false;
                            });
                          }
                          return regCamperCategory === lodge.camperCategory;
                        }
                        return true;
                      }
                      return true;
                    };
                    
                    if (!checkRestrictions(registration, targetLodge)) {
                      const restrictions = targetLodge.lodgeMainCategory === 'Family Lodge'
                        ? 'Adults, Guest Speaker, Elementary, Below Elementary, and Middle School (staying with parents) only'
                        : targetLodge.lodgeMainCategory === 'Student Lodge' && ((targetLodge.camperCategories && targetLodge.camperCategories.length > 0) || targetLodge.camperCategory || targetLodge.gender)
                          ? `${[(targetLodge.camperCategories && targetLodge.camperCategories.length > 0) ? targetLodge.camperCategories.join(', ') : targetLodge.camperCategory, targetLodge.gender].filter(Boolean).join(' ')}`
                          : 'this lodge type';
                      alert(`This registrant does not match the lodge restrictions. This lodge only accepts ${restrictions} registrations.`);
                      return;
                    }
                    
                    // Check if already assigned to this lodge (old structure)
                    if (targetLodge.assignedRooms && Array.isArray(targetLodge.assignedRooms)) {
                      const alreadyInThisLodge = targetLodge.assignedRooms.some(assignment => 
                        assignment.registrationId === registrationId
                      );
                      if (alreadyInThisLodge) {
                        alert('This registrant is already assigned to this lodge. Please remove the existing assignment first.');
                        return;
                      }
                    }
                    
                    // Check if already assigned to this lodge (new structure)
                    if (targetLodge.rooms && Array.isArray(targetLodge.rooms)) {
                      const alreadyAssigned = targetLodge.rooms.some(room => 
                        room.assignedBeds && room.assignedBeds.some(bed => bed.registrationId === registrationId)
                      );
                      if (alreadyAssigned) {
                        alert('This registrant is already assigned to this lodge. Please remove the existing assignment first.');
                        return;
                      }
                    }
                    
                    // Remove assignment from any previous lodge
                    for (const lodge of lodges) {
                      if (lodge.assignedRooms && Array.isArray(lodge.assignedRooms)) {
                        const hasAssignment = lodge.assignedRooms.some(assignment => 
                          assignment.registrationId === registrationId
                        );
                        if (hasAssignment) {
                          // Remove this registration from the previous lodge
                          const filteredAssignments = lodge.assignedRooms.filter(assignment => 
                            assignment.registrationId !== registrationId
                          );
                          await lodgesAPI.update(lodge.id, { assignedRooms: filteredAssignments });
                        }
                      }
                    }
                    
                    // Update registration with new room assignment
                    const updatedData = {
                      ...registration?.data || {},
                      lodgeId,
                      roomNumber,
                      bedNumber: bedNumber || null
                    };
                    await registrationsAPI.update(registrationId, { data: updatedData });
                    
                    // Add assignment to target lodge
                    // Support both new structure (rooms array) and old structure (assignedRooms)
                    const hasNewStructure = targetLodge.rooms && Array.isArray(targetLodge.rooms) && targetLodge.rooms.length > 0;
                    
                    if (hasNewStructure) {
                      // New structure: add to rooms.assignedBeds
                      const updatedRooms = targetLodge.rooms.map(room => {
                        if (room.roomNumber === roomNumber || room.roomNumber === roomNumber.toString()) {
                          const assignedBeds = room.assignedBeds || [];
                          // Check if this bed is already taken
                          const bedAlreadyTaken = assignedBeds.some(bed => bed.bedNumber === (bedNumber || 1));
                          if (bedAlreadyTaken) {
                            alert(`Bed ${bedNumber || 1} in Room ${roomNumber} is already assigned.`);
                            return room;
                          }
                          // Check if this registrant is already assigned to this room
                          const registrantAlreadyAssigned = assignedBeds.some(bed => bed.registrationId === registrationId);
                          if (registrantAlreadyAssigned) {
                            alert('This registrant is already assigned to this room.');
                            return room;
                          }
                          return {
                            ...room,
                            assignedBeds: [...assignedBeds, { 
                              registrationId, 
                              bedNumber: bedNumber || 1 
                            }]
                          };
                        }
                        return room;
                      });
                      await lodgesAPI.update(lodgeId, { rooms: updatedRooms });
                    } else {
                      // Old structure: add to assignedRooms (for backward compatibility)
                      const existingAssignments = targetLodge.assignedRooms || [];
                      const isDuplicate = existingAssignments.some(assignment => 
                        assignment.registrationId === registrationId
                      );
                      
                      if (!isDuplicate) {
                        const assignedRooms = [...existingAssignments, { 
                          registrationId, 
                          roomNumber,
                          bedNumber: bedNumber || 1
                        }];
                        await lodgesAPI.update(lodgeId, { assignedRooms });
                      }
                    }
                    
                    // Reload data
                    const [updatedRegistrations, updatedLodges] = await Promise.all([
                      registrationsAPI.getAll(),
                      lodgesAPI.getAll()
                    ]);
                    setRegistrations(updatedRegistrations);
                    setLodges(updatedLodges);
                  } catch (error) {
                    console.error('Error assigning room:', error);
                    alert('Failed to assign room. Please try again.');
                  }
                  }
                } : {})}
              />
            )}
            {currentView === 'roomAssignment' && hasPermission(userRole, 'view_room_assignment') && (
              <RoomAssignmentEngine
                registrations={registrations.filter(r => r.status === 'confirmed')}
                lodges={lodges}
                userRole={userRole}
                onUpdateRegistrations={(userRole !== ROLES.VIEWER && userRole !== ROLES.REGISTRATION_ADMIN) ? async () => {
                  const updatedRegistrations = await registrationsAPI.getAll();
                  setRegistrations(updatedRegistrations);
                } : null}
                onUpdateLodges={(userRole !== ROLES.VIEWER && userRole !== ROLES.REGISTRATION_ADMIN) ? async () => {
                  const updatedLodges = await lodgesAPI.getAll();
                  setLodges(updatedLodges);
                } : null}
              />
            )}
            {currentView === 'roomAttendanceReport' && hasRole(userRole, ROLES.ROOM_ADMIN) && (
              <RoomAttendanceReport />
            )}
            {currentView === 'badges' && hasPermission(userRole, 'view_badges') && (
              <BadgePrinting
                registrations={registrations}
                lodges={lodges}
                userRole={userRole}
              />
            )}
            {currentView === 'export' && hasPermission(userRole, 'view_export') && (
              <ExportView registrations={registrations} lodges={lodges} userRole={userRole} />
            )}
            {currentView === 'bulkEmail' && hasPermission(userRole, 'send_bulk_email') && (
              <BulkEmail registrations={registrations} userRole={userRole} />
            )}
            {currentView === 'scheduleBuilder' && hasPermission(userRole, 'view_schedule') && (
              <ScheduleBuilder userRole={userRole} />
            )}
            {currentView === 'transportation' && (hasPermission(userRole, 'manage_transportation') || userRole === ROLES.APPLICATION_ADMIN) && (
              <TransportationAdmin 
                userRole={userRole}
              />
            )}
            {currentView === 'campSettings' && hasRole(userRole, ROLES.REGISTRATION_ADMIN) && (
              <CampSettingsView
                campSettings={campSettings}
                campSettingsLoaded={campSettingsLoaded}
                onCampSettingsSaved={(merged) => {
                  setCampSettings(merged);
                  setCampSettingsLoaded(true);
                  setEmailTemplateCampSettings(merged);
                }}
                pricingSettings={pricingSettings}
                pricingSettingsLoaded={pricingSettingsLoaded}
                onPricingSettingsSaved={(merged) => { setPricingSettings(merged); setPricingSettingsLoaded(true); }}
                emailSettings={emailSettings}
                emailSettingsLoaded={emailSettingsLoaded}
                onEmailSettingsSaved={(merged) => { setEmailSettings(merged); setEmailSettingsLoaded(true); }}
                feeWaiverSettings={feeWaiverSettings}
                feeWaiverSettingsLoaded={feeWaiverSettingsLoaded}
                onFeeWaiverSettingsSaved={(merged) => { setFeeWaiverSettings(merged); setFeeWaiverSettingsLoaded(true); }}
                userRole={userRole}
                onRegistrationsCleared={() => setRegistrations([])}
                selfCheckInEnabled={selfCheckInEnabled}
                registrationsOpen={registrationsOpen}
                familyRegistrationsOpen={familyRegistrationsOpen}
                adultRegistrationsOpen={adultRegistrationsOpen}
                dayRegistrationsOpen={dayRegistrationsOpen}
                earlyBirdStudentEnabled={earlyBirdStudentEnabled}
                cardPaymentEnabled={cardPaymentEnabled}
                paypalEnabled={paypalEnabled}
                venmoEnabled={venmoEnabled}
                zelleEnabled={zelleEnabled}
                transportationEnabled={transportationEnabled}
                welcomePackageEnabled={welcomePackageEnabled}
                blockInitialRegistrationReceivedEmail={blockInitialRegistrationReceivedEmail}
                onToggleSelfCheckIn={() => {
                  setSelfCheckInEnabled((prev) => !prev);
                }}
                onToggleRegistrationsOpen={() => setRegistrationsOpen((prev) => !prev)}
                onToggleFamilyRegistrationsOpen={() => setFamilyRegistrationsOpen((prev) => !prev)}
                onToggleAdultRegistrationsOpen={() => setAdultRegistrationsOpen((prev) => !prev)}
                onToggleDayRegistrationsOpen={() => setDayRegistrationsOpen((prev) => !prev)}
                onToggleEarlyBirdStudent={() => setEarlyBirdStudentEnabled((prev) => !prev)}
                onToggleCardPayment={() => setCardPaymentEnabled((prev) => !prev)}
                onTogglePaypal={() => setPaypalEnabled((prev) => !prev)}
                onToggleVenmo={() => setVenmoEnabled((prev) => !prev)}
                onToggleZelle={() => setZelleEnabled((prev) => !prev)}
        onToggleTransportation={() => setTransportationEnabled((prev) => !prev)}
        onToggleWelcomePackage={() => setWelcomePackageEnabled((prev) => !prev)}
        onToggleBlockInitialRegistrationReceivedEmail={() =>
          setBlockInitialRegistrationReceivedEmail((prev) => !prev)
        }
              />
            )}
            {currentView === 'users' && hasPermission(userRole, 'manage_settings') && (
              <UserManagement
                currentUserRole={userRole}
                onUserUpdate={handleUserUpdate}
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

// Room Attendance Page (public - room leaders enter code and submit nightly attendance)
function RoomAttendancePage({ onBack }) {
  const apiBase = API_BASE_URL;
  const [step, setStep] = useState(1);
  const [code, setCode] = useState('');
  const [roomInfo, setRoomInfo] = useState(null);
  const [date, setDate] = useState(() => {
    try {
      return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
    } catch (_) {
      return new Date().toISOString().slice(0, 10);
    }
  });
  const [entries, setEntries] = useState([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const loadRoom = async () => {
    const trimmed = (code || '').trim().toUpperCase();
    if (!trimmed) {
      setError('Please enter your room code.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/room-roster/${encodeURIComponent(trimmed)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid code');
      setRoomInfo(data);
      setEntries((data.campers || []).map(c => ({ registrationId: c.registrationId, present: true })));
      setStep(2);
    } catch (e) {
      setError(e.message || 'Invalid or expired code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const setEntryPresent = (registrationId, present) => {
    setEntries(prev => prev.map(e => e.registrationId === registrationId ? { ...e, present } : e));
  };

  const submitAttendance = async () => {
    const trimmed = (code || '').trim().toUpperCase();
    if (!roomInfo || !trimmed) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/room-roster/${encodeURIComponent(trimmed)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, entries, notes })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit');
      setSubmitSuccess(true);
      setStep(3);
    } catch (e) {
      setError(e.message || 'Failed to submit attendance.');
    } finally {
      setLoading(false);
    }
  };

  const resetToCode = () => {
    setStep(1);
    setCode('');
    setRoomInfo(null);
    setEntries([]);
    setNotes('');
    setError('');
    setSubmitSuccess(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 max-w-lg w-full">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Room Attendance</h1>
          <button onClick={onBack} className="text-gray-500 hover:text-gray-700 text-sm">← Back</button>
        </div>
        {step === 1 && (
          <>
            <p className="text-gray-600 mb-4">Enter the room code provided by your admin to submit nightly attendance.</p>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12))}
              placeholder="e.g. ROOM101"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono text-lg uppercase"
              maxLength={12}
            />
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            <button
              onClick={loadRoom}
              disabled={loading}
              className="mt-4 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Continue'}
            </button>
          </>
        )}
        {step === 2 && roomInfo && (
          <>
            <p className="text-gray-600 mb-2"><strong>{roomInfo.lodgeName}</strong> – Room {roomInfo.roomNumber}</p>
            <p className="text-xs text-gray-500 mb-3">One submission per room per night. Submitting again for the same date will update your previous submission.</p>
            <label className="block text-sm text-gray-600 mt-4 mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Campers – mark Present or Absent</p>
              <ul className="space-y-2">
                {(roomInfo.campers || []).map(c => (
                  <li key={c.registrationId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span>{c.name}</span>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name={`p-${c.registrationId}`} checked={entries.find(e => e.registrationId === c.registrationId)?.present === true} onChange={() => setEntryPresent(c.registrationId, true)} />
                        <span className="text-sm">Present</span>
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name={`p-${c.registrationId}`} checked={entries.find(e => e.registrationId === c.registrationId)?.present === false} onChange={() => setEntryPresent(c.registrationId, false)} />
                        <span className="text-sm">Absent</span>
                      </label>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <label className="block text-sm text-gray-600 mt-4 mb-1">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Any notes..." />
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            <div className="mt-6 flex gap-3">
              <button onClick={() => setStep(1)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700">Back</button>
              <button onClick={submitAttendance} disabled={loading} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50">
                {loading ? 'Submitting...' : 'Submit Attendance'}
              </button>
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <p className="text-lg font-semibold text-gray-800">Attendance submitted successfully.</p>
              <button onClick={resetToCode} className="mt-6 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg">
                Submit another room
              </button>
              <button onClick={onBack} className="mt-3 w-full border border-gray-300 rounded-lg py-3 text-gray-700">Back to Home</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Room Attendance Report (admin – Room Admin / Application Admin)
function RoomAttendanceReport() {
  const apiBase = API_BASE_URL;
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate) params.set('toDate', toDate);
      const res = await fetch(`${apiBase}/room-roster/report?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load report');
      setSubmissions(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReport(); }, []);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-800">Room Attendance Report</h2>
      <p className="text-sm text-gray-600">Nightly roster submissions by room. Only Room Admin and Application Admin can view this report.</p>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs text-gray-600 mb-1">From date</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-3 py-2 border border-gray-300 rounded" />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">To date</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-3 py-2 border border-gray-300 rounded" />
        </div>
        <button onClick={loadReport} disabled={loading} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
          {loading ? 'Loading...' : 'Apply'}
        </button>
      </div>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lodge</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Room</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Submitted at</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Entries</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {submissions.length === 0 && !loading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No submissions in this range.</td></tr>
            )}
            {submissions.map(s => (
              <tr key={s.id}>
                <td className="px-4 py-2 text-sm">{s.date}</td>
                <td className="px-4 py-2 text-sm">{s.lodgeName}</td>
                <td className="px-4 py-2 text-sm">{s.roomNumber}</td>
                <td className="px-4 py-2 text-sm">{s.submittedAt ? new Date(s.submittedAt).toLocaleString() : '—'}</td>
                <td className="px-4 py-2 text-sm">{Array.isArray(s.entries) ? s.entries.length : 0}</td>
                <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate" title={s.notes}>{s.notes || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Toggle + status pill used for registration gates, payments, and logistics (Camp Settings). */
function OperationalSwitchRow({ label, checked, onToggle, statusOn, statusOff, variant = 'greenGray' }) {
  // Coerce API / storage quirks (e.g. string "false" is truthy in JS) so the thumb position matches reality
  const isOn =
    checked === true ||
    checked === 1 ||
    checked === '1' ||
    checked === 'true' ||
    checked === 'True';
  const v =
    variant === 'greenRed'
      ? {
          trackOn: 'bg-green-500',
          trackOff: 'bg-red-500',
          pillOn: 'bg-green-100 text-green-800',
          pillOff: 'bg-red-100 text-red-800'
        }
      : variant === 'amberGray'
        ? {
            trackOn: 'bg-amber-500',
            trackOff: 'bg-gray-300',
            pillOn: 'bg-amber-100 text-amber-800',
            pillOff: 'bg-gray-100 text-gray-600'
          }
        : {
            trackOn: 'bg-green-500',
            trackOff: 'bg-gray-300',
            pillOn: 'bg-green-100 text-green-800',
            pillOff: 'bg-gray-100 text-gray-600'
          };
  return (
    <>
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <button
          type="button"
          role="switch"
          aria-checked={isOn}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggle();
          }}
          className="relative cursor-pointer p-0 border-0 bg-transparent rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          <span className={`block w-14 h-7 rounded-full transition-colors ${isOn ? v.trackOn : v.trackOff}`}>
            <span
              className={`block w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${isOn ? 'translate-x-7' : 'translate-x-1'}`}
              style={{ marginTop: '2px' }}
            />
          </span>
        </button>
      </div>
      <span className={`text-sm px-3 py-1 rounded-full ${isOn ? v.pillOn : v.pillOff}`}>{isOn ? statusOn : statusOff}</span>
    </>
  );
}

// Admin manager for the Welcome Package documents shown on the public page.
// Upload / rename / delete PDFs; stored via the backend so no redeploy is needed.
function WelcomePackageDocsManager() {
  const [docs, setDocs] = useState(null); // null = loading
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const fileInputRef = useRef(null);

  const loadDocs = () => {
    welcomePackageDocsAPI
      .list()
      .then((result) => {
        setDocs(Array.isArray(result?.docs) ? result.docs : []);
        setError('');
      })
      .catch((e) => {
        setDocs([]);
        setError('Failed to load documents: ' + (e?.message || 'unknown error'));
      });
  };

  useEffect(() => {
    loadDocs();
  }, []);

  const handleFileSelected = (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError('Only PDF files are supported.');
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      setError('File is too large (max 6 MB).');
      return;
    }
    const label = newLabel.trim() || file.name.replace(/\.pdf$/i, '');
    setUploading(true);
    setError('');
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const dataUrl = String(reader.result || '');
        const contentBase64 = dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : dataUrl;
        await welcomePackageDocsAPI.upload({ label, fileName: file.name, contentBase64 });
        setNewLabel('');
        loadDocs();
      } catch (e) {
        setError('Upload failed: ' + (e?.message || 'unknown error'));
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.onerror = () => {
      setUploading(false);
      setError('Could not read the selected file.');
    };
    reader.readAsDataURL(file);
  };

  const handleRename = async (doc) => {
    const label = window.prompt('New display name for this document:', doc.label);
    if (label === null) return;
    const clean = label.trim();
    if (!clean || clean === doc.label) return;
    try {
      await welcomePackageDocsAPI.rename(doc.id, clean);
      loadDocs();
    } catch (e) {
      setError('Rename failed: ' + (e?.message || 'unknown error'));
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Remove "${doc.label}" from the Welcome Package page?`)) return;
    try {
      await welcomePackageDocsAPI.remove(doc.id);
      loadDocs();
    } catch (e) {
      setError('Delete failed: ' + (e?.message || 'unknown error'));
    }
  };

  return (
    <div className="border-t border-gray-200 pt-4 space-y-3">
      <div>
        <h4 className="font-medium text-gray-800">Welcome Package documents</h4>
        <p className="text-sm text-gray-600 mt-1">
          PDFs listed on the public Welcome Package page. Changes take effect immediately — no redeploy needed.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {docs === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : docs.length === 0 ? (
        <p className="text-sm text-gray-500">No documents. Upload a PDF below.</p>
      ) : (
        <ul className="space-y-2">
          {docs.map((doc) => (
            <li key={doc.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200">
              <FileText size={18} className="text-gray-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">{doc.label}</p>
                <p className="text-xs text-gray-500 truncate">
                  {doc.builtin
                    ? 'Built-in file shipped with the site'
                    : `${doc.fileName || 'document.pdf'}${doc.size ? ` · ${(doc.size / 1024).toFixed(0)} KB` : ''}${doc.uploadedAt ? ` · uploaded ${new Date(doc.uploadedAt).toLocaleDateString()}` : ''}`}
                </p>
              </div>
              <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                <a
                  href={welcomePackageDocUrl(doc)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-teal-600 hover:text-teal-800 font-medium"
                >
                  View
                </a>
                <button
                  type="button"
                  onClick={() => handleRename(doc)}
                  className="text-sm text-gray-600 hover:text-gray-900 font-medium"
                >
                  Rename
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(doc)}
                  className="text-sm text-red-600 hover:text-red-800 font-medium"
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="Display name (defaults to file name)"
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm flex-1 min-w-[220px]"
          disabled={uploading}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => handleFileSelected(e.target.files && e.target.files[0])}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={uploading}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
        >
          {uploading ? 'Uploading…' : 'Upload PDF'}
        </button>
      </div>
      <p className="text-xs text-gray-500">PDF only, max 6 MB per file.</p>
    </div>
  );
}

function CampOperationalSettingsSections({
  userRole,
  selfCheckInEnabled,
  registrationsOpen,
  familyRegistrationsOpen,
  adultRegistrationsOpen,
  dayRegistrationsOpen,
  earlyBirdStudentEnabled,
  cardPaymentEnabled,
  paypalEnabled,
  venmoEnabled,
  zelleEnabled,
  transportationEnabled,
  welcomePackageEnabled,
  blockInitialRegistrationReceivedEmail,
  onToggleSelfCheckIn,
  onToggleRegistrationsOpen,
  onToggleFamilyRegistrationsOpen,
  onToggleAdultRegistrationsOpen,
  onToggleDayRegistrationsOpen,
  onToggleEarlyBirdStudent,
  onToggleCardPayment,
  onTogglePaypal,
  onToggleVenmo,
  onToggleZelle,
  onToggleTransportation,
  onToggleWelcomePackage,
  onToggleBlockInitialRegistrationReceivedEmail
}) {
  const canOps =
    hasPermission(userRole, 'manage_settings') || hasPermission(userRole, 'manage_dashboard_toggles');
  const canEarlyBird = hasPermission(userRole, 'edit_registrations');

  if (!canOps && !canEarlyBird) return null;

  return (
    <div className="space-y-6">
      {canOps && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Registration availability</h3>
            <p className="text-sm text-gray-600 mt-1">Which registration types accept new sign-ups on the public site.</p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-3 items-center">
            <OperationalSwitchRow
              label="Registrations Open"
              checked={registrationsOpen}
              onToggle={onToggleRegistrationsOpen}
              statusOn="Registrations: OPEN"
              statusOff="Registrations: CLOSED"
              variant="greenRed"
            />
            <OperationalSwitchRow
              label="Family Registrations Open"
              checked={familyRegistrationsOpen}
              onToggle={onToggleFamilyRegistrationsOpen}
              statusOn="Family: OPEN"
              statusOff="Family: CLOSED"
              variant="greenRed"
            />
            <OperationalSwitchRow
              label="Adult Registrations Open"
              checked={adultRegistrationsOpen}
              onToggle={onToggleAdultRegistrationsOpen}
              statusOn="Adult: OPEN"
              statusOff="Adult: CLOSED"
              variant="greenRed"
            />
            <OperationalSwitchRow
              label="Day Registrations Open"
              checked={dayRegistrationsOpen}
              onToggle={onToggleDayRegistrationsOpen}
              statusOn="Day: OPEN"
              statusOff="Day: CLOSED"
              variant="greenRed"
            />
            {canEarlyBird && (
              <OperationalSwitchRow
                label="Early Bird Student Registration"
                checked={earlyBirdStudentEnabled}
                onToggle={onToggleEarlyBirdStudent}
                statusOn="Early Bird: ENABLED"
                statusOff="Early Bird: DISABLED"
                variant="greenGray"
              />
            )}
          </div>
        </div>
      )}

      {canOps && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Payment methods</h3>
            <p className="text-sm text-gray-600 mt-1">Offer card, Zelle, PayPal, and Venmo on public registration forms when enabled.</p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-3 items-center">
            <OperationalSwitchRow
              label="Credit/Debit Card Payment"
              checked={cardPaymentEnabled}
              onToggle={onToggleCardPayment}
              statusOn="Card Payment: ENABLED"
              statusOff="Card Payment: DISABLED"
              variant="greenGray"
            />
            <OperationalSwitchRow
              label="Zelle Payment"
              checked={zelleEnabled}
              onToggle={onToggleZelle}
              statusOn="Zelle: ENABLED"
              statusOff="Zelle: DISABLED"
              variant="greenGray"
            />
            <OperationalSwitchRow
              label="PayPal Payment"
              checked={paypalEnabled}
              onToggle={onTogglePaypal}
              statusOn="PayPal: ENABLED"
              statusOff="PayPal: DISABLED"
              variant="greenGray"
            />
            <OperationalSwitchRow
              label="Venmo Payment"
              checked={venmoEnabled}
              onToggle={onToggleVenmo}
              statusOn="Venmo: ENABLED"
              statusOff="Venmo: DISABLED"
              variant="greenGray"
            />
          </div>
        </div>
      )}

      {canOps && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Public resources</h3>
            <p className="text-sm text-gray-600 mt-1">Optional links on the public home page (PDF welcome pack and map).</p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-3 items-center">
            <OperationalSwitchRow
              label="Welcome Package"
              checked={welcomePackageEnabled}
              onToggle={onToggleWelcomePackage}
              statusOn="Welcome Package: ON"
              statusOff="Welcome Package: OFF"
              variant="greenGray"
            />
          </div>
          <WelcomePackageDocsManager />
        </div>
      )}

      {canOps && onToggleBlockInitialRegistrationReceivedEmail && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Email automation</h3>
            <p className="text-sm text-gray-600 mt-1">Optional guardrail when fixing duplicate codes—resend confirmations manually from admin.</p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-3 items-center">
            <OperationalSwitchRow
              label="Block initial registration-received email"
              checked={blockInitialRegistrationReceivedEmail}
              onToggle={onToggleBlockInitialRegistrationReceivedEmail}
              statusOn="Block: ON (resend manually)"
              statusOff="Block: OFF"
              variant="amberGray"
            />
          </div>
        </div>
      )}

      {canOps && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Check-in & logistics</h3>
            <p className="text-sm text-gray-600 mt-1">Self check-in on the home page and transportation signup.</p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-3 items-center">
            <OperationalSwitchRow
              label="Enable Self Check-In"
              checked={selfCheckInEnabled}
              onToggle={onToggleSelfCheckIn}
              statusOn="Self Check-In: ON"
              statusOff="Self Check-In: OFF"
              variant="greenGray"
            />
            <OperationalSwitchRow
              label="Transportation"
              checked={transportationEnabled}
              onToggle={onToggleTransportation}
              statusOn="Transportation: ENABLED"
              statusOff="Transportation: DISABLED"
              variant="greenGray"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Camp Settings (admin – Registration Admin and above; purge registrations: Application Admin only)
function CampSettingsView({
  campSettings,
  campSettingsLoaded,
  onCampSettingsSaved,
  pricingSettings,
  pricingSettingsLoaded,
  onPricingSettingsSaved,
  emailSettings,
  emailSettingsLoaded,
  onEmailSettingsSaved,
  feeWaiverSettings,
  feeWaiverSettingsLoaded,
  onFeeWaiverSettingsSaved,
  userRole,
  onRegistrationsCleared,
  selfCheckInEnabled,
  registrationsOpen,
  familyRegistrationsOpen,
  adultRegistrationsOpen,
  dayRegistrationsOpen,
  earlyBirdStudentEnabled,
  cardPaymentEnabled,
  paypalEnabled,
  venmoEnabled,
  zelleEnabled,
  transportationEnabled,
  welcomePackageEnabled,
  blockInitialRegistrationReceivedEmail,
  onToggleSelfCheckIn,
  onToggleRegistrationsOpen,
  onToggleFamilyRegistrationsOpen,
  onToggleAdultRegistrationsOpen,
  onToggleDayRegistrationsOpen,
  onToggleEarlyBirdStudent,
  onToggleCardPayment,
  onTogglePaypal,
  onToggleVenmo,
  onToggleZelle,
  onToggleTransportation,
  onToggleWelcomePackage,
  onToggleBlockInitialRegistrationReceivedEmail
}) {
  const [form, setForm] = useState(() => ({ ...campSettings }));
  const [pricingForm, setPricingForm] = useState(() => ({ ...DEFAULT_PRICING_SETTINGS, ...(pricingSettings || {}) }));
  const [emailForm, setEmailForm] = useState(() => ({ ...DEFAULT_EMAIL_SETTINGS, ...(emailSettings || {}) }));
  const [saveMessage, setSaveMessage] = useState('');
  const [pricingSaveMessage, setPricingSaveMessage] = useState('');
  const [emailSaveMessage, setEmailSaveMessage] = useState('');
  const [fwSaveMessage, setFwSaveMessage] = useState('');
  const [purgeInput, setPurgeInput] = useState('');
  const [purgeBusy, setPurgeBusy] = useState(false);
  // Track local "freshness" for each card. We start false even if the parent
  // already has loaded values — opening this page always triggers a fresh
  // re-fetch so the form mirrors whatever is actually persisted right now.
  const [campLoaded, setCampLoaded] = useState(!!campSettingsLoaded);
  const [pricingLoaded, setPricingLoaded] = useState(!!pricingSettingsLoaded);
  const [emailLoaded, setEmailLoaded] = useState(!!emailSettingsLoaded);
  const [fwLoaded, setFwLoaded] = useState(!!feeWaiverSettingsLoaded);
  const [feeWaiverForm, setFeeWaiverForm] = useState(() => mergeFeeWaiverSettings(feeWaiverSettings));
  const [refreshError, setRefreshError] = useState('');
  const canPurgeRegistrations = userRole === ROLES.APPLICATION_ADMIN;

  const campDayListForDayPasses = useMemo(() => getCampDateList(mergeCampSettings(form)), [form.campStartDate, form.campEndDate]);

  // Refresh all three settings every time the Camp Settings page is opened.
  // This guarantees the form fields always reflect what is actually persisted
  // (e.g. if the API was momentarily unreachable on app boot, we recover here)
  // and the Save buttons stay disabled until the fetch succeeds.
  useEffect(() => {
    let cancelled = false;
    setRefreshError('');
    (async () => {
      try {
        const camp = await settingsAPI.get('campSettings');
        if (cancelled) return;
        const merged = mergeCampSettings(camp.value);
        setForm(merged);
        setCampLoaded(true);
        onCampSettingsSaved(merged);
      } catch (e) {
        if (!cancelled) setRefreshError('Could not refresh camp settings from the server. Saving is disabled until reload succeeds.');
      }
      try {
        const pr = await settingsAPI.get('pricingSettings');
        if (cancelled) return;
        const merged = mergePricingSettings(pr.value);
        setPricingForm(merged);
        setPricingLoaded(true);
        onPricingSettingsSaved(merged);
      } catch (e) {
        if (!cancelled) setRefreshError(prev => prev || 'Could not refresh pricing from the server. Saving is disabled until reload succeeds.');
      }
      try {
        const em = await settingsAPI.get('emailSettings');
        if (cancelled) return;
        const merged = mergeEmailSettings(em.value);
        setEmailForm(merged);
        setEmailLoaded(true);
        onEmailSettingsSaved(merged);
      } catch (e) {
        if (!cancelled) setRefreshError(prev => prev || 'Could not refresh email addresses from the server. Saving is disabled until reload succeeds.');
      }
      try {
        const fw = await settingsAPI.get('feeWaiverSettings');
        if (cancelled) return;
        const mergedFw = mergeFeeWaiverSettings(fw.value);
        setFeeWaiverForm(mergedFw);
        setFwLoaded(true);
        onFeeWaiverSettingsSaved(mergedFw);
      } catch (e) {
        if (!cancelled) setRefreshError(prev => prev || 'Could not refresh fee waivers from the server. Saving is disabled until reload succeeds.');
      }
    })();
    return () => { cancelled = true; };
    // Intentionally only on mount; the parent's prop changes are reflected by
    // the focused [campSettings]/[pricingSettings]/[emailSettings] effects
    // below for any later updates triggered elsewhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (campSettingsLoaded) {
      setForm({ ...campSettings });
      setCampLoaded(true);
    }
  }, [campSettings, campSettingsLoaded]);

  useEffect(() => {
    if (pricingSettingsLoaded) {
      setPricingForm({ ...DEFAULT_PRICING_SETTINGS, ...(pricingSettings || {}) });
      setPricingLoaded(true);
    }
  }, [pricingSettings, pricingSettingsLoaded]);

  useEffect(() => {
    if (emailSettingsLoaded) {
      setEmailForm({ ...DEFAULT_EMAIL_SETTINGS, ...(emailSettings || {}) });
      setEmailLoaded(true);
    }
  }, [emailSettings, emailSettingsLoaded]);

  useEffect(() => {
    if (feeWaiverSettingsLoaded) {
      setFeeWaiverForm(mergeFeeWaiverSettings(feeWaiverSettings));
      setFwLoaded(true);
    }
  }, [feeWaiverSettings, feeWaiverSettingsLoaded]);

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setSaveMessage('');
  };

  const setPriceField = (key, value) => {
    setPricingForm(prev => ({ ...prev, [key]: value }));
    setPricingSaveMessage('');
  };

  const handleSaveCampSettings = async () => {
    setSaveMessage('');
    if (!campLoaded) {
      alert('Camp settings have not finished loading from the server. Please wait or reload the page before saving.');
      return;
    }
    try {
      const startIso = (form.campStartDate || '').trim();
      const endIso = (form.campEndDate || '').trim();
      const startOk = parseISODateLocal(startIso);
      const endOk = parseISODateLocal(endIso);
      if (!startOk || !endOk) {
        alert('Camp start date and end date must be valid calendar dates (use the pickers).');
        return;
      }
      if (endOk < startOk) {
        alert('Camp end date must be on or after the start date.');
        return;
      }
      const merged = mergeCampSettings({ ...form, campStartDate: startIso, campEndDate: endIso });
      await settingsAPI.set('campSettings', JSON.stringify(merged));
      onCampSettingsSaved(merged);
      setSaveMessage('Camp settings saved. The landing page will show these values.');
    } catch (e) {
      alert(e.message || 'Failed to save camp settings.');
    }
  };

  const handleSavePricing = async () => {
    setPricingSaveMessage('');
    if (!pricingLoaded) {
      alert('Pricing has not finished loading from the server. Please wait or reload the page before saving.');
      return;
    }
    try {
      const merged = mergePricingSettings(pricingForm);
      await settingsAPI.set('pricingSettings', JSON.stringify(merged));
      onPricingSettingsSaved(merged);
      setPricingSaveMessage('Pricing saved. New registrations will use these amounts.');
    } catch (e) {
      alert(e.message || 'Failed to save pricing.');
    }
  };

  const handleSaveFeeWaivers = async () => {
    setFwSaveMessage('');
    if (!fwLoaded) {
      alert('Fee waiver settings have not finished loading from the server. Please wait or reload the page before saving.');
      return;
    }
    try {
      const feeWaivers = (feeWaiverForm.feeWaivers || []).map((w) => {
        const capRaw = w.sponsorCap === '' || w.sponsorCap == null ? '' : parseFloat(w.sponsorCap);
        const cap = Number.isFinite(capRaw) && capRaw > 0 ? capRaw : '';
        let remRaw = w.remainingBalance === '' || w.remainingBalance == null ? '' : parseFloat(w.remainingBalance);
        if (cap !== '' && (!Number.isFinite(remRaw) || remRaw < 0)) remRaw = cap;
        if (cap !== '' && Number.isFinite(remRaw)) remRaw = Math.min(cap, Math.max(0, remRaw));
        return {
          id: w.id || `fw_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          code: String(w.code || '').trim().toUpperCase(),
          sponsorType: w.sponsorType === 'individual' ? 'individual' : 'church',
          churchName: String(w.churchName || '').trim(),
          individualName: String(w.individualName || '').trim(),
          sponsorCap: cap === '' ? '' : cap,
          remainingBalance: cap === '' ? '' : remRaw,
          blockWhenExhausted: !!w.blockWhenExhausted,
          active: w.active !== false
        };
      }).filter((w) => w.code);
      const discounts = (feeWaiverForm.discounts || []).map((d) => ({
        id: d.id || `disc_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        code: String(d.code || '').trim().toUpperCase(),
        discountType: d.discountType === 'fixed' ? 'fixed' : 'percent',
        value: Math.max(0, parseFloat(d.value) || 0),
        active: d.active !== false,
        maxUses:
          d.maxUses === '' || d.maxUses == null
            ? ''
            : Math.max(0, parseInt(d.maxUses, 10) || 0),
        usedCount: Math.max(0, parseInt(d.usedCount, 10) || 0)
      })).filter((d) => d.code);
      const merged = { feeWaivers, discounts };
      await settingsAPI.set('feeWaiverSettings', JSON.stringify(merged));
      onFeeWaiverSettingsSaved(merged);
      setFeeWaiverForm(merged);
      setFwSaveMessage('Fee waivers & discounts saved.');
    } catch (e) {
      alert(e.message || 'Failed to save fee waivers.');
    }
  };

  const handleResetPricing = () => {
    setPricingForm({ ...DEFAULT_PRICING_SETTINGS });
    setPricingSaveMessage('Reverted to defaults — click Save pricing to persist.');
  };

  const setEmailField = (key, value) => {
    setEmailForm(prev => ({ ...prev, [key]: value }));
    setEmailSaveMessage('');
  };

  const emailFromValid = isValidEmailAddress(emailForm.fromEmail);
  const emailReplyValid = isValidEmailAddress(emailForm.replyToEmail);
  const emailAlwaysCcValid =
    !String(emailForm.alwaysCcEmail || '').trim() || isValidEmailAddress(emailForm.alwaysCcEmail);

  const handleSaveEmail = async () => {
    setEmailSaveMessage('');
    if (!emailLoaded) {
      alert('Email addresses have not finished loading from the server. Please wait or reload the page before saving.');
      return;
    }
    if (!emailFromValid || !emailReplyValid) {
      alert('Please enter valid email addresses for both From and Reply-To.');
      return;
    }
    if (!emailAlwaysCcValid) {
      alert('Always CC must be empty or a valid email address.');
      return;
    }
    try {
      const merged = mergeEmailSettings({
        fromEmail: emailForm.fromEmail,
        replyToEmail: emailForm.replyToEmail,
        fromDisplayName: emailForm.fromDisplayName,
        alwaysCcEmail: emailForm.alwaysCcEmail
      });
      await settingsAPI.set('emailSettings', JSON.stringify(merged));
      onEmailSettingsSaved(merged);
      setEmailSaveMessage('Email addresses saved. New outbound emails will use these values.');
    } catch (e) {
      alert(e.message || 'Failed to save email addresses.');
    }
  };

  const handleResetEmail = () => {
    setEmailForm({ ...DEFAULT_EMAIL_SETTINGS });
    setEmailSaveMessage('Reverted to defaults — click Save email addresses to persist.');
  };

  const priceInput = (key, label, hint) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
        <input
          type="number"
          min="0"
          step="1"
          value={pricingForm[key] ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            const num = v === '' ? '' : parseFloat(v);
            setPriceField(key, v === '' ? '' : (Number.isFinite(num) && num >= 0 ? num : pricingForm[key]));
          }}
          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg"
        />
      </div>
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );

  const handlePurgeRegistrations = async () => {
    if (!canPurgeRegistrations) return;
    if (purgeInput !== CLEAR_REGISTRATIONS_CONFIRMATION) {
      alert(`Type exactly: ${CLEAR_REGISTRATIONS_CONFIRMATION}`);
      return;
    }
    if (!window.confirm('Final confirmation: permanently delete ALL registration records? This cannot be undone.')) {
      return;
    }
    setPurgeBusy(true);
    try {
      await registrationsAPI.clearAll(
        CLEAR_REGISTRATIONS_CONFIRMATION,
        process.env.REACT_APP_ADMIN_PURGE_KEY || undefined
      );
      setPurgeInput('');
      onRegistrationsCleared();
      alert('All registration records have been removed.');
    } catch (e) {
      alert(e.message || 'Failed to clear registrations.');
    } finally {
      setPurgeBusy(false);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Camp Settings</h2>
        <p className="text-sm text-gray-600 mt-1">
          Registration gates, payments, public resources, email automation, and logistics toggles are grouped below. Further sections control the public landing page (title, dates, venue), pricing, and outbound email addresses.
        </p>
      </div>

      {refreshError && (
        <div className="bg-yellow-50 border border-yellow-300 text-yellow-900 rounded-lg px-4 py-3 text-sm">
          {refreshError} <button type="button" onClick={() => window.location.reload()} className="underline ml-1">Reload page</button>
        </div>
      )}

      <CampOperationalSettingsSections
        userRole={userRole}
        selfCheckInEnabled={selfCheckInEnabled}
        registrationsOpen={registrationsOpen}
        familyRegistrationsOpen={familyRegistrationsOpen}
        adultRegistrationsOpen={adultRegistrationsOpen}
        dayRegistrationsOpen={dayRegistrationsOpen}
        earlyBirdStudentEnabled={earlyBirdStudentEnabled}
        cardPaymentEnabled={cardPaymentEnabled}
        paypalEnabled={paypalEnabled}
        venmoEnabled={venmoEnabled}
        zelleEnabled={zelleEnabled}
        transportationEnabled={transportationEnabled}
        welcomePackageEnabled={welcomePackageEnabled}
        blockInitialRegistrationReceivedEmail={blockInitialRegistrationReceivedEmail}
        onToggleSelfCheckIn={onToggleSelfCheckIn}
        onToggleRegistrationsOpen={onToggleRegistrationsOpen}
        onToggleFamilyRegistrationsOpen={onToggleFamilyRegistrationsOpen}
        onToggleAdultRegistrationsOpen={onToggleAdultRegistrationsOpen}
        onToggleDayRegistrationsOpen={onToggleDayRegistrationsOpen}
        onToggleEarlyBirdStudent={onToggleEarlyBirdStudent}
        onToggleCardPayment={onToggleCardPayment}
        onTogglePaypal={onTogglePaypal}
        onToggleVenmo={onToggleVenmo}
        onToggleZelle={onToggleZelle}
        onToggleTransportation={onToggleTransportation}
        onToggleWelcomePackage={onToggleWelcomePackage}
        onToggleBlockInitialRegistrationReceivedEmail={onToggleBlockInitialRegistrationReceivedEmail}
      />

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="border-b border-gray-100 pb-3 mb-2">
          <h3 className="text-lg font-semibold text-gray-800">Landing page &amp; notices</h3>
          <p className="text-sm text-gray-600 mt-1">Hero content, dates, venue, theme, verse, and yellow registration notices.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Camp title (main headline)</label>
          <input type="text" value={form.campTitle || ''} onChange={(e) => setField('campTitle', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Registration portal URL (QR codes &amp; email links)</label>
          <input
            type="url"
            inputMode="url"
            value={form.registrationPortalBaseUrl || ''}
            onChange={(e) => setField('registrationPortalBaseUrl', e.target.value.trim())}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
            placeholder="https://your-camp-site.example (leave blank to use build-time REACT_APP_PUBLIC_URL)"
          />
          <p className="text-xs text-gray-500 mt-1">
            Full base URL where campers open the site (no <code className="bg-gray-100 px-1 rounded">?code=</code> part). When set, registration QR codes and links in emails use this host instead of an old value baked into the admin app build.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Camp start date</label>
            <input
              type="date"
              value={(form.campStartDate || '').trim().slice(0, 10)}
              onChange={(e) => setField('campStartDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">Used for day-registration options and display (unless you override the text below). Use “Day passes — which days” below to limit which dates appear on the Day registration form.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Camp end date</label>
            <input
              type="date"
              value={(form.campEndDate || '').trim().slice(0, 10)}
              onChange={(e) => setField('campEndDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">Inclusive last day of camp (max 14 days).</p>
          </div>
        </div>
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-700">
          <span className="font-medium text-gray-800">Preview (what visitors see if override is blank): </span>
          {formatCampDateRange((form.campStartDate || '').trim(), (form.campEndDate || '').trim()) || '— set start and end dates —'}
        </div>
        {campDayListForDayPasses.length > 0 && (
          <div className="rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-3 space-y-2">
            <div className="text-sm font-medium text-gray-800">Day passes — which days are available?</div>
            <p className="text-xs text-gray-600">
              Leave all unchecked to allow <strong>every</strong> day in the camp date range. Check specific dates to limit the Day registration form to only those days (and a combined option when two or more are selected).
            </p>
            <div className="flex flex-wrap gap-3">
              {campDayListForDayPasses.map((d) => {
                const iso = formatISODateLocal(d);
                const label = d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
                const checked = Array.isArray(form.dayPassAvailableDates) && form.dayPassAvailableDates.includes(iso);
                return (
                  <label key={iso} className="inline-flex items-center gap-2 text-sm text-gray-800 bg-white border border-gray-200 rounded-md px-2 py-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setForm((prev) => {
                          const cur = Array.isArray(prev.dayPassAvailableDates) ? [...prev.dayPassAvailableDates] : [];
                          if (on) {
                            if (!cur.includes(iso)) cur.push(iso);
                            cur.sort();
                            return { ...prev, dayPassAvailableDates: cur };
                          }
                          return { ...prev, dayPassAvailableDates: cur.filter((x) => x !== iso) };
                        });
                        setSaveMessage('');
                      }}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
            <button
              type="button"
              className="text-xs font-medium text-blue-700 hover:text-blue-900 underline"
              onClick={() => {
                setField('dayPassAvailableDates', []);
              }}
            >
              Reset — use all days in range
            </button>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date text override (optional)</label>
          <input
            type="text"
            value={form.campDates || ''}
            onChange={(e) => setField('campDates', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Leave blank to use the preview above"
          />
          <p className="text-xs text-gray-500 mt-1">Only fill this if you need custom wording instead of the automatic range.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Conference center name</label>
            <input type="text" value={form.conferenceCenterName || ''} onChange={(e) => setField('conferenceCenterName', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input type="text" value={form.location || ''} onChange={(e) => setField('location', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g. Texas" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Camp theme</label>
          <input type="text" value={form.campTheme || ''} onChange={(e) => setField('campTheme', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bible verse reference</label>
          <input type="text" value={form.bibleVerseReference || ''} onChange={(e) => setField('bibleVerseReference', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g. Hebrews 12:28-29" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bible verse text</label>
          <textarea value={form.bibleVerseText || ''} onChange={(e) => setField('bibleVerseText', e.target.value)} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Custom message (below title)</label>
          <input type="text" value={form.customMessage || ''} onChange={(e) => setField('customMessage', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>

        <div className="border-t border-gray-200 pt-6 mt-4">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Registration notices (yellow callout)</h3>
          <p className="text-xs text-gray-600 mb-4">
            These appear on the registration type selection screen and on registration forms. For Day registration, the second block below is used on the form. You may use simple HTML in text fields (e.g. <code className="bg-gray-100 px-1 rounded">&lt;strong&gt;text&lt;/strong&gt;</code>, <code className="bg-gray-100 px-1 rounded">&lt;a href=&quot;mailto:…&quot;&gt;</code>).
          </p>

          <p className="text-sm font-medium text-gray-700 mb-2">Standard (selection page + non-day registration forms)</p>
          <div className="space-y-3 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notice title</label>
              <input type="text" value={form.registrationNoticeTitle || ''} onChange={(e) => setField('registrationNoticeTitle', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Important Notice" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Main paragraph (HTML allowed)</label>
              <textarea value={form.registrationNoticeBodyHtml || ''} onChange={(e) => setField('registrationNoticeBodyHtml', e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment section heading</label>
              <input type="text" value={form.registrationPaymentPolicyTitle || ''} onChange={(e) => setField('registrationPaymentPolicyTitle', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Payment Policy:" />
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment bullet 1</label>
                <input type="text" value={form.registrationPaymentBullet1 || ''} onChange={(e) => setField('registrationPaymentBullet1', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment bullet 2</label>
                <input type="text" value={form.registrationPaymentBullet2 || ''} onChange={(e) => setField('registrationPaymentBullet2', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment bullet 3</label>
                <input type="text" value={form.registrationPaymentBullet3 || ''} onChange={(e) => setField('registrationPaymentBullet3', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm" />
              </div>
            </div>
          </div>

          <p className="text-sm font-medium text-gray-700 mb-2">Day registration form only</p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Intro paragraph (HTML allowed)</label>
              <textarea value={form.dayRegistrationNoticeIntroHtml || ''} onChange={(e) => setField('dayRegistrationNoticeIntroHtml', e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment section heading</label>
              <input type="text" value={form.dayRegistrationPaymentTitle || ''} onChange={(e) => setField('dayRegistrationPaymentTitle', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Payment:" />
            </div>
            {[1, 2, 3, 4].map((n) => (
              <div key={n}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{`Payment bullet ${n}`}</label>
                <input
                  type="text"
                  value={form[`dayRegistrationPaymentBullet${n}`] || ''}
                  onChange={(e) => setField(`dayRegistrationPaymentBullet${n}`, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6 mt-4 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Zelle, Venmo &amp; PayPal (sending details)</h3>
            <p className="text-sm text-gray-600 mt-1">
              These appear after each method name (e.g. <strong>Zelle – …</strong>) on the registration form, in admin payment dropdowns, and in registration emails. Day-registration yellow notice bullets are edited separately above if you need different wording.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zelle (recipient / email)</label>
              <input
                type="text"
                value={form.paymentZelleRecipient || ''}
                onChange={(e) => setField('paymentZelleRecipient', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder={DEFAULT_CAMP_SETTINGS.paymentZelleRecipient}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Venmo (username or business name)</label>
              <input
                type="text"
                value={form.paymentVenmoRecipient || ''}
                onChange={(e) => setField('paymentVenmoRecipient', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder={DEFAULT_CAMP_SETTINGS.paymentVenmoRecipient}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">PayPal (email or link label)</label>
              <input
                type="text"
                value={form.paymentPaypalRecipient || ''}
                onChange={(e) => setField('paymentPaypalRecipient', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder={DEFAULT_CAMP_SETTINGS.paymentPaypalRecipient}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6 mt-4 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Automated email layout</h3>
            <p className="text-sm text-gray-600 mt-1">
              Shared header and footer for registration emails sent from this app (received, confirmed, reminders, resend). Leave overrides blank to use your camp title, theme, verse, dates, and venue from above. Use <strong>Help &amp; support email</strong> below for footers and general contact. Zelle / Venmo / PayPal lines in emails follow <strong>Zelle, Venmo &amp; PayPal (sending details)</strong> above.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email header title (optional override)</label>
              <input
                type="text"
                value={form.emailTemplateHeaderTitle || ''}
                onChange={(e) => setField('emailTemplateHeaderTitle', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder={`Default: “${DEFAULT_CAMP_SETTINGS.campTitle}”`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email header subtitle (optional override)</label>
              <input
                type="text"
                value={form.emailTemplateHeaderSubtitle || ''}
                onChange={(e) => setField('emailTemplateHeaderSubtitle', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Default: theme and verse reference from above"
              />
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-2">
            <label className="block text-sm font-medium text-gray-700">Email header logo image (optional)</label>
            <p className="text-xs text-gray-600">
              PNG, JPEG, GIF, or WebP, max 400 KB. Shown as a <strong>100×100px circle</strong> centered above the blue header. Use a <strong>square</strong> image (e.g. 240×240) for correct cropping; Microsoft Outlook does not reliably support automatic cropping of wide rectangular artwork.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                className="text-sm max-w-full"
                onChange={(e) => {
                  const input = e.target;
                  const f = input.files && input.files[0];
                  input.value = '';
                  if (!f) return;
                  if (!/^image\/(png|jpeg|gif|webp)$/i.test(f.type)) {
                    alert('Please choose a PNG, JPEG, GIF, or WebP image.');
                    return;
                  }
                  const maxBytes = 400 * 1024;
                  if (f.size > maxBytes) {
                    alert('Image must be 400 KB or smaller so messages stay deliverable.');
                    return;
                  }
                  const reader = new FileReader();
                  reader.onload = () => {
                    const url = reader.result;
                    if (typeof url !== 'string') return;
                    const compact = url.replace(/\s/g, '');
                    if (compact.length > EMAIL_BANNER_DATA_URL_MAX_LEN) {
                      alert('That image is too large after encoding. Try a smaller or more compressed file.');
                      return;
                    }
                    setField('emailTemplateBannerDataUrl', compact);
                  };
                  reader.onerror = () => alert('Could not read the image file.');
                  reader.readAsDataURL(f);
                }}
              />
              {form.emailTemplateBannerDataUrl ? (
                <button
                  type="button"
                  onClick={() => setField('emailTemplateBannerDataUrl', '')}
                  className="text-sm px-3 py-1 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Remove logo
                </button>
              ) : null}
            </div>
            {form.emailTemplateBannerDataUrl ? (
              <div className="pt-2 border-t border-gray-200 mt-2">
                <p className="text-xs text-gray-500 mb-2">Preview</p>
                <img
                  src={form.emailTemplateBannerDataUrl}
                  alt=""
                  className="w-[100px] h-[100px] rounded-full object-cover border border-gray-200 bg-white mx-auto block"
                />
              </div>
            ) : null}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Footer team line</label>
              <input
                type="text"
                value={form.emailFooterTeamName || ''}
                onChange={(e) => setField('emailFooterTeamName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="e.g. ICPF Awake Camp Team"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Footer venue line (optional override)</label>
              <input
                type="text"
                value={form.emailFooterVenueLine || ''}
                onChange={(e) => setField('emailFooterVenueLine', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Default: conference center and location from above"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Date line in the footer uses <strong>Date text override</strong> or your camp start/end dates (same as the landing page).
          </p>
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-4 mb-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-1">Help &amp; support email</h4>
            <p className="text-xs text-gray-700 mb-3">
              Used in registration email footers, the API check-in confirmation footer, and on-screen &ldquo;contact us&rdquo; notices when registration types are closed. <strong>Not</strong> the same as your Zelle/Venmo/PayPal <em>sending</em> lines—configure those under <strong>Zelle, Venmo &amp; PayPal (sending details)</strong> above.
            </p>
            <label className="block text-xs font-medium text-gray-600 mb-1">Email address</label>
            <input
              type="email"
              value={form.emailFooterSupportEmail || ''}
              onChange={(e) => setField('emailFooterSupportEmail', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
              placeholder={DEFAULT_CAMP_SETTINGS.emailFooterSupportEmail}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact box heading</label>
              <input
                type="text"
                value={form.emailFooterContactHeading || ''}
                onChange={(e) => setField('emailFooterContactHeading', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Questions? Contact Us:"
              />
            </div>
          </div>
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">Footer contacts (name, role, phone)</label>
              <button
                type="button"
                onClick={() => {
                  setForm((prev) => ({
                    ...prev,
                    emailFooterContacts: [
                      ...(Array.isArray(prev.emailFooterContacts) ? prev.emailFooterContacts : []),
                      { name: '', role: '', phone: '' }
                    ]
                  }));
                  setSaveMessage('');
                }}
                className="text-sm px-3 py-1 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Add row
              </button>
            </div>
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
              {(Array.isArray(form.emailFooterContacts) ? form.emailFooterContacts : []).map((row, idx) => (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end bg-white p-3 rounded border border-gray-100">
                  <div className="sm:col-span-3">
                    <label className="block text-xs text-gray-500 mb-0.5">Name</label>
                    <input
                      type="text"
                      value={row.name || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm((prev) => {
                          const list = [...(Array.isArray(prev.emailFooterContacts) ? prev.emailFooterContacts : [])];
                          list[idx] = { ...list[idx], name: v };
                          return { ...prev, emailFooterContacts: list };
                        });
                        setSaveMessage('');
                      }}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div className="sm:col-span-4">
                    <label className="block text-xs text-gray-500 mb-0.5">Role</label>
                    <input
                      type="text"
                      value={row.role || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm((prev) => {
                          const list = [...(Array.isArray(prev.emailFooterContacts) ? prev.emailFooterContacts : [])];
                          list[idx] = { ...list[idx], role: v };
                          return { ...prev, emailFooterContacts: list };
                        });
                        setSaveMessage('');
                      }}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <label className="block text-xs text-gray-500 mb-0.5">Phone</label>
                    <input
                      type="text"
                      value={row.phone || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm((prev) => {
                          const list = [...(Array.isArray(prev.emailFooterContacts) ? prev.emailFooterContacts : [])];
                          list[idx] = { ...list[idx], phone: v };
                          return { ...prev, emailFooterContacts: list };
                        });
                        setSaveMessage('');
                      }}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2 flex sm:justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setForm((prev) => {
                          const list = [...(Array.isArray(prev.emailFooterContacts) ? prev.emailFooterContacts : [])];
                          list.splice(idx, 1);
                          return { ...prev, emailFooterContacts: list };
                        });
                        setSaveMessage('');
                      }}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">Up to 12 rows. Empty rows are dropped when you save.</p>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Family registration</h4>
          <div className="max-w-sm">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="familyFreeChildMaxAge">
              Max age for free Below Elementary children (inclusive)
            </label>
            <input
              id="familyFreeChildMaxAge"
              type="number"
              min={0}
              max={18}
              value={form.familyFreeChildMaxAge ?? DEFAULT_CAMP_SETTINGS.familyFreeChildMaxAge}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') {
                  setField('familyFreeChildMaxAge', '');
                  return;
                }
                const n = parseInt(v, 10);
                setField('familyFreeChildMaxAge', Number.isFinite(n) ? n : form.familyFreeChildMaxAge);
              }}
              className="w-28 px-3 py-2 border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">
              Children in the Below Elementary category with age at most this number pay $0 toward the family flat fee; the fee is split only among the other family members (0–18).
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={handleSaveCampSettings}
            disabled={!campLoaded}
            title={!campLoaded ? 'Loading current values from the server…' : ''}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {campLoaded ? 'Save camp settings' : 'Loading…'}
          </button>
          {saveMessage && <span className="text-sm text-green-700">{saveMessage}</span>}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Registration Pricing</h3>
          <p className="text-xs text-gray-600 mt-1">
            Amounts shown on the registration cards and used to calculate totals at checkout. Family registrations use the family flat rates below; for the split, Below Elementary children age {getFamilyFreeChildMaxAge(form)} and under pay $0 (set in the Family registration section above).
          </p>
        </div>

        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Individual</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {priceInput('student', 'Student')}
            {priceInput('earlyBirdStudent', 'Early Bird Student', 'Shown when Early Bird is enabled.')}
            {priceInput('adult', 'Adult')}
            {priceInput('test', 'Test (admin only)')}
          </div>
        </div>

        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Day Registration</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {priceInput('dayOneDay', 'Single camp day', 'Used when a registrant selects one day in range.')}
            {priceInput('dayTwoDay', 'Full camp (exactly two days)', 'Used when camp is two consecutive days and they choose both days.')}
          </div>
        </div>

        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Family (flat price per family size)</h4>
          <p className="text-xs text-gray-500 mb-3">Each option is a single flat amount for the whole family, regardless of the mix of adults and students. The total is split across paying members on the receipt (Below Elementary at age {getFamilyFreeChildMaxAge(form)} and under are excluded from the split).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {priceInput('family2', 'Family of 2 (flat)')}
            {priceInput('family3', 'Family of 3 (flat)')}
            {priceInput('family4', 'Family of 4 (flat)')}
            {priceInput('family5', 'Family of 5 (flat)')}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={handleSavePricing}
            disabled={!pricingLoaded}
            title={!pricingLoaded ? 'Loading current values from the server…' : ''}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pricingLoaded ? 'Save pricing' : 'Loading…'}
          </button>
          <button
            type="button"
            onClick={handleResetPricing}
            disabled={!pricingLoaded}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset to defaults
          </button>
          {pricingSaveMessage && <span className="text-sm text-green-700">{pricingSaveMessage}</span>}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Fee waivers &amp; discounts</h3>
          <p className="text-xs text-gray-600 mt-1">
            Discounts apply first, then sponsor waivers, to each registration line. Family registrations use separate codes per member. Optional sponsor cap tracks remaining balance; enable &quot;Block if balance can&apos;t cover full line&quot; to reject a code when the member&apos;s share exceeds what&apos;s left.
          </p>
        </div>
        <div className="border-t pt-3 space-y-3">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-700">Sponsor fee waivers</h4>
            <button
              type="button"
              className="text-sm text-blue-600 hover:underline"
              onClick={() =>
                setFeeWaiverForm((prev) => ({
                  ...prev,
                  feeWaivers: [
                    ...(prev.feeWaivers || []),
                    {
                      id: `fw_${Date.now()}`,
                      code: '',
                      sponsorType: 'church',
                      churchName: '',
                      individualName: '',
                      sponsorCap: '',
                      remainingBalance: '',
                      blockWhenExhausted: false,
                      active: true
                    }
                  ]
                }))
              }
            >
              Add fee waiver
            </button>
          </div>
          {(feeWaiverForm.feeWaivers || []).map((w, idx) => (
            <div key={w.id || idx} className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <input
                  className="px-2 py-1.5 border border-gray-300 rounded text-sm font-mono uppercase"
                  placeholder="Waiver code"
                  value={w.code || ''}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    setFeeWaiverForm((prev) => {
                      const arr = [...(prev.feeWaivers || [])];
                      arr[idx] = { ...arr[idx], code: v };
                      return { ...prev, feeWaivers: arr };
                    });
                  }}
                />
                <select
                  className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                  value={w.sponsorType || 'church'}
                  onChange={(e) => {
                    setFeeWaiverForm((prev) => {
                      const arr = [...(prev.feeWaivers || [])];
                      arr[idx] = { ...arr[idx], sponsorType: e.target.value };
                      return { ...prev, feeWaivers: arr };
                    });
                  }}
                >
                  <option value="church">Church sponsor</option>
                  <option value="individual">Individual sponsor</option>
                </select>
                <input
                  className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="Church name"
                  value={w.churchName || ''}
                  onChange={(e) => {
                    setFeeWaiverForm((prev) => {
                      const arr = [...(prev.feeWaivers || [])];
                      arr[idx] = { ...arr[idx], churchName: e.target.value };
                      return { ...prev, feeWaivers: arr };
                    });
                  }}
                />
                <input
                  className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="Individual sponsor name"
                  value={w.individualName || ''}
                  onChange={(e) => {
                    setFeeWaiverForm((prev) => {
                      const arr = [...(prev.feeWaivers || [])];
                      arr[idx] = { ...arr[idx], individualName: e.target.value };
                      return { ...prev, feeWaivers: arr };
                    });
                  }}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 items-center">
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="Sponsor cap ($) optional"
                  value={w.sponsorCap === undefined || w.sponsorCap === null ? '' : w.sponsorCap}
                  onChange={(e) => {
                    setFeeWaiverForm((prev) => {
                      const arr = [...(prev.feeWaivers || [])];
                      arr[idx] = { ...arr[idx], sponsorCap: e.target.value };
                      return { ...prev, feeWaivers: arr };
                    });
                  }}
                />
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="Remaining ($)"
                  value={w.remainingBalance === undefined || w.remainingBalance === null ? '' : w.remainingBalance}
                  onChange={(e) => {
                    setFeeWaiverForm((prev) => {
                      const arr = [...(prev.feeWaivers || [])];
                      arr[idx] = { ...arr[idx], remainingBalance: e.target.value };
                      return { ...prev, feeWaivers: arr };
                    });
                  }}
                />
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!w.blockWhenExhausted}
                    onChange={(e) => {
                      setFeeWaiverForm((prev) => {
                        const arr = [...(prev.feeWaivers || [])];
                        arr[idx] = { ...arr[idx], blockWhenExhausted: e.target.checked };
                        return { ...prev, feeWaivers: arr };
                      });
                    }}
                  />
                  Block if balance can&apos;t cover full line
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={w.active !== false}
                    onChange={(e) => {
                      setFeeWaiverForm((prev) => {
                        const arr = [...(prev.feeWaivers || [])];
                        arr[idx] = { ...arr[idx], active: e.target.checked };
                        return { ...prev, feeWaivers: arr };
                      });
                    }}
                  />
                  Active
                </label>
              </div>
              <button
                type="button"
                className="text-xs text-red-600 hover:underline"
                onClick={() =>
                  setFeeWaiverForm((prev) => ({
                    ...prev,
                    feeWaivers: (prev.feeWaivers || []).filter((_, i) => i !== idx)
                  }))
                }
              >
                Remove waiver
              </button>
            </div>
          ))}
        </div>
        <div className="border-t pt-3 space-y-3">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-700">Discounts</h4>
            <button
              type="button"
              className="text-sm text-blue-600 hover:underline"
              onClick={() =>
                setFeeWaiverForm((prev) => ({
                  ...prev,
                  discounts: [
                    ...(prev.discounts || []),
                    {
                      id: `disc_${Date.now()}`,
                      code: '',
                      discountType: 'percent',
                      value: 10,
                      maxUses: '',
                      usedCount: 0,
                      active: true
                    }
                  ]
                }))
              }
            >
              Add discount
            </button>
          </div>
          {(feeWaiverForm.discounts || []).map((d, idx) => (
            <div key={d.id || idx} className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 items-center">
                <input
                  className="px-2 py-1.5 border border-gray-300 rounded text-sm font-mono uppercase"
                  placeholder="Discount code"
                  value={d.code || ''}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    setFeeWaiverForm((prev) => {
                      const arr = [...(prev.discounts || [])];
                      arr[idx] = { ...arr[idx], code: v };
                      return { ...prev, discounts: arr };
                    });
                  }}
                />
                <select
                  className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                  value={d.discountType || 'percent'}
                  onChange={(e) => {
                    setFeeWaiverForm((prev) => {
                      const arr = [...(prev.discounts || [])];
                      arr[idx] = { ...arr[idx], discountType: e.target.value };
                      return { ...prev, discounts: arr };
                    });
                  }}
                >
                  <option value="percent">Percent off</option>
                  <option value="fixed">Fixed amount ($)</option>
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="Value"
                  value={d.value === undefined || d.value === null ? '' : d.value}
                  onChange={(e) => {
                    setFeeWaiverForm((prev) => {
                      const arr = [...(prev.discounts || [])];
                      arr[idx] = { ...arr[idx], value: e.target.value };
                      return { ...prev, discounts: arr };
                    });
                  }}
                />
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="Max uses (blank = unlimited)"
                  value={d.maxUses === undefined || d.maxUses === null || d.maxUses === '' ? '' : d.maxUses}
                  onChange={(e) => {
                    setFeeWaiverForm((prev) => {
                      const arr = [...(prev.discounts || [])];
                      arr[idx] = { ...arr[idx], maxUses: e.target.value };
                      return { ...prev, discounts: arr };
                    });
                  }}
                />
              </div>
              <p className="text-xs text-gray-500">Times used (read-only): {d.usedCount || 0}</p>
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={d.active !== false}
                  onChange={(e) => {
                    setFeeWaiverForm((prev) => {
                      const arr = [...(prev.discounts || [])];
                      arr[idx] = { ...arr[idx], active: e.target.checked };
                      return { ...prev, discounts: arr };
                    });
                  }}
                />
                Active
              </label>
              <button
                type="button"
                className="text-xs text-red-600 hover:underline"
                onClick={() =>
                  setFeeWaiverForm((prev) => ({
                    ...prev,
                    discounts: (prev.discounts || []).filter((_, i) => i !== idx)
                  }))
                }
              >
                Remove discount
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={handleSaveFeeWaivers}
            disabled={!fwLoaded}
            title={!fwLoaded ? 'Loading current values from the server…' : ''}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {fwLoaded ? 'Save fee waivers & discounts' : 'Loading…'}
          </button>
          {fwSaveMessage && <span className="text-sm text-green-700">{fwSaveMessage}</span>}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Email Addresses</h3>
          <p className="text-xs text-gray-600 mt-1">
            Controls the visible <strong>From</strong> and <strong>Reply-To</strong> addresses on every outbound email (registration confirmations, payment receipts, OTPs, etc.). Optional <strong>From display name</strong> sets the label shown next to the address in the inbox. Optional <strong>Always CC</strong> adds one address to the CC line on <em>every</em> message the API sends (merged with any CC included in a given send request). Saved values take effect immediately and override the corresponding env vars (<code className="bg-gray-100 px-1 rounded">O365_FROM_EMAIL</code>, <code className="bg-gray-100 px-1 rounded">EMAIL_REPLY_TO</code>, <code className="bg-gray-100 px-1 rounded">O365_FROM_NAME</code> / <code className="bg-gray-100 px-1 rounded">RESEND_FROM_NAME</code> when display name is set). You can also set a default always-CC with <code className="bg-gray-100 px-1 rounded">EMAIL_ALWAYS_CC</code> on the server if this field is left blank. The SMTP/Graph login account itself is not changed by these fields.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From display name (optional)</label>
          <input
            type="text"
            value={emailForm.fromDisplayName || ''}
            onChange={(e) => setEmailField('fromDisplayName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="e.g. ICPF Awake Camp (leave blank to use server env default)"
          />
          <p className="text-xs text-gray-500 mt-1">Recipients see this next to your From address. Leave blank to use <code className="bg-gray-100 px-1 rounded">RESEND_FROM_NAME</code> or <code className="bg-gray-100 px-1 rounded">O365_FROM_NAME</code> from the API server environment.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From email address</label>
            <input
              type="email"
              value={emailForm.fromEmail || ''}
              onChange={(e) => setEmailField('fromEmail', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${emailFromValid ? 'border-gray-300' : 'border-red-400'}`}
              placeholder="e.g. icpfsecamp@icpfse.org"
            />
            {!emailFromValid && <p className="text-xs text-red-600 mt-1">Enter a valid email address.</p>}
            <p className="text-xs text-gray-500 mt-1">Shown as the sender. Should match a mailbox your sending account is authorized to send as.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reply-To email address</label>
            <input
              type="email"
              value={emailForm.replyToEmail || ''}
              onChange={(e) => setEmailField('replyToEmail', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${emailReplyValid ? 'border-gray-300' : 'border-red-400'}`}
              placeholder="e.g. johanjeson2002@gmail.com"
            />
            {!emailReplyValid && <p className="text-xs text-red-600 mt-1">Enter a valid email address.</p>}
            <p className="text-xs text-gray-500 mt-1">Where replies from recipients are delivered.</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Always CC (optional)</label>
          <input
            type="email"
            value={emailForm.alwaysCcEmail || ''}
            onChange={(e) => setEmailField('alwaysCcEmail', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg ${emailAlwaysCcValid ? 'border-gray-300' : 'border-red-400'}`}
            placeholder="e.g. coordinator@example.org — copied on every outbound email"
          />
          {!emailAlwaysCcValid && <p className="text-xs text-red-600 mt-1">Enter a valid email or leave blank.</p>}
          <p className="text-xs text-gray-500 mt-1">
            This address is added on the server for all sends (including OTPs, check-in, and registration mail). It is merged with any message-specific CC so the same address is not duplicated.
          </p>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={handleSaveEmail}
            disabled={!emailLoaded || !emailFromValid || !emailReplyValid || !emailAlwaysCcValid}
            title={!emailLoaded ? 'Loading current values from the server…' : ''}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {emailLoaded ? 'Save email addresses' : 'Loading…'}
          </button>
          <button
            type="button"
            onClick={handleResetEmail}
            disabled={!emailLoaded}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset to defaults
          </button>
          {emailSaveMessage && <span className="text-sm text-green-700">{emailSaveMessage}</span>}
        </div>
      </div>

      {canPurgeRegistrations && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
          <h3 className="text-lg font-bold text-red-900">Refresh registration data</h3>
          <p className="text-sm text-red-800 mt-2">
            Permanently deletes <strong>all</strong> registration records from the database. Users, lodges, settings, and waivers on disk are not removed by this action. This operation cannot be undone.
          </p>
          {process.env.REACT_APP_ADMIN_PURGE_KEY && (
            <p className="text-xs text-red-700 mt-2">Server also requires matching <code className="bg-red-100 px-1 rounded">REACT_APP_ADMIN_PURGE_KEY</code> and <code className="bg-red-100 px-1 rounded">ADMIN_PURGE_KEY</code> on the API.</p>
          )}
          <p className="text-sm font-mono bg-white border border-red-200 rounded px-2 py-1 mt-3 text-red-900">
            Type this phrase to enable the button: {CLEAR_REGISTRATIONS_CONFIRMATION}
          </p>
          <input
            type="text"
            value={purgeInput}
            onChange={(e) => setPurgeInput(e.target.value)}
            placeholder="Confirmation phrase"
            className="mt-3 w-full px-3 py-2 border border-red-300 rounded-lg"
            autoComplete="off"
          />
          <button
            type="button"
            disabled={purgeBusy || purgeInput !== CLEAR_REGISTRATIONS_CONFIRMATION}
            onClick={handlePurgeRegistrations}
            className="mt-4 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {purgeBusy ? 'Working…' : 'Delete all registrations'}
          </button>
        </div>
      )}
      {!canPurgeRegistrations && (
        <p className="text-xs text-gray-500">Only Application Admins can run &quot;Refresh registration data.&quot;</p>
      )}
    </div>
  );
}

// Landing Page Component
function LandingPage({ onNavigate, selfCheckInEnabled, registrationsOpen, dayRegistrationsOpen = true, settingLoaded = false, registrations, maxGeneralSeats, maxFamilySeats, transportationEnabled, roomAttendanceEnabled = false, welcomePackageEnabled = true, campSettings: camp = DEFAULT_CAMP_SETTINGS }) {
  // Calculate seats taken (day and earlyBirdStudent count toward general)
  const generalRegistrations = registrations.filter(r => 
    r.type === 'student' || r.type === 'adult' || r.type === 'test' || r.type === 'earlyBirdStudent' || r.type === 'day'
  );
  const familyRegistrations = registrations.filter(r => 
    r.type && r.type.startsWith('family')
  );
  
  const generalSeatsTaken = generalRegistrations.length;
  const familySeatsTaken = familyRegistrations.length;
  // eslint-disable-next-line no-unused-vars
  const generalSeatsLeft = maxGeneralSeats - generalSeatsTaken;
  // eslint-disable-next-line no-unused-vars
  const familySeatsLeft = maxFamilySeats - familySeatsTaken;
  
  // Landing hero background (static image or replace path with .gif / video sources)
  const landingBackground = process.env.PUBLIC_URL + '/landing-background.png';
  const logoPath = process.env.PUBLIC_URL + '/logo.png'; // Update with your logo path
  
  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 w-full h-full z-0">
        <img
          src={landingBackground}
          alt="Background"
          className="w-full h-full object-cover"
          style={{ minHeight: '100vh' }}
          onError={(e) => {
            // Fallback to gradient if image not found
            e.target.style.display = 'none';
            e.target.parentElement.classList.add('bg-gradient-to-br', 'from-blue-500', 'to-blue-700');
          }}
        />
        {/* Alternative: Use video element if using MP4/WebM
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
          style={{ minHeight: '100vh' }}
        >
          <source src={landingBackground} type="video/mp4" />
          <source src={landingBackground} type="video/webm" />
          Your browser does not support the video tag.
        </video>
        */}
      </div>
      
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black bg-opacity-40 z-10"></div>
      
      {/* Logo in top right */}
      <div className="absolute top-4 right-4 z-20">
        <img
          src={logoPath}
          alt="ICPF Logo"
          className="h-10 sm:h-16 w-auto object-contain max-w-[120px] sm:max-w-none"
          onError={(e) => {
            // Hide logo if not found
            e.target.style.display = 'none';
          }}
        />
      </div>
      
      {/* Content */}
      <div className="max-w-4xl w-full relative z-10 pt-16 sm:pt-0">
        <div className="text-center mb-12 px-4 sm:px-0">
          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-4 drop-shadow-lg pr-2 sm:pr-0">{camp.campTitle || DEFAULT_CAMP_SETTINGS.campTitle}</h1>
          <p className="text-xl text-blue-100 drop-shadow-md mb-2">{camp.customMessage || DEFAULT_CAMP_SETTINGS.customMessage}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-lg text-blue-100 drop-shadow-md mb-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{getDisplayCampDates(mergeCampSettings(camp))}</span>
            </div>
            <div className="flex items-center gap-2">
              <Home className="w-5 h-5" />
              <span>{[camp.conferenceCenterName, camp.location].filter(Boolean).join(', ') || [DEFAULT_CAMP_SETTINGS.conferenceCenterName, DEFAULT_CAMP_SETTINGS.location].join(', ')}</span>
            </div>
          </div>
          {/* Camp Theme */}
          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-6 border-2 border-white border-opacity-30 max-w-2xl mx-auto">
            <div className="flex flex-col items-center gap-3">
              <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg mb-2">{camp.campTheme || DEFAULT_CAMP_SETTINGS.campTheme}</h2>
              <p className="text-lg md:text-xl text-blue-100 drop-shadow-md italic mb-3">{camp.bibleVerseReference || DEFAULT_CAMP_SETTINGS.bibleVerseReference}</p>
              <p className="text-base md:text-lg text-white drop-shadow-md text-center leading-relaxed">
                &ldquo;{camp.bibleVerseText || DEFAULT_CAMP_SETTINGS.bibleVerseText}&rdquo;
              </p>
            </div>
          </div>
        </div>

        {/* Only show "registrations closed" banner when ALL registration types are closed */}
        {settingLoaded && !registrationsOpen && !dayRegistrationsOpen && (
          <div className="bg-red-50 border-2 border-red-400 rounded-xl p-6 mb-6 text-center">
            <XCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-800 mb-2">Registrations Are Currently Closed</h2>
            <p className="text-red-700 text-lg">
              We are no longer accepting new registrations at this time.
            </p>
          </div>
        )}

        <div
          className={`grid gap-6 ${
            welcomePackageEnabled || selfCheckInEnabled || transportationEnabled || roomAttendanceEnabled
              ? 'md:grid-cols-2 lg:grid-cols-3'
              : 'md:grid-cols-2'
          }`}
        >
          {/* Welcome Package - Open access static PDF downloads */}
          {welcomePackageEnabled && (
            <button
              onClick={() => onNavigate('welcomePackage')}
              className="bg-white rounded-xl shadow-2xl p-8 transition-all transform text-left group hover:shadow-3xl hover:-translate-y-2 cursor-pointer"
            >
              <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6 transition bg-teal-100 group-hover:bg-teal-200">
                <FileText className="w-8 h-8 text-teal-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">Welcome Package</h2>
              <p className="text-gray-600 mb-4">
                Welcome package and camp site map.
              </p>
              <div className="flex items-center text-teal-600 font-semibold">
                <span>View & Download</span>
                <span className="ml-2 group-hover:ml-4 transition-all">→</span>
              </div>
            </button>
          )}

          {/* Self Check-In Card - Only shown when admin enables it */}
          {selfCheckInEnabled && (
            <button
              onClick={() => onNavigate('selfCheckIn')}
              className="bg-white rounded-xl shadow-2xl p-8 hover:shadow-3xl transition-all transform hover:-translate-y-2 text-left group"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-6 group-hover:bg-purple-200 transition">
                <QrCode className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">Self Check-In</h2>
              <p className="text-gray-600 mb-4">
                Check in for the camp. Search for your registration and complete your check-in.
              </p>
              <div className="flex items-center text-purple-600 font-semibold">
                <span>Check In Now</span>
                <span className="ml-2 group-hover:ml-4 transition-all">→</span>
              </div>
            </button>
          )}

          {/* Camp Registration Form Card - enabled when any registration type is open (Day has its own toggle, independent of main registrations) */}
          <button
            onClick={() => settingLoaded && (registrationsOpen || dayRegistrationsOpen) && onNavigate('selectRegistrationType')}
            disabled={!settingLoaded || (!registrationsOpen && !dayRegistrationsOpen)}
            className={`bg-white rounded-xl shadow-2xl p-8 transition-all transform text-left group ${
              settingLoaded && (registrationsOpen || dayRegistrationsOpen)
                ? 'hover:shadow-3xl hover:-translate-y-2 cursor-pointer'
                : 'opacity-50 cursor-not-allowed'
            }`}
          >
            <div className={`flex items-center justify-center w-16 h-16 rounded-full mb-6 transition ${
              settingLoaded && (registrationsOpen || dayRegistrationsOpen) ? 'bg-blue-100 group-hover:bg-blue-200' : 'bg-gray-200'
            }`}>
              <Users className={`w-8 h-8 ${settingLoaded && (registrationsOpen || dayRegistrationsOpen) ? 'text-blue-600' : 'text-gray-400'}`} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Camp Registration Form</h2>
            <p className="text-gray-600 mb-4">
              {!settingLoaded
                ? 'Loading...'
                : (registrationsOpen || dayRegistrationsOpen)
                  ? registrationsOpen
                    ? 'Register for the camp experience. Fill out the registration form with your details.'
                    : 'Day registration is still open. Register for day-only attendance.'
                  : 'Registrations are currently closed. Please check back later.'
              }
            </p>
            {settingLoaded && (registrationsOpen || dayRegistrationsOpen) && (
            <div className="flex items-center text-blue-600 font-semibold">
              <span>Start Registration</span>
              <span className="ml-2 group-hover:ml-4 transition-all">→</span>
            </div>
            )}
          </button>

          {/* Check Registration Details Card */}
          <button
            onClick={() => onNavigate('checkRegistration')}
            className="bg-white rounded-xl shadow-2xl p-8 hover:shadow-3xl transition-all transform hover:-translate-y-2 text-left group"
          >
            <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6 group-hover:bg-green-200 transition">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Check Registration Details and Lodge Assignments</h2>
            <p className="text-gray-600 mb-4">
              Already registered? View your registration status, payment details, and lodge assignment.
            </p>
            <div className="flex items-center text-green-600 font-semibold">
              <span>Check Status</span>
              <span className="ml-2 group-hover:ml-4 transition-all">→</span>
            </div>
          </button>

          {/* Transportation Request Card - Only shown when admin enables it. Available even when registrations are closed. */}
          {transportationEnabled && (
            <button
              onClick={() => onNavigate('transportationForm')}
              className="bg-white rounded-xl shadow-2xl p-8 transition-all transform text-left group hover:shadow-3xl hover:-translate-y-2 cursor-pointer"
            >
              <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6 transition bg-orange-100 group-hover:bg-orange-200">
                <Bus className="w-8 h-8 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">Transportation Request</h2>
              <p className="text-gray-600 mb-4">
                Request transportation from IPC Hebron Parking Lot to the campsite. One-way ($40) or round trip ($80).
              </p>
              <div className="flex items-center text-orange-600 font-semibold">
                <span>Request Transportation</span>
                <span className="ml-2 group-hover:ml-4 transition-all">→</span>
              </div>
            </button>
          )}

          {/* Room Attendance - Only shown when enabled by Room Admin or App Admin in Lodging. */}
          {roomAttendanceEnabled && (
            <button
              onClick={() => onNavigate('roomAttendance')}
              className="bg-white rounded-xl shadow-2xl p-8 transition-all transform text-left group hover:shadow-3xl hover:-translate-y-2 cursor-pointer"
            >
              <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6 transition bg-indigo-100 group-hover:bg-indigo-200">
                <ClipboardList className="w-8 h-8 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">Room Attendance</h2>
              <p className="text-gray-600 mb-4">
                Room leaders: enter your room code and submit nightly attendance (present/absent) for your room.
              </p>
              <div className="flex items-center text-indigo-600 font-semibold">
                <span>Submit Attendance</span>
                <span className="ml-2 group-hover:ml-4 transition-all">→</span>
              </div>
            </button>
          )}
        </div>

        {/* Admin Login Link */}
        <div className="text-center mt-12">
          <button
            onClick={() => onNavigate('adminLogin')}
            className="text-white hover:text-blue-100 text-sm underline"
          >
            Admin Login
          </button>
        </div>
      </div>
    </div>
  );
}

// Password Change Modal Component
function PasswordChangeModal({ user, onPasswordChanged, onCancel }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setIsProcessing(true);
    try {
      await usersAPI.changePassword(user.id, currentPassword, newPassword);
      // Success - call the callback
      onPasswordChanged();
    } catch (error) {
      console.error('Error changing password:', error);
      setError(error.message || 'Failed to change password. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mx-auto mb-4">
            <Lock className="w-8 h-8 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">Change Password Required</h2>
          <p className="text-gray-600 mt-2">
            You are using a temporary password. Please set a new password to continue.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Password (Temporary)
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your temporary password"
              required
              disabled={isProcessing}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter new password (min 6 characters)"
              required
              disabled={isProcessing}
              minLength={6}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Confirm new password"
              required
              disabled={isProcessing}
              minLength={6}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={isProcessing}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Changing Password...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Password Reset Modal Component
function PasswordResetModal({ onClose, onSuccess }) {
  const [step, setStep] = useState('email'); // 'email', 'code', 'password'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!email) {
      setError('Email is required');
      return;
    }

    setIsProcessing(true);
    try {
      await usersAPI.requestPasswordReset(email);
      setSuccess('Reset code sent to your email. Please check your inbox.');
      setStep('code');
    } catch (error) {
      setError(error.message || 'Failed to send reset code. Please check your email address.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!code) {
      setError('Reset code is required');
      return;
    }

    setIsProcessing(true);
    try {
      await usersAPI.verifyResetCode(email, code);
      setSuccess('Code verified successfully. Please set your new password.');
      setStep('password');
    } catch (error) {
      setError(error.message || 'Invalid reset code. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsProcessing(true);
    try {
      await usersAPI.resetPassword(email, code, newPassword);
      setSuccess('Password reset successfully! You can now login with your new password.');
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (error) {
      setError(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4">
            <Settings className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">Password Reset</h2>
          <p className="text-gray-600 mt-2">
            {step === 'email' && 'Enter your email to receive a reset code'}
            {step === 'code' && 'Enter the reset code sent to your email'}
            {step === 'password' && 'Set your new password'}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
            {success}
          </div>
        )}

        {step === 'email' && (
          <form onSubmit={handleRequestCode} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email"
                required
                disabled={isProcessing}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isProcessing}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isProcessing}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Sending...' : 'Send Reset Code'}
              </button>
            </div>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reset Code
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl font-mono tracking-widest"
                placeholder="Enter code"
                maxLength={6}
                required
                disabled={isProcessing}
              />
              <p className="text-xs text-gray-500 mt-2">
                Check your email for the 6-digit reset code
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError('');
                  setSuccess('');
                }}
                disabled={isProcessing}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isProcessing}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Verifying...' : 'Verify Code'}
              </button>
            </div>
          </form>
        )}

        {step === 'password' && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter new password"
                required
                disabled={isProcessing}
                minLength={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Confirm new password"
                required
                disabled={isProcessing}
                minLength={6}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setStep('code');
                  setNewPassword('');
                  setConfirmPassword('');
                  setError('');
                  setSuccess('');
                }}
                disabled={isProcessing}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isProcessing}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// Admin Login Component
function AdminLogin({ onLogin, onBack, onForgotPassword }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await onLogin(username, password);
    if (result && typeof result === 'object') {
      if (!result.success) {
        setError('Invalid username or password');
        setPassword('');
      } else if (result.requiresPasswordChange) {
        // Password change modal will be shown by parent component
        // Don't show error, just wait for password change
      }
    } else if (!result || (typeof result === 'object' && !result.success)) {
      setError('Invalid username or password');
      setPassword('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4">
              <Settings className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800">Admin Login</h2>
            <p className="text-gray-600 mt-2">Access the admin dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter username"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter password"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              Login
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="button"
              onClick={onBack}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition"
            >
              Back to Home
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// Registration Type Selection Component
function RegistrationTypeSelection({ onSelectType, onBack, registrations, maxGeneralSeats, maxFamilySeats, maxMiddleSchoolSeats, maxHighSchoolSeats, maxCollegeSeats, maxFamilyRoomsSeats, earlyBirdStudentEnabled, familyRegistrationsOpen = true, adultRegistrationsOpen = true, dayRegistrationsOpen = true, registrationsOpen = true, isAdmin = false, pricingSettings = DEFAULT_PRICING_SETTINGS, campSettings: campForNotice = null }) {
  const helpSupportEmail = getHelpSupportEmail(campForNotice);
  const helpMailHref = `mailto:${encodeURIComponent(helpSupportEmail)}`;
  // Calculate seats taken (day registrations count toward general seats)
  const generalRegistrations = registrations.filter(r => 
    r.type === 'student' || r.type === 'adult' || r.type === 'test' || r.type === 'earlyBirdStudent' || r.type === 'day'
  );
  const familyRegistrations = registrations.filter(r => 
    r.type && r.type.startsWith('family')
  );
  
  const generalSeatsTaken = generalRegistrations.length;
  const familySeatsTaken = familyRegistrations.length;
  const generalSeatsLeft = maxGeneralSeats - generalSeatsTaken;
  const familySeatsLeft = maxFamilySeats - familySeatsTaken;
  
  // Category counts and available seats
  const middleSchoolRegistrations = registrations.filter(r => 
    r.data?.camperCategory === 'Middle School' || r.camperCategory === 'Middle School'
  );
  const highSchoolRegistrations = registrations.filter(r => 
    r.data?.camperCategory === 'High School' || r.camperCategory === 'High School'
  );
  const collegeRegistrations = registrations.filter(r => 
    r.data?.camperCategory === 'College' || r.camperCategory === 'College'
  );
  const familyRoomsRegistrations = registrations.filter(r => 
    r.type && r.type.startsWith('family')
  );
  
  const middleSchoolCount = middleSchoolRegistrations.length;
  const highSchoolCount = highSchoolRegistrations.length;
  const collegeCount = collegeRegistrations.length;
  const familyRoomsCount = familyRoomsRegistrations.length;
  
  // Ensure we have valid numbers, use defaults if undefined
  const maxMS = maxMiddleSchoolSeats ?? 200;
  const maxHS = maxHighSchoolSeats ?? 200;
  const maxCol = maxCollegeSeats ?? 100;
  const maxFR = maxFamilyRoomsSeats ?? 100;
  
  // eslint-disable-next-line no-unused-vars
  const middleSchoolSeatsLeft = maxMS - middleSchoolCount;
  // eslint-disable-next-line no-unused-vars
  const highSchoolSeatsLeft = maxHS - highSchoolCount;
  // eslint-disable-next-line no-unused-vars
  const collegeSeatsLeft = maxCol - collegeCount;
  // eslint-disable-next-line no-unused-vars
  const familyRoomsSeatsLeft = maxFR - familyRoomsCount;
  
  const pricing = { ...DEFAULT_PRICING_SETTINGS, ...(pricingSettings || {}) };
  const registrationTypes = [
    { id: 'test', label: 'Test Registration', price: pricing.test, description: `Test registration for payment testing ($${pricing.test})`, category: 'general' },
    { id: 'student', label: 'Student', price: pricing.student, description: 'Individual student registration', category: 'general' },
    { id: 'adult', label: 'Adult', price: pricing.adult, description: 'Individual adult registration', category: 'general' },
    { id: 'day', label: 'Day Registration', price: pricing.dayOneDay, description: `Day-only attendance ($${pricing.dayOneDay}/day). Food included. No accommodations.`, category: 'general' },
    { id: 'family2', label: 'Family of 2', price: pricing.family2, description: 'Two family members (at least one adult required)', category: 'family' },
    { id: 'family3', label: 'Family of 3', price: pricing.family3, description: 'Three family members (at least one adult required)', category: 'family' },
    { id: 'family4', label: 'Family of 4', price: pricing.family4, description: 'Four family members (at least one adult required)', category: 'family' },
    { id: 'family5', label: 'Family of 5', price: pricing.family5, description: 'Five family members (at least one adult required)', category: 'family' },
  ];
  
  // Filter out test registration for non-admin users only (Day, Student, Adult, Family are for everyone)
  let filteredRegistrationTypes = isAdmin 
    ? registrationTypes 
    : registrationTypes.filter(type => type.id !== 'test');
  
  // When main registrations are closed, only show types with their own toggle (Day, Adult, Family)
  if (!registrationsOpen) {
    filteredRegistrationTypes = filteredRegistrationTypes.filter(type => 
      type.id === 'day' || type.id === 'adult' || type.category === 'family'
    );
  }
  
  // Filter out regular student registration if early bird is enabled (only when main regs open)
  if (registrationsOpen && earlyBirdStudentEnabled) {
    filteredRegistrationTypes = filteredRegistrationTypes.filter(type => type.id !== 'student');
  }
  
  // Filter out family registration types when family registrations are closed
  if (!familyRegistrationsOpen) {
    filteredRegistrationTypes = filteredRegistrationTypes.filter(type => type.category !== 'family');
  }
  
  // Filter out adult registration when adult registrations are closed
  if (!adultRegistrationsOpen) {
    filteredRegistrationTypes = filteredRegistrationTypes.filter(type => type.id !== 'adult');
  }
  
  // Filter out day registration when day registrations are closed
  if (!dayRegistrationsOpen) {
    filteredRegistrationTypes = filteredRegistrationTypes.filter(type => type.id !== 'day');
  }
  
  // Add early bird student if enabled (only when main registrations open; insert after test, before adult)
  if (registrationsOpen && earlyBirdStudentEnabled) {
    const adultIndex = filteredRegistrationTypes.findIndex(type => type.id === 'adult');
    filteredRegistrationTypes.splice(adultIndex, 0, { 
      id: 'earlyBirdStudent', 
      label: 'Early Bird Student', 
      price: pricing.earlyBirdStudent, 
      description: 'Early bird student registration (limited time offer)', 
      category: 'general' 
    });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full">
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <button
            onClick={onBack}
            className="mb-6 text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            ← Back to Home
          </button>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Registration Type Selection</h2>
            <p className="text-gray-600">Please select your registration type to continue</p>
          </div>

          <RegistrationFlowNoticeCallout camp={campForNotice} dayVariant={false} />

          {!familyRegistrationsOpen && (
            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 mb-6">
              <p className="text-red-900 font-medium">
                <strong>Family registrations closed:</strong> Family registration is now full. Adults and students may still register separately with separated accommodations. Waitlisted families will be contacted if cancellations occur. We apologize for any inconvenience. For any questions please reach out to us at{' '}
                <a href={helpMailHref} className="text-red-700 underline hover:text-red-800">{helpSupportEmail}</a>.
              </p>
            </div>
          )}

          {!adultRegistrationsOpen && (
            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 mb-6">
              <p className="text-red-900 font-medium">
                <strong>Adult registrations closed.</strong> Individual adult registration is not available at this time. For any questions please reach out to us at{' '}
                <a href={helpMailHref} className="text-red-700 underline hover:text-red-800">{helpSupportEmail}</a>.
              </p>
            </div>
          )}

          {!dayRegistrationsOpen && (
            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 mb-6">
              <p className="text-red-900 font-medium">
                <strong>Day registrations closed.</strong> Day-only registration is not available at this time. For any questions please reach out to us at{' '}
                <a href={helpMailHref} className="text-red-700 underline hover:text-red-800">{helpSupportEmail}</a>.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRegistrationTypes.map((type) => {
              const seatsLeft = type.category === 'general' ? generalSeatsLeft : familySeatsLeft;
              const isSoldOut = seatsLeft <= 0;
              
              return (
              <button
                key={type.id}
                  onClick={() => !isSoldOut && onSelectType(type.id)}
                  disabled={isSoldOut}
                  className={`bg-white border-2 rounded-lg p-6 transition-all text-left group ${
                    isSoldOut
                      ? 'border-red-300 bg-red-50 opacity-60 cursor-not-allowed'
                      : type.id === 'test' 
                        ? 'border-yellow-400 hover:border-yellow-500 bg-yellow-50 hover:shadow-lg' 
                        : type.id === 'earlyBirdStudent'
                        ? 'border-green-400 hover:border-green-500 bg-green-50 hover:shadow-lg'
                        : 'border-gray-200 hover:border-blue-500 hover:shadow-lg'
                  }`}
                >
                  {type.id === 'test' && (
                    <div className="mb-2">
                      <span className="inline-block bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded">
                        TEST ONLY
                      </span>
                    </div>
                  )}
                  {type.id === 'earlyBirdStudent' && (
                    <div className="mb-2">
                      <span className="inline-block bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
                        EARLY BIRD
                      </span>
                    </div>
                  )}
                  {isSoldOut && (
                    <div className="mb-2">
                      <span className="inline-block bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                        SOLD OUT
                      </span>
                    </div>
                  )}
                <div className="flex justify-between items-start mb-3">
                    <h3 className={`text-xl font-bold ${
                      isSoldOut 
                        ? 'text-red-600' 
                        : type.id === 'test' 
                          ? 'text-yellow-800 group-hover:text-yellow-900' 
                          : type.id === 'earlyBirdStudent'
                          ? 'text-green-800 group-hover:text-green-900'
                          : 'text-gray-800 group-hover:text-blue-600'
                    }`}>
                    {type.label}
                  </h3>
                    {type.price !== null && (
                    <span className={`text-2xl font-bold ${
                      isSoldOut 
                        ? 'text-red-600' 
                        : type.id === 'test' 
                          ? 'text-yellow-600' 
                          : type.id === 'earlyBirdStudent'
                          ? 'text-green-600'
                          : 'text-blue-600'
                    }`}>${type.price}</span>
                    )}
                </div>
                  <p className={`text-sm mb-2 ${
                    isSoldOut 
                      ? 'text-red-700' 
                      : type.id === 'test' 
                        ? 'text-yellow-700' 
                        : type.id === 'earlyBirdStudent'
                        ? 'text-green-700'
                        : 'text-gray-600'
                  }`}>
                    {type.description}
                  </p>
                  {!isSoldOut && isAdmin && (
                    <div className="mt-2 text-xs text-gray-500">
                      {seatsLeft} {seatsLeft === 1 ? 'seat' : 'seats'} left
                    </div>
                  )}
                  {!isSoldOut && (
                    <div className={`mt-4 flex items-center font-semibold text-sm ${
                      type.id === 'test' ? 'text-yellow-600' : type.id === 'earlyBirdStudent' ? 'text-green-600' : 'text-blue-600'
                    }`}>
                  <span>Select</span>
                  <span className="ml-2 group-hover:ml-4 transition-all">→</span>
                </div>
                  )}
              </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Format registration type for display (guards against corrupted/invalid data like email fragments)
function getRegistrationTypeLabel(reg) {
  const raw = (reg?.type || reg?.data?.type || '').trim();
  if (!raw) return (reg?.camperCategory || reg?.data?.camperCategory) || 'N/A';
  if (raw.includes('@') || raw.includes('.com') || raw.length > 30) return (reg?.camperCategory || reg?.data?.camperCategory) || 'N/A';
  const labels = { student: 'Student', earlyBirdStudent: 'Early Bird Student', adult: 'Adult', day: 'Day Registration', test: 'Test' };
  if (labels[raw]) return labels[raw];
  if (raw.startsWith('family')) { const n = raw.replace('family', '') || '1'; return n === '1' ? 'Family' : `Family (${n} members)`; }
  return raw;
}

// Check Registration Status Component
function CheckRegistrationStatus({ registrations, lodges = [], onBack, selfCheckInEnabled, onStartCheckIn, onCheckIn }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBy, setSearchBy] = useState('email');
  const [foundRegistrations, setFoundRegistrations] = useState([]);
  const [searched, setSearched] = useState(false);
  const [checkInSuccess, setCheckInSuccess] = useState({});

  // Keep found registrations in sync when parent registrations update (e.g. after check-in)
  useEffect(() => {
    setFoundRegistrations(prev => prev.map(reg => registrations.find(r => r.id === reg.id) || reg));
  }, [registrations]);

  // OTP states for email verification
  const [emailForOTP, setEmailForOTP] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [sendingOTP, setSendingOTP] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setOtpError('');
    setOtpSuccess('');
    
    if (!emailForOTP) {
      setOtpError('Please enter your email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailForOTP)) {
      setOtpError('Please enter a valid email address');
      return;
    }

    setSendingOTP(true);
    try {
      const response = await fetch(`${API_BASE_URL}/otp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: emailForOTP }),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        if (response.status === 404) {
          throw new Error('OTP endpoint not found. Please ensure the backend server has been updated and restarted.');
        }
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send OTP');
      }

      setOtpSent(true);
      setOtpSuccess('Verification code sent! Please check your email.');
      setOtpCode(''); // Clear previous OTP input
    } catch (error) {
      console.error('Error sending OTP:', error);
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        setOtpError('Network error. Please check if the backend server is running.');
      } else {
        setOtpError(error.message || 'Failed to send verification code. Please try again.');
      }
    } finally {
      setSendingOTP(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setOtpError('');
    setOtpSuccess('');

    if (!otpCode || otpCode.length !== 6) {
      setOtpError('Please enter the 6-digit verification code');
      return;
    }

    setVerifyingOTP(true);
    try {
      const response = await fetch(`${API_BASE_URL}/otp/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: emailForOTP, 
          otp: otpCode 
        }),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        if (response.status === 404) {
          throw new Error('OTP endpoint not found. Please ensure the backend server has been updated and restarted.');
        }
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Invalid verification code');
      }

      if (data.success && data.verified) {
        // OTP verified - set the registrations and show them
        setFoundRegistrations(data.registrations || []);
        setSearched(true);
        setOtpSent(false); // Hide OTP form
        setOtpCode('');
      } else {
        throw new Error('Verification failed');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        setOtpError('Network error. Please check if the backend server is running.');
      } else {
        setOtpError(error.message || 'Invalid verification code. Please try again.');
      }
      setOtpCode(''); // Clear OTP input on error
    } finally {
      setVerifyingOTP(false);
    }
  };

  const handleResendOTP = () => {
    setOtpSent(false);
    setOtpCode('');
    setOtpError('');
    setOtpSuccess('');
    // Trigger send OTP again
    handleSendOTP({ preventDefault: () => {} });
  };

  const getLodgeAssignment = (reg) => {
    if (reg.lodgeId) {
      const lodge = lodges.find(l => l.id === reg.lodgeId);
      return lodge ? { lodgeName: lodge.name, roomNumber: reg.data?.roomNumber || reg.roomNumber } : null;
    }
    if (reg.data?.lodgeId) {
      const lodge = lodges.find(l => l.id === reg.data.lodgeId);
      return lodge ? { lodgeName: lodge.name, roomNumber: reg.data.roomNumber || reg.roomNumber } : null;
    }
    for (const lodge of lodges) {
      const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
      if (hasNewStructure) {
        for (const room of lodge.rooms) {
          if (room.assignedBeds && room.assignedBeds.some(bed => bed.registrationId === reg.id)) {
            return { lodgeName: lodge.name, roomNumber: room.roomNumber };
          }
        }
      } else if (lodge.assignedRooms && Array.isArray(lodge.assignedRooms)) {
        const assignment = lodge.assignedRooms.find(a => a.registrationId === reg.id);
        if (assignment) return { lodgeName: lodge.name, roomNumber: assignment.roomNumber };
      }
    }
    return null;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearched(true);
    
    // For email search, require OTP verification first
      if (searchBy === 'email') {
      setEmailForOTP(searchTerm);
      // Don't search directly - OTP flow will handle it
      return;
    }
    
    // For registration code, search directly (no OTP needed)
    let results = [];
    const registration = registrations.find(r => r.qrCode === searchTerm || r.registrationCode === searchTerm);
    if (registration) {
      results = [registration];
    }
    setFoundRegistrations(results);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <button
            onClick={onBack}
            className="mb-6 text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            ← Back to Home
          </button>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Check Registration Details and Lodge Assignments</h2>
            <p className="text-gray-600">Enter your email or registration code to view details</p>
          </div>

          {/* OTP Verification Flow for Email Search */}
          {searchBy === 'email' && !searched && (
            <div className="space-y-6">
              {!otpSent ? (
                <form onSubmit={handleSendOTP} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
              </label>
                  <input
                      type="email"
                      value={emailForOTP}
                      onChange={(e) => {
                        setEmailForOTP(e.target.value);
                        setSearchTerm(e.target.value);
                        setOtpError('');
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Enter your email address"
                      required
                      disabled={sendingOTP}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      We'll send a verification code to your email for security
                    </p>
                  </div>

                  {otpError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-800 text-sm">{otpError}</p>
                    </div>
                  )}

                  {otpSuccess && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-green-800 text-sm">{otpSuccess}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={sendingOTP}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {sendingOTP ? 'Sending Verification Code...' : 'Send Verification Code'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOTP} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Verification Code
                </label>
                  <input
                      type="text"
                      value={otpCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtpCode(value);
                        setOtpError('');
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-center text-2xl font-mono tracking-widest"
                      placeholder="000000"
                      maxLength="6"
                      required
                      disabled={verifyingOTP}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Enter the 6-digit code sent to {emailForOTP}
                    </p>
              </div>

                  {otpError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-800 text-sm">{otpError}</p>
            </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={verifyingOTP || otpCode.length !== 6}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {verifyingOTP ? 'Verifying...' : 'Verify Code'}
                    </button>
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={sendingOTP}
                      className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      Resend
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Registration Code Search (No OTP Required) */}
          {searchBy === 'qrCode' && !searched && (
            <form onSubmit={handleSearch} className="space-y-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Registration Code
                </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Enter your registration code"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              Search Registration
            </button>
          </form>
          )}

          {/* Search Method Selection */}
          {!searched && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search by
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="email"
                    checked={searchBy === 'email'}
                    onChange={(e) => {
                      setSearchBy(e.target.value);
                      setOtpSent(false);
                      setEmailForOTP('');
                      setOtpCode('');
                      setOtpError('');
                      setOtpSuccess('');
                      setSearchTerm('');
                    }}
                    className="mr-2"
                  />
                  Email Address
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="qrCode"
                    checked={searchBy === 'qrCode'}
                    onChange={(e) => {
                      setSearchBy(e.target.value);
                      setOtpSent(false);
                      setEmailForOTP('');
                      setOtpCode('');
                      setOtpError('');
                      setOtpSuccess('');
                      setSearchTerm('');
                    }}
                    className="mr-2"
                  />
                  Registration Code
                </label>
              </div>
            </div>
          )}

          {searched && (
            <div className="mt-8">
              {foundRegistrations.length > 0 ? (
                <div className="space-y-6">
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">
                          {foundRegistrations.length} Registration{foundRegistrations.length > 1 ? 's' : ''} Found!
                        </h3>
                        {emailForOTP && (
                          <p className="text-sm text-gray-600 mt-1">All registrations for: {emailForOTP}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {foundRegistrations.map((foundRegistration, index) => (
                    <div key={foundRegistration.id || index} className="bg-white border-2 border-gray-200 rounded-lg p-6">
                      {foundRegistrations.length > 1 && (
                        <div className="mb-4 pb-4 border-b border-gray-200">
                          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                            Registration {index + 1} of {foundRegistrations.length}
                          </span>
                        </div>
                      )}

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="min-w-0">
                        <p className="text-sm text-gray-600">Full Name</p>
                        <p className="font-semibold text-gray-800">{foundRegistration.name}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-600">Gender</p>
                        <p className="font-semibold text-gray-800 capitalize">{foundRegistration.gender || 'N/A'}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-semibold text-gray-800 break-all">{foundRegistration.email}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-600">Mobile Number</p>
                            <p className="font-semibold text-gray-800">{foundRegistration.mobileNumber || foundRegistration.mobile || 'N/A'}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-600">Address</p>
                        <p className="font-semibold text-gray-800 break-words">
                              {foundRegistration.addressLine1 || foundRegistration.address || 'N/A'}
                          {foundRegistration.city && `, ${foundRegistration.city}`}
                          {foundRegistration.state && `, ${foundRegistration.state}`}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-600">T-Shirt Size</p>
                            <p className="font-semibold text-gray-800">{foundRegistration.tshirtSize || foundRegistration.data?.tshirtSize || 'N/A'}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-600">Registration Type</p>
                            <p className="font-semibold text-gray-800">{getRegistrationTypeLabel(foundRegistration)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Amount</p>
                            <p className="font-semibold text-gray-800">${foundRegistration.amount || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Registration Status</p>
                        <StatusBadge status={foundRegistration.status} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Payment Status</p>
                        <PaymentBadge status={foundRegistration.paymentStatus} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Check-In Status</p>
                        <p className="font-semibold text-gray-800">
                          {foundRegistration.checkedIn ? '✓ Checked In' : 'Not Checked In'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Registration Code</p>
                            <p className="font-mono text-sm font-semibold text-gray-800">{foundRegistration.registrationCode || foundRegistration.qrCode || 'N/A'}</p>
                      </div>
                          {foundRegistration.data?.camperCategory && (
                            <div>
                              <p className="text-sm text-gray-600">Camper Category</p>
                              <p className="font-semibold text-gray-800">{foundRegistration.data.camperCategory}</p>
                            </div>
                          )}
                    </div>

                    {foundRegistration.allergies && foundRegistration.allergies !== 'none' && (
                      <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm font-semibold text-red-800 mb-1">Allergies</p>
                        <p className="text-gray-700 capitalize">{foundRegistration.allergies}</p>
                        {foundRegistration.allergyDescription && (
                          <p className="text-gray-700 mt-1 text-sm">{foundRegistration.allergyDescription}</p>
                        )}
                      </div>
                    )}

                        {foundRegistration.emergencyContactName && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm font-semibold text-blue-800 mb-2">Emergency Contact</p>
                      <p className="text-gray-700">
                              {foundRegistration.emergencyContactName} {foundRegistration.emergencyContactRelationship && `(${foundRegistration.emergencyContactRelationship})`}
                      </p>
                            {foundRegistration.emergencyContactPhone && (
                      <p className="text-gray-700">{foundRegistration.emergencyContactPhone}</p>
                            )}
                    </div>
                        )}

                        {(() => {
                      const assignment = getLodgeAssignment(foundRegistration);
                      return assignment ? (
                        <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                          <p className="text-sm font-semibold text-purple-800 mb-1">Lodge Assignment</p>
                          <p className="text-gray-700">
                            {assignment.lodgeName} - Room {assignment.roomNumber}
                          </p>
                        </div>
                      ) : null;
                    })()}

                        {foundRegistration.registeredAt && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600">
                        Registered on: {new Date(foundRegistration.registeredAt).toLocaleString()}
                      </p>
                    </div>
                        )}

                    {/* Check-In Button - Only shown when self check-in is enabled. Participant form required first when onStartCheckIn provided. */}
                    {selfCheckInEnabled && foundRegistration.status === 'confirmed' && !foundRegistration.checkedIn && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        {onStartCheckIn && <p className="text-sm text-gray-600 mb-3">You will complete the required participant form, then check in.</p>}
                        <button
                          onClick={() => {
                            if (onStartCheckIn) {
                              onStartCheckIn(foundRegistration.id);
                            } else if (onCheckIn) {
                              onCheckIn(foundRegistration.id);
                              setCheckInSuccess(prev => ({ ...prev, [foundRegistration.id]: true }));
                              setFoundRegistrations(prev => prev.map(reg =>
                                reg.id === foundRegistration.id
                                  ? { ...reg, checkedIn: true, checkInTime: new Date().toISOString() }
                                  : reg
                              ));
                              setTimeout(() => {
                                setCheckInSuccess(prev => {
                                  const newState = { ...prev };
                                  delete newState[foundRegistration.id];
                                  return newState;
                                });
                              }, 3000);
                            }
                          }}
                          className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-5 h-5" />
                          Check In
                        </button>
                            {checkInSuccess[foundRegistration.id] && (
                          <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded-lg text-center">
                            <p className="text-green-800 font-semibold">Successfully checked in!</p>
                            <p className="text-sm text-green-700 mt-1">Check-in time: {new Date().toLocaleString()}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {selfCheckInEnabled && foundRegistration.status === 'confirmed' && foundRegistration.checkedIn && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="bg-green-100 border border-green-300 rounded-lg p-4 text-center">
                          <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                          <p className="text-green-800 font-semibold">Already Checked In</p>
                          {foundRegistration.checkInTime && (
                            <p className="text-sm text-green-700 mt-1">
                              Check-in time: {new Date(foundRegistration.checkInTime).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {selfCheckInEnabled && foundRegistration.status !== 'confirmed' && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4 text-center">
                          <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                          <p className="text-yellow-800 font-semibold">Check-in Not Available</p>
                          <p className="text-sm text-yellow-700 mt-1">
                            Your registration must be confirmed before you can check in.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 text-center">
                  <XCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-gray-800 mb-2">No Registration Found</h3>
                  <p className="text-gray-600">
                    We couldn't find a registration with that {searchBy === 'email' ? 'email address' : 'registration code'}.
                    Please check and try again.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Welcome Package View (PDF downloads managed from the admin dashboard, open access)
const WELCOME_PACKAGE_FALLBACK_DOCS = [
  { id: 'builtin-welcome-package', label: 'Welcome Package', builtin: true, file: 'welcome-package.pdf' },
  { id: 'builtin-campsitemap', label: 'Camp Site Map', builtin: true, file: 'campsitemap.pdf' },
];

function WelcomePackageView({ onBack }) {
  const [docs, setDocs] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    welcomePackageDocsAPI
      .list()
      .then((result) => {
        if (!cancelled) setDocs(Array.isArray(result?.docs) ? result.docs : WELCOME_PACKAGE_FALLBACK_DOCS);
      })
      .catch(() => {
        // API unreachable: fall back to the static PDFs shipped with the site
        if (!cancelled) setDocs(WELCOME_PACKAGE_FALLBACK_DOCS);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <ChevronLeft size={20} />
        Back
      </button>
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">Welcome Package</h1>
      <p className="text-gray-600 mb-8">View the documents below.</p>
      <div className="space-y-4">
        {docs === null && <p className="text-gray-500">Loading documents…</p>}
        {docs !== null && docs.length === 0 && (
          <p className="text-gray-500">No documents have been published yet. Please check back soon.</p>
        )}
        {docs !== null &&
          docs.map((doc) => (
            <a
              key={doc.id}
              href={welcomePackageDocUrl(doc)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <FileText size={24} className="text-gray-500 flex-shrink-0" />
              <span className="font-medium text-gray-900">{doc.label}</span>
              <span className="text-teal-600 font-medium ml-auto">View →</span>
            </a>
          ))}
      </div>
    </div>
  );
}

// Transportation Form Component
function TransportationForm({ onBack, onSuccess, campSettings: campForPayment = DEFAULT_CAMP_SETTINGS }) {
  const zelleRecipient = getPaymentZelleRecipient(campForPayment);
  const campTitleForMemo = getCampTitleForPayments(campForPayment);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    parentName: '',
    parentPhone: '',
    registrationCode: '',
    tripType: 'oneWay', // 'oneWay' or 'roundTrip'
    oneWayDirection: 'toCampsite', // 'toCampsite' or 'fromCampsite'
    departureFromHouston: '', // 'thursday230' | 'friday230' - only when starting from IPC Hebron
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validatingRegistration, setValidatingRegistration] = useState(false);
  const [registrationValid, setRegistrationValid] = useState(null); // null, true, or false
  const [registrationInfo, setRegistrationInfo] = useState(null);

  // Validate registration code
  const validateRegistrationCode = async (code) => {
    if (!code || !code.trim()) {
      setRegistrationValid(null);
      setRegistrationInfo(null);
      return;
    }

    setValidatingRegistration(true);
    try {
      const response = await fetch(`${API_BASE_URL}/registrations/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ registrationCode: code.trim() }),
      });

      if (!response.ok) {
        setRegistrationValid(false);
        setRegistrationInfo(null);
        setValidatingRegistration(false);
        return;
      }

      const registrations = await response.json();
      // Check if we got a valid registration array with at least one active registration
      if (Array.isArray(registrations) && registrations.length > 0) {
        const registration = registrations[0]; // Get first matching registration
        // Check if registration is not deleted and is confirmed
        if (!registration.deletedAt && registration.status === 'confirmed') {
          setRegistrationValid(true);
          setRegistrationInfo(registration);
        } else {
          setRegistrationValid(false);
          setRegistrationInfo(null);
        }
      } else {
        setRegistrationValid(false);
        setRegistrationInfo(null);
      }
    } catch (error) {
      console.error('Error validating registration code:', error);
      setRegistrationValid(false);
      setRegistrationInfo(null);
    } finally {
      setValidatingRegistration(false);
    }
  };

  // Debounce registration code validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.registrationCode) {
        validateRegistrationCode(formData.registrationCode);
      } else {
        setRegistrationValid(null);
        setRegistrationInfo(null);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.registrationCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError("Student's name is required");
      return;
    }
    if (!formData.parentName.trim()) {
      setError("Parent's/Guardian's name is required");
      return;
    }
    if (!formData.parentPhone.trim()) {
      setError("Parent's/Guardian's phone number is required");
      return;
    }
    if (!formData.email.trim()) {
      setError('Preferred email for communications is required');
      return;
    }
    // If registration code was entered, it must be valid
    if (formData.registrationCode.trim() && registrationValid !== true) {
      setError('Please enter a valid Camp Registration ID or leave it blank');
      return;
    }

    // When starting from IPC Hebron (one-way to campsite or round trip), departure date is required
    const startsFromHouston = formData.tripType === 'roundTrip' || (formData.tripType === 'oneWay' && formData.oneWayDirection === 'toCampsite');
    if (startsFromHouston && !formData.departureFromHouston) {
      setError('Please select the date of departure from Houston.');
      return;
    }

    const amount = formData.tripType === 'oneWay' ? 40 : 80;
    const pickupPoint = formData.tripType === 'roundTrip' 
      ? 'IPC Hebron Parking Lot' 
      : formData.oneWayDirection === 'toCampsite' 
        ? 'IPC Hebron Parking Lot' 
        : 'Campsite';
    const destination = formData.tripType === 'roundTrip'
      ? 'Campsite'
      : formData.oneWayDirection === 'toCampsite'
        ? 'Campsite'
        : 'IPC Hebron Parking Lot';

    setIsSubmitting(true);
    try {
      await transportationAPI.create({
        ...formData,
        amount: amount,
        pickupPoint: pickupPoint,
        destination: destination,
        registrationId: registrationInfo?.id || null,
        status: 'pending',
        paymentStatus: 'pending',
      });
      onSuccess();
    } catch (error) {
      console.error('Error submitting transportation request:', error);
      setError(error.message || 'Failed to submit transportation request. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-2xl p-8">
        <button
          onClick={onBack}
          className="mb-6 text-gray-600 hover:text-gray-800 flex items-center gap-2"
        >
          ← Back to Home
        </button>

        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mx-auto mb-4">
            <Bus className="w-8 h-8 text-orange-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Transportation Request</h2>
          <p className="text-gray-600">Transportation request form for Houston-area registrants</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Student's Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Enter student's full name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Parent's/Guardian's Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.parentName}
              onChange={(e) => handleChange('parentName', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Enter parent's/guardian's name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Parent's/Guardian's Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={formData.parentPhone}
              onChange={(e) => handleChange('parentPhone', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Enter parent's/guardian's phone number"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred email for communications <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Enter preferred email for communications"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Student's phone number (if any)
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Enter student's phone number if any"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Camp Registration ID <span className="text-gray-400 text-xs font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.registrationCode}
              onChange={(e) => handleChange('registrationCode', e.target.value.toUpperCase())}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                registrationValid === false 
                  ? 'border-red-500 bg-red-50' 
                  : registrationValid === true 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-300'
              }`}
              placeholder="Enter your registration code (optional)"
            />
            {validatingRegistration && (
              <p className="text-sm text-gray-500 mt-1">Validating registration code...</p>
            )}
            {registrationValid === false && !validatingRegistration && (
              <p className="text-sm text-red-600 mt-1">Invalid registration code. Please enter a valid, confirmed camp registration ID.</p>
            )}
            {registrationValid === true && !validatingRegistration && registrationInfo && (
              <p className="text-sm text-green-600 mt-1">
                ✓ Valid registration found: {registrationInfo.name || 'Registered participant'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trip Type <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition">
                <input
                  type="radio"
                  name="tripType"
                  value="oneWay"
                  checked={formData.tripType === 'oneWay'}
                  onChange={(e) => handleChange('tripType', e.target.value)}
                  className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                />
                <div className="ml-3 flex-1">
                  <div className="font-medium text-gray-900">One Way</div>
                  <div className="text-sm text-gray-500">$40</div>
                  {formData.tripType === 'oneWay' && (
                    <div className="mt-2 space-y-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="oneWayDirection"
                          value="toCampsite"
                          checked={formData.oneWayDirection === 'toCampsite'}
                          onChange={(e) => handleChange('oneWayDirection', e.target.value)}
                          className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="ml-2 text-sm">From IPC Hebron Parking Lot → Campsite</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="oneWayDirection"
                          value="fromCampsite"
                          checked={formData.oneWayDirection === 'fromCampsite'}
                          onChange={(e) => handleChange('oneWayDirection', e.target.value)}
                          className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="ml-2 text-sm">From Campsite → IPC Hebron Parking Lot</span>
                      </label>
                    </div>
                  )}
                </div>
              </label>
              <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition">
                <input
                  type="radio"
                  name="tripType"
                  value="roundTrip"
                  checked={formData.tripType === 'roundTrip'}
                  onChange={(e) => handleChange('tripType', e.target.value)}
                  className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                />
                <div className="ml-3 flex-1">
                  <div className="font-medium text-gray-900">Round Trip</div>
                  <div className="text-sm text-gray-500">From IPC Hebron Parking Lot → Campsite</div>
                  <div className="text-sm text-gray-500">From Campsite → IPC Hebron Parking Lot</div>
                  <div className="text-lg font-semibold text-orange-600 mt-1">$80</div>
                </div>
              </label>
            </div>
          </div>

          {((formData.tripType === 'oneWay' && formData.oneWayDirection === 'toCampsite') || formData.tripType === 'roundTrip') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of departure from Houston <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition">
                  <input
                    type="radio"
                    name="departureFromHouston"
                    value="thursday230"
                    checked={formData.departureFromHouston === 'thursday230'}
                    onChange={(e) => handleChange('departureFromHouston', e.target.value)}
                    className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="ml-3">Thursday 2:30 PM</span>
                </label>
                <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition">
                  <input
                    type="radio"
                    name="departureFromHouston"
                    value="friday230"
                    checked={formData.departureFromHouston === 'friday230'}
                    onChange={(e) => handleChange('departureFromHouston', e.target.value)}
                    className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="ml-3">Friday 2:30 PM</span>
                </label>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Payment instructions:</strong> Payment must be made via Zelle to <strong>{zelleRecipient}</strong>.
              In the Zelle memo, include your <strong>camp registration number</strong>
              {registrationValid === true && formData.registrationCode.trim() ? (
                <>
                  {' '}
                  (<strong>{formData.registrationCode.trim()}</strong>)
                </>
              ) : null}{' '}
              and the camp name <strong>{campTitleForMemo}</strong> (from camp settings) so we can match your payment.
              Your request will be confirmed once payment is received and verified.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onBack}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-6 rounded-lg transition disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : `Submit Request ($${formData.tripType === 'oneWay' ? '40' : '80'})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Self Check-In Component
function SelfCheckIn({ registrations, selfCheckInEnabled, onStartCheckIn, onCheckIn, onStartCheckInWizard, onBack }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBy, setSearchBy] = useState('email');
  const [foundRegistrations, setFoundRegistrations] = useState([]);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set()); // Multi-select for family check-in
  const [searched, setSearched] = useState(false);
  const [checkInSuccess, setCheckInSuccess] = useState(false);
  const [checkInSuccessNames, setCheckInSuccessNames] = useState([]); // For multi-check-in success message
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  
  // OTP states for email verification
  const [emailForOTP, setEmailForOTP] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [sendingOTP, setSendingOTP] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');

  // Keep foundRegistrations in sync with parent registrations (e.g. after wizard completes)
  useEffect(() => {
    setFoundRegistrations(prev => {
      if (prev.length === 0) return prev;
      return prev.map(r => {
        const fromParent = registrations.find(x => x.id === r.id);
        return fromParent ? { ...r, ...fromParent } : r;
      });
    });
  }, [registrations]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setOtpError('');
    setOtpSuccess('');
    
    if (!emailForOTP) {
      setOtpError('Please enter your email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailForOTP)) {
      setOtpError('Please enter a valid email address');
      return;
    }

    setSendingOTP(true);
    try {
      const response = await fetch(`${API_BASE_URL}/otp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: emailForOTP }),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        if (response.status === 404) {
          throw new Error('OTP endpoint not found. Please ensure the backend server has been updated and restarted.');
        }
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send OTP');
      }

      setOtpSent(true);
      setOtpSuccess('Verification code sent! Please check your email.');
      setOtpCode(''); // Clear previous OTP input
    } catch (error) {
      console.error('Error sending OTP:', error);
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        setOtpError('Network error. Please check if the backend server is running.');
      } else {
        setOtpError(error.message || 'Failed to send verification code. Please try again.');
      }
    } finally {
      setSendingOTP(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setOtpError('');
    setOtpSuccess('');

    if (!otpCode || otpCode.length !== 6) {
      setOtpError('Please enter the 6-digit verification code');
      return;
    }

    setVerifyingOTP(true);
    try {
      const response = await fetch(`${API_BASE_URL}/otp/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: emailForOTP, 
          otp: otpCode 
        }),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        if (response.status === 404) {
          throw new Error('OTP endpoint not found. Please ensure the backend server has been updated and restarted.');
        }
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Invalid verification code');
      }

      if (data.success && data.verified) {
        // OTP verified - store all registrations for this email
        const emailRegistrations = data.registrations || [];
        setFoundRegistrations(emailRegistrations);
        setSearched(true);
        if (emailRegistrations.length === 1) {
          setSelectedRegistration(emailRegistrations[0]);
          setSelectedIds(new Set([emailRegistrations[0].id]));
        } else {
          setSelectedRegistration(null);
          setSelectedIds(new Set());
        }
        setOtpSent(false); // Hide OTP form
        setOtpCode('');
      } else {
        throw new Error('Verification failed');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        setOtpError('Network error. Please check if the backend server is running.');
      } else {
        setOtpError(error.message || 'Invalid verification code. Please try again.');
      }
      setOtpCode(''); // Clear OTP input on error
    } finally {
      setVerifyingOTP(false);
    }
  };

  const handleResendOTP = () => {
    setOtpSent(false);
    setOtpCode('');
    setOtpError('');
    setOtpSuccess('');
    // Trigger send OTP again
    handleSendOTP({ preventDefault: () => {} });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // If searching by email, require OTP verification first
    if (searchBy === 'email') {
      if (!searchTerm) {
        setOtpError('Please enter your email address');
        return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(searchTerm)) {
        setOtpError('Please enter a valid email address');
        return;
      }
      
      setEmailForOTP(searchTerm);
      // Automatically send OTP
      await handleSendOTP(e);
      return;
    }
    
    // For registration code search, proceed directly
    setSearched(true);
    setFoundRegistrations([]);
    setSelectedRegistration(null);
    
    try {
        // Search by registration code (qrCode maps to registrationCode)
      // API now always returns an array: [] for no matches, [{...}] for one match
      const searchResults = await registrationsAPI.search(null, searchTerm);
      if (searchResults && Array.isArray(searchResults) && searchResults.length > 0) {
        setFoundRegistrations(searchResults);
        setSelectedRegistration(searchResults[0]);
        setSelectedIds(searchResults.length === 1 ? new Set([searchResults[0].id]) : new Set());
      } else {
        setFoundRegistrations([]);
        setSelectedRegistration(null);
      }
    } catch (error) {
      console.error('Error searching registration:', error);
      setFoundRegistrations([]);
      setSelectedRegistration(null);
      setSelectedIds(new Set());
    }
  };

  // Helper: is registration eligible for immediate check-in (has completed waiver/form)
  const isEligibleForCheckIn = (reg) => {
    const status = reg.status || reg.data?.status;
    const checkedIn = reg.checkedIn ?? reg.data?.checkedIn ?? false;
    return status === 'confirmed' && reg.waiverSigned && !checkedIn;
  };

  // Helper: needs participant form before check-in
  const needsParticipantForm = (reg) => {
    const status = reg.status || reg.data?.status;
    const checkedIn = reg.checkedIn ?? reg.data?.checkedIn ?? false;
    return status === 'confirmed' && !reg.waiverSigned && !checkedIn;
  };

  // Single registration check-in (from card button)
  const handleCheckIn = (reg) => {
    const target = reg || selectedRegistration;
    if (!target) return;
    // If participant form required first, start that flow
    if (needsParticipantForm(target) && onStartCheckIn) {
      onStartCheckIn(target.id);
      return;
    }
    if (isEligibleForCheckIn(target) && onCheckIn) {
      onCheckIn(target.id);
      setCheckInSuccess(true);
      setCheckInSuccessNames([target.name]);
      const updatedRegistrations = foundRegistrations.map(r =>
        r.id === target.id ? { ...r, checkedIn: true, checkInTime: new Date().toISOString() } : r
      );
      setFoundRegistrations(updatedRegistrations);
      setSelectedRegistration(updatedRegistrations.find(r => r.id === target.id));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(target.id); return n; });
      setTimeout(() => { setCheckInSuccess(false); setCheckInSuccessNames([]); }, 5000);
    }
  };

  // Batch check-in for multiple selected (only eligible registrations)
  const handleCheckInSelected = async () => {
    const toCheckIn = foundRegistrations.filter(r => selectedIds.has(r.id) && isEligibleForCheckIn(r));
    if (toCheckIn.length === 0) return;
    setIsCheckingIn(true);
    try {
      const names = [];
      for (const reg of toCheckIn) {
        await onCheckIn(reg.id);
        names.push(reg.name);
      }
      const idsToUpdate = new Set(toCheckIn.map(r => r.id));
      const now = new Date().toISOString();
      setFoundRegistrations(prev => prev.map(r =>
        idsToUpdate.has(r.id) ? { ...r, checkedIn: true, checkInTime: now } : r
      ));
      setSelectedIds(prev => { const n = new Set(prev); toCheckIn.forEach(r => n.delete(r.id)); return n; });
      setCheckInSuccess(true);
      setCheckInSuccessNames(names);
      setTimeout(() => { setCheckInSuccess(false); setCheckInSuccessNames([]); }, 6000);
    } catch (err) {
      console.error('Check-in error:', err);
    } finally {
      setIsCheckingIn(false);
    }
  };

  // Start family wizard: check in eligible selected first, then run Campsite→ICPF→check-in for each who needs the form (data submitted per step)
  const handleStartFormsWizard = async () => {
    const selectedNeedForm = foundRegistrations.filter(r => selectedIds.has(r.id) && needsParticipantForm(r));
    const selectedEligible = foundRegistrations.filter(r => selectedIds.has(r.id) && isEligibleForCheckIn(r));
    if (selectedNeedForm.length === 0 && selectedEligible.length === 0) return;
    if (selectedNeedForm.length === 0) {
      await handleCheckInSelected();
      return;
    }
    setIsCheckingIn(true);
    try {
      for (const reg of selectedEligible) {
        await onCheckIn(reg.id);
      }
      if (selectedEligible.length > 0) {
        const idsToUpdate = new Set(selectedEligible.map(r => r.id));
        const now = new Date().toISOString();
        setFoundRegistrations(prev => prev.map(r =>
          idsToUpdate.has(r.id) ? { ...r, checkedIn: true, checkInTime: now } : r
        ));
        setSelectedIds(prev => { const n = new Set(prev); selectedEligible.forEach(r => n.delete(r.id)); return n; });
        setCheckInSuccess(true);
        setCheckInSuccessNames(selectedEligible.map(r => r.name));
        setTimeout(() => { setCheckInSuccess(false); setCheckInSuccessNames([]); }, 4000);
      }
      if (selectedNeedForm.length > 0 && onStartCheckInWizard) {
        onStartCheckInWizard(selectedNeedForm.map(r => r.id));
      }
    } catch (err) {
      console.error('Wizard start error:', err);
    } finally {
      setIsCheckingIn(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllEligible = () => {
    const eligible = foundRegistrations.filter(isEligibleForCheckIn).map(r => r.id);
    setSelectedIds(prev => new Set([...prev, ...eligible]));
  };

  if (!selfCheckInEnabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-xl shadow-2xl p-8 text-center">
            <button
              onClick={onBack}
              className="mb-6 text-gray-600 hover:text-gray-800 flex items-center gap-2"
            >
              ← Back to Home
            </button>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Self Check-In Not Available</h2>
            <p className="text-gray-600">Self check-in is currently disabled. Please contact an administrator.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <button
            onClick={onBack}
            className="mb-6 text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            ← Back to Home
          </button>

          <div className="text-center mb-8">
            <div className="flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4 mx-auto">
              <QrCode className="w-8 h-8 text-purple-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Self Check-In</h2>
            <p className="text-gray-600">Search for your registration to check in</p>
          </div>

          <form onSubmit={handleSearch} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search by
              </label>
              <div className="flex gap-4 mb-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="email"
                    checked={searchBy === 'email'}
                    onChange={(e) => {
                      setSearchBy(e.target.value);
                      // Reset OTP states when switching search methods
                      setEmailForOTP('');
                      setOtpSent(false);
                      setOtpCode('');
                      setOtpError('');
                      setOtpSuccess('');
                      setSearched(false);
                      setFoundRegistrations([]);
                      setSelectedRegistration(null);
                      setSelectedIds(new Set());
                    }}
                    className="mr-2"
                  />
                  Email Address
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="code"
                    checked={searchBy === 'code'}
                    onChange={(e) => {
                      setSearchBy(e.target.value);
                      // Reset OTP states when switching search methods
                      setEmailForOTP('');
                      setOtpSent(false);
                      setOtpCode('');
                      setOtpError('');
                      setOtpSuccess('');
                      setSearched(false);
                      setFoundRegistrations([]);
                      setSelectedRegistration(null);
                      setSelectedIds(new Set());
                    }}
                    className="mr-2"
                  />
                  Registration Code or Family Code
                </label>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={searchBy === 'email' ? 'Enter your email address' : 'Enter registration code (e.g. ICPFFAM029) or family code for all members'}
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              {searchBy === 'email' ? 'Send Verification Code' : 'Search Registration'}
            </button>
          </form>


          {otpSent && !searched && (
            <div className="mt-6 p-6 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Email Verification</h3>
              <p className="text-sm text-gray-600 mb-4">
                We've sent a 6-digit verification code to <strong>{emailForOTP}</strong>. 
                Please enter the code below to verify your email address.
              </p>
              
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest"
                    placeholder="000000"
                    maxLength="6"
                    required
                  />
                </div>

                {otpError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{otpError}</p>
                  </div>
                )}

                {otpSuccess && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-600">{otpSuccess}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={verifyingOTP || otpCode.length !== 6}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {verifyingOTP ? 'Verifying...' : 'Verify Code'}
                  </button>
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={sendingOTP}
                    className="px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    Resend
                  </button>
                </div>
              </form>
            </div>
          )}

          {searched && (
            <div className={`mt-8 ${foundRegistrations.length > 1 ? 'pb-32' : ''}`}>
              {foundRegistrations.length > 0 ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="w-8 h-8 text-purple-600 flex-shrink-0" />
                    <h3 className="text-xl font-bold text-gray-800">
                      {foundRegistrations.length === 1 ? 'Registration Found!' : `Found ${foundRegistrations.length} Registrations`}
                    </h3>
                  </div>

                  {/* Guided instructions for multiple registrations (e.g. family) */}
                  {foundRegistrations.length > 1 && (
                    <div className="mb-4 p-5 bg-purple-50 border-2 border-purple-200 rounded-xl">
                      <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                        <span className="flex w-6 h-6 items-center justify-center rounded-full bg-purple-600 text-white text-sm">1</span>
                        Select who to check in
                      </h4>
                      <p className="text-sm text-purple-800 mb-3 ml-8">
                        Check the box next to each person you want to check in. You can check in multiple family members at once.
                      </p>
                      <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                        <span className="flex w-6 h-6 items-center justify-center rounded-full bg-purple-600 text-white text-sm">2</span>
                        Click &quot;Check In Selected&quot; or &quot;Complete Forms & Check In&quot;
                      </h4>
                      <p className="text-sm text-purple-800 ml-8">
                        <strong>Check In Selected</strong> checks in everyone who has already completed the participant form. <strong>Complete Forms & Check In</strong> walks you through the participant and waiver forms for each selected person one by one; each form is submitted as you finish it, so if you cancel later, already-submitted data is saved.
                      </p>
                    </div>
                  )}

                  {/* Summary stats for multiple */}
                  {foundRegistrations.length > 1 && (() => {
                    const eligible = foundRegistrations.filter(isEligibleForCheckIn);
                    const needForm = foundRegistrations.filter(needsParticipantForm);
                    const alreadyCheckedIn = foundRegistrations.filter(r => r.checkedIn ?? r.data?.checkedIn);
                    return (
                      <div className="flex flex-wrap gap-3 mb-4">
                        {eligible.length > 0 && (
                          <span className="px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                            {eligible.length} ready to check in
                          </span>
                        )}
                        {needForm.length > 0 && (
                          <span className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                            {needForm.length} need participant form
                          </span>
                        )}
                        {alreadyCheckedIn.length > 0 && (
                          <span className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                            {alreadyCheckedIn.length} already checked in
                          </span>
                        )}
                      </div>
                    );
                  })()}

                  <div className="space-y-3">
                    {foundRegistrations.map((registration) => {
                      const status = registration.status || registration.data?.status;
                      const checkedIn = registration.checkedIn ?? registration.data?.checkedIn ?? false;
                      const eligible = isEligibleForCheckIn(registration);
                      const needsForm = needsParticipantForm(registration);
                      const isSelected = selectedIds.has(registration.id);
                      const isMulti = foundRegistrations.length > 1;
                      return (
                      <div
                        key={registration.id}
                        onClick={() => isMulti && setSelectedRegistration(registration)}
                        className={`bg-white border-2 rounded-lg p-6 transition-all ${
                          isMulti ? 'cursor-pointer' : ''
                        } ${
                          isSelected && eligible ? 'border-green-500 bg-green-50' :
                          selectedRegistration?.id === registration.id ? 'border-purple-500 bg-purple-50' :
                          'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              {isMulti ? (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => { e.stopPropagation(); toggleSelect(registration.id); }}
                                  disabled={checkedIn || status !== 'confirmed'}
                                  className="w-4 h-4 text-purple-600 rounded border-gray-300"
                                />
                              ) : (
                                <input
                                  type="radio"
                                  checked={selectedRegistration?.id === registration.id}
                                  onChange={() => setSelectedRegistration(registration)}
                                  className="w-4 h-4 text-purple-600"
                                />
                              )}
                              <h4 className="text-lg font-semibold text-gray-800">{registration.name}</h4>
                              {eligible && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">Ready</span>
                              )}
                              {needsForm && (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">Form required</span>
                              )}
                              {checkedIn && (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">Checked in</span>
                              )}
                            </div>
                          </div>
                          <StatusBadge status={status} />
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="min-w-0">
                            <p className="text-gray-600">Email</p>
                            <p className="font-medium text-gray-800 break-all">{registration.email}</p>
                      </div>
                      <div className="min-w-0">
                            <p className="text-gray-600">Registration Type</p>
                            <p className="font-medium text-gray-800">{getRegistrationTypeLabel(registration)}</p>
                      </div>
                      <div>
                            <p className="text-gray-600">Check-In Status</p>
                            <p className="font-medium text-gray-800">
                              {checkedIn ? '✓ Checked In' : 'Not Checked In'}
                            </p>
                      </div>
                          {registration.checkInTime && (
                      <div>
                              <p className="text-gray-600">Check-In Time</p>
                              <p className="font-medium text-gray-800">
                                {new Date(registration.checkInTime).toLocaleString()}
                        </p>
                      </div>
                          )}
                    </div>
                        {/* Per-card Check In button */}
                        {status === 'confirmed' && !checkedIn && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCheckIn(registration); }}
                              className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition"
                            >
                              {needsForm ? 'Complete Form & Check In' : 'Check In'}
                            </button>
                            {needsForm && (
                              <p className="text-xs text-amber-700 mt-1">Requires participant form first</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                    })}
                  </div>

                  {/* Batch Check In Selected - for multiple registrations */}
                  {foundRegistrations.length > 1 && (() => {
                    const selectedEligible = foundRegistrations.filter(r => selectedIds.has(r.id) && isEligibleForCheckIn(r));
                    const selectedNeedForm = foundRegistrations.filter(r => selectedIds.has(r.id) && needsParticipantForm(r));
                    const hasEligibleSelected = selectedEligible.length > 0;
                    const hasNeedFormSelected = selectedNeedForm.length > 0;
                    const hasAnySelected = selectedIds.size > 0;
                    return (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          onClick={handleCheckInSelected}
                          disabled={!hasEligibleSelected || isCheckingIn}
                          className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-5 h-5" />
                          {isCheckingIn ? 'Checking In...' : `Check In Selected (${selectedEligible.length})`}
                        </button>
                        {onStartCheckInWizard && hasNeedFormSelected && (
                          <button
                            onClick={handleStartFormsWizard}
                            disabled={isCheckingIn}
                            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition flex items-center justify-center gap-2"
                          >
                            {isCheckingIn ? 'Starting...' : `Complete Forms & Check In (${selectedNeedForm.length})`}
                          </button>
                        )}
                        {foundRegistrations.filter(isEligibleForCheckIn).length > selectedEligible.length && (
                          <button
                            onClick={selectAllEligible}
                            className="px-4 py-2 text-sm text-purple-600 hover:text-purple-800 hover:underline"
                          >
                            Select all ready to check in
                          </button>
                        )}
                      </div>
                      {hasAnySelected && !hasEligibleSelected && !hasNeedFormSelected && (
                        <p className="mt-2 text-sm text-amber-700">
                          Selected members are already checked in. Select others to check in or complete forms.
                        </p>
                      )}
                      {hasAnySelected && hasNeedFormSelected && (
                        <p className="mt-2 text-sm text-purple-700">
                          Use &quot;Complete Forms & Check In&quot; to fill out participant and waiver forms one by one; each form is submitted as you go. If you cancel later, already-submitted data is saved.
                        </p>
                      )}
                    </div>
                    );
                  })()}

                  {/* Single registration - Check In Now button */}
                  {foundRegistrations.length === 1 && selectedRegistration && (() => {
                    const status = selectedRegistration.status || selectedRegistration.data?.status;
                    const checkedIn = selectedRegistration.checkedIn ?? selectedRegistration.data?.checkedIn ?? false;
                    return (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      {status === 'confirmed' && !checkedIn && (
                        <div>
                          {needsParticipantForm(selectedRegistration) && (
                            <p className="text-sm text-gray-600 mb-3">You will complete the required Trinity Pines Participant form, then check in.</p>
                          )}
                          <button
                            onClick={() => handleCheckIn(selectedRegistration)}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2"
                          >
                            <CheckCircle className="w-5 h-5" />
                            {needsParticipantForm(selectedRegistration) ? 'Complete Form & Check In' : 'Check In Now'}
                          </button>
                        </div>
                      )}
                      {status === 'confirmed' && checkedIn && (
                        <div className="bg-green-100 border border-green-300 rounded-lg p-4 text-center">
                          <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                          <p className="text-green-800 font-semibold">Already Checked In</p>
                          {(selectedRegistration.checkInTime || selectedRegistration.data?.checkInTime) && (
                            <p className="text-sm text-green-700 mt-1">
                              Check-in time: {new Date(selectedRegistration.checkInTime || selectedRegistration.data?.checkInTime).toLocaleString()}
                            </p>
                          )}
                        </div>
                      )}
                      {status !== 'confirmed' && (
                        <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4 text-center">
                          <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                          <p className="text-yellow-800 font-semibold">Registration Not Confirmed</p>
                          <p className="text-sm text-yellow-700 mt-1">
                            Your registration must be confirmed before you can check in. Please contact an administrator.
                          </p>
                        </div>
                      )}
                    </div>
                    );
                  })()}

                  {/* Success message - single or multi */}
                  {checkInSuccess && checkInSuccessNames.length > 0 && (
                    <div className="mt-4 p-4 bg-green-100 border border-green-300 rounded-lg text-center">
                      <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="text-green-800 font-semibold text-lg">Successfully Checked In!</p>
                      <p className="text-sm text-green-700 mt-1">
                        {checkInSuccessNames.join(', ')}
                      </p>
                      <p className="text-xs text-green-600 mt-1">Check-in time: {new Date().toLocaleString()}</p>
                    </div>
                  )}

                  {/* Sticky actions - for multiple: Check In Selected and/or Complete Forms & Check In */}
                  {foundRegistrations.length > 1 && (() => {
                    const selectedEligible = foundRegistrations.filter(r => selectedIds.has(r.id) && isEligibleForCheckIn(r));
                    const selectedNeedForm = foundRegistrations.filter(r => selectedIds.has(r.id) && needsParticipantForm(r));
                    const showSticky = selectedEligible.length > 0 || (onStartCheckInWizard && selectedNeedForm.length > 0);
                    if (!showSticky) return null;
                    return (
                      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur border-t border-gray-200 shadow-lg z-10">
                        <div className="max-w-2xl mx-auto flex flex-wrap gap-3">
                          {selectedEligible.length > 0 && (
                            <button
                              onClick={handleCheckInSelected}
                              disabled={isCheckingIn}
                              className="flex-1 min-w-[140px] bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2 shadow-lg"
                            >
                              <CheckCircle className="w-5 h-5" />
                              {isCheckingIn ? 'Checking In...' : `Check In ${selectedEligible.length}`}
                            </button>
                          )}
                          {onStartCheckInWizard && selectedNeedForm.length > 0 && (
                            <button
                              onClick={handleStartFormsWizard}
                              disabled={isCheckingIn}
                              className="flex-1 min-w-[140px] bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2 shadow-lg"
                            >
                              {isCheckingIn ? 'Starting...' : `Forms & Check In (${selectedNeedForm.length})`}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 text-center">
                  <XCircle className="w-8 h-8 text-red-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Registration Not Found</h3>
                  <p className="text-gray-600">
                    No registration found with the provided {searchBy === 'email' ? 'email address' : 'registration code'}.
                    Please check your information and try again.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Transportation Admin Component  
function TransportationAdmin({ userRole }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busSeatCount, setBusSeatCount] = useState(50);
  const [filterStatus, setFilterStatus] = useState('all');
  const [seatCountEditMode, setSeatCountEditMode] = useState(false);
  const [tempSeatCount, setTempSeatCount] = useState(50);

  useEffect(() => {
    loadRequests();
    loadSeatCount();
  }, []);

  const loadRequests = async () => {
    try {
      const data = await transportationAPI.getAll();
      setRequests(data);
    } catch (error) {
      console.error('Error loading transportation requests:', error);
      alert('Failed to load transportation requests');
    } finally {
      setLoading(false);
    }
  };

  const loadSeatCount = async () => {
    try {
      const setting = await settingsAPI.get('transportationBusSeatCount');
      if (setting && setting.value) {
        setBusSeatCount(parseInt(setting.value) || 50);
        setTempSeatCount(parseInt(setting.value) || 50);
      }
    } catch (error) {
      // Use default
    }
  };

  const saveSeatCount = async () => {
    try {
      await settingsAPI.set('transportationBusSeatCount', tempSeatCount.toString());
      setBusSeatCount(tempSeatCount);
      setSeatCountEditMode(false);
    } catch (error) {
      console.error('Error saving seat count:', error);
      alert('Failed to save seat count');
    }
  };

  const handleUpdateRequest = async (id, updates) => {
    try {
      await transportationAPI.update(id, updates);
      await loadRequests();
    } catch (error) {
      console.error('Error updating request:', error);
      alert('Failed to update request');
    }
  };

  const sameTravelDay = (a, b) => a?.departureFromHouston && a.departureFromHouston === b?.departureFromHouston;

  const handleApprove = (request) => {
    // Auto-assign the next available seat if not already assigned
    if (!request.seatNumber) {
      // Only consider seats on the same bus (same travel day)
      const allAssignedSeats = requests
        .filter(req => req.seatNumber && req.id !== request.id && req.status !== 'rejected' && sameTravelDay(req, request))
        .map(req => req.seatNumber);

      // Find the first available seat (lowest unassigned seat number) on this bus
      let nextSeat = null;
      for (let seat = 1; seat <= busSeatCount; seat++) {
        if (!allAssignedSeats.includes(seat)) {
          nextSeat = seat;
          break;
        }
      }

      if (nextSeat) {
        handleUpdateRequest(request.id, { status: 'confirmed', seatNumber: nextSeat });
      } else {
        const dayLabel = request.departureFromHouston === 'thursday230' ? 'Thursday' : request.departureFromHouston === 'friday230' ? 'Friday' : 'this';
        alert(`No seats available on the ${dayLabel} bus. Please increase the bus seat count or reject some requests.`);
        return;
      }
    } else {
      // Check if the assigned seat is already taken by another request on the same bus
      const seatConflict = requests.find(req =>
        req.id !== request.id &&
        req.seatNumber === request.seatNumber &&
        req.status !== 'rejected' &&
        sameTravelDay(req, request)
      );

      if (seatConflict) {
        alert(`Seat ${request.seatNumber} is already assigned to ${seatConflict.name}. Please assign a different seat.`);
        return;
      }

      // Seat already assigned and available, just confirm
      handleUpdateRequest(request.id, { status: 'confirmed' });
    }
  };

  const handleReject = (request) => {
    if (window.confirm('Are you sure you want to reject this request?')) {
      handleUpdateRequest(request.id, { status: 'rejected', seatNumber: null });
    }
  };

  const handlePaymentStatusChange = (request, newStatus) => {
    handleUpdateRequest(request.id, { paymentStatus: newStatus });
  };

  const handleSeatAssignment = (request, seatNumber) => {
    if (seatNumber && seatNumber > 0 && seatNumber <= busSeatCount) {
      handleUpdateRequest(request.id, { seatNumber: parseInt(seatNumber) });
    } else {
      alert(`Seat number must be between 1 and ${busSeatCount}`);
    }
  };

  const exportTransportationToCsv = () => {
    const escapeCsv = (val) => {
      if (val == null) return '';
      const s = String(val);
      if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };
    const travelDayLabel = (key) => {
      if (key === 'thursday230') return 'Thursday 2:30 PM';
      if (key === 'friday230') return 'Friday 2:30 PM';
      return key || '';
    };
    const columns = [
      'id', 'name', 'email', 'phone', 'parentName', 'parentPhone', 'registrationCode', 'registrationId',
      'tripType', 'oneWayDirection', 'departureFromHouston', 'Travel Day (label)', 'amount', 'pickupPoint', 'destination',
      'status', 'paymentStatus', 'seatNumber', 'createdAt', 'updatedAt'
    ];
    const headerRow = columns.join(',');
    const dataRows = requests.map(req => {
      const row = [
        req.id,
        req.name,
        req.email,
        req.phone,
        req.parentName,
        req.parentPhone,
        req.registrationCode,
        req.registrationId,
        req.tripType,
        req.oneWayDirection,
        req.departureFromHouston,
        travelDayLabel(req.departureFromHouston),
        req.amount,
        req.pickupPoint,
        req.destination,
        req.status,
        req.paymentStatus,
        req.seatNumber,
        req.createdAt,
        req.updatedAt
      ];
      return row.map(escapeCsv).join(',');
    });
    const csv = [headerRow, ...dataRows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transportation-requests-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredRequests = requests.filter(req => {
    if (filterStatus === 'all') return true;
    return req.status === filterStatus;
  });

  const confirmedRequests = requests.filter(req => req.status === 'confirmed');
  const pendingRequests = requests.filter(req => req.status === 'pending');
  const paidRequests = requests.filter(req => req.paymentStatus === 'paid');

  // Generate a single bus seating chart for a given travel day
  const renderSingleBusChart = (departureKey, label, headerColor, borderColor) => {
    const busRequests = confirmedRequests.filter(req => req.departureFromHouston === departureKey);
    const assignedSeats = busRequests.filter(req => req.seatNumber).map(req => req.seatNumber);
    const availableSeats = busSeatCount - assignedSeats.length;

    const seats = [];
    const seatsPerRow = 4;
    const rows = Math.ceil(busSeatCount / seatsPerRow);

    for (let row = 1; row <= rows; row++) {
      const rowSeats = [];
      for (let seatInRow = 1; seatInRow <= seatsPerRow; seatInRow++) {
        const seatNumber = (row - 1) * seatsPerRow + seatInRow;
        if (seatNumber > busSeatCount) break;

        const request = busRequests.find(req => req.seatNumber === seatNumber);
        const isOccupied = !!request;
        const isPaid = request && request.paymentStatus === 'paid';

        rowSeats.push(
          <div
            key={seatNumber}
            className={`w-12 h-12 rounded border-2 flex items-center justify-center text-xs font-semibold cursor-pointer transition ${
              isOccupied
                ? isPaid
                  ? 'bg-green-500 text-white border-green-600'
                  : 'bg-yellow-400 text-gray-800 border-yellow-500'
                : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
            }`}
            title={request ? `${request.name} - ${request.tripType === 'oneWay' ? 'One Way' : 'Round Trip'}` : `Seat ${seatNumber} - Available`}
          >
            {seatNumber}
          </div>
        );
      }
      seats.push(
        <div key={row} className="flex gap-2 items-center">
          {rowSeats[0]}
          {rowSeats[1]}
          <div className="w-8 border-t-2 border-gray-400"></div>
          {rowSeats[2]}
          {rowSeats[3]}
        </div>
      );
    }

    return (
      <div key={departureKey} className={`space-y-2 p-4 rounded-lg border-2 ${borderColor}`}>
        <div className="text-center mb-4">
          <div className={`inline-block px-4 py-2 ${headerColor} text-white rounded-lg font-semibold`}>
            {label} BUS
          </div>
          <div className="mt-2 text-sm font-semibold text-gray-700">
            Seats Available: <span className="text-blue-600">{availableSeats}</span> / {busSeatCount}
          </div>
        </div>
        <div className="space-y-1">{seats}</div>
      </div>
    );
  };

  // Generate bus seating charts for Thursday and Friday
  const renderBusSeatingChart = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderSingleBusChart('thursday230', 'THURSDAY 2:30 PM', 'bg-indigo-600', 'border-indigo-300 bg-indigo-50/50')}
        {renderSingleBusChart('friday230', 'FRIDAY 2:30 PM', 'bg-amber-600', 'border-amber-300 bg-amber-50/50')}
      </div>
      <div className="flex gap-6 justify-center text-xs flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 border-2 border-green-600 rounded"></div>
          <span>Paid & Confirmed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-yellow-400 border-2 border-yellow-500 rounded"></div>
          <span>Pending Payment</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-100 border-2 border-gray-300 rounded"></div>
          <span>Available</span>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading transportation requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Transportation Management</h2>
            <p className="text-gray-600 mt-1">Manage transportation requests and bus seating</p>
          </div>
          <div className="flex items-center gap-4">
            {seatCountEditMode ? (
              <>
                <input
                  type="number"
                  value={tempSeatCount}
                  onChange={(e) => setTempSeatCount(parseInt(e.target.value) || 50)}
                  min="1"
                  max="100"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                />
                <button
                  onClick={saveSeatCount}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setTempSeatCount(busSeatCount);
                    setSeatCountEditMode(false);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <span className="text-sm text-gray-600">Bus Seats: <strong>{busSeatCount}</strong></span>
                {(hasPermission(userRole, 'manage_transportation') || userRole === ROLES.APPLICATION_ADMIN) && (
                  <button
                    onClick={() => setSeatCountEditMode(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit Seat Count
                  </button>
                )}
                <button
                  onClick={exportTransportationToCsv}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </>
            )}
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{requests.length}</div>
            <div className="text-sm text-gray-600">Total Requests</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{pendingRequests.length}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{confirmedRequests.length}</div>
            <div className="text-sm text-gray-600">Confirmed</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{paidRequests.length}</div>
            <div className="text-sm text-gray-600">Paid</div>
          </div>
          <div className="bg-indigo-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-indigo-600">{requests.filter(r => r.departureFromHouston === 'thursday230').length}</div>
            <div className="text-sm text-gray-600">Thursday Bus</div>
          </div>
          <div className="bg-amber-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-amber-600">{requests.filter(r => r.departureFromHouston === 'friday230').length}</div>
            <div className="text-sm text-gray-600">Friday Bus</div>
          </div>
        </div>

        {/* Bus Seating Chart */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Bus Seating Chart</h3>
          {renderBusSeatingChart()}
        </div>

        {/* Filter */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {/* Requests Table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student's Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent / Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reg. Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trip Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Travel Day</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seat</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="px-4 py-8 text-center text-gray-500">
                      No transportation requests found
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{request.name}</td>
                      <td className="px-4 py-3 text-sm">{request.parentName ? `${request.parentName}${request.parentPhone ? ` · ${request.parentPhone}` : ''}` : request.parentPhone || '—'}</td>
                      <td className="px-4 py-3 text-sm">{request.email}</td>
                      <td className="px-4 py-3 text-sm">{request.phone}</td>
                      <td className="px-4 py-3 text-sm font-mono">{request.registrationCode || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm">
                        {request.tripType === 'oneWay' ? 'One Way ($40)' : 'Round Trip ($80)'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {request.departureFromHouston === 'thursday230' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">Thursday 2:30 PM</span>
                        ) : request.departureFromHouston === 'friday230' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">Friday 2:30 PM</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {(request.pickupPoint === 'Campsite' ? 'From Campsite' : 'From IPC Hebron Parking Lot')} → {(request.destination || 'Campsite') === 'IPC Hebron Parking Lot' ? 'IPC Hebron Parking Lot' : (request.destination || 'Campsite')}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          request.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={request.paymentStatus || 'pending'}
                          onChange={(e) => handlePaymentStatusChange(request, e.target.value)}
                          disabled={!hasPermission(userRole, 'edit_transportation_payment') && userRole !== ROLES.APPLICATION_ADMIN}
                          className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {request.status === 'confirmed' ? (
                          <input
                            type="number"
                            value={request.seatNumber || ''}
                            onChange={(e) => handleSeatAssignment(request, e.target.value)}
                            min="1"
                            max={busSeatCount}
                            disabled={!hasPermission(userRole, 'manage_transportation') && userRole !== ROLES.APPLICATION_ADMIN}
                            className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                            placeholder="Seat #"
                          />
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {request.status === 'pending' && (hasPermission(userRole, 'approve_transportation') || userRole === ROLES.APPLICATION_ADMIN) && (
                            <>
                              <button
                                onClick={() => handleApprove(request)}
                                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleReject(request)}
                                className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                              >
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Dashboard Component
function Dashboard({ registrations, lodges, userRole, maxGeneralSeats, maxFamilySeats, maxMiddleSchoolSeats, maxHighSchoolSeats, maxCollegeSeats, maxFamilyRoomsSeats, onUpdateMaxGeneralSeats, onUpdateMaxFamilySeats, onUpdateMaxMiddleSchoolSeats, onUpdateMaxHighSchoolSeats, onUpdateMaxCollegeSeats, onUpdateMaxFamilyRoomsSeats }) {
  const stats = {
    total: registrations.filter(r => r.status === 'confirmed' || r.status === 'pending').length,
    pending: registrations.filter(r => r.status === 'pending').length,
    confirmed: registrations.filter(r => r.status === 'confirmed').length,
    checkedIn: registrations.filter(r => r.checkedIn).length,
    totalRevenue: registrations.reduce((sum, r) => {
      // For fully paid registrations, use the full amount (don't double count with partialPayment)
      if (r.paymentStatus === 'paid') {
        return sum + (parseFloat(r.amount) || 0);
      }
      // For registrations with partial payments (but not fully paid), add the partial payment amount
      const partialPaid = parseFloat(r.partialPayment) || 0;
      if (partialPaid > 0) {
        return sum + partialPaid;
      }
      // For all other registrations, add 0
      return sum;
    }, 0),
    // Payment to be received from partial payments (only count if there's actually a partial payment)
    paymentToBeReceived: registrations
      .filter(r => {
        const partialPaid = parseFloat(r.partialPayment) || 0;
        const remaining = parseFloat(r.remainingPayment) || 0;
        // Only count if there's a partial payment AND remaining payment > 0
        return r && partialPaid > 0 && remaining > 0;
      })
      .reduce((sum, r) => sum + (parseFloat(r.remainingPayment) || 0), 0),
    // Total money to be received from unpaid registrations
    totalUnpaidAmount: registrations
      .filter(r => r && r.paymentStatus !== 'paid' && r.amount)
      .reduce((sum, r) => {
        const totalAmount = parseFloat(r.amount) || 0;
        const partialPaid = parseFloat(r.partialPayment) || 0;
        const remaining = r.remainingPayment ? parseFloat(r.remainingPayment) : Math.max(0, totalAmount - partialPaid);
        return sum + remaining;
      }, 0),
    // Category counts
    elementary: registrations.filter(r => 
      r.data?.camperCategory === 'Elementary' || r.camperCategory === 'Elementary'
    ).length,
    middleSchool: registrations.filter(r => 
      r.data?.camperCategory === 'Middle School' || r.camperCategory === 'Middle School'
    ).length,
    highSchool: registrations.filter(r => 
      r.data?.camperCategory === 'High School' || r.camperCategory === 'High School'
    ).length,
    college: registrations.filter(r => 
      r.data?.camperCategory === 'College' || r.camperCategory === 'College'
    ).length,
    guestSpeaker: registrations.filter(r => 
      r.data?.camperCategory === 'Guest Speaker' || r.camperCategory === 'Guest Speaker'
    ).length,
    familyRooms: registrations.filter(r => 
      r.type && r.type.startsWith('family')
    ).length
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        {(hasPermission(userRole, 'manage_settings') || hasPermission(userRole, 'manage_dashboard_toggles')) && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-600">
              Registration gates, payments, email blocking, self check-in, and transportation are configured under{' '}
              <strong className="text-gray-800">Camp Settings</strong> in the sidebar.
            </p>
            {/* Capacity Settings */}
            <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-3">
              <div className="flex items-center gap-4">
                <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">Capacity:</span>
                <div className="flex items-center gap-2 flex-1">
                  <label className="text-xs text-gray-600 whitespace-nowrap">General</label>
                  <input
                    type="number"
                    min="0"
                    value={maxGeneralSeats}
                    onChange={(e) => onUpdateMaxGeneralSeats(parseInt(e.target.value) || 0)}
                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center gap-2 flex-1">
                  <label className="text-xs text-gray-600 whitespace-nowrap">Family</label>
                  <input
                    type="number"
                    min="0"
                    value={maxFamilySeats}
                    onChange={(e) => onUpdateMaxFamilySeats(parseInt(e.target.value) || 0)}
                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center gap-4 pt-2 border-t border-gray-200">
                <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">Category Limits:</span>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 flex-1">
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-gray-600 whitespace-nowrap">MS</label>
                    <input
                      type="number"
                      min="0"
                      value={maxMiddleSchoolSeats}
                      onChange={(e) => onUpdateMaxMiddleSchoolSeats(parseInt(e.target.value) || 0)}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      title="Middle School"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-gray-600 whitespace-nowrap">HS</label>
                    <input
                      type="number"
                      min="0"
                      value={maxHighSchoolSeats}
                      onChange={(e) => onUpdateMaxHighSchoolSeats(parseInt(e.target.value) || 0)}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      title="High School"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-gray-600 whitespace-nowrap">College</label>
                    <input
                      type="number"
                      min="0"
                      value={maxCollegeSeats}
                      onChange={(e) => onUpdateMaxCollegeSeats(parseInt(e.target.value) || 0)}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-gray-600 whitespace-nowrap">Family</label>
                    <input
                      type="number"
                      min="0"
                      value={maxFamilyRoomsSeats}
                      onChange={(e) => onUpdateMaxFamilyRoomsSeats(parseInt(e.target.value) || 0)}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Registrations"
          value={stats.total}
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Pending Approval"
          value={stats.pending}
          icon={Clock}
          color="yellow"
        />
        <StatCard
          title="Confirmed"
          value={stats.confirmed}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title="Checked In"
          value={stats.checkedIn}
          icon={QrCode}
          color="purple"
        />
      </div>

      {/* Category Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Elementary"
          value={stats.elementary}
          icon={Users}
          color="yellow"
        />
        <StatCard
          title="Middle School"
          value={stats.middleSchool}
          icon={Users}
          color="blue"
        />
        <StatCard
          title="High School"
          value={stats.highSchool}
          icon={Users}
          color="indigo"
        />
        <StatCard
          title="College"
          value={stats.college}
          icon={Users}
          color="purple"
        />
        <StatCard
          title="Guest Speaker"
          value={stats.guestSpeaker}
          icon={Users}
          color="orange"
        />
        <StatCard
          title="Family Rooms"
          value={stats.familyRooms}
          icon={Bed}
          color="green"
        />
      </div>

      {userRole && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-2 text-gray-700">Total Revenue Received</h3>
          <div className="text-3xl font-bold text-green-600">
            ${stats.totalRevenue.toFixed(2)}
            </div>
            <p className="text-sm text-gray-500 mt-1">From fully paid registrations</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-2 text-gray-700">Payment to be Received</h3>
            <div className="text-3xl font-bold text-orange-600">
              ${stats.paymentToBeReceived.toFixed(2)}
            </div>
            <p className="text-sm text-gray-500 mt-1">From partial payments</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-2 text-gray-700">Total Unpaid Amount</h3>
            <div className="text-3xl font-bold text-red-600">
              ${stats.totalUnpaidAmount.toFixed(2)}
            </div>
            <p className="text-sm text-gray-500 mt-1">From all unpaid registrations</p>
          </div>
        </div>
      )}

      {/* Recent Registrations */}
      {userRole && registrations.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Recent Registrations</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {registrations.slice(0, 5).map(reg => (
                  <tr key={reg.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{reg.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="capitalize">{reg.type}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={reg.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <PaymentBadge status={(() => {
                        // Check if this is a non-primary family member
                        // Method 1: Check familyCode and familyPosition
                        if (reg.familyCode && reg.familyPosition && reg.familyPosition !== 1) {
                          return 'linked';
                        }
                        // Method 2: Check registration code pattern (ICPFFAM001-1, ICPFFAM001-2, etc.)
                        const regCode = reg.qrCode || reg.registrationCode || '';
                        if (regCode && typeof regCode === 'string' && regCode.startsWith('ICPFFAM')) {
                          const match = regCode.match(/^ICPFFAM\d+-(\d+)$/);
                          if (match && parseInt(match[1], 10) !== 1) {
                            return 'linked';
                          }
                        }
                        // Method 3: Check old format (FAM_ prefix) - legacy support
                        if (reg.familyCode && typeof reg.familyCode === 'string' && reg.familyCode.startsWith('FAM_')) {
                          if (reg.familyPosition && reg.familyPosition !== 1) {
                            return 'linked';
                          }
                        }
                        return reg.paymentStatus;
                      })()} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to generate next sequential ICPF registration code
const generateICPFCode = (existingRegistrations) => {
  // Extract all existing codes (check both qrCode and registrationCode fields)
  const allCodes = existingRegistrations
    .map(reg => reg.qrCode || reg.registrationCode || '')
    .filter(code => code && typeof code === 'string' && code.startsWith('ICPF'));
  
  // Extract numbers from ICPF codes (exclude ICPFFAM codes)
  const numbers = allCodes
    .map(code => {
      // Match ICPF001, ICPF002, etc. but not ICPFFAM001-1
      const match = code.match(/^ICPF(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter(num => num > 0);
  
  // Find the highest number
  const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
  
  // Generate next sequential code
  const nextNumber = maxNumber + 1;
  return `ICPF${String(nextNumber).padStart(3, '0')}`;
};

const generateFamilyCode = (existingRegistrations) => {
  // Extract all existing family codes (from familyCode field and from registration codes)
  const allFamilyCodes = existingRegistrations
    .map(reg => {
      // Check familyCode field first
      if (reg.familyCode && typeof reg.familyCode === 'string' && reg.familyCode.startsWith('ICPFFAM')) {
        return reg.familyCode;
      }
      // Also check registration codes that match ICPFFAM pattern
      const code = reg.qrCode || reg.registrationCode || '';
      if (code && typeof code === 'string' && code.startsWith('ICPFFAM')) {
        // Extract family code from ICPFFAM001-1 format (get ICPFFAM001)
        const match = code.match(/^(ICPFFAM\d+)/);
        return match ? match[1] : null;
      }
      return null;
    })
    .filter(code => code !== null);
  
  // Extract numbers from ICPFFAM codes
  const numbers = allFamilyCodes
    .map(code => {
      const match = code.match(/^ICPFFAM(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter(num => num > 0);
  
  // Find the highest number
  const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
  
  // Generate next sequential family code
  const nextNumber = maxNumber + 1;
  return `ICPFFAM${String(nextNumber).padStart(3, '0')}`;
};

// Helper function to generate Day registration codes (prefix from camp dates + pricing; legacy Fri/Sat fallback)
const generateDayRegistrationCode = (attendanceDays, existingRegistrations, dayOptions = []) => {
  const fromOptions = dayOptions.find((o) => o.value === attendanceDays);
  const legacyPrefixMap = { 'Friday': 'ICPFDAYF', 'Friday & Saturday': 'ICPFDAYTF', Saturday: 'ICPFDAYS' };
  const prefix = fromOptions?.codePrefix || legacyPrefixMap[attendanceDays] || 'ICPFDAYF';
  const allCodes = existingRegistrations
    .map(reg => reg.qrCode || reg.registrationCode || '')
    .filter(code => code && typeof code === 'string' && code.startsWith(prefix));
  const numbers = allCodes
    .map(code => {
      const match = code.match(new RegExp(`^${prefix}(\\d+)$`));
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter(num => num > 0);
  const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
  const nextNumber = maxNumber + 1;
  return `${prefix}${String(nextNumber).padStart(3, '0')}`;
};

// Helper function to check if a registration code is already in use
const isRegistrationCodeDuplicate = (code, registrations, excludeRegistrationId = null) => {
  if (!code || !registrations || !Array.isArray(registrations)) {
    return false;
  }
  
  const normalizedCode = code.toUpperCase().trim();
  
  return registrations.some(reg => {
    // Skip the registration being edited
    if (excludeRegistrationId && reg.id === excludeRegistrationId) {
      return false;
    }
    
    // Skip deleted registrations
    if (reg.status === 'deleted' || reg.deletedAt) {
      return false;
    }
    
    const regCode = (reg.qrCode || reg.registrationCode || '').toUpperCase().trim();
    return regCode === normalizedCode && regCode !== '';
  });
};

// Helper function to check if a family code is already in use
const isFamilyCodeDuplicate = (familyCode, registrations, excludeRegistrationId = null) => {
  if (!familyCode || !registrations || !Array.isArray(registrations)) {
    return false;
  }
  
  const normalizedFamilyCode = familyCode.toUpperCase().trim();
  
  // Check if any registration has this family code (excluding the one being edited and its current family)
  const registration = registrations.find(r => r && r.id === excludeRegistrationId);
  const currentFamilyCode = registration?.familyCode;
  
  return registrations.some(reg => {
    // Skip the registration being edited
    if (excludeRegistrationId && reg.id === excludeRegistrationId) {
      return false;
    }
    
    // Skip deleted registrations
    if (reg.status === 'deleted' || reg.deletedAt) {
      return false;
    }
    
    // If editing a registration that's part of a family, don't flag its current family members
    if (currentFamilyCode && reg.familyCode === currentFamilyCode) {
      return false;
    }
    
    const regFamilyCode = (reg.familyCode || '').toUpperCase().trim();
    return regFamilyCode === normalizedFamilyCode && regFamilyCode !== '';
  });
};

/** First enabled option in order: card → Zelle → Venmo → PayPal. */
function firstAvailableRegistrationPaymentMethod(cardPaymentEnabled, zelleEnabled, venmoEnabled, paypalEnabled) {
  if (cardPaymentEnabled) return 'card';
  if (zelleEnabled) return 'zelle';
  if (venmoEnabled) return 'venmo';
  if (paypalEnabled) return 'paypal';
  return 'card';
}

// Registration Form Component
function RegistrationForm({
  registrationType,
  onRegister,
  onBack,
  registrations = [],
  cardPaymentEnabled = true,
  paypalEnabled = true,
  venmoEnabled = true,
  zelleEnabled = true,
  blockInitialRegistrationReceivedEmail = false,
  userRole = null,
  pricingSettings = DEFAULT_PRICING_SETTINGS,
  campSettings: campForNotice = null
}) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    gender: '',
    camperCategory: '',
    email: '',
    confirmEmail: '',
    firstTimeAttending: '',
    dateOfBirth: '',
    age: '',
    addressLine1: '',
    addressLine2: '',
    country: 'United States',
    state: '',
    city: '',
    zip: '',
    countryCode: 'United States +1',
    mobileNumber: '',
    homePhone: '',
    churchName: '',
    pastorName: '',
    allergies: '',
    allergyDescription: '',
    emergencyContactRelationship: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    parentGuardianName: '',
    parentGuardianEmail: '',
    parentGuardianPhone: '',
    tshirtSize: '',
    type: '',
    specialRequests: '', // Hidden from registrants, only for admin
    stayPreference: 'withGroup', // For middle-schoolers: withParents or withGroup
    attendanceDays: '', // For day registration: Friday, Friday & Saturday, Saturday
    promoCode: '' // Single field: discount OR sponsor waiver code (resolved on server)
  });
  const [familyMembers, setFamilyMembers] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState(() =>
    firstAvailableRegistrationPaymentMethod(cardPaymentEnabled, zelleEnabled, venmoEnabled, paypalEnabled)
  );
  const [installmentPlan, setInstallmentPlan] = useState(null); // null, 2, or 3 for family registrations
  const [currentInstallment, setCurrentInstallment] = useState(1); // Track which installment (1, 2, or 3)
  
  // Reset payment method if the selected option is turned off in Camp Settings
  useEffect(() => {
    const valid =
      (paymentMethod === 'card' && cardPaymentEnabled) ||
      (paymentMethod === 'zelle' && zelleEnabled) ||
      (paymentMethod === 'venmo' && venmoEnabled) ||
      (paymentMethod === 'paypal' && paypalEnabled);
    if (!valid) {
      setPaymentMethod(
        firstAvailableRegistrationPaymentMethod(cardPaymentEnabled, zelleEnabled, venmoEnabled, paypalEnabled)
      );
    }
  }, [paypalEnabled, venmoEnabled, zelleEnabled, paymentMethod, cardPaymentEnabled]);
  
  const [cardDetails, setCardDetails] = useState({
    number: '',
    expiry: '',
    cvv: '',
    paymentSucceeded: false,
    transactionId: null,
    paymentIntentId: null
  });
  const [processing, setProcessing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  // Ref to prevent double submission (state updates are async; ref is synchronous)
  const isSubmittingRef = useRef(false);
  const [feePreviewTotal, setFeePreviewTotal] = useState(null);
  const [feePreviewError, setFeePreviewError] = useState('');

  const pricing = { ...DEFAULT_PRICING_SETTINGS, ...(pricingSettings || {}) };
  const dayRegistrationSelectOptions = useMemo(() => {
    const pr = { ...DEFAULT_PRICING_SETTINGS, ...(pricingSettings || {}) };
    return getDayRegistrationOptions(mergeCampSettings(campForNotice), pr);
  }, [campForNotice, pricingSettings]);

  const campFees = {
    test: pricing.test,
    student: pricing.student,
    earlyBirdStudent: pricing.earlyBirdStudent,
    adult: pricing.adult,
    family2: pricing.family2,
    family3: pricing.family3,
    family4: pricing.family4,
    family5: pricing.family5
  };

  const isFamilyRegistration = registrationType && registrationType.startsWith('family');
  const isStudentRegistration = registrationType === 'student' || registrationType === 'earlyBirdStudent';
  const isAdultRegistration = registrationType === 'adult';
  const isDayRegistration = registrationType === 'day';
  const isTestRegistration = registrationType === 'test';
  // eslint-disable-next-line no-unused-vars
  const isEarlyBirdStudent = registrationType === 'earlyBirdStudent';
  const isGuestSpeaker = isAdultRegistration && formData.camperCategory === 'Guest Speaker';

  // Per-member amount for a family registration: flat family total is split
  // among paying members only (Below Elementary children at or under the
  // configured max age get $0). If everyone would be free, split evenly across
  // all members. The first paying member absorbs the rounding remainder.
  const getFamilyMemberAmount = (memberIndex, memberCount) => {
    if (!isFamilyRegistration) return 0;
    const total = parseFloat(campFees[registrationType]);
    if (!Number.isFinite(total) || total <= 0 || !memberCount) return 0;
    const campMerged = mergeCampSettings(campForNotice);
    const isIdxFree = (idx) => {
      const m = familyMembers[idx];
      return m && isFamilyBelowElementaryFreeForFlatSplit(m, campMerged);
    };
    const payingIndices = [];
    for (let i = 0; i < memberCount; i++) {
      if (!isIdxFree(i)) payingIndices.push(i);
    }
    const splitIndices = payingIndices.length > 0 ? payingIndices : [...Array(memberCount).keys()];
    if (payingIndices.length > 0 && isIdxFree(memberIndex)) return 0;
    const n = splitIndices.length;
    const amongIndex = splitIndices.indexOf(memberIndex);
    const base = Math.floor((total * 100) / n) / 100;
    if (amongIndex === 0) {
      const remainder = Math.round((total - base * n) * 100) / 100;
      return Math.round((base + remainder) * 100) / 100;
    }
    return base;
  };

  const getTotalAmount = () => {
    // Guest Speaker registrations are free
    if (isGuestSpeaker) {
      return 0;
    }
    
    // Family registrations now use a flat per-family-size price set in Camp Settings.
    if (isFamilyRegistration) {
      const flat = parseFloat(campFees[registrationType]);
      return Number.isFinite(flat) ? flat : 0;
    }
    
    if (registrationType && campFees[registrationType] !== null && campFees[registrationType] !== undefined) {
      return campFees[registrationType];
    }
    if (registrationType === 'student') return pricing.student;
    if (registrationType === 'earlyBirdStudent') return pricing.earlyBirdStudent;
    if (registrationType === 'adult') return pricing.adult;
    if (registrationType === 'day') {
      const opt = dayRegistrationSelectOptions.find((o) => o.value === formData.attendanceDays);
      if (opt && typeof opt.price === 'number') return opt.price;
      const days = formData.attendanceDays;
      if (days === 'Friday & Saturday') return pricing.dayTwoDay;
      if (days === 'Friday' || days === 'Saturday') return pricing.dayOneDay;
      return pricing.dayOneDay;
    }
    return pricing.adult;
  };

  const resolveMoneyTotal = () => {
    if (isGuestSpeaker) return 0;
    if (feePreviewTotal != null && !feePreviewError) return feePreviewTotal;
    return getTotalAmount();
  };

  // Calculate installment amount for family registrations
  const getInstallmentAmount = () => {
    if (!isFamilyRegistration || !installmentPlan) {
      return resolveMoneyTotal();
    }
    const total = resolveMoneyTotal();
    return Math.ceil(total / installmentPlan); // Round up to avoid cents issues
  };

  // Get payment amount (either full or installment)
  const getPaymentAmount = () => {
    if (isFamilyRegistration && paymentMethod === 'card' && installmentPlan) {
      return getInstallmentAmount();
    }
    return resolveMoneyTotal();
  };

  useEffect(() => {
    if (!isDayRegistration) return;
    if (!dayRegistrationSelectOptions.length) {
      if (formData.attendanceDays) setFormData((prev) => ({ ...prev, attendanceDays: '' }));
      return;
    }
    if (!formData.attendanceDays) return;
    if (!dayRegistrationSelectOptions.some((o) => o.value === formData.attendanceDays)) {
      setFormData((prev) => ({ ...prev, attendanceDays: '' }));
    }
  }, [isDayRegistration, registrationType, dayRegistrationSelectOptions, formData.attendanceDays]);

  const usStates = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
    'Wisconsin', 'Wyoming'
  ];

  // Helper function to calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Helper functions for family registrations
  const createEmptyMember = () => ({
    firstName: '',
    lastName: '',
    middleName: '',
    gender: '',
    camperCategory: '',
    email: '',
    confirmEmail: '',
    mobileNumber: '',
    dateOfBirth: '',
    age: '',
    firstTimeAttending: '',
    campVolunteer: '',
    tshirtSize: '',
    allergies: '',
    allergyDescription: '',
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    parentGuardianName: '',
    parentGuardianEmail: '',
    parentGuardianPhone: '',
    stayPreference: 'withGroup', // Default to withGroup for middle-schoolers
    promoCode: '',
    applyDiscountWaiverToMember: true
  });

  // For day registration: default camper category to Adults
  useEffect(() => {
    if (registrationType === 'day') {
      setFormData(prev => ({ ...prev, camperCategory: 'Adults', attendanceDays: prev.attendanceDays || '' }));
    }
  }, [registrationType]);

  // Initialize family members based on registration type
  useEffect(() => {
    if (registrationType && registrationType.startsWith('family')) {
      const familySize = parseInt(registrationType.replace('family', ''));
      const members = [];
      for (let i = 0; i < familySize; i++) {
        const member = createEmptyMember();
        // First member must be an adult
        if (i === 0) {
          member.camperCategory = 'Adults';
        }
        members.push(member);
      }
      setFamilyMembers(members);
    }
  }, [registrationType]);

  const updateFamilyMember = (index, field, value) => {
    const updated = [...familyMembers];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-calculate age when date of birth changes
    if (field === 'dateOfBirth') {
      const age = calculateAge(value);
      updated[index].age = age;
    }
    
    setFamilyMembers(updated);
  };

  useEffect(() => {
    if (isGuestSpeaker) {
      setFeePreviewTotal(null);
      setFeePreviewError('');
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        setFeePreviewError('');
        if (isFamilyRegistration && (!familyMembers || familyMembers.length === 0)) {
          setFeePreviewTotal(null);
          return;
        }
        const lines = isFamilyRegistration
          ? familyMembers.map((m, i) => ({
              baseAmount: getFamilyMemberAmount(i, familyMembers.length),
              promoCode: m.applyDiscountWaiverToMember === false ? '' : (m.promoCode || '')
            }))
          : [
              {
                baseAmount: getTotalAmount(),
                promoCode: formData.promoCode || ''
              }
            ];
        const res = await feeWaiversAPI.preview(lines);
        if (!cancelled) setFeePreviewTotal(res.totalDue);
      } catch (err) {
        if (!cancelled) {
          setFeePreviewError(err.message || 'Could not validate fee waiver or discount codes.');
          setFeePreviewTotal(null);
        }
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [
    isGuestSpeaker,
    isFamilyRegistration,
    registrationType,
    pricingSettings,
    campForNotice,
    formData.attendanceDays,
    formData.camperCategory,
    formData.promoCode,
    familyMembers
  ]);


  const validateFamily = () => {
    if (isFamilyRegistration) {
      // Check minimum age and first member requirements for all members
      for (let i = 0; i < familyMembers.length; i++) {
        const member = familyMembers[i];
        if (!member.dateOfBirth) {
          alert(`Please enter date of birth for family member ${i + 1}!`);
          return false;
        }
        const age = calculateAge(member.dateOfBirth);
        
        // First member must be adult (25+ years)
        if (i === 0) {
          if (!member.camperCategory || member.camperCategory !== 'Adults') {
            alert('The first family member must be an adult (25+ years old)!');
            return false;
          }
          if (age < 25) {
            alert(`The first family member must be at least 25 years old! Current age: ${age}`);
            return false;
          }
        }
        
        // All members must be at least 11 years old, except for Elementary and Below Elementary categories
        const isElementaryOrBelow = member.camperCategory === 'Elementary' || member.camperCategory === BELOW_ELEMENTARY_CAMPER_CATEGORY;
        if (!isElementaryOrBelow && age < 11) {
          alert(`Family member ${i + 1} must be at least 11 years old! Current age: ${age}`);
          return false;
        }
      }

      // Validate email confirmation for all family members
      for (let i = 0; i < familyMembers.length; i++) {
        const member = familyMembers[i];
        if (member.email && member.confirmEmail && member.email !== member.confirmEmail) {
          alert(`Email and Confirm Email must match for family member ${i + 1}!`);
          return false;
        }
      }
    }
    return true;
  };

  // Main submission function - can be called from form submit or programmatically
  const handleSubmit = async (e, paymentOverride = null) => {
    if (e && e.preventDefault) {
    e.preventDefault();
    }
    // Prevent double submission: ref is synchronous, so a second call (e.g. form + auto-submit) will bail out
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    try {
    // Validate consent agreement
    if (!consentAccepted) {
      alert('Please read and accept the consent agreement to continue.');
      return;
    }
    
    // Validate email confirmation
    if (formData.email !== formData.confirmEmail) {
      alert('Email and Confirm Email must match!');
      return;
    }
    
    // Validate family has adult
    if (!validateFamily()) {
      return;
    }

    if (!isGuestSpeaker && feePreviewError) {
      alert(feePreviewError);
      return;
    }
    
    // For card payments, ensure payment was completed (skip for Guest Speaker)
    // Use paymentOverride if provided (from auto-submit after payment), otherwise check state
    const paymentSucceeded = paymentOverride ? paymentOverride.success : cardDetails.paymentSucceeded;
    const transactionId = paymentOverride ? paymentOverride.transactionId : cardDetails.transactionId;
    // eslint-disable-next-line no-unused-vars
    const paymentIntentId = paymentOverride ? paymentOverride.paymentIntentId : cardDetails.paymentIntentId;
    
    if (paymentMethod === 'card' && !isGuestSpeaker) {
      const totalDueBeforeSubmit = resolveMoneyTotal();
      if (totalDueBeforeSubmit > 0 && !paymentSucceeded) {
        alert('Please complete the payment first before submitting your registration.');
        return;
      }
    }
    
    setProcessing(true);

    const mergedCampForRegEmail = mergeCampSettings(campForNotice);
    const emailEventName = (mergedCampForRegEmail.campTitle || '').trim() || DEFAULT_CAMP_SETTINGS.campTitle;
    const emailEventNameEsc = escapeHtmlForEmail(emailEventName);

    // Generate family code using ICPF format - use the first member's registration code as the family code
    // We'll generate it after creating the first registration code
    let familyCode = null;
    const totalAmount = resolveMoneyTotal();

    if (isFamilyRegistration) {
      // Create multiple registrations for family
      const newRegistrations = [];
      
      // Capture the primary family email address BEFORE processing (so it doesn't get lost)
      const primaryFamilyEmail = formData.email || (familyMembers[0] && familyMembers[0].email);
      console.log('📧 Captured primary family email:', primaryFamilyEmail);
      
      // Generate family code in ICPFFAM001 format
      familyCode = generateFamilyCode(registrations);
      
      // Validate that the generated family code is not a duplicate
      if (isFamilyCodeDuplicate(familyCode, registrations)) {
        alert(`Error: Generated family code "${familyCode}" is already in use. Please contact support.`);
        setProcessing(false);
        return;
      }
      
      for (let i = 0; i < familyMembers.length; i++) {
        const member = familyMembers[i];
        const isAdult = member.camperCategory && (member.camperCategory === 'Adults' || member.camperCategory.includes('Adult'));
        
        // Generate registration code in ICPFFAM001-1, ICPFFAM001-2 format
        const memberPosition = i + 1;
        const registrationCode = `${familyCode}-${memberPosition}`;
        
        // Validate that the registration code is not a duplicate
        if (isRegistrationCodeDuplicate(registrationCode, registrations)) {
          alert(`Error: Generated registration code "${registrationCode}" is already in use. Please contact support.`);
          setProcessing(false);
          return;
        }
        
        const registration = {
          id: 'reg_' + Date.now() + '_' + i + '_' + Math.random().toString(36).substr(2, 9),
          name: `${member.firstName} ${member.middleName ? member.middleName + ' ' : ''}${member.lastName}`,
          firstName: member.firstName,
          lastName: member.lastName,
          middleName: member.middleName,
          gender: member.gender,
          camperCategory: member.camperCategory,
          email: member.email || formData.email,
          confirmEmail: member.confirmEmail || member.email || formData.email,
          mobileNumber: member.mobileNumber,
          dateOfBirth: member.dateOfBirth,
          age: member.age,
          firstTimeAttending: member.firstTimeAttending,
          campVolunteer: member.campVolunteer,
          tshirtSize: member.tshirtSize,
          allergies: member.allergies,
          allergyDescription: member.allergyDescription,
          emergencyContactName: member.emergencyContactName,
          emergencyContactRelationship: member.emergencyContactRelationship,
          emergencyContactPhone: member.emergencyContactPhone,
          parentGuardianName: member.parentGuardianName,
          parentGuardianEmail: member.parentGuardianEmail,
          parentGuardianPhone: member.parentGuardianPhone,
          stayPreference: member.stayPreference || (member.camperCategory === 'Middle School' ? 'withGroup' : null),
          churchName: formData.churchName,
          pastorName: formData.pastorName,
          type: isAdult ? 'adult' : 'student',
          amount: getFamilyMemberAmount(i, familyMembers.length),
          promoCode:
            member.applyDiscountWaiverToMember === false ? '' : (member.promoCode || '').trim(),
          familyCode: familyCode,
          familyType: registrationType,
          familyPosition: i + 1,
          familyTotal: totalAmount,
          paymentMethod,
          paymentStatus: i === 0 ? (paymentMethod === 'card' ? (installmentPlan ? 'partial' : 'processing') : 'pending') : 'linked',
          installmentPlan: i === 0 && isFamilyRegistration && installmentPlan ? {
            totalInstallments: installmentPlan,
            currentInstallment: currentInstallment,
            installmentAmount: getInstallmentAmount(),
            totalAmount: resolveMoneyTotal(),
            paidAmount: getPaymentAmount(),
            remainingAmount: resolveMoneyTotal() - getPaymentAmount()
          } : null,
          status: 'pending',
          registeredAt: new Date().toISOString(),
          checkedIn: false,
          qrCode: registrationCode,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
          countryCode: formData.countryCode,
          homePhone: formData.homePhone,
          specialRequests: '' // Initialize empty, only admin can set this
        };
        
        newRegistrations.push(registration);
      }

      // Use already-completed payment if card payment was successful
      if (paymentMethod === 'card') {
        if (paymentSucceeded && transactionId) {
          if (installmentPlan && installmentPlan > 1) {
            // Partial payment for installment plan
            newRegistrations[0].paymentStatus = 'partial';
            newRegistrations[0].installmentPayments = newRegistrations[0].installmentPayments || [];
            newRegistrations[0].installmentPayments.push({
              installmentNumber: currentInstallment,
              amount: getPaymentAmount(),
              transactionId: transactionId,
              paymentDate: new Date().toISOString()
            });
            // Store installment plan info
            newRegistrations[0].installmentPlan = {
              totalInstallments: installmentPlan,
              currentInstallment: currentInstallment,
              installmentAmount: getInstallmentAmount(),
              totalAmount: resolveMoneyTotal(),
              paidAmount: getPaymentAmount(),
              remainingAmount: resolveMoneyTotal() - getPaymentAmount()
            };
          } else {
            // Full payment
          newRegistrations[0].paymentStatus = 'paid';
          newRegistrations[0].transactionId = transactionId;
          newRegistrations[0].paymentDate = new Date().toISOString();
          }
        } else if (resolveMoneyTotal() <= 0 && !isGuestSpeaker) {
          newRegistrations[0].paymentStatus = 'paid';
          newRegistrations[0].paymentMethod = 'waived';
        } else {
          // Payment not completed yet
          alert('Please complete the payment first before submitting your registration.');
          setProcessing(false);
          return;
        }
      }

      // Register all family members sequentially so backend-assigned family code can be applied to the rest
      const createdFamilyRows = [];
      try {
        const firstCreated = await onRegister(newRegistrations[0]);
        createdFamilyRows.push(firstCreated || newRegistrations[0]);
        if (firstCreated && firstCreated.familyCode && firstCreated.familyCode !== newRegistrations[0].familyCode) {
          const newBase = firstCreated.familyCode;
          for (let i = 1; i < newRegistrations.length; i++) {
            newRegistrations[i].familyCode = newBase;
            newRegistrations[i].qrCode = `${newBase}-${i + 1}`;
            newRegistrations[i].registrationCode = `${newBase}-${i + 1}`;
          }
        }
        for (let i = 1; i < newRegistrations.length; i++) {
          const cre = await onRegister(newRegistrations[i]);
          createdFamilyRows.push(cre || newRegistrations[i]);
        }
      } catch (error) {
        console.error('Error creating family registrations:', error);
        alert(formatApiErrorMessage(error, 'Failed to create family registrations. Please try again.'));
        setProcessing(false);
        return;
      }

      const primarySaved = createdFamilyRows[0] || {};
      const familyCodeForEmail = primarySaved.familyCode || familyCode;
      const totalAfterFees = createdFamilyRows.reduce((sum, r) => sum + (parseFloat(r && r.amount) || 0), 0);
      const familyFullyCovered =
        totalAfterFees <= 0.005 || registrationHasNoPaymentDue(primarySaved);

      // Send registration received email after successful registration (use saved amounts / payment flags)
      const familyMembersList = createdFamilyRows.map((creg, i) => {
        const reg = newRegistrations[i] || {};
        const memberAmount = parseFloat(creg.amount);
        const displayAmt = Number.isFinite(memberAmount) ? memberAmount : 0;
        return {
          name: creg.name || reg.name,
          category: creg.camperCategory || reg.camperCategory || 'N/A',
          registrationCode: creg.qrCode || creg.registrationCode || reg.qrCode,
          amount: `$${displayAmt.toFixed(2)}`
        };
      });

      const familyEmailItems = [
        {
          label: 'Family Code',
          value: `<strong style="color: #3b82f6; font-family: monospace; font-size: 16px;">${familyCodeForEmail}</strong>`
        },
        { label: 'Number of Family Members', value: createdFamilyRows.length },
        {
          label: 'Total Amount',
          value: `<strong style="color: #ef4444; font-size: 18px;">$${totalAfterFees.toFixed(2)}</strong>`
        }
      ];

      if (familyFullyCovered) {
        familyEmailItems.push(
          {
            label: 'Payment',
            value:
              '<span style="color: #059669; font-weight: 600;">You are all set — no payment is required.</span> Sponsor waivers and discounts cover your family&apos;s registration as shown above.'
          },
          {
            label: 'Payment status',
            value: '<span style="color: #059669; font-weight: 600;">Paid (fully covered)</span>'
          }
        );
      } else {
        familyEmailItems.push({
          label: 'Payment Method',
          value: escapeHtmlForEmail(
            paymentMethodDisplayLabel(primarySaved.paymentMethod || paymentMethod, campForNotice)
          )
        });

        if (installmentPlan) {
          familyEmailItems.push(
            { label: 'Payment Plan', value: `${installmentPlan} Installments` },
            { label: 'Current Installment', value: `Installment ${currentInstallment} of ${installmentPlan}` },
            { label: 'Amount Paid', value: `$${getPaymentAmount()}` },
            { label: 'Remaining Balance', value: `$${(resolveMoneyTotal() - getPaymentAmount()).toFixed(2)}` }
          );
        }

        if (paymentMethod === 'card' && primarySaved.paymentStatus === 'paid') {
          familyEmailItems.push(
            { label: 'Payment Status', value: '<span style="color: #10b981; font-weight: 600;">Paid</span>' },
            { label: 'Transaction ID', value: transactionId }
          );
        } else if (paymentMethod === 'card' && primarySaved.paymentStatus === 'partial') {
          familyEmailItems.push(
            { label: 'Payment Status', value: '<span style="color: #f59e0b; font-weight: 600;">Partial Payment</span>' },
            { label: 'Amount Paid', value: `$${getPaymentAmount()}` },
            { label: 'Remaining', value: `$${(resolveMoneyTotal() - getPaymentAmount()).toFixed(2)}` },
            { label: 'Transaction ID', value: transactionId }
          );
        } else if (primarySaved.paymentStatus === 'paid') {
          familyEmailItems.push({
            label: 'Payment Status',
            value: '<span style="color: #10b981; font-weight: 600;">Paid</span>'
          });
        } else {
          familyEmailItems.push({
            label: 'Payment Status',
            value: '<span style="color: #ef4444; font-weight: 600;">Pending</span>'
          });
        }
      }

      const familyPaymentFollowUpHtml = familyFullyCovered
        ? `<div style="background-color: #ecfdf5; border-left: 4px solid #059669; padding: 15px; margin: 12px 0; border-radius: 4px;">
          <p style="margin: 0; color: #065f46; font-size: 14px; line-height: 1.6;">
            <strong>Payment complete:</strong> We recorded your family&apos;s discounts and/or sponsor waivers. You do not need to send a separate payment for the total shown above.
          </p>
        </div>`
        : `<div style="background-color: #dbeafe; border-left: 4px solid #3b82f6; padding: 15px; margin: 12px 0; border-radius: 4px;">
          <p style="margin: 0; color: #1e40af; font-size: 14px; line-height: 1.6;">
            <strong>💳 Important:</strong> Your registration will only be confirmed after payment is received. Please complete your payment to secure your spot.
          </p>
        </div>`;

      const familyPayMethod = primarySaved.paymentMethod || paymentMethod;
      const familyPayStatus = primarySaved.paymentStatus || 'pending';
      const memoFamCode =
        familyCodeForEmail || primarySaved.qrCode || primarySaved.registrationCode || '';
      const familyNonCardPaymentMemoHtml =
        !familyFullyCovered && shouldAppendNonCardPaymentMemo(familyPayMethod, familyPayStatus)
          ? buildNonCardPaymentMemoInstructionsHtml(campForNotice, familyPayMethod, memoFamCode)
          : '';

      // Add family members section with QR codes
      const familyMembersHtml = `
        <div style="background-color: #f9fafb; border-radius: 8px; padding: 14px; margin: 12px 0;">
          <h3 style="margin: 0 0 15px 0; color: #374151; font-size: 18px; font-weight: 600;">Family Members Registered:</h3>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background-color: #f3f4f6;">
                <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Name</th>
                <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Category</th>
                <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Registration Code</th>
                <th style="padding: 10px; text-align: center; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">QR Code</th>
                <th style="padding: 10px; text-align: right; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${familyMembersList.map((member, index) => {
                const qrCodeUrl = buildPublicRegistrationViewUrl(member.registrationCode);
                const qrCodeImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrCodeUrl)}`;
                return `
                <tr style="border-bottom: 1px solid #e5e7eb;">
                  <td style="padding: 10px; color: #374151; font-size: 14px;">${member.name}</td>
                  <td style="padding: 10px; color: #374151; font-size: 14px;">${member.category}</td>
                  <td style="padding: 10px; color: #3b82f6; font-size: 14px; font-family: monospace;">${member.registrationCode}</td>
                  <td style="padding: 10px; text-align: center;">
                    <img src="${qrCodeImageUrl}" alt="QR Code for ${member.registrationCode}" style="width: 150px; height: 150px; border: 2px solid #e5e7eb; border-radius: 4px;" />
                  </td>
                  <td style="padding: 10px; text-align: right; color: #374151; font-size: 14px; font-weight: 600;">${member.amount}</td>
                </tr>
              `;
              }).join('')}
              <tr style="background-color: #fef3c7;">
                <td colspan="4" style="padding: 10px; text-align: right; color: #92400e; font-size: 16px; font-weight: 600;">Total:</td>
                <td style="padding: 10px; text-align: right; color: #92400e; font-size: 16px; font-weight: 600;">$${totalAfterFees.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      `;

      const familyEmailContent = `
        <p style="margin: 0 0 14px 0; color: #374151; font-size: 16px; line-height: 1.6;">
          Dear <strong>${formData.firstName} ${formData.lastName}</strong>,
        </p>
        <p style="margin: 0 0 14px 0; color: #374151; font-size: 16px; line-height: 1.6;">
          Thank you for registering your family for <strong>${emailEventNameEsc}</strong>!
        </p>
        ${formatEmailSection('Registration Details', familyEmailItems)}
        ${familyMembersHtml}
        <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 12px 0; border-radius: 4px;">
          <p style="margin: 0; color: #92400e; font-size: 14px; line-height: 1.6;">
            <strong>⏳ Pending Approval:</strong> Your registration is now pending admin approval. You will receive a confirmation email once your registration has been reviewed and approved.
          </p>
        </div>
        ${familyPaymentFollowUpHtml}
        ${familyNonCardPaymentMemoHtml}
      `;
      
      const familyEmailBody = createEmailTemplate(familyEmailContent, 'Family Registration Received');

      if (!blockInitialRegistrationReceivedEmail) {
        // For family registrations, don't send a single QR code since all QR codes are already in the email table
        console.log('📧 Preparing to send family registration email to:', primaryFamilyEmail);
        console.log('📧 Email body length:', familyEmailBody ? familyEmailBody.length : 0);
        try {
          await sendEmail(
            primaryFamilyEmail,
            `${emailEventName} Family Registration Received`,
            familyEmailBody,
            null // No single QR code needed - all QR codes are in the email table
          );
          console.log('✅ Family registration email sent successfully to', primaryFamilyEmail);
        } catch (emailError) {
          console.error('❌ Error sending family registration email:', emailError);
          // Don't fail the registration if email fails, but log it
        }
      } else {
        console.log('📧 Block initial registration received email is ON – skipping family registration email. Admin can use Resend Registration Received after fixing codes.');
      }
      
    } else {
      // Single registration (student, adult, day, or test)
      if (isDayRegistration) {
        if (!dayRegistrationSelectOptions.length) {
          const cMerged = mergeCampSettings(campForNotice);
          const fullDays = getCampDateList(cMerged);
          if (!fullDays.length) {
            alert(
              'Day registration is not available: camp start/end dates are missing, invalid, or span more than 14 days. Please contact the organizer.'
            );
          } else {
            alert(
              'Day registration is not available: no days are enabled for day passes in Camp Settings (Day passes — which days), or every camp day was excluded. Please contact the organizer.'
            );
          }
          setProcessing(false);
          return;
        }
        if (!formData.attendanceDays) {
          alert('Please select which days you will attend.');
          setProcessing(false);
          return;
        }
      }
      const registration = {
        id: 'reg_' + Date.now(),
        name: `${formData.firstName} ${formData.middleName ? formData.middleName + ' ' : ''}${formData.lastName}`,
        ...formData,
        camperCategory: isTestRegistration ? (formData.camperCategory || 'Test') : formData.camperCategory,
        dateOfBirth: formData.dateOfBirth,
        age: formData.age,
        stayPreference: formData.camperCategory === 'Middle School' ? (formData.stayPreference || 'withGroup') : null,
        type: registrationType,
        amount: getTotalAmount(),
        familyCode: null,
        paymentMethod: isGuestSpeaker ? 'waived' : paymentMethod,
        paymentStatus: isGuestSpeaker ? 'waived' : (paymentMethod === 'card' ? 'processing' : 'pending'),
        status: 'pending',
        registeredAt: new Date().toISOString(),
        checkedIn: false,
        qrCode: isDayRegistration
          ? generateDayRegistrationCode(formData.attendanceDays, registrations, dayRegistrationSelectOptions)
          : generateICPFCode(registrations),
        attendanceDays: isDayRegistration ? formData.attendanceDays : undefined,
        dayRegistration: isDayRegistration,
        specialRequests: '' // Initialize empty, only admin can set this
      };
      
      // Validate that the generated registration code is not a duplicate
      if (isRegistrationCodeDuplicate(registration.qrCode, registrations)) {
        alert(`Error: Generated registration code "${registration.qrCode}" is already in use. Please contact support.`);
        setProcessing(false);
        return;
      }

      // Use already-completed payment if card payment was successful (skip for Guest Speaker)
      if (paymentMethod === 'card' && !isGuestSpeaker) {
        const indivTotalDue = resolveMoneyTotal();
        if (paymentSucceeded && transactionId) {
          registration.paymentStatus = 'paid';
          registration.transactionId = transactionId;
          registration.paymentDate = new Date().toISOString();
        } else if (indivTotalDue <= 0) {
          registration.paymentStatus = 'paid';
          registration.paymentMethod = 'waived';
        } else {
          // Payment not completed yet
          alert('Please complete the payment first before submitting your registration.');
          setProcessing(false);
          return;
        }
      }

      const registrationTypeLabel = registrationType === 'test' ? 'Test Registration' : registrationType === 'student' ? 'Student' : registrationType === 'earlyBirdStudent' ? 'Early Bird Student' : registrationType === 'adult' ? 'Adult' : registrationType === 'day' ? 'Day Registration' : registrationType;

      let savedRegistration;
      try {
        savedRegistration = await onRegister(registration);
      } catch (error) {
        console.error('Error creating registration:', error);
        alert(formatApiErrorMessage(error, 'Failed to create registration. Please try again.'));
        setProcessing(false);
        return;
      }
      const emailReg = savedRegistration || registration;
      const noPaymentDue = !isGuestSpeaker && registrationHasNoPaymentDue(emailReg);

      const registrationEmailItems = [
        {
          label: 'Registration Code',
          value: `<strong style="color: #3b82f6; font-family: monospace; font-size: 16px;">${emailReg.qrCode || emailReg.registrationCode || registration.qrCode}</strong>`
        },
        { label: 'Registration Type', value: registrationTypeLabel }
      ];
      if (isDayRegistration && registration.attendanceDays) {
        registrationEmailItems.push({ label: 'Attendance Days', value: registration.attendanceDays });
        registrationEmailItems.push({ label: 'Note', value: 'Food included. No accommodations provided.' });
      }

      if (isGuestSpeaker) {
        registrationEmailItems.push(
          { label: 'Camper Category', value: 'Guest Speaker' },
          { label: 'Amount', value: '$0 (Fee Waived)' },
          { label: 'Payment Status', value: '<span style="color: #10b981; font-weight: 600;">Waived (No payment required)</span>' }
        );
      } else if (noPaymentDue) {
        const amt = Number.isFinite(parseFloat(emailReg.amount)) ? parseFloat(emailReg.amount) : 0;
        registrationEmailItems.push(
          { label: 'Amount', value: `$${amt.toFixed(2)}` },
          {
            label: 'Payment',
            value:
              '<span style="color: #059669; font-weight: 600;">You are all set — no payment is required.</span> Your sponsor waiver or discount covers your registration fee.'
          },
          {
            label: 'Payment status',
            value:
              '<span style="color: #059669; font-weight: 600;">Paid (fee covered by waiver or discount)</span>'
          }
        );
        const sponsor = (emailReg.feeWaiverSponsorName || '').trim();
        if (sponsor) {
          registrationEmailItems.push({
            label: 'Sponsor',
            value: escapeHtmlForEmail(sponsor)
          });
        }
      } else {
        const amt = Number.isFinite(parseFloat(emailReg.amount)) ? parseFloat(emailReg.amount) : parseFloat(registration.amount) || 0;
        registrationEmailItems.push(
          { label: 'Amount', value: `$${amt.toFixed(2)}` },
          {
            label: 'Payment Method',
            value: escapeHtmlForEmail(
              paymentMethodDisplayLabel(emailReg.paymentMethod || paymentMethod, campForNotice)
            )
          }
        );
        if (emailReg.paymentMethod === 'card' && emailReg.paymentStatus === 'paid') {
          registrationEmailItems.push(
            { label: 'Payment Status', value: '<span style="color: #10b981; font-weight: 600;">Paid</span>' },
            ...(emailReg.transactionId ? [{ label: 'Transaction ID', value: emailReg.transactionId }] : [])
          );
        } else if (emailReg.paymentStatus === 'paid') {
          registrationEmailItems.push({
            label: 'Payment Status',
            value: '<span style="color: #10b981; font-weight: 600;">Paid</span>'
          });
        } else if (emailReg.paymentStatus === 'partial') {
          registrationEmailItems.push({
            label: 'Payment Status',
            value: '<span style="color: #f59e0b; font-weight: 600;">Partial payment</span>'
          });
        } else {
          registrationEmailItems.push({
            label: 'Payment Status',
            value: '<span style="color: #ef4444; font-weight: 600;">Pending</span>'
          });
        }
      }

      const paymentFollowUpHtml = isGuestSpeaker
        ? ''
        : noPaymentDue
          ? `<div style="background-color: #ecfdf5; border-left: 4px solid #059669; padding: 15px; margin: 12px 0; border-radius: 4px;">
          <p style="margin: 0; color: #065f46; font-size: 14px; line-height: 1.6;">
            <strong>Payment complete:</strong> We recorded your discount or sponsor waiver. You do not need to send a separate payment for the amount shown above.
          </p>
        </div>`
          : `<div style="background-color: #dbeafe; border-left: 4px solid #3b82f6; padding: 15px; margin: 12px 0; border-radius: 4px;">
          <p style="margin: 0; color: #1e40af; font-size: 14px; line-height: 1.6;">
            <strong>💳 Important:</strong> Your registration will only be confirmed after payment is received. Please complete your payment to secure your spot.
          </p>
        </div>`;

      const singlePm = emailReg.paymentMethod || paymentMethod;
      const singleSt = emailReg.paymentStatus != null ? emailReg.paymentStatus : 'pending';
      const singleMemoCode = emailReg.qrCode || emailReg.registrationCode || registration.qrCode || '';
      const singleNonCardPaymentMemoHtml =
        !isGuestSpeaker &&
        !noPaymentDue &&
        shouldAppendNonCardPaymentMemo(singlePm, singleSt)
          ? buildNonCardPaymentMemoInstructionsHtml(campForNotice, singlePm, singleMemoCode)
          : '';

      const registrationEmailContent = `
        <p style="margin: 0 0 14px 0; color: #374151; font-size: 16px; line-height: 1.6;">
          Dear <strong>${formData.firstName} ${formData.lastName}</strong>,
        </p>
        <p style="margin: 0 0 14px 0; color: #374151; font-size: 16px; line-height: 1.6;">
          Thank you for registering for <strong>${emailEventNameEsc}</strong>!
        </p>
        ${formatEmailSection('Registration Details', registrationEmailItems)}
        <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 12px 0; border-radius: 4px;">
          <p style="margin: 0; color: #92400e; font-size: 14px; line-height: 1.6;">
            <strong>⏳ Pending Approval:</strong> Your registration is now pending admin approval. You will receive a confirmation email once your registration has been reviewed and approved.
          </p>
        </div>
        ${paymentFollowUpHtml}
        ${singleNonCardPaymentMemoHtml}
      `;

      const registrationEmailBody = createEmailTemplate(registrationEmailContent, 'Registration Received');

      if (!blockInitialRegistrationReceivedEmail) {
        const qrCodeText = emailReg.qrCode || emailReg.registrationCode || registration.qrCode;
        const qrCodeUrl = buildPublicRegistrationViewUrl(qrCodeText);
        await sendEmail(
          formData.email,
          `${emailEventName} Registration Received`,
          registrationEmailBody,
          qrCodeUrl
        );
      } else {
        console.log('📧 Block initial registration received email is ON – skipping registration received email. Admin can fix duplicate code then use Resend Registration Received.');
      }
    }

    setProcessing(false);
    setSubmitted(true);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  if (submitted) {
    const handleBack = () => {
      // Reset form state
      setSubmitted(false);
      setConsentAccepted(false);
      setFormData({
        firstName: '',
        lastName: '',
        middleName: '',
        gender: '',
        camperCategory: '',
        email: '',
        confirmEmail: '',
        firstTimeAttending: '',
        dateOfBirth: '',
        age: '',
        addressLine1: '',
        addressLine2: '',
        country: 'United States',
        state: '',
        city: '',
        zip: '',
        countryCode: 'United States +1',
        mobileNumber: '',
        homePhone: '',
        churchName: '',
        pastorName: '',
        allergies: '',
        allergyDescription: '',
        emergencyContactRelationship: '',
        emergencyContactName: '',
        emergencyContactPhone: '',
        parentGuardianName: '',
        parentGuardianEmail: '',
        parentGuardianPhone: '',
        tshirtSize: '',
        type: '',
        specialRequests: '',
        stayPreference: 'withGroup', // For middle-schoolers: withParents or withGroup
        promoCode: ''
      });
      setFamilyMembers([]);
      setPaymentMethod(firstAvailableRegistrationPaymentMethod(cardPaymentEnabled, zelleEnabled, venmoEnabled, paypalEnabled));
      setInstallmentPlan(null);
      setCurrentInstallment(1);
      setCardDetails({ 
        number: '', 
        expiry: '', 
        cvv: '', 
        paymentSucceeded: false,
        transactionId: null,
        paymentIntentId: null
      });
      // Call onBack if provided, otherwise just reset the form
      if (onBack) {
        onBack();
      }
    };

    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Registration Received!</h2>
        <p className="text-gray-600 mb-4">
          Thank you for registering. You will receive a confirmation email once an admin approves your registration.
        </p>
        <p className="text-sm text-gray-500 mb-2">
          Check your email for registration details and QR code.
        </p>
        <p className="text-sm text-amber-600 font-medium mb-6">
          📧 <strong>Tip:</strong> If you don't see the email, please check your spam/junk folder.
        </p>
        <button
          onClick={handleBack}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition"
        >
          Back to Registration
        </button>
      </div>
    );
  }

  if (!registrationType) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-gray-600 mb-4">Please select a registration type first.</p>
        {onBack && (
          <button
            onClick={onBack}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Go Back
          </button>
        )}
      </div>
    );
  }

  const registrationTypeLabels = {
    test: 'Test Registration',
    student: 'Student Registration',
    adult: 'Adult Registration',
    day: 'Day Registration',
    earlyBirdStudent: 'Early Bird Student Registration',
    family2: 'Family of 2 Registration',
    family3: 'Family of 3 Registration',
    family4: 'Family of 4 Registration',
    family5: 'Family of 5 Registration'
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          {registrationTypeLabels[registrationType] || 'Register for ICPF Awake Camp 2026'}
        </h2>
        {onBack && (
          <button
            onClick={onBack}
            className="text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            ← Back
          </button>
        )}
      </div>
      
      {/* Important Notice — copy configurable in Camp Settings */}
      <RegistrationFlowNoticeCallout camp={campForNotice} dayVariant={isDayRegistration} />
      
      <form 
        onSubmit={(e) => {
          e.preventDefault();
          const totalDue = resolveMoneyTotal();
          if (paymentMethod === 'card' && totalDue > 0 && !cardDetails.paymentSucceeded) {
            alert('Please complete the payment first before submitting your registration.');
            return;
          }
          handleSubmit(e);
        }} 
        className="space-y-8"
      >
        {/* Participant Information - Only for single registrations */}
        {!isFamilyRegistration && (
        <div>
          <h3 className="text-lg font-semibold bg-gray-600 text-white px-4 py-2 rounded mb-4">Participant Information</h3>
          {isDayRegistration && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Which days will you attend?
              </label>
              {!dayRegistrationSelectOptions.length ? (
                <p className="text-sm text-amber-800">
                  Camp dates are not configured yet. Please try again later or contact the organizer.
                </p>
              ) : (
                <select
                  required
                  value={formData.attendanceDays}
                  onChange={(e) => setFormData({ ...formData, attendanceDays: e.target.value })}
                  className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select attendance days</option>
                  {dayRegistrationSelectOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs text-gray-600 mt-1">Meals included. No accommodations.</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> First Name
              </label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Last Name
              </label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Middle Name
              </label>
              <input
                type="text"
                value={formData.middleName}
                onChange={(e) => setFormData({ ...formData, middleName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Gender
              </label>
              <select
                required
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Camper Category
              </label>
              <select
                required
                value={formData.camperCategory}
                onChange={(e) => setFormData({ ...formData, camperCategory: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select</option>
                {isTestRegistration && <option value="Test">Test</option>}
                <option value="Middle School">Middle School</option>
                <option value="High School">High School</option>
                <option value="College">College</option>
                {(isAdultRegistration || isTestRegistration || isDayRegistration) && <option value="Adults">Adults</option>}
                {isAdultRegistration && hasPermission(userRole, 'edit_registrations') && <option value="Guest Speaker">Guest Speaker</option>}
              </select>
            </div>
            {isStudentRegistration && formData.camperCategory === 'Middle School' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Stay Preference
                </label>
                <select
                  required
                  value={formData.stayPreference || 'withGroup'}
                  onChange={(e) => setFormData({ ...formData, stayPreference: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="withGroup">Stay with Middle School Group</option>
                  <option value="withParents">Stay with Parents (Family Room)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose whether to stay with other middle-schoolers or with your family (if registering as a family)
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Date of Birth
              </label>
              <input
                type="date"
                required
                value={formData.dateOfBirth}
                onChange={(e) => {
                  const age = calculateAge(e.target.value);
                  setFormData({ ...formData, dateOfBirth: e.target.value, age });
                }}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 11)).toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum age: 11 years</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Age
              </label>
              <input
                type="text"
                readOnly
                value={formData.age || ''}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                placeholder="Auto-calculated"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Email
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Confirm Email
              </label>
              <input
                type="email"
                required
                value={formData.confirmEmail}
                onChange={(e) => setFormData({ ...formData, confirmEmail: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Church Name
              </label>
              <input
                type="text"
                required
                value={formData.churchName}
                onChange={(e) => setFormData({ ...formData, churchName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your church name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Pastor Name
              </label>
              <input
                type="text"
                required
                value={formData.pastorName}
                onChange={(e) => setFormData({ ...formData, pastorName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your pastor's name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Are you first time attending
              </label>
              <select
                value={formData.firstTimeAttending}
                onChange={(e) => setFormData({ ...formData, firstTimeAttending: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address Line 1
              </label>
              <input
                type="text"
                value={formData.addressLine1}
                onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address Line 2
              </label>
              <input
                type="text"
                value={formData.addressLine2}
                onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <select
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State
              </label>
              <select
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select</option>
                {usStates.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zip
              </label>
              <input
                type="text"
                value={formData.zip}
                onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Country Code
              </label>
              <select
                required
                value={formData.countryCode}
                onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="United States +1">United States +1</option>
                <option value="Canada +1">Canada +1</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Mobile number
              </label>
              <input
                type="tel"
                required
                value={formData.mobileNumber}
                onChange={(e) => setFormData({ ...formData, mobileNumber: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Home Phone
              </label>
              <input
                type="tel"
                value={formData.homePhone}
                onChange={(e) => setFormData({ ...formData, homePhone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Allergies
              </label>
              <select
                required
                value={formData.allergies}
                onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select</option>
                <option value="none">None</option>
                <option value="food">Food Allergies</option>
                <option value="medication">Medication Allergies</option>
                <option value="environmental">Environmental Allergies</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Allergy Description
              </label>
              <input
                type="text"
                value={formData.allergyDescription}
                onChange={(e) => setFormData({ ...formData, allergyDescription: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Please describe any allergies in detail"
              />
            </div>

            {!isDayRegistration && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> T-Shirt Size
              </label>
              <select
                required
                value={formData.tshirtSize}
                onChange={(e) => setFormData({ ...formData, tshirtSize: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select</option>
                <option value="Youth small">Youth small</option>
                <option value="Youth Medium">Youth Medium</option>
                <option value="Youth Large">Youth Large</option>
                <option value="Adult small">Adult small</option>
                <option value="Adult Medium">Adult Medium</option>
                <option value="Adult Large">Adult Large</option>
                <option value="Adult XL">Adult XL</option>
                <option value="Adult XXL">Adult XXL</option>
              </select>
            </div>
            )}
          </div>
        </div>
        )}

        {/* Emergency Contact - Only for Adults */}
        {isAdultRegistration && !isFamilyRegistration && (
          <div>
            <h3 className="text-lg font-semibold bg-gray-600 text-white px-4 py-2 rounded mb-4">Emergency Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Relationship
                </label>
                <input
                  type="text"
                  required
                  value={formData.emergencyContactRelationship}
                  onChange={(e) => setFormData({ ...formData, emergencyContactRelationship: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Parent, Spouse, Sibling"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.emergencyContactName}
                  onChange={(e) => setFormData({ ...formData, emergencyContactName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Phone
                </label>
                <input
                  type="tel"
                  required
                  value={formData.emergencyContactPhone}
                  onChange={(e) => setFormData({ ...formData, emergencyContactPhone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        )}

        {/* Parent/Guardian Information - Only for Students */}
        {isStudentRegistration && !isFamilyRegistration && (
          <div className="border-2 border-yellow-400 rounded-lg">
            <h3 className="text-lg font-semibold bg-yellow-100 text-yellow-900 px-4 py-2 rounded-t mb-4">
              Parent/Guardian Information (Required for Students)
            </h3>
            <div className="px-4 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> Parent/Guardian Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.parentGuardianName}
                    onChange={(e) => setFormData({ ...formData, parentGuardianName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> Parent/Guardian Email
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.parentGuardianEmail}
                    onChange={(e) => setFormData({ ...formData, parentGuardianEmail: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> Parent/Guardian Phone
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.parentGuardianPhone}
                    onChange={(e) => setFormData({ ...formData, parentGuardianPhone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Family Members Registration */}
        {isFamilyRegistration && (
          <div>
            {/* Home Address - on top */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold bg-gray-600 text-white px-4 py-2 rounded mb-4">
                Home Address
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> Home Address (Line 1)
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.addressLine1}
                    onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Street address (home, not church)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Home Address (Line 2)
                  </label>
                  <input
                    type="text"
                    value={formData.addressLine2}
                    onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Apt, suite, unit (optional)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> City
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> State
                  </label>
                  <select
                    required
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select</option>
                    {usStates.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> Zip
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <select
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>
            {/* Church Information */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold bg-gray-600 text-white px-4 py-2 rounded mb-4">
                Church Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> Church Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.churchName}
                    onChange={(e) => setFormData({ ...formData, churchName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your church name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> Pastor Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.pastorName}
                    onChange={(e) => setFormData({ ...formData, pastorName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your pastor's name"
                  />
                </div>
              </div>
            </div>

            {/* Family Registration Notice */}
            {isFamilyRegistration && (
              <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-5 mb-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-blue-900 mb-2">Family Registration Guidelines</h4>
                    <div className="text-sm text-blue-800 space-y-2">
                      <p className="font-medium">
                        Please note the following important requirements for family registrations:
                      </p>
                      <ul className="list-disc list-inside space-y-1.5 ml-2">
                        <li><strong>The first family member must be an adult (25 years or older)</strong> and will serve as the primary contact for the family.</li>
                        <li><strong>Elementary and {belowElementaryDisplayLabel(campForNotice)} categories are exclusively available for family registrations only.</strong> These children must be accompanied by a family member and cannot register individually.</li>
                        <li>All family members will be registered together and elementary/below elementary kids will be accommodated with the family.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <h3 className="text-lg font-semibold bg-gray-600 text-white px-4 py-2 rounded mb-4">
              Family Members Information (First member must be an adult, 25+ years)
            </h3>
            <p className="text-sm text-gray-700 mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <strong>Discounts and sponsor waivers:</strong> check &quot;Apply discount or sponsor waiver to this member&quot; only for people who should use a code. Enter the codes on those members only; each code applies to that member&apos;s share of the family fee (not the whole family in one field).
            </p>
            <div className="space-y-6">
              {familyMembers.map((member, index) => (
                <div key={index} className="border-2 border-blue-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold mb-4 text-gray-800">
                    Family Member {index + 1}
                    {index === 0 && <span className="text-red-600 text-sm ml-2">(Must be adult, 25+ years)</span>}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="text-red-500">*</span> First Name
                      </label>
                      <input
                        type="text"
                        required
                        value={member.firstName}
                        onChange={(e) => updateFamilyMember(index, 'firstName', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="text-red-500">*</span> Last Name
                      </label>
                      <input
                        type="text"
                        required
                        value={member.lastName}
                        onChange={(e) => updateFamilyMember(index, 'lastName', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="text-red-500">*</span> Date of Birth
                      </label>
                      <input
                        type="date"
                        required
                        value={member.dateOfBirth}
                        onChange={(e) => updateFamilyMember(index, 'dateOfBirth', e.target.value)}
                        max={(() => {
                          // For first member, must be at least 25 years old
                          if (index === 0) {
                            return new Date(new Date().setFullYear(new Date().getFullYear() - 25)).toISOString().split('T')[0];
                          }
                          // For Elementary or Below Elementary, allow any age (no minimum)
                          if (member.camperCategory === 'Elementary' || member.camperCategory === BELOW_ELEMENTARY_CAMPER_CATEGORY) {
                            return new Date().toISOString().split('T')[0]; // Today's date (no minimum age)
                          }
                          // For all other categories, minimum 11 years old
                          return new Date(new Date().setFullYear(new Date().getFullYear() - 11)).toISOString().split('T')[0];
                        })()}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      {index === 0 && (
                        <p className="text-xs text-red-600 mt-1">Must be at least 25 years old</p>
                      )}
                      {index !== 0 && (member.camperCategory === 'Elementary' || member.camperCategory === BELOW_ELEMENTARY_CAMPER_CATEGORY) && (
                        <p className="text-xs text-gray-500 mt-1">No minimum age requirement</p>
                      )}
                      {index !== 0 && member.camperCategory && member.camperCategory !== 'Elementary' && member.camperCategory !== BELOW_ELEMENTARY_CAMPER_CATEGORY && (
                        <p className="text-xs text-gray-500 mt-1">Minimum age: 11 years</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Age
                      </label>
                      <input
                        type="text"
                        readOnly
                        value={member.age || ''}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                        placeholder="Auto-calculated"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="text-red-500">*</span> Camper Category
                      </label>
                      <select
                        required
                        value={member.camperCategory}
                        onChange={(e) => updateFamilyMember(index, 'camperCategory', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select</option>
                        {index === 0 ? (
                          // First family member must be an adult
                          <option value="Adults">Adults</option>
                        ) : (
                          // Other family members can be any category
                          <>
                            <option value={BELOW_ELEMENTARY_CAMPER_CATEGORY}>{belowElementaryDisplayLabel(campForNotice)}</option>
                        <option value="Elementary">Elementary</option>
                        <option value="Middle School">Middle School</option>
                        <option value="High School">High School</option>
                        <option value="College">College</option>
                        <option value="Adults">Adults</option>
                          </>
                        )}
                      </select>
                    </div>
                    {member.camperCategory === 'Middle School' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <span className="text-red-500">*</span> Stay Preference
                        </label>
                        <select
                          required
                          value={member.stayPreference || 'withGroup'}
                          onChange={(e) => updateFamilyMember(index, 'stayPreference', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="withGroup">Stay with Middle School Group</option>
                          <option value="withParents">Stay with Parents (Family Room)</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Choose whether to stay with other middle-schoolers or with your family
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Time Attending
                      </label>
                      <select
                        value={member.firstTimeAttending}
                        onChange={(e) => updateFamilyMember(index, 'firstTimeAttending', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="text-red-500">*</span> Email
                      </label>
                      <input
                        type="email"
                        required
                        value={member.email}
                        onChange={(e) => updateFamilyMember(index, 'email', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="text-red-500">*</span> Confirm Email
                      </label>
                      <input
                        type="email"
                        required
                        value={member.confirmEmail}
                        onChange={(e) => updateFamilyMember(index, 'confirmEmail', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="text-red-500">*</span> Contact Number
                      </label>
                      <input
                        type="tel"
                        required
                        value={member.mobileNumber}
                        onChange={(e) => updateFamilyMember(index, 'mobileNumber', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="md:col-span-3 border-t border-dashed border-gray-200 pt-3 mt-1">
                      <label className="flex items-start gap-2 text-sm text-gray-800 mb-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={member.applyDiscountWaiverToMember !== false}
                          onChange={(e) => {
                            const on = e.target.checked;
                            const updated = [...familyMembers];
                            updated[index] = {
                              ...updated[index],
                              applyDiscountWaiverToMember: on,
                              ...(on ? {} : { promoCode: '' })
                            };
                            setFamilyMembers(updated);
                          }}
                        />
                        <span>
                          <strong>Apply discount or sponsor waiver to this member</strong>
                          <span className="block text-xs text-gray-600 font-normal mt-0.5">
                            Uncheck if this person should not use a code. Codes below apply only to this member&apos;s share of the fee.
                          </span>
                        </span>
                      </label>
                      <p className="text-sm font-medium text-gray-700 mb-2">Discount / waiver codes (optional)</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Discount or sponsor waiver code (optional)
                          </label>
                          <input
                            type="text"
                            disabled={member.applyDiscountWaiverToMember === false}
                            value={member.promoCode || ''}
                            onChange={(e) => updateFamilyMember(index, 'promoCode', e.target.value.toUpperCase())}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="Enter your discount or sponsor code"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Camp Volunteer?
                      </label>
                      <select
                        value={member.campVolunteer}
                        onChange={(e) => updateFamilyMember(index, 'campVolunteer', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select (Optional)</option>
                        <option value="Student leader">Student leader</option>
                        <option value="Cabin Leader">Cabin Leader</option>
                        <option value="Core Leader">Core Leader</option>
                        <option value="Worship team">Worship team</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="text-red-500">*</span> T-Shirt Size
                      </label>
                      <select
                        required
                        value={member.tshirtSize}
                        onChange={(e) => updateFamilyMember(index, 'tshirtSize', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select</option>
                        <option value="Youth small">Youth small</option>
                        <option value="Youth Medium">Youth Medium</option>
                        <option value="Youth Large">Youth Large</option>
                        <option value="Adult small">Adult small</option>
                        <option value="Adult Medium">Adult Medium</option>
                        <option value="Adult Large">Adult Large</option>
                        <option value="Adult XL">Adult XL</option>
                        <option value="Adult XXL">Adult XXL</option>
                      </select>
                    </div>
                    {(member.camperCategory === 'Adults' || index === 0) ? (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <span className="text-red-500">*</span> Emergency Contact Name
                          </label>
                          <input
                            type="text"
                            required
                            value={member.emergencyContactName}
                            onChange={(e) => updateFamilyMember(index, 'emergencyContactName', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <span className="text-red-500">*</span> Emergency Contact Phone
                          </label>
                          <input
                            type="tel"
                            required
                            value={member.emergencyContactPhone}
                            onChange={(e) => updateFamilyMember(index, 'emergencyContactPhone', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <span className="text-red-500">*</span> Parent/Guardian Name
                          </label>
                          <input
                            type="text"
                            required
                            value={member.parentGuardianName}
                            onChange={(e) => updateFamilyMember(index, 'parentGuardianName', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <span className="text-red-500">*</span> Parent/Guardian Phone
                          </label>
                          <input
                            type="tel"
                            required
                            value={member.parentGuardianPhone}
                            onChange={(e) => updateFamilyMember(index, 'parentGuardianPhone', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Consent Agreement */}
        <div>
          <h3 className="text-lg font-semibold bg-gray-600 text-white px-4 py-2 rounded mb-4">Consent Agreement</h3>
          <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-6 mb-4">
            <p className="text-sm text-gray-700 leading-relaxed mb-4">
              I authorize ICPF to provide medical treatment to my child when needed. I understand that every effort will be made to contact me regarding medical attention given to my child. I further acknowledge that I am responsible for costs associated with any medical and or related services provided to my child. I also understand that I am fully liable for any damage caused intentionally or otherwise by my child. Damage caused by my child will be billed directly to me as the responsible party. My child is willing to cooperate with the overall spirit and schedule of the camp. My child will obey and adhere to the Camp Leaders' directions and security policies. I agree that my child's failure to comply with Camp Leaders' directions and security policy will result in immediate expulsion from the Camp without refund of the Registration Fee.
            </p>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                required
                checked={consentAccepted}
                onChange={(e) => setConsentAccepted(e.target.checked)}
                className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                <span className="text-red-500">*</span> I have read and agree to the terms and conditions stated above
              </span>
            </label>
          </div>
        </div>

        {/* Payment Information - Skip for Guest Speaker */}
        {!isGuestSpeaker && (
        <div>
          <h3 className="text-lg font-semibold bg-gray-600 text-white px-4 py-2 rounded mb-4">Payment Information</h3>
          
          <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
            <p className="text-sm font-medium text-gray-800">Discount / waiver codes</p>
            <p className="text-xs text-gray-600">
              Enter <strong>one</strong> code in the field below: the system automatically detects whether it is a discount or a sponsor waiver. Amount due updates automatically. If your balance is fully covered, no card payment is required.
            </p>
            {!isFamilyRegistration && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Discount or sponsor waiver code (optional)
                </label>
                <input
                  type="text"
                  value={formData.promoCode || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, promoCode: e.target.value.toUpperCase() }))}
                  className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Enter your discount or sponsor code"
                />
              </div>
            )}
            {isFamilyRegistration && (
            <p className="text-xs text-gray-600">
              Use the checkbox on each family member above to choose who may use a code, then enter <strong>one</strong> discount or sponsor waiver code per checked member (the system detects which type it is).
            </p>
            )}
            {feePreviewError ? (
              <p className="text-sm text-red-600">{feePreviewError}</p>
            ) : (
              feePreviewTotal != null && (
                <p className="text-sm text-gray-700">
                  <strong>Amount due after waivers / discounts:</strong>{' '}
                  <span className="text-blue-700 font-semibold">${feePreviewTotal.toFixed(2)}</span>
                  {Math.abs(feePreviewTotal - getTotalAmount()) > 0.005 && (
                    <span className="text-gray-500 ml-2">(listed price before credits: ${getTotalAmount().toFixed(2)})</span>
                  )}
                </p>
              )
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="text-red-500">*</span> Payment Method
            </label>
            <div className="flex flex-wrap gap-4">
              {cardPaymentEnabled && (
              <label className="flex items-center">
                <input
                  type="radio"
                  value="card"
                  checked={paymentMethod === 'card'}
                  onChange={(e) => {
                    setPaymentMethod(e.target.value);
                    // Don't reset installment plan when switching to card
                  }}
                  className="mr-2"
                />
                Credit/Debit Card
              </label>
              )}
              {zelleEnabled && (
              <label className="flex items-center">
                <input
                  type="radio"
                  value="zelle"
                  checked={paymentMethod === 'zelle'}
                  onChange={(e) => {
                    setPaymentMethod(e.target.value);
                    setInstallmentPlan(null); // Reset installment plan for non-card payments
                  }}
                  className="mr-2"
                />
                {paymentMethodDisplayLabel('zelle', campForNotice)}
              </label>
              )}
              {venmoEnabled && (
              <label className="flex items-center">
                <input
                  type="radio"
                  value="venmo"
                  checked={paymentMethod === 'venmo'}
                  onChange={(e) => {
                    setPaymentMethod(e.target.value);
                    setInstallmentPlan(null); // Reset installment plan for non-card payments
                  }}
                  className="mr-2"
                />
                {paymentMethodDisplayLabel('venmo', campForNotice)}
              </label>
              )}
              {paypalEnabled && (
              <label className="flex items-center">
                <input
                  type="radio"
                  value="paypal"
                  checked={paymentMethod === 'paypal'}
                  onChange={(e) => {
                    setPaymentMethod(e.target.value);
                    setInstallmentPlan(null); // Reset installment plan for non-card payments
                  }}
                  className="mr-2"
                />
                {paymentMethodDisplayLabel('paypal', campForNotice)}
              </label>
              )}
            </div>
          </div>

          {paymentMethod === 'card' && !isGuestSpeaker && resolveMoneyTotal() <= 0 && (
            <p className="text-sm text-green-700 mb-4">
              No card payment needed — your discount or waiver covers the full amount. Submit the form to complete registration.
            </p>
          )}

          {/* Installment Plan Selection - Only for Family Registrations with Card Payment */}
          {isFamilyRegistration && paymentMethod === 'card' && resolveMoneyTotal() > 0 && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Payment Plan (Optional)
              </label>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="installmentPlan"
                    value=""
                    checked={installmentPlan === null}
                    onChange={(e) => {
                      setInstallmentPlan(null);
                      setCurrentInstallment(1);
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <div>
                    <span className="font-medium text-gray-800">Pay in Full</span>
                    <span className="text-gray-600 ml-2">${resolveMoneyTotal().toFixed(2)}</span>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="installmentPlan"
                    value="2"
                    checked={installmentPlan === 2}
                    onChange={(e) => {
                      setInstallmentPlan(2);
                      setCurrentInstallment(1);
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <div>
                    <span className="font-medium text-gray-800">2 Installments</span>
                    <span className="text-gray-600 ml-2">
                      ${getInstallmentAmount()} per payment (Total: ${resolveMoneyTotal().toFixed(2)})
                    </span>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="installmentPlan"
                    value="3"
                    checked={installmentPlan === 3}
                    onChange={(e) => {
                      setInstallmentPlan(3);
                      setCurrentInstallment(1);
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <div>
                    <span className="font-medium text-gray-800">3 Installments</span>
                    <span className="text-gray-600 ml-2">
                      ${getInstallmentAmount()} per payment (Total: ${resolveMoneyTotal().toFixed(2)})
                    </span>
                  </div>
                </label>
              </div>
              {installmentPlan && (
                <div className="mt-3 p-3 bg-blue-100 rounded border border-blue-300">
                  <p className="text-sm text-blue-800">
                    <strong>Installment Plan Selected:</strong> You will pay ${getInstallmentAmount()} now, 
                    and the remaining ${(resolveMoneyTotal() - getInstallmentAmount()).toFixed(2)} can be paid later.
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    After this payment, you'll receive instructions on how to make the remaining payment(s).
                  </p>
                </div>
              )}
            </div>
          )}

          {paymentMethod === 'card' && resolveMoneyTotal() > 0 && (
            <div 
              className="space-y-4"
              onClick={(e) => {
                // Prevent any clicks inside from bubbling to form
                e.stopPropagation();
              }}
              onKeyDown={(e) => {
                // Prevent Enter key from submitting form
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              {stripePromise ? (
                <Elements stripe={stripePromise}>
                  <StripePaymentForm
                    amount={getPaymentAmount()}
                    payDisabled={!!feePreviewError && !isGuestSpeaker}
                    onSuccess={(result) => {
                      // Payment succeeded - store result for form submission
                      console.log('✅ Payment succeeded:', result);
                      setCardDetails({
                        ...cardDetails,
                        paymentSucceeded: true,
                        transactionId: result.transactionId,
                        paymentIntentId: result.paymentIntentId,
                      });
                      setProcessing(false);
                      
                      // Auto-submit registration after successful payment
                      // Wait a moment to show success message, then automatically submit
                      setTimeout(() => {
                        // Create a synthetic event object for handleSubmit
                        const syntheticEvent = {
                          preventDefault: () => {},
                          target: { form: null }
                        };
                        
                        // Pass payment details directly to handleSubmit to avoid state timing issues
                        const paymentOverride = {
                          success: true,
                          transactionId: result.transactionId,
                          paymentIntentId: result.paymentIntentId
                        };
                        
                        // Call handleSubmit directly to auto-submit the registration
                        handleSubmit(syntheticEvent, paymentOverride);
                      }, 1500); // 1.5 second delay to show payment success message
                    }}
                    onError={(error) => {
                      console.error('❌ Payment error:', error);
                      setCardDetails({
                        ...cardDetails,
                        paymentSucceeded: false,
                      });
                      setProcessing(false);
                    }}
                    onProcessingChange={(isProcessing) => {
                      setProcessing(isProcessing);
                    }}
                    cardDetails={cardDetails}
                    setCardDetails={setCardDetails}
                  />
                </Elements>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 text-sm font-semibold mb-2">
                    ⚠️ Stripe Payment Not Configured
                  </p>
                  <p className="text-yellow-700 text-sm mb-2">
                    Credit card payments are not available. Please use{' '}
                    {[
                      zelleEnabled && 'Zelle',
                      venmoEnabled && 'Venmo',
                      paypalEnabled && 'PayPal'
                    ]
                      .filter(Boolean)
                      .join(', ') || 'an enabled alternate payment method'}{' '}
                    (if offered above), or configure the Stripe payment gateway.
                  </p>
                  <p className="text-xs text-yellow-600 mt-2">
                    To enable card payments: Set REACT_APP_STRIPE_PUBLISHABLE_KEY in .env file and rebuild.
                  </p>
              </div>
              )}
            </div>
          )}

          {(paymentMethod === 'zelle' || paymentMethod === 'venmo' || paymentMethod === 'paypal') && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="space-y-2">
                <p className="text-sm font-semibold text-yellow-900 mb-2">
                  {paymentMethod === 'zelle'
                    ? 'Zelle Payment Instructions'
                    : paymentMethod === 'venmo'
                    ? 'Venmo Payment Instructions'
                    : 'PayPal Payment Instructions'
                  }
                </p>
              <p className="text-sm text-yellow-800">
                  Please complete your payment to{' '}
                  <strong>
                    {paymentMethod === 'zelle'
                      ? getPaymentZelleRecipient(campForNotice)
                      : paymentMethod === 'venmo'
                      ? getPaymentVenmoRecipient(campForNotice)
                      : getPaymentPaypalRecipient(campForNotice)
                    }
                  </strong>
                </p>
                <div className="bg-yellow-100 border-l-4 border-yellow-400 p-3 mt-3">
                  <p className="text-sm font-medium text-yellow-900 mb-1">
                    Important: memo for Zelle, Venmo, or PayPal
                  </p>
                  <p className="text-sm text-yellow-800">
                    <strong>
                      Include your registration number and the camp name {getCampTitleForPayments(campForNotice)} in the payment memo or notes.
                    </strong>{' '}
                    Your registration number is assigned after you submit this form and is included in your confirmation email. Use that number when you pay so we can match your payment.
                  </p>
                </div>
                <p className="text-sm text-yellow-800 mt-2">
                  Bring proof of payment to the camp office on the first day.
                </p>
              </div>
            </div>
          )}

          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Total Amount:</span>
              <span className="text-2xl font-bold text-blue-600">
                ${resolveMoneyTotal().toFixed(2)}
              </span>
            </div>
              {isFamilyRegistration && paymentMethod === 'card' && installmentPlan && resolveMoneyTotal() > 0 && (
                <>
                  <div className="flex justify-between items-center text-sm text-gray-600 pt-2 border-t border-blue-200">
                    <span>Payment Plan:</span>
                    <span className="font-medium">{installmentPlan} Installments</span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <span>Amount Due Now:</span>
                    <span className="font-semibold text-blue-700">${getPaymentAmount()}</span>
                  </div>
                  {installmentPlan > 1 && (
                    <div className="flex justify-between items-center text-xs text-gray-500">
                      <span>Remaining Balance:</span>
                      <span>${(resolveMoneyTotal() - getPaymentAmount()).toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        )}

        {isGuestSpeaker && (
          <div className="mb-4 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <h3 className="text-lg font-semibold text-green-800">Guest Speaker Registration</h3>
            </div>
            <p className="text-sm text-green-700">
              As a Guest Speaker, your registration fee has been waived. No payment is required.
            </p>
          </div>
        )}

        {paymentMethod === 'card' && cardDetails.paymentSucceeded && !submitted && (
          <div className="bg-green-50 border-2 border-green-400 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <p className="text-lg font-bold text-green-800">✅ Payment Successful!</p>
            </div>
            <p className="text-base text-green-700 font-semibold mb-2">
              Your payment of ${getPaymentAmount().toFixed(2)} has been processed successfully.
              {isFamilyRegistration && installmentPlan && installmentPlan > 1 && (
                <span className="block text-sm mt-1">
                  Remaining balance: ${(resolveMoneyTotal() - getPaymentAmount()).toFixed(2)}
                </span>
              )}
            </p>
            <p className="text-sm text-green-800 bg-green-100 rounded p-2 border border-green-300">
              <strong>Submitting registration...</strong> Your registration will be automatically submitted in a moment.
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={processing || (paymentMethod === 'card' && cardDetails.paymentSucceeded && !submitted)}
          className={`w-full text-white font-semibold py-3 px-6 rounded-lg transition disabled:opacity-50 ${
            paymentMethod === 'card' && cardDetails.paymentSucceeded && !submitted
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {processing ? 'Processing Registration...' : paymentMethod === 'card' && cardDetails.paymentSucceeded && !submitted ? 'Submitting Registration...' : 'Complete Registration'}
        </button>
      </form>
    </div>
  );
}

// Check-In View Component
function CheckInView({ registrations, onCheckIn, onUncheckIn, onResubmitWaiver, onWaiverRequired, onReloadRegistrations, onStartCampsiteForm, userRole }) {
  const [scanMode, setScanMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [scannedCode, setScannedCode] = useState('');
  const [scanError, setScanError] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [waiverNotification, setWaiverNotification] = useState(null); // { registration, qrCode }
  const [filterCheckInStatus, setFilterCheckInStatus] = useState('all');
  const [filterRegistrationType, setFilterRegistrationType] = useState('all');
  const [filterCamperCategory, setFilterCamperCategory] = useState('all');
  const [filterWaiverStatus, setFilterWaiverStatus] = useState('all');
  const qrScannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  const confirmedRegistrations = registrations.filter(r => r.status === 'confirmed');
  const checkedInRegistrations = confirmedRegistrations.filter(r => r.checkedIn);

  const getCat = (r) => (r.data?.camperCategory || r.camperCategory || '').trim();
  const getType = (r) => (r.type || r.data?.type || '').toLowerCase();

  // Check-in counts by camper category and Day
  const checkInCounts = {
    total: checkedInRegistrations.length,
    totalConfirmed: confirmedRegistrations.length,
    adults: checkedInRegistrations.filter(r => {
      const c = getCat(r);
      return c === 'Adults' || c === 'Adult' || (c && c.includes('Adult'));
    }).length,
    middleSchool: checkedInRegistrations.filter(r => getCat(r) === 'Middle School').length,
    highSchool: checkedInRegistrations.filter(r => getCat(r) === 'High School').length,
    college: checkedInRegistrations.filter(r => getCat(r) === 'College').length,
    guestSpeaker: checkedInRegistrations.filter(r => getCat(r) === 'Guest Speaker').length,
    elementary: checkedInRegistrations.filter(r => getCat(r) === 'Elementary').length,
    belowElementary: checkedInRegistrations.filter(r => getCat(r) === BELOW_ELEMENTARY_CAMPER_CATEGORY).length,
    day: checkedInRegistrations.filter(r => getType(r) === 'day').length,
  };

  // Get unique values for filter dropdowns
  const registrationTypes = Array.from(new Set(
    confirmedRegistrations.map(r => (r.type || '').toLowerCase()).filter(Boolean)
  )).sort();
  const camperCategories = Array.from(new Set(
    confirmedRegistrations
      .map(r => r.camperCategory || r.data?.camperCategory)
      .filter(cat => cat && String(cat).trim() !== '')
  )).sort();
  
  const filteredRegistrations = confirmedRegistrations.filter(reg => {
    // Search filter
    const matchesSearch = !searchTerm.trim() || 
      (reg.name && reg.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (reg.email && reg.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      ((reg.qrCode || reg.registrationCode || '').toLowerCase().includes(searchTerm.toLowerCase()));
    if (!matchesSearch) return false;

    // Check-in status filter
    if (filterCheckInStatus === 'checked_in' && !reg.checkedIn) return false;
    if (filterCheckInStatus === 'not_checked_in' && reg.checkedIn) return false;

    // Registration type filter
    if (filterRegistrationType !== 'all') {
      const regType = (reg.type || '').toLowerCase();
      if (regType !== filterRegistrationType) return false;
    }

    // Camper category filter
    if (filterCamperCategory !== 'all') {
      const cat = (reg.camperCategory || reg.data?.camperCategory || '').trim();
      if (cat !== filterCamperCategory) return false;
    }

    // Waiver status filter
    if (filterWaiverStatus !== 'all') {
      if (filterWaiverStatus === 'signed' && !reg.waiverSigned) return false;
      if (filterWaiverStatus === 'not_signed' && reg.waiverSigned) return false;
      if (filterWaiverStatus === 'collected_onsite' && reg.waiver?.status !== 'collected onsite') return false;
    }

    return true;
  });

  // Parse QR code from scanned text (could be URL or just code)
  const parseQRCode = (scannedText) => {
    if (!scannedText) return null;
    
    // If it's a URL with ?code= parameter, extract the code
    const urlMatch = scannedText.match(/[?&]code=([^&]+)/);
    if (urlMatch) {
      return decodeURIComponent(urlMatch[1]);
    }
    
    // If it already looks like a QR code (starts with QR_), return as is
    if (scannedText.startsWith('QR_')) {
      return scannedText;
    }
    
    // If it's an ICPF code (ICPF001, ICPF002, etc.), return as is
    if (scannedText.startsWith('ICPF') && /^ICPF\d+$/.test(scannedText)) {
      return scannedText;
    }
    
    // If it's an ICPFFAM code (ICPFFAM001-1, ICPFFAM001-2, etc.), return as is
    if (scannedText.startsWith('ICPFFAM') && /^ICPFFAM\d+-\d+$/.test(scannedText)) {
      return scannedText;
    }
    
    // If it's a Day registration code (ICPFDAYF001, ICPFDAYTF001, ICPFDAYS001, etc.)
    if (/^ICPFDAY(F|TF|S)\d+$/.test(scannedText)) {
      return scannedText;
    }
    
    // Try to extract QR code from any text
    const qrMatch = scannedText.match(/QR_[A-Z0-9_]+/);
    if (qrMatch) {
      return qrMatch[0];
    }
    
    // Try to extract ICPF code from any text
    const icpfMatch = scannedText.match(/ICPF\d+/);
    if (icpfMatch) {
      return icpfMatch[0];
    }
    
    // Try to extract ICPFFAM code from any text
    const icpfFamMatch = scannedText.match(/ICPFFAM\d+-\d+/);
    if (icpfFamMatch) {
      return icpfFamMatch[0];
    }
    
    // Return the text as-is if no pattern matches (might be a registration code)
    return scannedText;
  };

  const handleQRScan = (scannedText) => {
    setScanError('');
    const qrCode = parseQRCode(scannedText);
    
    if (!qrCode) {
      setScanError('Invalid QR code format. Please scan a valid registration QR code.');
      return;
    }

    // Find registration by qrCode or registrationCode
    const registration = confirmedRegistrations.find(r => 
      r.qrCode === qrCode || 
      r.registrationCode === qrCode ||
      r.qrCode === scannedText ||
      r.registrationCode === scannedText
    );

    if (!registration) {
      setScanError(`Registration not found for QR code: ${qrCode}`);
      return;
    }

    if (registration.checkedIn) {
      setScanError(`${registration.name} is already checked in.`);
      return;
    }

    // Check if waiver is signed
    if (!registration.waiverSigned) {
      // Stop scanning and show waiver notification
      stopScanning();
      setScanMode(false);
      setWaiverNotification({ registration, qrCode });
      return;
    }

    // Waiver is signed, proceed with check-in
      onCheckIn(registration.id);
    setScannedCode(qrCode);
    setScanError('');
    setTimeout(() => {
      setScannedCode('');
    }, 3000);
  };

  const handleWaiverPaper = async () => {
    if (!waiverNotification) return;
    
    // For paper waiver, mark as collected onsite and proceed with check-in
    // The volunteer confirms the paper form was filled onsite
    try {
      const registrationId = waiverNotification.registration.id;
      
      // Mark waiver as collected onsite and check in
      // Set waiverSigned to true and add status indicating it was collected onsite
      await registrationsAPI.update(registrationId, {
        waiverSigned: true,
        waiverSignedAt: new Date().toISOString(),
        waiver: {
          status: 'collected onsite',
          collectedAt: new Date().toISOString(),
          method: 'paper'
        },
        checkedIn: true,
        checkInTime: new Date().toISOString()
      });
      
      // Reload registrations to get updated state
      // This ensures the parent component has the latest waiver and check-in status
      // We don't need to call onCheckIn since we've already updated checkedIn in the API above
      if (onReloadRegistrations) {
        await onReloadRegistrations();
      }
      
      setScannedCode(waiverNotification.qrCode);
      setWaiverNotification(null);
      setScanError('');
      setTimeout(() => {
        setScannedCode('');
      }, 3000);
    } catch (error) {
      console.error('Error processing paper waiver check-in:', error);
      setScanError('Failed to process check-in. Please try again.');
      setWaiverNotification(null);
    }
  };

  const handleWaiverDigital = () => {
    if (!waiverNotification) return;
    
    // Trigger waiver form via callback
    if (onWaiverRequired) {
      onWaiverRequired(waiverNotification.registration.id);
    }
    setWaiverNotification(null);
  };

  // Start QR code scanning
  const startScanning = async () => {
    if (!qrScannerRef.current) return;

    try {
      setScanError('');
      setIsScanning(true);

      const html5QrCode = new Html5Qrcode(qrScannerRef.current.id);
      html5QrCodeRef.current = html5QrCode;

      // Get available cameras
      const devices = await Html5Qrcode.getCameras();
      if (devices && devices.length === 0) {
        throw new Error('No cameras found on this device.');
      }

      // Prefer rear camera (environment facing) over front camera (user facing)
      // Rear cameras typically have "environment" facing mode or labels containing "back", "rear", etc.
      let cameraId = null;
      
      // First, try to find a camera with "environment" facing mode (rear camera)
      const rearCamera = devices.find(device => 
        device.label && (
          device.label.toLowerCase().includes('back') ||
          device.label.toLowerCase().includes('rear') ||
          device.label.toLowerCase().includes('environment') ||
          device.facingMode === 'environment'
        )
      );
      
      if (rearCamera) {
        cameraId = rearCamera.id;
      } else {
        // If no rear camera found by label, try to find by facingMode
        // Some devices may not have labels but have facingMode property
        const environmentCamera = devices.find(device => device.facingMode === 'environment');
        if (environmentCamera) {
          cameraId = environmentCamera.id;
        } else {
          // Fallback: use the last camera (often the rear camera on mobile devices)
          // or the first camera if only one is available
          cameraId = devices.length > 1 ? devices[devices.length - 1].id : devices[0].id;
        }
      }

      await html5QrCode.start(
        cameraId,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          // Successfully scanned a QR code
          handleQRScan(decodedText);
          // Stop scanning after successful scan
          stopScanning();
        },
        (errorMessage) => {
          // Ignore scanning errors (they're frequent while scanning)
          // Only show errors for actual failures
        }
      );
    } catch (error) {
      console.error('Error starting QR scanner:', error);
      setScanError(error.message || 'Failed to start camera. Please check permissions and try again.');
      setIsScanning(false);
    }
  };

  // Stop QR code scanning
  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
      } catch (error) {
        console.error('Error stopping QR scanner:', error);
      }
      html5QrCodeRef.current = null;
    }
    setIsScanning(false);
  };

  // Handle scan mode toggle
  const toggleScanMode = async () => {
    if (scanMode) {
      await stopScanning();
      setScanMode(false);
    } else {
      setScanMode(true);
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        startScanning();
      }, 100);
    }
  };

  // Cleanup on unmount or when scanMode changes
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        stopScanning();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop scanning when scanMode is turned off
  useEffect(() => {
    if (!scanMode && html5QrCodeRef.current) {
      stopScanning();
    }
  }, [scanMode]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Check-In</h2>

        {/* Check-in counters by category */}
        <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
          <p className="text-sm font-semibold text-green-800 mb-3">Checked In Summary</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className="text-xs text-gray-600 font-medium">Total</p>
              <p className="text-lg font-bold text-green-700">{checkInCounts.total} / {checkInCounts.totalConfirmed}</p>
              <p className="text-xs text-gray-500">checked in</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className="text-xs text-gray-600 font-medium">Adults</p>
              <p className="text-lg font-bold text-green-700">{checkInCounts.adults}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className="text-xs text-gray-600 font-medium">Middle School</p>
              <p className="text-lg font-bold text-green-700">{checkInCounts.middleSchool}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className="text-xs text-gray-600 font-medium">High School</p>
              <p className="text-lg font-bold text-green-700">{checkInCounts.highSchool}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className="text-xs text-gray-600 font-medium">College</p>
              <p className="text-lg font-bold text-green-700">{checkInCounts.college}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className="text-xs text-gray-600 font-medium">Guest Speaker</p>
              <p className="text-lg font-bold text-green-700">{checkInCounts.guestSpeaker}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className="text-xs text-gray-600 font-medium">Elementary</p>
              <p className="text-lg font-bold text-green-700">{checkInCounts.elementary}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className="text-xs text-gray-600 font-medium">Below Elementary</p>
              <p className="text-lg font-bold text-green-700">{checkInCounts.belowElementary}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-blue-200 bg-blue-50">
              <p className="text-xs text-gray-600 font-medium">Day Check-In</p>
              <p className="text-lg font-bold text-blue-700">{checkInCounts.day}</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-3">Filters</p>
          <div className="flex flex-wrap gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Check-In Status</label>
              <select
                value={filterCheckInStatus}
                onChange={(e) => setFilterCheckInStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All</option>
                <option value="checked_in">Checked In</option>
                <option value="not_checked_in">Not Checked In</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Registration Type</label>
              <select
                value={filterRegistrationType}
                onChange={(e) => setFilterRegistrationType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All</option>
                {registrationTypes.map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Camper Category</label>
              <select
                value={filterCamperCategory}
                onChange={(e) => setFilterCamperCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All</option>
                {camperCategories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Waiver Status</label>
              <select
                value={filterWaiverStatus}
                onChange={(e) => setFilterWaiverStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All</option>
                <option value="signed">Signed</option>
                <option value="not_signed">Not Signed</option>
                <option value="collected_onsite">Collected Onsite</option>
              </select>
            </div>
            {(filterCheckInStatus !== 'all' || filterRegistrationType !== 'all' || filterCamperCategory !== 'all' || filterWaiverStatus !== 'all') && (
              <button
                onClick={() => {
                  setFilterCheckInStatus('all');
                  setFilterRegistrationType('all');
                  setFilterCamperCategory('all');
                  setFilterWaiverStatus('all');
                }}
                className="self-end px-3 py-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Showing {filteredRegistrations.length} of {confirmedRegistrations.length} registrations
          </p>
        </div>
        
        <div className="flex gap-4 mb-6 flex-wrap">
          <button
            onClick={toggleScanMode}
            disabled={isScanning && !scanMode}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
              scanMode
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Camera className="w-5 h-5" />
            {scanMode ? 'Stop Scanning' : 'Scan QR Code'}
          </button>
          
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search by name, email, or QR code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {scanMode && (
          <div className="mb-6 p-6 bg-gray-100 rounded-lg">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <QrCode className="w-5 h-5" />
                QR Code Scanner
              </h3>
              <p className="text-sm text-gray-600">
                Position the QR code within the camera viewfinder. The scanner will automatically detect and process the code.
              </p>
            </div>
            
            {/* QR Scanner Container */}
            <div className="w-full max-w-md mx-auto">
              <div 
                id="qr-scanner-container"
                ref={qrScannerRef}
                className="w-full bg-black rounded-lg overflow-hidden"
                style={{ minHeight: '300px' }}
              />
            </div>
            
            {/* Manual QR Code Input (fallback) */}
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-2 text-center">Or manually enter QR code:</p>
            <input
              type="text"
                placeholder="Paste or type QR code here..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value) {
                    handleQRScan(e.target.value);
                    e.target.value = '';
                  }
                }}
              onChange={(e) => {
                  if (e.target.value && e.target.value.length > 10) {
                  handleQRScan(e.target.value);
                  e.target.value = '';
                }
              }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {scanError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <span className="text-red-800 text-sm">{scanError}</span>
              </div>
            )}
          </div>
        )}

        {scannedCode && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800">Successfully checked in registration: {scannedCode}</span>
          </div>
        )}

        {/* Participant Form Required (when QR scan finds registration that has not completed form) */}
        {waiverNotification && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-8 h-8 text-yellow-600" />
                <h3 className="text-xl font-bold text-gray-800">Participant Form Required</h3>
              </div>
              <div className="mb-6">
                <p className="text-gray-700 mb-2">
                  <strong>{waiverNotification.registration.name}</strong> needs to complete the participant form before check-in.
                </p>
              </div>
              <div className="space-y-3">
                <button
                  onClick={handleWaiverDigital}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition flex items-center justify-center gap-2"
                >
                  <QrCode className="w-5 h-5" />
                  Fill Participant Form on Device
                </button>
                <button
                  onClick={() => {
                    setWaiverNotification(null);
                    setScanMode(true);
                    setTimeout(() => startScanning(), 100);
                  }}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Waiver</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredRegistrations.map(reg => (
                <tr key={reg.id}>
                  <td className="px-6 py-4 whitespace-nowrap font-medium">{reg.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{reg.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="capitalize">{reg.type}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {reg.checkedIn ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        Checked In
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-500">
                        <Clock className="w-4 h-4" />
                        Not Checked In
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {reg.waiverSigned ? (
                      <div className="flex items-center gap-2">
                        {reg.waiver?.status === 'collected onsite' ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            Collected Onsite
                          </span>
                        ) : (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Signed
                        </span>
                        )}
                        {(reg.waiver?.pdfPath || reg.waiver?.pdfBase64) && (
                          <button
                            onClick={async () => {
                              try {
                                // Use new file-based endpoint if pdfPath exists, otherwise fallback to base64
                                if (reg.waiver?.pdfPath) {
                                  // New file-based storage - fetch from API
                                  const response = await fetch(`${API_BASE_URL}/registrations/${reg.id}/waiver`);
                                  if (!response.ok) {
                                    throw new Error(`Failed to fetch waiver: ${response.statusText}`);
                                  }
                                  const blob = await response.blob();
                                  const url = URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `waiver-${(reg.name || 'unknown').replace(/\s+/g, '-')}-${reg.id}.pdf`;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  URL.revokeObjectURL(url);
                                } else if (reg.waiver?.pdfBase64) {
                                  // Backward compatibility: handle base64 waivers
                                  let base64String = reg.waiver.pdfBase64;
                                  
                                  // Check if it's a string (might be stored as object or other format)
                                  if (typeof base64String !== 'string') {
                                    console.error('PDF base64 is not a string:', typeof base64String, base64String);
                                    throw new Error('Invalid PDF data format');
                                  }
                                  
                                  // Remove data URL prefix if present (e.g., "data:application/pdf;base64,")
                                  if (base64String.includes(',')) {
                                    base64String = base64String.split(',')[1];
                                  }
                                  
                                  // Clean the base64 string: remove whitespace, newlines, etc.
                                  base64String = base64String.trim().replace(/\s/g, '');
                                  
                                  // Validate base64 string
                                  if (!base64String || base64String.length === 0) {
                                    throw new Error('Invalid PDF data: base64 string is empty');
                                  }
                                  
                                  // Validate base64 format (should only contain A-Z, a-z, 0-9, +, /, =)
                                  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
                                  if (!base64Regex.test(base64String)) {
                                    console.error('Invalid base64 format. First 100 chars:', base64String.substring(0, 100));
                                    throw new Error('Invalid base64 format in stored PDF data');
                                  }
                                  
                                  // Decode base64 to binary
                                  let binaryString;
                                  try {
                                    binaryString = atob(base64String);
                                  } catch (atobError) {
                                    console.error('Error decoding base64:', atobError);
                                    throw new Error('Failed to decode PDF data. The PDF may be corrupted or the base64 string is invalid.');
                                  }
                                  
                                  // Convert to Uint8Array
                                  const bytes = new Uint8Array(binaryString.length);
                                  for (let i = 0; i < binaryString.length; i++) {
                                    bytes[i] = binaryString.charCodeAt(i);
                                  }
                                  
                                  // Validate PDF header (%PDF)
                                  if (bytes.length < 4 || 
                                      bytes[0] !== 0x25 || // %
                                      bytes[1] !== 0x50 || // P
                                      bytes[2] !== 0x44 || // D
                                      bytes[3] !== 0x46) { // F
                                    throw new Error('This PDF is corrupted and cannot be opened. The waiver needs to be regenerated - please have the participant sign the waiver again.');
                                  }
                                  
                                  // Create blob and download
                                  const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
                                  const url = URL.createObjectURL(pdfBlob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `waiver-${(reg.name || 'unknown').replace(/\s+/g, '-')}-${reg.id}.pdf`;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  URL.revokeObjectURL(url);
                                }
                              } catch (error) {
                                console.error('Error downloading waiver PDF:', error);
                                alert(`Failed to download waiver PDF: ${error.message || 'Unknown error'}`);
                              }
                            }}
                            className="text-blue-600 hover:text-blue-800"
                            title="Download Signed Waiver"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        Not Signed
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {!reg.checkedIn ? (
                      <button
                        onClick={() => {
                          if (onStartCampsiteForm) {
                            onStartCampsiteForm(reg.id);
                          } else if (onCheckIn) {
                            onCheckIn(reg.id);
                          }
                        }}
                        disabled={(!onCheckIn && !onStartCampsiteForm) || userRole === ROLES.VIEWER}
                        className={`px-4 py-2 rounded-lg text-sm transition ${
                          (!onCheckIn && !onStartCampsiteForm) || userRole === ROLES.VIEWER
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        Check In
                        </button>
                      ) : (
                        <>
                          {onUncheckIn && userRole !== ROLES.VIEWER && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Are you sure you want to uncheck-in ${reg.name}?`)) {
                                  onUncheckIn(reg.id);
                                }
                              }}
                              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm transition"
                              title="Uncheck In"
                            >
                              Uncheck In
                      </button>
                    )}
                        </>
                      )}
                      {onResubmitWaiver && userRole !== ROLES.VIEWER && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Do you want to have ${reg.name} complete the participant form again?`)) {
                              onResubmitWaiver(reg.id);
                            }
                          }}
                          className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm transition"
                          title="Resubmit Waiver"
                        >
                          Resubmit Waiver
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredRegistrations.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No confirmed registrations found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Campsite Participant Form (Trinity Pines) - submit to Zapier as part of check-in
const CAMPSITE_FORM_ZAPIER_URL = 'https://hooks.zapier.com/hooks/catch/23648795/u3usvq1/';
const CAMPSITE_FORM_GROUP = 'Intercollegiate Prayer Fellowship';

function CampsiteParticipantForm({ registration, onComplete, onCancel }) {
  const [formData, setFormData] = useState({
    group: CAMPSITE_FORM_GROUP,
    participant_name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    birthdate: '',
    age: '',
    sex: '',
    home_phone: '',
    church_group: '',
    church_city: '',
    church_state: '',
    guardian_name: '',
    guardian_relation: '',
    guardian_email: '',
    guardian_phone_day: '',
    guardian_phone_evening: '',
    guardian_phone_other: '',
    emergency1_name: '',
    emergency1_phone: '',
    emergency1_relation: '',
    emergency2_name: '',
    emergency2_phone: '',
    emergency2_relation: '',
    medical_conditions: '',
    allergies: '',
    med_exceptions: '',
    waiver: false,
    participant_signature: '',
    signature_date: '',
    guardian_signature: '',
    guardian_signature_date: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const isMinor = formData.age !== '' && parseInt(formData.age, 10) < 18;

  // Pre-fill from registration
  useEffect(() => {
    if (!registration) return;
    const data = registration.data || {};
    const addr = registration.addressLine1 || data.addressLine1 || registration.address || '';
    const dob = registration.dateOfBirth || data.dateOfBirth;
    let birthdate = '';
    if (dob) {
      const d = new Date(dob);
      birthdate = d.toISOString().slice(0, 10);
    }
    const ageVal = registration.age ?? data.age ?? '';
    setFormData(prev => ({
      ...prev,
      participant_name: registration.name || '',
      address: addr,
      city: registration.city || data.city || '',
      state: registration.state || data.state || '',
      zip: registration.zip || data.zip || '',
      birthdate,
      age: ageVal ? String(ageVal) : '',
      sex: (registration.gender || data.gender || '').toLowerCase() === 'male' ? 'Male' : (registration.gender || data.gender || '').toLowerCase() === 'female' ? 'Female' : '',
      home_phone: registration.homePhone || registration.mobileNumber || registration.phone || data.homePhone || data.mobileNumber || '',
      church_group: registration.churchName || data.churchName || '',
      church_city: registration.churchCity || data.churchCity || '',
      church_state: registration.churchState || data.churchState || '',
      guardian_name: registration.parentGuardianName || data.parentGuardianName || '',
      guardian_relation: registration.parentGuardianRelationship || data.parentGuardianRelationship || registration.emergencyContactRelationship || data.emergencyContactRelationship || '',
      guardian_email: registration.parentGuardianEmail || data.parentGuardianEmail || '',
      guardian_phone_day: registration.parentGuardianPhone || data.parentGuardianPhone || '',
      emergency1_name: registration.emergencyContactName || data.emergencyContactName || '',
      emergency1_phone: registration.emergencyContactPhone || data.emergencyContactPhone || '',
      emergency1_relation: registration.emergencyContactRelationship || data.emergencyContactRelationship || '',
      allergies: registration.allergies || registration.allergyDescription || data.allergies || data.allergyDescription || '',
    }));
  }, [registration]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!formData.participant_name.trim()) {
      setSubmitError('Participant name is required.');
      return;
    }
    if (!formData.participant_signature.trim()) {
      setSubmitError('Participant signature (type name) is required.');
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (!formData.signature_date) {
      setFormData(prev => ({ ...prev, signature_date: today }));
    }
    if (isMinor && (!formData.guardian_signature.trim() || !formData.guardian_signature_date)) {
      setSubmitError('Guardian signature and date are required for minors.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('group', formData.group);
      fd.append('participant_name', formData.participant_name);
      fd.append('address', formData.address);
      fd.append('city', formData.city);
      fd.append('state', formData.state);
      fd.append('zip', formData.zip);
      fd.append('birthdate', formData.birthdate);
      fd.append('age', formData.age);
      fd.append('sex', formData.sex);
      fd.append('home_phone', formData.home_phone);
      fd.append('church_group', formData.church_group);
      fd.append('church_city', formData.church_city);
      fd.append('church_state', formData.church_state);
      fd.append('guardian_name', formData.guardian_name);
      fd.append('guardian_relation', formData.guardian_relation);
      fd.append('guardian_email', formData.guardian_email);
      fd.append('guardian_phone_day', formData.guardian_phone_day);
      fd.append('guardian_phone_evening', formData.guardian_phone_evening);
      fd.append('guardian_phone_other', formData.guardian_phone_other);
      fd.append('emergency1_name', formData.emergency1_name);
      fd.append('emergency1_phone', formData.emergency1_phone);
      fd.append('emergency1_relation', formData.emergency1_relation);
      fd.append('emergency2_name', formData.emergency2_name);
      fd.append('emergency2_phone', formData.emergency2_phone);
      fd.append('emergency2_relation', formData.emergency2_relation);
      fd.append('medical_conditions', formData.medical_conditions);
      fd.append('allergies', formData.allergies);
      fd.append('med_exceptions', formData.med_exceptions);
      if (formData.waiver) fd.append('waiver', 'on');
      fd.append('participant_signature', formData.participant_signature);
      fd.append('signature_date', formData.signature_date || new Date().toISOString().slice(0, 10));
      fd.append('guardian_signature', formData.guardian_signature);
      fd.append('guardian_signature_date', formData.guardian_signature_date);

      // Send to Zapier
      console.log('[Campsite Form] Sending POST to Zapier:', CAMPSITE_FORM_ZAPIER_URL);
      const res = await fetch(CAMPSITE_FORM_ZAPIER_URL, {
        method: 'POST',
        body: fd,
      });
      console.log('[Campsite Form] Zapier response:', res.status, res.statusText);
      if (!res.ok) throw new Error(`Submission failed: ${res.status}`);
      const responseText = await res.text();
      if (responseText) console.log('[Campsite Form] Zapier response body:', responseText);

      // Also save a JSON copy to the backend server
      try {
        const archiveRes = await fetch(`${API_BASE_URL}/waiver-submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            registrationId: registration?.id,
            registration: { id: registration?.id, name: registration?.name, email: registration?.email },
            waiverType: 'campsite-participant-form',
            formData,
            submittedAt: new Date().toISOString(),
          })
        });
        if (!archiveRes.ok) {
          const errText = await archiveRes.text();
          console.warn('[Campsite Form] Server archive failed:', archiveRes.status, errText);
        }
      } catch (archiveErr) {
        console.warn('[Campsite Form] Server archive failed (Zapier succeeded):', archiveErr);
      }

      onComplete(registration?.id);
    } catch (err) {
      console.error('Campsite form submit error:', err);
      setSubmitError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Participant Registration &amp; Release Form</h1>
          <button type="button" onClick={onCancel} className="text-gray-600 hover:text-gray-800">Cancel</button>
        </div>
        <p className="text-sm text-gray-600 mb-4">Trinity Pines Conference Center · 4341 FM 356, Trinity, TX 75862 · Required for check-in.</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <section>
            <h2 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Participant</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Participant Full Name *</label>
                <input type="text" name="participant_name" value={formData.participant_name} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mailing Address</label>
                <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                <input type="text" name="state" value={formData.state} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zip</label>
                <input type="text" name="zip" value={formData.zip} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Birth Date *</label>
                <input type="date" name="birthdate" value={formData.birthdate} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age *</label>
                <input type="text" name="age" value={formData.age} onChange={handleChange} placeholder="e.g. 15" className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sex *</label>
                <select name="sex" value={formData.sex} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Home Phone *</label>
                <input type="tel" name="home_phone" value={formData.home_phone} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Name of Church or Group</label>
                <input type="text" name="church_group" value={formData.church_group} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Church City</label>
                <input type="text" name="church_city" value={formData.church_city} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Church State</label>
                <input type="text" name="church_state" value={formData.church_state} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Parent/Guardian (if attendee is a minor)</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" name="guardian_name" value={formData.guardian_name} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relationship to Camper</label>
                <input type="text" name="guardian_relation" value={formData.guardian_relation} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent/Guardian Email</label>
                <input type="email" name="guardian_email" value={formData.guardian_email} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Day) *</label>
                <input type="tel" name="guardian_phone_day" value={formData.guardian_phone_day} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Evening)</label>
                <input type="tel" name="guardian_phone_evening" value={formData.guardian_phone_evening} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Other)</label>
                <input type="tel" name="guardian_phone_other" value={formData.guardian_phone_other} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Emergency Contacts</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact 1 Name *</label>
                <input type="text" name="emergency1_name" value={formData.emergency1_name} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact 1 Phone *</label>
                <input type="tel" name="emergency1_phone" value={formData.emergency1_phone} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact 1 Relation</label>
                <input type="text" name="emergency1_relation" value={formData.emergency1_relation} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact 2 Name</label>
                <input type="text" name="emergency2_name" value={formData.emergency2_name} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact 2 Phone</label>
                <input type="tel" name="emergency2_phone" value={formData.emergency2_phone} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact 2 Relation</label>
                <input type="text" name="emergency2_relation" value={formData.emergency2_relation} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Medical Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Diseases, chronic or recurring illness</label>
                <input type="text" name="medical_conditions" value={formData.medical_conditions} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g. diabetes, asthma" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Allergies (food, medications, insect sting, other)</label>
                <input type="text" name="allergies" value={formData.allergies} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Exceptions to medication permission</label>
                <input type="text" name="med_exceptions" value={formData.med_exceptions} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
            </div>
          </section>

          <section className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 space-y-2">
            <p>I, and my parents or legal guardian (if a minor), am/are fully informed about and aware that during my stay at Trinity Pines Conference Center certain risks and dangers may occur. I authorize the use of my or my child&apos;s photograph or video on Trinity Pines electronic and print media. I am aware of risks associated with infectious diseases and preventative measures. In consideration of Trinity Pines providing and my willingness to engage in these activities, I hold Trinity Pines, its owners, officers, directors, trustees, agents, employees, and/or volunteers harmless from any and all claims. I certify that I/my child are current on required immunizations or are exempt. I authorize first aid/medical personnel to examine, treat, or administer medications as deemed necessary. I accept sole responsibility for payment of any medical care. I have read and agree to the Participant Waiver &amp; Release.</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" name="waiver" checked={formData.waiver} onChange={handleChange} required />
              <span>I have read and agree to the Participant Waiver &amp; Release *</span>
            </label>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Signatures</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Signature of Participant (type name) *</label>
                <input type="text" name="participant_signature" value={formData.participant_signature} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input type="date" name="signature_date" value={formData.signature_date || today} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              {isMinor && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Signature of Parent/Guardian (if minor) *</label>
                    <input type="text" name="guardian_signature" value={formData.guardian_signature} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent/Guardian Date *</label>
                    <input type="date" name="guardian_signature_date" value={formData.guardian_signature_date} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                  </div>
                </>
              )}
            </div>
          </section>

          {submitError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{submitError}</div>}
          <div className="flex gap-3">
            <button type="submit" disabled={submitting} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 px-4 rounded-lg">
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
            <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ICPF USA Awake Camp Waiver Form - Assumption of Risk, Waiver, Release and Indemnification
function ICPFWaiverForm({ registration, onComplete, onCancel }) {
  const [formData, setFormData] = useState({
    participantName: '',
    signatureDate: new Date().toISOString().slice(0, 10),
    participantSignature: '',
    parentName: '',
    parentSignature: '',
    parentSignatureDate: '',
    emergencyContactName: '',
    emergencyPrimaryPhone: '',
    emergencySecondaryPhone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const age = registration?.age ?? registration?.data?.age ?? '';
  const isMinor = age !== '' && parseInt(age, 10) < 18;

  useEffect(() => {
    if (!registration) return;
    const data = registration.data || {};
    setFormData(prev => ({
      ...prev,
      participantName: registration.name || '',
      emergencyContactName: registration.emergencyContactName || data.emergencyContactName || '',
      emergencyPrimaryPhone: registration.emergencyContactPhone || data.emergencyContactPhone || registration.parentGuardianPhone || data.parentGuardianPhone || '',
      emergencySecondaryPhone: registration.homePhone || data.homePhone || '',
      parentName: registration.parentGuardianName || data.parentGuardianName || '',
      parentSignatureDate: isMinor ? new Date().toISOString().slice(0, 10) : '',
    }));
  }, [registration, isMinor]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!formData.participantSignature.trim()) {
      setSubmitError('Participant signature is required.');
      return;
    }
    if (!formData.participantName.trim()) {
      setSubmitError('Participant name is required.');
      return;
    }
    if (!formData.emergencyContactName.trim()) {
      setSubmitError('Emergency contact name is required.');
      return;
    }
    if (!formData.emergencyPrimaryPhone.trim()) {
      setSubmitError('Emergency primary phone number is required.');
      return;
    }
    if (isMinor && !formData.parentSignature.trim()) {
      setSubmitError('Parent/guardian signature is required for minors.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/generate-icpf-waiver-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formData,
          isMinor,
          registrationId: registration.id,
          registrationName: registration.name,
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const result = await response.json();
      const pdfBase64 = result.pdfBase64;

      if (!pdfBase64) throw new Error('No PDF data returned from server');

      const icpfWaiverData = {
        signedAt: new Date().toISOString(),
        pdfBase64,
        formData,
        type: 'icpf-waiver',
      };

      await registrationsAPI.update(registration.id, {
        icpfWaiver: icpfWaiverData,
        icpfWaiverSigned: true,
        icpfWaiverSignedAt: new Date().toISOString(),
      });

      try {
        const archiveRes = await fetch(`${API_BASE_URL}/waiver-submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            registrationId: registration.id,
            registration: { id: registration.id, name: registration.name, email: registration.email },
            waiver: icpfWaiverData,
            waiverType: 'icpf-waiver',
          })
        });
        if (!archiveRes.ok) {
          const errText = await archiveRes.text();
          console.warn('[ICPF Waiver] Archive save failed:', archiveRes.status, errText);
        }
      } catch (archiveErr) {
        console.warn('ICPF waiver archive failed (registration saved):', archiveErr);
      }

      if (onComplete) onComplete(icpfWaiverData);
    } catch (error) {
      console.error('Error submitting ICPF waiver:', error);
      setSubmitError(error.message || 'Failed to submit. Please try again.');
      setSubmitting(false);
    }
  };

  if (!registration || !registration.id) {
    return (
      <div className="min-h-screen bg-gray-100 py-8 px-4 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
          <p className="text-gray-700 mb-4">Registration not found. Please go back and try again.</p>
          <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">ICPF Awake Camp 2026 Waiver</h1>
          <button type="button" onClick={onCancel} className="text-gray-600 hover:text-gray-800">
            <X className="w-6 h-6" />
          </button>
        </div>
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Assumption of Risk, Waiver, Release, and Indemnification Agreement</h2>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 max-h-64 overflow-y-auto text-sm text-gray-700 leading-relaxed space-y-3">
          <p>Each participant must fill out this form. All forms should be turned in to ICPF before or upon arrival.</p>
          <p>I wish to participate in AWAKE 2026-related activities organized by ICPF Ministries from March 19 through March 22, 2026, at Trinity Pines and Conference Center in Trinity, TX. I understand that Trinity Pines and Conference Center has an on-site lake; however, there will be no aquatic activities, including swimming, kayaking, fishing, or any other water-related activities, during this event. Additionally, there will be no outdoor recreational activities, team or individual sports (including basketball or soccer), or other physical activities as part of AWAKE 2026.</p>
          <p>All activities associated with AWAKE 2026 will be limited to indoor sessions, meetings, and organized program events. I acknowledge that I am responsible (or that my legal guardian is responsible, if I am under eighteen) for any personal limitations or restrictions I deem necessary. Although the planned activities are limited to indoor program functions, I understand that participation in any organized event may still carry general risks, and I voluntarily assume responsibility for my participation.</p>
          <p>In consideration of my participation in the AWAKE 2026 program activities, which are limited to indoor sessions, meetings, and organized program events, and to the extent permitted by law, I knowingly and voluntarily assume any and all risks of injury, illness, or death arising from or relating to my participation in these activities. I hereby waive, release, and discharge any and all actions, claims, demands, or causes of action of any kind against ICPF Inc., its affiliates, contractors, vendors, officers, agents, sponsors, volunteers, and representatives (collectively, the "Releasees") arising from or in any way related to my voluntary participation in the program.</p>
          <p>I understand and agree that this Assumption of Risk, Waiver, Release, and Indemnification Agreement means that if I am injured, become ill, or die as a result of my participation in the program activities, neither I nor my family, heirs, or legal representatives may, under any circumstances permitted by law, bring any claim or lawsuit against the Releasees for damages arising from such injury, illness, or death.</p>
          <p>I further understand and agree that I must remain on the Trinity Pines and Conference Center premises at all times during the event, and that leaving the camp property, loitering outside the designated areas, or staying in any unauthorized accommodations is not permitted without the approval of the event organizers and ICPF Inc.</p>
          <p>I agree to indemnify, defend, and hold harmless the Releasees and any of their insurers or subrogees from and against any loss, damage, liability, claim, or expense arising out of or in any way relating to my participation in the program activities.</p>
          <p>I understand and agree that I would not have been permitted to participate in AWAKE 2026 unless I had signed this Assumption of Risk, Waiver, Release, and Indemnification Agreement.</p>
          <p>I give permission for photos and videos of the participant to be taken during ICPF Awake Camp. I understand that these images may be used by ICPF for purposes such as newsletters, presentations, websites, social media, and other communications related to the camp. I understand that no compensation will be provided for the use of these photos or videos.</p>
          <p className="font-semibold">I certify that I have carefully read this Agreement, fully understand its terms, have had the opportunity to ask questions and receive satisfactory answers, and am signing it voluntarily, without pressure or coercion, with full awareness of its legal effect and impact on my rights.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <section>
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Participant Signature</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input type="date" value={formData.signatureDate} onChange={(e) => handleChange('signatureDate', e.target.value)} required className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Signature (Type Full Name) *</label>
                <input type="text" value={formData.participantSignature} onChange={(e) => handleChange('participantSignature', e.target.value)} required placeholder="Type your full name as signature" className="w-full border border-gray-300 rounded-lg px-3 py-2 italic" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Printed Name *</label>
                <input type="text" value={formData.participantName} onChange={(e) => handleChange('participantName', e.target.value)} required className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
            </div>
          </section>

          {isMinor && (
            <section>
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Parent / Legal Guardian (Under 18)</h3>
              <p className="text-sm text-gray-600 mb-3">I am the parent or legal guardian of the child whose name and signature appear above. I have read and understand this Agreement, and consent on behalf of the Participant to its terms.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input type="date" value={formData.parentSignatureDate} onChange={(e) => handleChange('parentSignatureDate', e.target.value)} required className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Signature (Type Full Name) *</label>
                  <input type="text" value={formData.parentSignature} onChange={(e) => handleChange('parentSignature', e.target.value)} required placeholder="Type parent's full name" className="w-full border border-gray-300 rounded-lg px-3 py-2 italic" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Printed Name of Parent *</label>
                  <input type="text" value={formData.parentName} onChange={(e) => handleChange('parentName', e.target.value)} required className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                </div>
              </div>
            </section>
          )}

          <section>
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Emergency Contact</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name of Contact *</label>
                <input type="text" value={formData.emergencyContactName} onChange={(e) => handleChange('emergencyContactName', e.target.value)} required className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Phone *</label>
                <input type="tel" value={formData.emergencyPrimaryPhone} onChange={(e) => handleChange('emergencyPrimaryPhone', e.target.value)} required className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Phone</label>
                <input type="tel" value={formData.emergencySecondaryPhone} onChange={(e) => handleChange('emergencySecondaryPhone', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
            </div>
          </section>

          {submitError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">{submitError}</div>
          )}

          <div className="flex gap-3">
            <button type="submit" disabled={submitting} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 px-6 rounded-lg transition">
              {submitting ? 'Submitting...' : 'Sign & Submit Waiver'}
            </button>
            <button type="button" onClick={onCancel} disabled={submitting} className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Waiver Form Component
function WaiverForm({ registration, onComplete, onCancel }) {
  const CHURCH_DEFAULTS = {
    name: 'ICPF',
    city: 'Houston',
    state: 'Texas'
  };

  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    birthMonth: '',
    birthDay: '',
    birthYear: '',
    age: '',
    sex: '',
    homePhone: '',
    churchName: CHURCH_DEFAULTS.name,
    churchCity: CHURCH_DEFAULTS.city,
    churchState: CHURCH_DEFAULTS.state,
    parentGuardianName: '',
    parentGuardianRelation: '',
    parentGuardianDaytimePhone: '',
    parentGuardianEveningPhone: '',
    parentGuardianOtherPhone: '',
    parentGuardianEmail: '',
    diseases: '',
    allergies: '',
    otcMedicationConsent: true,
    otcExceptions: '',
    emergencyContact1Name: '',
    emergencyContact1Phone: '',
    emergencyContact1Relation: '',
    emergencyContact2Name: '',
    emergencyContact2Phone: '',
    emergencyContact2Relation: '',
    immunizationCurrent: true,
    immunizationExempt: false
  });

  // Separate signature refs and states for participant and parent/guardian
  const participantSignatureRef = useRef(null);
  const parentSignatureRef = useRef(null);
  const [participantSignatureEmpty, setParticipantSignatureEmpty] = useState(true);
  const [parentSignatureEmpty, setParentSignatureEmpty] = useState(true);
  const [isMinor, setIsMinor] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [participantSignatureName, setParticipantSignatureName] = useState('');
  const [parentSignatureName, setParentSignatureName] = useState('');
  const [participantSignatureDate, setParticipantSignatureDate] = useState('');
  const [parentSignatureDate, setParentSignatureDate] = useState('');

  // Pre-fill form data from registration
  useEffect(() => {
    if (registration) {
      // CHURCH_DEFAULTS is a constant, safe to ignore in dependencies
      const nameParts = (registration.name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts[nameParts.length - 1] || '';
      const middleName = nameParts.slice(1, -1).join(' ') || '';

      // Calculate age from date of birth
      let age = '';
      if (registration.dateOfBirth || registration.data?.dateOfBirth) {
        const dob = new Date(registration.dateOfBirth || registration.data.dateOfBirth);
        const today = new Date();
        age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
          age--;
        }
        age = age.toString();
      } else if (registration.age) {
        age = registration.age.toString();
      }

      // Parse date of birth
      let birthMonth = '', birthDay = '', birthYear = '';
      if (registration.dateOfBirth || registration.data?.dateOfBirth) {
        const dob = new Date(registration.dateOfBirth || registration.data.dateOfBirth);
        birthMonth = (dob.getMonth() + 1).toString().padStart(2, '0');
        birthDay = dob.getDate().toString().padStart(2, '0');
        birthYear = dob.getFullYear().toString();
      }

      const calculatedIsMinor = parseInt(age) < 18;
      setIsMinor(calculatedIsMinor);

      setFormData({
        firstName: firstName,
        middleName: middleName,
        lastName: lastName,
        suffix: '',
        street: registration.addressLine1 || registration.address || registration.data?.addressLine1 || '',
        city: registration.city || registration.data?.city || '',
        state: registration.state || registration.data?.state || '',
        zip: registration.zip || registration.data?.zip || '',
        birthMonth: birthMonth,
        birthDay: birthDay,
        birthYear: birthYear,
        age: age,
        sex: registration.gender ? (registration.gender.charAt(0).toUpperCase() === 'M' ? 'M' : 'F') : '',
        homePhone: registration.homePhone || registration.mobileNumber || registration.mobile || registration.data?.homePhone || '',
        churchName: CHURCH_DEFAULTS.name,
        churchCity: CHURCH_DEFAULTS.city,
        churchState: CHURCH_DEFAULTS.state,
        parentGuardianName: registration.parentGuardianName || registration.data?.parentGuardianName || registration.emergencyContactName || '',
        parentGuardianRelation: registration.parentGuardianRelationship || registration.data?.parentGuardianRelationship || registration.emergencyContactRelationship || '',
        parentGuardianDaytimePhone: '',
        parentGuardianEveningPhone: registration.parentGuardianPhone || registration.emergencyContactPhone || '',
        parentGuardianOtherPhone: '',
        parentGuardianEmail: registration.parentGuardianEmail || registration.data?.parentGuardianEmail || '',
        diseases: '',
        allergies: registration.allergies && registration.allergies !== 'none' ? registration.allergies : '',
        otcMedicationConsent: true,
        otcExceptions: '',
        emergencyContact1Name: registration.emergencyContactName || registration.data?.emergencyContactName || '',
        emergencyContact1Phone: registration.emergencyContactPhone || registration.data?.emergencyContactPhone || '',
        emergencyContact1Relation: registration.emergencyContactRelationship || registration.data?.emergencyContactRelationship || '',
        emergencyContact2Name: '',
        emergencyContact2Phone: '',
        emergencyContact2Relation: '',
        immunizationCurrent: true,
        immunizationExempt: false
      });

      // Clear signatures and reset states
      if (participantSignatureRef.current) {
        participantSignatureRef.current.clear();
      }
      if (parentSignatureRef.current) {
        parentSignatureRef.current.clear();
      }
      setParticipantSignatureEmpty(true);
      setParentSignatureEmpty(true);
      setParticipantSignatureName('');
      setParentSignatureName('');
      setParticipantSignatureDate('');
      setParentSignatureDate('');
      
      // Set initial signature date
      const today = new Date().toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: 'numeric' 
      });
      if (!calculatedIsMinor) {
        setParticipantSignatureDate(today);
      } else {
        setParentSignatureDate(today);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registration]); // CHURCH_DEFAULTS is a constant object, safe to omit

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleParticipantSignatureEnd = () => {
    if (participantSignatureRef.current && !participantSignatureRef.current.isEmpty()) {
      setParticipantSignatureEmpty(false);
    }
  };

  const handleParentSignatureEnd = () => {
    if (parentSignatureRef.current && !parentSignatureRef.current.isEmpty()) {
      setParentSignatureEmpty(false);
    }
  };

  const handleClearParticipantSignature = () => {
    if (participantSignatureRef.current) {
      participantSignatureRef.current.clear();
      setParticipantSignatureEmpty(true);
    }
    setParticipantSignatureName('');
  };

  const handleClearParentSignature = () => {
    if (parentSignatureRef.current) {
      parentSignatureRef.current.clear();
      setParentSignatureEmpty(true);
    }
    setParentSignatureName('');
  };

  const handleGenerateParticipantSignatureFromName = () => {
    const name = participantSignatureName.trim();
    if (!name) {
      alert('Please enter a name to generate the signature.');
      return;
    }
    if (!participantSignatureRef.current) {
      alert('Signature canvas not initialized. Please try again.');
      return;
    }
    try {
      const baseCanvas = participantSignatureRef.current.getCanvas
        ? participantSignatureRef.current.getCanvas()
        : participantSignatureRef.current._canvas;
      if (!baseCanvas) {
        throw new Error('Canvas not available');
      }
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = baseCanvas.width;
      tempCanvas.height = baseCanvas.height;
      const ctx = tempCanvas.getContext('2d');
      ctx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
      ctx.fillStyle = '#000';
      ctx.font = "48px 'Brush Script MT', 'Segoe Script', 'Pacifico', cursive";
      ctx.textBaseline = 'middle';
      ctx.fillText(name, 30, tempCanvas.height / 2);
      const generatedSignature = tempCanvas.toDataURL('image/png');
      participantSignatureRef.current.clear();
      participantSignatureRef.current.fromDataURL(generatedSignature);
      setParticipantSignatureEmpty(false);
    } catch (error) {
      console.error('Error generating signature from name:', error);
      alert('Unable to generate signature automatically. Please sign manually.');
    }
  };

  const handleGenerateParentSignatureFromName = () => {
    const name = parentSignatureName.trim();
    if (!name) {
      alert('Please enter a name to generate the signature.');
      return;
    }
    if (!parentSignatureRef.current) {
      alert('Signature canvas not initialized. Please try again.');
      return;
    }
    try {
      const baseCanvas = parentSignatureRef.current.getCanvas
        ? parentSignatureRef.current.getCanvas()
        : parentSignatureRef.current._canvas;
      if (!baseCanvas) {
        throw new Error('Canvas not available');
      }
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = baseCanvas.width;
      tempCanvas.height = baseCanvas.height;
      const ctx = tempCanvas.getContext('2d');
      ctx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
      ctx.fillStyle = '#000';
      ctx.font = "48px 'Brush Script MT', 'Segoe Script', 'Pacifico', cursive";
      ctx.textBaseline = 'middle';
      ctx.fillText(name, 30, tempCanvas.height / 2);
      const generatedSignature = tempCanvas.toDataURL('image/png');
      parentSignatureRef.current.clear();
      parentSignatureRef.current.fromDataURL(generatedSignature);
      setParentSignatureEmpty(false);
    } catch (error) {
      console.error('Error generating signature from name:', error);
      alert('Unable to generate signature automatically. Please sign manually.');
    }
  };

  // PDF generation is now handled server-side via /api/generate-waiver-pdf endpoint

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isMinor) {
      const requiredParentFields = [
        'parentGuardianName',
        'parentGuardianRelation',
        'parentGuardianDaytimePhone',
        'parentGuardianEveningPhone',
        'parentGuardianOtherPhone',
        'parentGuardianEmail'
      ];
      const missingFields = requiredParentFields.filter(
        (field) => !formData[field] || !formData[field].toString().trim()
      );
      if (missingFields.length > 0) {
        alert('All parent/guardian fields are required for minors. Please complete them before submitting.');
        return;
      }
    }

    // Check signature based on age
    const currentSignatureEmpty = isMinor ? parentSignatureEmpty : participantSignatureEmpty;
    const currentSignatureRef = isMinor ? parentSignatureRef : participantSignatureRef;
    const currentSignatureDate = isMinor ? parentSignatureDate : participantSignatureDate;

    if (currentSignatureEmpty) {
      alert(isMinor 
        ? 'Please provide parent/guardian signature before submitting.'
        : 'Please provide your signature before submitting.');
      return;
    }

    if (!currentSignatureDate || !currentSignatureDate.trim()) {
      alert(isMinor
        ? 'Please enter the parent/guardian signature date.'
        : 'Please enter the participant signature date.');
      return;
    }

    if (!currentSignatureRef.current) {
      alert('Signature canvas not initialized. Please refresh the page and try again.');
      return;
    }

    if (!registration || !registration.id) {
      alert('Registration information is missing. Please contact an administrator.');
      return;
    }

    setSubmitting(true);

    try {
      // Get signature data URL
      let signatureDataURL = '';
      try {
        if (currentSignatureRef.current && !currentSignatureRef.current.isEmpty()) {
          signatureDataURL = currentSignatureRef.current.toDataURL();
        }
      } catch (err) {
        console.warn('Could not get signature data URL:', err);
      }

      console.log('Sending request to backend to generate PDF...');
      
      // Call backend API to generate PDF
      // Note: API_BASE_URL already includes '/api', so we use '/generate-waiver-pdf'
      const response = await fetch(`${API_BASE_URL}/generate-waiver-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formData: formData,
          signatureDataURL: signatureDataURL,
          signatureDate: currentSignatureDate,
          isMinor: isMinor
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const result = await response.json();
      const pdfBase64 = result.pdfBase64;
      
      if (!pdfBase64) {
        throw new Error('No PDF data returned from server');
      }

      const sizeInMB = (pdfBase64.length * 3) / 4 / 1024 / 1024;
      console.log('PDF received from server, base64 length:', pdfBase64.length, `(~${sizeInMB.toFixed(2)} MB)`);
      
      if (sizeInMB > 5) {
        console.warn(`Warning: PDF is large (${sizeInMB.toFixed(2)} MB). This may cause issues.`);
      }

      // Store signed waiver in registration
      const waiverData = {
        signedAt: new Date().toISOString(),
        pdfBase64: pdfBase64,
        formData: formData,
        signatureDataURL: signatureDataURL
      };

      console.log('Updating registration with waiver data...');
      console.log('Waiver data size:', {
        pdfBase64: pdfBase64.length,
        formData: JSON.stringify(formData).length,
        signatureDataURL: signatureDataURL.length,
        total: pdfBase64.length + JSON.stringify(formData).length + signatureDataURL.length
      });
      
      // Update registration with waiver
      const updateResult = await registrationsAPI.update(registration.id, {
        waiver: waiverData,
        waiverSigned: true,
        waiverSignedAt: new Date().toISOString()
      });
      console.log('Waiver saved successfully:', updateResult);

      // Also POST to waiver-submit endpoint to ensure archive copy + Zapier forward (if configured)
      try {
        const archiveRes = await fetch(`${API_BASE_URL}/waiver-submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            registrationId: registration.id,
            registration: { id: registration.id, name: registration.name, email: registration.email },
            waiver: waiverData,
            waiverSigned: true,
            waiverSignedAt: new Date().toISOString()
          })
        });
        if (!archiveRes.ok) {
          const errText = await archiveRes.text();
          console.warn('[Waiver] Archive save failed:', archiveRes.status, errText);
        }
      } catch (waiverSubmitErr) {
        console.warn('Waiver archive/Zapier forward failed (registration saved):', waiverSubmitErr);
      }

      // Call completion callback
      if (onComplete) {
        onComplete(waiverData);
      }
    } catch (error) {
      console.error('Error submitting waiver:', error);
      console.error('Error stack:', error.stack);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        registration: registration ? { id: registration.id, name: registration.name } : 'null'
      });
      
      // Show more detailed error message
      let errorMessage = error.message || 'Unknown error occurred';
      
      // If we have full error details, include them
      if (error.fullError) {
        const details = error.fullError.details || error.fullError.error;
        if (details && details !== errorMessage) {
          errorMessage = `${errorMessage}\n\nDetails: ${details}`;
        }
      }
      
      // If it's a 500 error, suggest checking backend logs
      if (error.status === 500) {
        errorMessage += '\n\nThis is a server error. Please check the backend server logs for more details.';
      }
      
      alert(`Failed to submit waiver: ${errorMessage}\n\nPlease check the browser console for more details.`);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Participant Registration & Release Form</h2>
              <p className="text-sm text-gray-600 mt-1">Trinity Pines Conference Center</p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-xs text-gray-500 italic">
            INSTRUCTIONS: Complete a separate form for each person attending. All requested information is applicable. Type or print legibly in dark ink.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
                <input
                  type="text"
                  value={formData.middleName}
                  onChange={(e) => handleInputChange('middleName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Suffix</label>
                <input
                  type="text"
                  value={formData.suffix}
                  onChange={(e) => handleInputChange('suffix', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
                <input
                  type="text"
                  required
                  value={formData.street}
                  onChange={(e) => handleInputChange('street', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <input
                  type="text"
                  required
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                <input
                  type="text"
                  required
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zip *</label>
                <input
                  type="text"
                  required
                  value={formData.zip}
                  onChange={(e) => handleInputChange('zip', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Birth Month *</label>
                  <input
                    type="text"
                    required
                    value={formData.birthMonth}
                    onChange={(e) => handleInputChange('birthMonth', e.target.value.replace(/\D/g, '').slice(0, 2))}
                    placeholder="MM"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Day *</label>
                  <input
                    type="text"
                    required
                    value={formData.birthDay}
                    onChange={(e) => handleInputChange('birthDay', e.target.value.replace(/\D/g, '').slice(0, 2))}
                    placeholder="DD"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
                  <input
                    type="text"
                    required
                    value={formData.birthYear}
                    onChange={(e) => handleInputChange('birthYear', e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="YYYY"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age *</label>
                <input
                  type="text"
                  required
                  value={formData.age}
                  onChange={(e) => handleInputChange('age', e.target.value.replace(/\D/g, ''))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sex *</label>
                <select
                  required
                  value={formData.sex}
                  onChange={(e) => handleInputChange('sex', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Home Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.homePhone}
                  onChange={(e) => handleInputChange('homePhone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Church/Group Name</label>
                <input
                  type="text"
                  value={formData.churchName}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Church City</label>
                <input
                  type="text"
                  value={formData.churchCity}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Church State</label>
                <input
                  type="text"
                  value={formData.churchState}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>
            </div>

            {/* Minor Information */}
            {isMinor && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-md font-semibold text-gray-800 mb-4">Parent/Guardian Information (Required for Minors)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent/Guardian Name *</label>
                    <input
                      type="text"
                      required={isMinor}
                      value={formData.parentGuardianName}
                      onChange={(e) => handleInputChange('parentGuardianName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relation to Participant *</label>
                    <input
                      type="text"
                      required={isMinor}
                      value={formData.parentGuardianRelation}
                      onChange={(e) => handleInputChange('parentGuardianRelation', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Daytime Phone</label>
                    <input
                      type="tel"
                      value={formData.parentGuardianDaytimePhone}
                      onChange={(e) => handleInputChange('parentGuardianDaytimePhone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Evening Phone *</label>
                    <input
                      type="tel"
                      required={isMinor}
                      value={formData.parentGuardianEveningPhone}
                      onChange={(e) => handleInputChange('parentGuardianEveningPhone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Other Phone</label>
                    <input
                      type="tel"
                      value={formData.parentGuardianOtherPhone}
                      onChange={(e) => handleInputChange('parentGuardianOtherPhone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.parentGuardianEmail}
                      onChange={(e) => handleInputChange('parentGuardianEmail', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Medical Information */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Medical Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Diseases, Chronic or Recurring Illness (such as diabetes, asthma, seizures)
                </label>
                <textarea
                  value={formData.diseases}
                  onChange={(e) => handleInputChange('diseases', e.target.value)}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter any chronic illnesses or diseases"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Allergies (food, medications, insect sting, other)
                </label>
                <textarea
                  value={formData.allergies}
                  onChange={(e) => handleInputChange('allergies', e.target.value)}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter any allergies"
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-3">
                  By signing below, I give permission for the Camp Health Supervisor to give the following over-the-counter medication in accordance with standard label directions: acetaminophen, ibuprofen, antihistamine, decongestant, cough medicine, anti-nausea, anti-diarrheal, and antibacterial ointment.
                </p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.otcMedicationConsent}
                    onChange={(e) => handleInputChange('otcMedicationConsent', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">I consent to OTC medication administration</span>
                </label>
                {formData.otcMedicationConsent && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Exceptions (if any):</label>
                    <textarea
                      value={formData.otcExceptions}
                      onChange={(e) => handleInputChange('otcExceptions', e.target.value)}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="List any exceptions to OTC medication consent"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Emergency Contacts */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              If parent cannot be reached in an emergency, please contact:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-medium text-gray-700">Emergency Contact 1</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.emergencyContact1Name}
                    onChange={(e) => handleInputChange('emergencyContact1Name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone #</label>
                  <input
                    type="tel"
                    value={formData.emergencyContact1Phone}
                    onChange={(e) => handleInputChange('emergencyContact1Phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relation to Participant</label>
                  <input
                    type="text"
                    value={formData.emergencyContact1Relation}
                    onChange={(e) => handleInputChange('emergencyContact1Relation', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <h4 className="font-medium text-gray-700">Emergency Contact 2</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.emergencyContact2Name}
                    onChange={(e) => handleInputChange('emergencyContact2Name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone #</label>
                  <input
                    type="tel"
                    value={formData.emergencyContact2Phone}
                    onChange={(e) => handleInputChange('emergencyContact2Phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relation to Participant</label>
                  <input
                    type="text"
                    value={formData.emergencyContact2Relation}
                    onChange={(e) => handleInputChange('emergencyContact2Relation', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Waiver Agreement */}
          <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              AGREEMENT TO ATTEND, PARTICIPATE, ASSUMPTION OF RISK AND LIABILITY WAIVER
            </h3>
            <div className="space-y-4 text-sm text-gray-700 max-h-96 overflow-y-auto">
              <p>
                <strong>1. Assumption of Risk:</strong> I acknowledge that staying at Trinity Pines Conference Center involves inherent risks, dangers, and hazards including, but not limited to: wilderness hazards, natural forces, forces of nature, wild animals, insects, and unpredictable weather conditions. Activities may include volleyball, soccer, softball, basketball, archery, wilderness hiking, swimming, watercrafts, and an adventure course with zip lines. I also authorize the use of photographs or videos for marketing purposes.
              </p>
              <p>
                <strong>2. Infectious Disease Risk:</strong> I acknowledge the risk of exposure to viral and infectious diseases, including COVID-19, due to close contact with others. I understand that preventative measures such as hand washing, social distancing, and face masks may reduce but not eliminate the risk. I willingly choose to participate despite these risks.
              </p>
              <p>
                <strong>3. Release and Hold Harmless:</strong> I hereby hold Trinity Pines Conference Center, its owners, officers, directors, trustees, agents, employees, and volunteers harmless from all claims, liabilities, suits, actions, causes, damages, or losses arising from my stay or participation, including but not limited to emotional, physical, or death. I certify that I/my child am/is current on immunizations or exempt for reasons of conscience.
              </p>
              <p>
                <strong>4. Medical Authorization:</strong> I authorize first aid/medical personnel to examine, treat, or administer medications. In case of emergency and inability to reach a parent, I authorize obtaining medical care (hospitalization, injection, anesthesia, surgery) from licensed providers. I accept sole responsibility for payment of medical care and release Trinity Pines from related claims.
              </p>
              <p>
                <strong>5. Severability:</strong> This release, waiver, and indemnity agreement is broad and inclusive under Texas law. If any portion is invalid, the balance remains valid.
              </p>
              <p>
                <strong>6. Acknowledgment:</strong> I further state that I HAVE CAREFULLY READ THE FOREGOING RELEASE AND KNOW THE CONTENTS THEREOF AND I SIGN THIS RELEASE AS MY OWN FREE ACT. This is a legally binding agreement, which I have read and have understood.
              </p>
            </div>

            <div className="mt-6 space-y-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  required
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  I have read and understand the waiver agreement above *
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.immunizationCurrent}
                  onChange={(e) => handleInputChange('immunizationCurrent', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  I certify that I/my child am/is current on immunizations
                </span>
              </label>
              {!formData.immunizationCurrent && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.immunizationExempt}
                    onChange={(e) => handleInputChange('immunizationExempt', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Exempt for reasons of conscience
                  </span>
                </label>
              )}
            </div>
          </div>

          {/* Signature Section */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Signature</h3>
            <div className="space-y-6">
              {/* Participant Signature (for age 18+) */}
              {!isMinor && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Participant Signature * (Required for participants 18 years and older)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Participants 18 or older must sign below.
                  </p>
                  <div className="border-2 border-gray-300 rounded-lg bg-white" style={{ width: '100%', height: '150px' }}>
                    <SignatureCanvas
                      ref={participantSignatureRef}
                      canvasProps={{
                        width: 800,
                        height: 150,
                        className: 'signature-canvas w-full h-full'
                      }}
                      onEnd={handleParticipantSignatureEnd}
                    />
                  </div>
                  <div className="flex flex-col gap-3 mt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Type Name to Generate Signature (optional)
                      </label>
                      <div className="flex flex-col md:flex-row gap-2">
                        <input
                          type="text"
                          value={participantSignatureName}
                          onChange={(e) => setParticipantSignatureName(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="e.g., John Doe"
                        />
                        <button
                          type="button"
                          onClick={handleGenerateParticipantSignatureFromName}
                          disabled={!participantSignatureName.trim()}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          Generate Signature
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        We will render the typed name in a cursive font directly on the signature line.
                      </p>
                    </div>
                    <div className="flex justify-between items-center">
                      <button
                        type="button"
                        onClick={handleClearParticipantSignature}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Clear Signature
                      </button>
                      {!participantSignatureEmpty && (
                        <span className="text-sm text-green-600">✓ Signature captured</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Participant Signature Date *
                    </label>
                    <input
                      type="text"
                      required
                      value={participantSignatureDate}
                      onChange={(e) => setParticipantSignatureDate(e.target.value)}
                      className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="MM/DD/YYYY"
                    />
                  </div>
                </div>
              )}

              {/* Parent/Guardian Signature (for minors) */}
              {isMinor && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parent / Legal Guardian Signature * (Required for participants under 18)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    A parent or legal guardian must sign on behalf of participants under 18.
                  </p>
                  <div className="border-2 border-gray-300 rounded-lg bg-white" style={{ width: '100%', height: '150px' }}>
                    <SignatureCanvas
                      ref={parentSignatureRef}
                      canvasProps={{
                        width: 800,
                        height: 150,
                        className: 'signature-canvas w-full h-full'
                      }}
                      onEnd={handleParentSignatureEnd}
                    />
                  </div>
                  <div className="flex flex-col gap-3 mt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Type Name to Generate Signature (optional)
                      </label>
                      <div className="flex flex-col md:flex-row gap-2">
                        <input
                          type="text"
                          value={parentSignatureName}
                          onChange={(e) => setParentSignatureName(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="e.g., Jane Doe"
                        />
                        <button
                          type="button"
                          onClick={handleGenerateParentSignatureFromName}
                          disabled={!parentSignatureName.trim()}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          Generate Signature
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        We will render the typed name in a cursive font directly on the signature line.
                      </p>
                    </div>
                    <div className="flex justify-between items-center">
                      <button
                        type="button"
                        onClick={handleClearParentSignature}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Clear Signature
                      </button>
                      {!parentSignatureEmpty && (
                        <span className="text-sm text-green-600">✓ Signature captured</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Parent/Guardian Signature Date *
                    </label>
                    <input
                      type="text"
                      required
                      value={parentSignatureDate}
                      onChange={(e) => setParentSignatureDate(e.target.value)}
                      className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="MM/DD/YYYY"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || (isMinor ? parentSignatureEmpty : participantSignatureEmpty)}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Clock className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  Sign and Submit Waiver
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Lodge Management Component
function LodgeManagement({ lodges, setLodges, registrations, userRole, onAssignRoom, onRemoveRoom, roomAttendanceEnabled, onToggleRoomAttendance, campSettings = DEFAULT_CAMP_SETTINGS }) {
  // Ensure Registration Admin cannot remove assignments even if prop is passed
  const canRemoveRoom = onRemoveRoom && hasPermission(userRole, 'assign_rooms') && userRole !== ROLES.REGISTRATION_ADMIN;
  const [showAddLodge, setShowAddLodge] = useState(false);
  const [editingLodge, setEditingLodge] = useState(null); // Lodge being edited
  const [newLodge, setNewLodge] = useState({ 
    name: '', 
    lodgeMainCategory: '', 
    camperCategory: '', 
    camperCategories: [], // For Multi-Category: array of allowed categories
    gender: '',
    rooms: [] // Array of { roomNumber: string, bedCount: number }
  });
  const [selectedLodge, setSelectedLodge] = useState(null);
  const [selectedRoomForAssignment, setSelectedRoomForAssignment] = useState(null); // { lodgeId, roomNumber }
  
  const canCreateLodge = hasPermission(userRole, 'create_lodges');
  const canAssignRooms = hasPermission(userRole, 'assign_rooms');
  // Delete lodges: Only Room Admin and Application Admin (not Registration Admin)
  const canDeleteLodge = userRole === ROLES.ROOM_ADMIN || userRole === ROLES.APPLICATION_ADMIN;
  // Roster codes: only Room Admin and Application Admin can set the code
  const canSetRosterCode = userRole === ROLES.ROOM_ADMIN || userRole === ROLES.APPLICATION_ADMIN;
  // Registration Admin can only view, not assign
  const canViewOnly = userRole === ROLES.REGISTRATION_ADMIN;

  const handleAddRoom = () => {
    const roomNumber = newLodge.rooms.length + 1;
    setNewLodge({
      ...newLodge,
      rooms: [...newLodge.rooms, { roomNumber: roomNumber.toString(), bedCount: 1 }]
    });
  };

  const handleRemoveRoom = (index) => {
    const updatedRooms = newLodge.rooms.filter((_, i) => i !== index);
    // Renumber rooms
    const renumberedRooms = updatedRooms.map((room, i) => ({
      ...room,
      roomNumber: (i + 1).toString()
    }));
    setNewLodge({ ...newLodge, rooms: renumberedRooms });
  };

  const handleUpdateRoom = (index, field, value) => {
    const updatedRooms = [...newLodge.rooms];
    updatedRooms[index] = { ...updatedRooms[index], [field]: value };
    setNewLodge({ ...newLodge, rooms: updatedRooms });
  };

  const handleAddLodge = async () => {
    // Prevent Registration Admin from adding lodges
    if (userRole === ROLES.REGISTRATION_ADMIN) {
      alert('You do not have permission to add lodges.');
      return;
    }
    try {
      // Validate required fields
      if (!newLodge.name || newLodge.rooms.length === 0 || !newLodge.lodgeMainCategory) {
        alert('Please fill in all required fields (Name, at least one room, Lodge Main Category).');
        return;
      }
      
      // Validate restrictions based on lodge category (gender is optional for Student Lodges)
      if (newLodge.lodgeMainCategory === 'Student Lodge') {
        if (newLodge.camperCategory === 'Multi-Category') {
          if (!newLodge.camperCategories || newLodge.camperCategories.length === 0) {
            alert('Multi-Category lodges require at least one camper category to be selected.');
            return;
          }
        } else if (!newLodge.camperCategory) {
          alert('Student Lodges require Camper Category to be specified.');
          return;
        }
      }
      
      // Validate all rooms have bed counts
      const invalidRooms = newLodge.rooms.filter(room => !room.bedCount || room.bedCount < 1);
      if (invalidRooms.length > 0) {
        alert('All rooms must have at least 1 bed.');
        return;
      }
      
      // Convert rooms structure for storage
      const rooms = newLodge.rooms.map(room => ({
        roomNumber: room.roomNumber,
        bedCount: parseInt(room.bedCount),
        assignedBeds: [] // Array of { registrationId, bedNumber }
      }));
      
    const lodge = {
      id: 'lodge_' + Date.now(),
      name: newLodge.name,
        rooms: rooms,
        totalRooms: rooms.length, // Keep for backward compatibility
        assignedRooms: [], // Keep for backward compatibility
        lodgeMainCategory: newLodge.lodgeMainCategory,
        camperCategory: newLodge.camperCategory === 'Multi-Category' ? null : (newLodge.camperCategory || null),
        camperCategories: (newLodge.camperCategory === 'Multi-Category' && (newLodge.camperCategories || []).length > 0) ? newLodge.camperCategories : null,
        gender: newLodge.gender || null
      };
      await lodgesAPI.create(lodge);
      // Reload lodges from API
      const updatedLodges = await lodgesAPI.getAll();
      setLodges(updatedLodges);
      setNewLodge({ name: '', lodgeMainCategory: '', camperCategory: '', camperCategories: [], gender: '', rooms: [] });
    setShowAddLodge(false);
    } catch (error) {
      console.error('Error creating lodge:', error);
      alert('Failed to create lodge. Please try again.');
    }
  };

  const handleStartEdit = (lodge) => {
    // Prevent Registration Admin from editing
    if (userRole === ROLES.REGISTRATION_ADMIN) {
      alert('You do not have permission to edit lodges.');
      return;
    }
    // Convert lodge to edit format
    const rooms = (lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0)
      ? lodge.rooms.map(room => ({
          roomNumber: room.roomNumber,
          bedCount: room.bedCount || 1,
          assignedBeds: room.assignedBeds || [], // Preserve existing assignments
          rosterCode: room.rosterCode || ''
        }))
      : (lodge.totalRooms 
          ? Array.from({ length: lodge.totalRooms }, (_, i) => ({
              roomNumber: (i + 1).toString(),
              bedCount: 1,
              assignedBeds: (lodge.assignedRooms || [])
                .filter(a => a.roomNumber === (i + 1).toString() || a.roomNumber === (i + 1))
                .map(a => ({ registrationId: a.registrationId, bedNumber: a.bedNumber || 1 }))
            }))
          : []);
    
    setEditingLodge({
      id: lodge.id,
      name: lodge.name || '',
      lodgeMainCategory: lodge.lodgeMainCategory || '',
      camperCategory: (lodge.camperCategories && lodge.camperCategories.length > 0) ? 'Multi-Category' : (lodge.camperCategory || ''),
      camperCategories: lodge.camperCategories || [],
      gender: lodge.gender || '',
      rooms: rooms
    });
    setSelectedLodge(null); // Close any expanded lodge view
  };

  const handleUpdateLodge = async () => {
    // Prevent Registration Admin from updating
    if (userRole === ROLES.REGISTRATION_ADMIN) {
      alert('You do not have permission to update lodges.');
      return;
    }
    try {
      if (!editingLodge) return;
      
      // Validate required fields
      if (!editingLodge.name || editingLodge.rooms.length === 0 || !editingLodge.lodgeMainCategory) {
        alert('Please fill in all required fields (Name, at least one room, Lodge Main Category).');
        return;
      }
      
      // Validate restrictions based on lodge category (gender is optional for Student Lodges)
      if (editingLodge.lodgeMainCategory === 'Student Lodge') {
        if (editingLodge.camperCategory === 'Multi-Category') {
          if (!(editingLodge.camperCategories || []).length) {
            alert('Multi-Category lodges require at least one category to be selected.');
            return;
          }
        } else if (!editingLodge.camperCategory) {
          alert('Student Lodges require Camper Category to be specified.');
          return;
        }
      }
      
      // Validate all rooms have bed counts
      const invalidRooms = editingLodge.rooms.filter(room => !room.bedCount || room.bedCount < 1);
      if (invalidRooms.length > 0) {
        alert('All rooms must have at least 1 bed.');
        return;
      }

      // Preserve existing assignments when updating rooms
      const updatedRooms = editingLodge.rooms.map(room => {
        // Find existing room data to preserve assignments
        const existingLodge = lodges.find(l => l.id === editingLodge.id);
        let preservedAssignments = [];
        
        if (existingLodge) {
          // Check new structure first
          if (existingLodge.rooms && Array.isArray(existingLodge.rooms)) {
            const existingRoom = existingLodge.rooms.find(r => 
              r.roomNumber === room.roomNumber || r.roomNumber === room.roomNumber.toString()
            );
            if (existingRoom && existingRoom.assignedBeds) {
              // Filter out assignments that exceed new bed count
              preservedAssignments = existingRoom.assignedBeds.filter(bed => 
                bed.bedNumber <= parseInt(room.bedCount)
              );
            }
          } else {
            // Check old structure
            const existingAssignments = (existingLodge.assignedRooms || []).filter(a => 
              a.roomNumber === room.roomNumber || a.roomNumber === room.roomNumber.toString()
            );
            preservedAssignments = existingAssignments
              .filter(a => (a.bedNumber || 1) <= parseInt(room.bedCount))
              .map(a => ({ registrationId: a.registrationId, bedNumber: a.bedNumber || 1 }));
          }
        }
        
        const rosterCode = (room.rosterCode && String(room.rosterCode).trim()) || null;
        return {
          roomNumber: room.roomNumber,
          bedCount: parseInt(room.bedCount),
          assignedBeds: preservedAssignments,
          ...(rosterCode ? { rosterCode } : {})
        };
      });

      // Build update object
      const updateData = {
        name: editingLodge.name,
        rooms: updatedRooms,
        totalRooms: updatedRooms.length, // Keep for backward compatibility
        lodgeMainCategory: editingLodge.lodgeMainCategory,
        camperCategory: editingLodge.camperCategory === 'Multi-Category' ? null : (editingLodge.camperCategory || null),
        camperCategories: (editingLodge.camperCategory === 'Multi-Category' && (editingLodge.camperCategories || []).length > 0) ? editingLodge.camperCategories : null,
        gender: editingLodge.gender || null
      };

      // Preserve old assignedRooms structure if it exists (for backward compatibility)
      const existingLodge = lodges.find(l => l.id === editingLodge.id);
      if (existingLodge && existingLodge.assignedRooms && Array.isArray(existingLodge.assignedRooms)) {
        // Convert new room structure back to old assignedRooms format
        const assignedRooms = [];
        updatedRooms.forEach(room => {
          if (room.assignedBeds && room.assignedBeds.length > 0) {
            room.assignedBeds.forEach(bed => {
              assignedRooms.push({
                registrationId: bed.registrationId,
                roomNumber: room.roomNumber,
                bedNumber: bed.bedNumber
              });
            });
          }
        });
        updateData.assignedRooms = assignedRooms;
      }

      await lodgesAPI.update(editingLodge.id, updateData);
      
      // Reload lodges from API
      const updatedLodges = await lodgesAPI.getAll();
      setLodges(updatedLodges);
      setEditingLodge(null);
      alert('Lodge updated successfully!');
    } catch (error) {
      console.error('Error updating lodge:', error);
      alert('Failed to update lodge. Please try again.');
    }
  };

  const handleEditAddRoom = () => {
    if (!editingLodge) return;
    const roomNumber = editingLodge.rooms.length + 1;
    setEditingLodge({
      ...editingLodge,
      rooms: [...editingLodge.rooms, { roomNumber: roomNumber.toString(), bedCount: 1, assignedBeds: [] }]
    });
  };

  const handleEditRemoveRoom = (index) => {
    if (!editingLodge) return;
    const updatedRooms = editingLodge.rooms.filter((_, i) => i !== index);
    // Renumber rooms
    const renumberedRooms = updatedRooms.map((room, i) => ({
      ...room,
      roomNumber: (i + 1).toString()
    }));
    setEditingLodge({ ...editingLodge, rooms: renumberedRooms });
  };

  const handleEditUpdateRoom = (index, field, value) => {
    if (!editingLodge) return;
    const updatedRooms = [...editingLodge.rooms];
    updatedRooms[index] = { ...updatedRooms[index], [field]: value };
    setEditingLodge({ ...editingLodge, rooms: updatedRooms });
  };

  // Helper function to calculate bed statistics for a lodge
  const getLodgeBedStats = (lodge) => {
    let totalBeds = 0;
    let assignedBeds = 0;
    
    // Check new structure first (rooms array with bedCount)
    if (lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0) {
      lodge.rooms.forEach(room => {
        const bedCount = room.bedCount || 1;
        totalBeds += bedCount;
        assignedBeds += (room.assignedBeds?.length || 0);
      });
    } else {
      // Old structure: assume 1 bed per room
      totalBeds = lodge.totalRooms || 0;
      assignedBeds = lodge.assignedRooms?.length || 0;
    }
    
    return {
      totalBeds,
      assignedBeds,
      availableBeds: totalBeds - assignedBeds
    };
  };

  // Helper: registrations eligible for Family Lodge (adults, guest speaker, elementary, below elementary, middle school staying with parents only)
  const isFamilyLodgeEligible = (registration) => {
    const regData = registration.data || {};
    const category = (regData.camperCategory || registration.camperCategory || '').trim();
    const stayPreference = regData.stayPreference || registration.stayPreference || '';
    const isAdult = category === 'Adults' || category === 'Adult' || (category && category.includes('Adult'));
    const isGuestSpeaker = category === 'Guest Speaker';
    const isElementary = category === 'Elementary';
    const isBelowElementary = category === BELOW_ELEMENTARY_CAMPER_CATEGORY;
    const isMiddleWithParents = category === 'Middle School' && stayPreference === 'withParents';
    return isAdult || isGuestSpeaker || isElementary || isBelowElementary || isMiddleWithParents;
  };

  // Helper function to check if a registration matches lodge restrictions
  const matchesLodgeRestrictions = (registration, lodge) => {
    // Only show confirmed registrations for assignment
    if (registration.status !== 'confirmed') {
      return false;
    }
    
    // Family Lodges: only adults, guest speaker, elementary, below elementary, and middle school staying with parents
    if (lodge.lodgeMainCategory === 'Family Lodge') {
      return isFamilyLodgeEligible(registration);
    }
    
    // Student Lodges have restrictions (category and gender are optional)
    if (lodge.lodgeMainCategory === 'Student Lodge') {
      const hasCategoryRestriction = (lodge.camperCategories && lodge.camperCategories.length > 0) || lodge.camperCategory;
      if (!hasCategoryRestriction && !lodge.gender) {
        return true; // No restrictions defined, allow all
      }

      const regData = registration.data || {};
      const regType = registration.type || '';

      // Exclude family-type registrations only when lodge does NOT allow Adults (students-only lodge)
      const lodgeAllowsAdults = (lodge.camperCategories || []).some(c => {
        const cc = (c || '').trim().toLowerCase();
        return cc === 'adult' || cc === 'adults' || cc.includes('adult');
      });
      if (regType && regType.toLowerCase().includes('family') && !lodgeAllowsAdults) {
        return false;
      }

      const regCamperCategory = (regData.camperCategory || registration.camperCategory || '').trim();
      const regGender = (regData.gender || registration.gender || '').trim();

      if (lodge.gender && regGender && regGender.toLowerCase() !== (lodge.gender || '').trim().toLowerCase()) return false;

      if (hasCategoryRestriction) {
        if (lodge.camperCategories && lodge.camperCategories.length > 0) {
          const belowElem = BELOW_ELEMENTARY_CAMPER_CATEGORY.toLowerCase();
          return lodge.camperCategories.some(c => {
            const cc = (c || '').trim().toLowerCase();
            const rcc = regCamperCategory.toLowerCase();
            if (cc === rcc) return true;
            if (cc === 'adult' && (rcc === 'adults' || rcc.includes('adult'))) return true;
            // Elementary in multi-category also allows the Below Elementary camper bucket
            if (cc === 'elementary' && rcc === belowElem) return true;
            if (cc === belowElem && rcc === belowElem) return true;
            return false;
          });
        }
        return regCamperCategory.toLowerCase() === (lodge.camperCategory || '').trim().toLowerCase();
      }
      return true;
    }
    
    // Default: allow if no restrictions
    return true;
  };

  // Filter to show only unassigned registrations that match lodge restrictions
  // This filter is used when a specific lodge is selected
  const getUnassignedRegistrationsForLodge = (lodgeId) => {
    const lodge = lodges.find(l => l.id === lodgeId);
    if (!lodge) return [];
    
    const filtered = registrations.filter(r => {
      // Day registrations have no accommodation — exclude from lodge assignment
      const regType = (r.type || r.data?.type || r.data?.registrationType || '').toString().toLowerCase();
      if (regType === 'day') return false;
      
      // Check direct lodgeId
      if (r.lodgeId) return false;
      
      // Check lodgeId in data
      if (r.data?.lodgeId) return false;
      
      // Check if in any lodge's assignedRooms (old structure)
      for (const l of lodges) {
        if (l.assignedRooms && Array.isArray(l.assignedRooms)) {
          const isAssigned = l.assignedRooms.some(assignment => 
            assignment.registrationId === r.id
          );
          if (isAssigned) return false;
        }
        // Check if in any lodge's rooms.assignedBeds (new structure)
        if (l.rooms && Array.isArray(l.rooms)) {
          for (const room of l.rooms) {
            if (room.assignedBeds && room.assignedBeds.some(bed => bed.registrationId === r.id)) {
              return false;
            }
          }
        }
      }
      
      // Check if registration matches lodge restrictions
      return matchesLodgeRestrictions(r, lodge);
    });
    
    return filtered;
  };
  
  // General unassigned registrations (used when no specific lodge selected)
  // eslint-disable-next-line no-unused-vars
  const unassignedRegistrations = registrations.filter(r => {
    // Day registrations have no accommodation — exclude from lodge assignment
    const regType = (r.type || r.data?.type || r.data?.registrationType || '').toString().toLowerCase();
    if (regType === 'day') return false;
    
    // Check direct lodgeId
    if (r.lodgeId) return false;
    
    // Check lodgeId in data
    if (r.data?.lodgeId) return false;
    
    // Check if in any lodge's assignedRooms (old structure)
    for (const lodge of lodges) {
      if (lodge.assignedRooms && Array.isArray(lodge.assignedRooms)) {
        const isAssigned = lodge.assignedRooms.some(assignment => 
          assignment.registrationId === r.id
        );
        if (isAssigned) return false;
      }
      // Check if in any lodge's rooms.assignedBeds (new structure)
      if (lodge.rooms && Array.isArray(lodge.rooms)) {
        for (const room of lodge.rooms) {
          if (room.assignedBeds && room.assignedBeds.some(bed => bed.registrationId === r.id)) {
            return false;
          }
        }
      }
    }
    
    return true;
  });

  // Count confirmed registrations by category and gender (for Lodge section summary)
  const confirmedForLodging = (registrations || []).filter(r =>
    r && r.status === 'confirmed' && !r.deletedAt
  );
  const getCat = (r) => (r.data?.camperCategory || r.camperCategory || '').trim();
  const getGender = (r) => {
    const g = (r.data?.gender || r.gender || '').trim();
    return g.toLowerCase() === 'female' ? 'Female' : g.toLowerCase() === 'male' ? 'Male' : g || null;
  };
  const lodgeCounts = {
    middleSchoolMale: confirmedForLodging.filter(r => getCat(r) === 'Middle School' && getGender(r) === 'Male').length,
    middleSchoolFemale: confirmedForLodging.filter(r => getCat(r) === 'Middle School' && getGender(r) === 'Female').length,
    highSchoolMale: confirmedForLodging.filter(r => getCat(r) === 'High School' && getGender(r) === 'Male').length,
    highSchoolFemale: confirmedForLodging.filter(r => getCat(r) === 'High School' && getGender(r) === 'Female').length,
    collegeMale: confirmedForLodging.filter(r => getCat(r) === 'College' && getGender(r) === 'Male').length,
    collegeFemale: confirmedForLodging.filter(r => getCat(r) === 'College' && getGender(r) === 'Female').length,
    guestSpeakers: confirmedForLodging.filter(r => getCat(r) === 'Guest Speaker').length,
    adultsMale: confirmedForLodging.filter(r => {
      const cat = getCat(r);
      const isAdult = cat === 'Adults' || cat === 'Adult' || (cat && cat.includes('Adult'));
      return isAdult && cat !== 'Guest Speaker' && getGender(r) === 'Male';
    }).length,
    adultsFemale: confirmedForLodging.filter(r => {
      const cat = getCat(r);
      const isAdult = cat === 'Adults' || cat === 'Adult' || (cat && cat.includes('Adult'));
      return isAdult && cat !== 'Guest Speaker' && getGender(r) === 'Female';
    }).length,
  };

  const canToggleRoomAttendance = userRole === ROLES.ROOM_ADMIN || userRole === ROLES.APPLICATION_ADMIN;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Lodge Management</h2>
          {canCreateLodge && (
          <button
            onClick={() => setShowAddLodge(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
          >
            Add Lodge
          </button>
          )}
        </div>

        {/* Room Attendance card toggle - Room Admin and App Admin only */}
        {canToggleRoomAttendance && onToggleRoomAttendance != null && (
          <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                role="button"
                tabIndex={0}
                onClick={() => onToggleRoomAttendance()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggleRoomAttendance(); }}
                className={`relative w-11 h-6 rounded-full transition-colors ${roomAttendanceEnabled ? 'bg-indigo-600' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${roomAttendanceEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
              </div>
              <span className="text-sm font-medium text-gray-800">Show Room Attendance card on landing page</span>
            </label>
            <p className="text-xs text-gray-600 mt-2">When on, the public Room Attendance card appears on the home page for room leaders to submit nightly attendance.</p>
          </div>
        )}

        {/* Registration counts by category (confirmed only) */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Confirmed Registrations (Lodging)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 text-sm">
            <div>
              <p className="font-medium text-gray-600">Middle School</p>
              <p className="text-gray-800">Male: <span className="font-semibold">{lodgeCounts.middleSchoolMale}</span> · Female: <span className="font-semibold">{lodgeCounts.middleSchoolFemale}</span></p>
            </div>
            <div>
              <p className="font-medium text-gray-600">High School</p>
              <p className="text-gray-800">Male: <span className="font-semibold">{lodgeCounts.highSchoolMale}</span> · Female: <span className="font-semibold">{lodgeCounts.highSchoolFemale}</span></p>
            </div>
            <div>
              <p className="font-medium text-gray-600">College</p>
              <p className="text-gray-800">Male: <span className="font-semibold">{lodgeCounts.collegeMale}</span> · Female: <span className="font-semibold">{lodgeCounts.collegeFemale}</span></p>
            </div>
            <div>
              <p className="font-medium text-gray-600">Guest Speakers</p>
              <p className="text-gray-800"><span className="font-semibold">{lodgeCounts.guestSpeakers}</span></p>
            </div>
            <div>
              <p className="font-medium text-gray-600">Adults (non-Guest Speaker)</p>
              <p className="text-gray-800">Male: <span className="font-semibold">{lodgeCounts.adultsMale}</span> · Female: <span className="font-semibold">{lodgeCounts.adultsFemale}</span></p>
            </div>
          </div>
        </div>

        {showAddLodge && canCreateLodge && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-4">Add New Lodge</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Lodge Name
                </label>
                <input
                  type="text"
                  value={newLodge.name}
                  onChange={(e) => setNewLodge({ ...newLodge, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter lodge name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Lodge Main Category
                </label>
                <select
                  value={newLodge.lodgeMainCategory}
                  onChange={(e) => {
                    const category = e.target.value;
                    setNewLodge({ 
                      ...newLodge, 
                      lodgeMainCategory: category,
                      camperCategory: category === 'Family Lodge' ? '' : newLodge.camperCategory,
                      camperCategories: category === 'Family Lodge' ? [] : newLodge.camperCategories,
                      gender: category === 'Family Lodge' ? '' : newLodge.gender
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select Category</option>
                  <option value="Family Lodge">Family Lodge</option>
                  <option value="Student Lodge">Student Lodge</option>
                </select>
              </div>
              {newLodge.lodgeMainCategory === 'Student Lodge' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> Camper Category
                    </label>
                    <select
                      value={newLodge.camperCategory}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewLodge({ 
                          ...newLodge, 
                          camperCategory: val,
                          camperCategories: val === 'Multi-Category' ? (newLodge.camperCategories || []) : []
                        });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select Category</option>
                      <option value="Multi-Category">Multi-Category (multiple student types)</option>
                      <option value="Elementary">Elementary</option>
                      <option value="Middle School">Middle School</option>
                      <option value="High School">High School</option>
                      <option value="College">College</option>
                      <option value="Adult">Adult</option>
                    </select>
                  </div>
                  {newLodge.camperCategory === 'Multi-Category' && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select allowed categories</label>
                      <div className="flex flex-wrap gap-4">
                        {['Elementary', BELOW_ELEMENTARY_CAMPER_CATEGORY, 'Middle School', 'High School', 'College', 'Adult'].map(cat => (
                          <label key={cat} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(newLodge.camperCategories || []).includes(cat)}
                              onChange={(e) => {
                                const arr = newLodge.camperCategories || [];
                                const next = e.target.checked ? [...arr, cat] : arr.filter(c => c !== cat);
                                setNewLodge({ ...newLodge, camperCategories: next });
                              }}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm">{cat === BELOW_ELEMENTARY_CAMPER_CATEGORY ? belowElementaryDisplayLabel(campSettings) : cat}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gender (optional)
                    </label>
                    <select
                      value={newLodge.gender}
                      onChange={(e) => setNewLodge({ ...newLodge, gender: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select Gender (optional)</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                </>
              )}
            </div>
            {newLodge.lodgeMainCategory === 'Family Lodge' && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Family Lodge:</strong> Only Adults, Guest Speaker, Elementary, {belowElementaryDisplayLabel(campSettings)}, and Middle School students staying with parents. The registrant dropdown for this lodge will show only those campers.
                </p>
              </div>
            )}
            {newLodge.lodgeMainCategory === 'Student Lodge' && (
              (newLodge.camperCategory === 'Multi-Category' 
                ? (newLodge.camperCategories || []).length > 0 
                : newLodge.camperCategory
              ) && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Restrictions:</strong> This lodge will only accept {[newLodge.camperCategory === 'Multi-Category' 
                    ? (newLodge.camperCategories || []).join(', ') 
                    : newLodge.camperCategory, newLodge.gender].filter(Boolean).join(' ')} registrations.
                </p>
              </div>
            ))}
            
            {/* Rooms Management Section */}
            <div className="mt-6 border-t pt-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-semibold text-gray-700">
                  <span className="text-red-500">*</span> Rooms ({newLodge.rooms.length})
                </h4>
                <button
                  type="button"
                  onClick={handleAddRoom}
                  disabled={userRole === ROLES.REGISTRATION_ADMIN}
                  className={`px-3 py-1 rounded text-sm transition flex items-center gap-1 ${
                    userRole === ROLES.REGISTRATION_ADMIN
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  <UserPlus className="w-4 h-4" />
                  Add Room
                </button>
              </div>
              
              {newLodge.rooms.length === 0 ? (
                <div className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded">
                  No rooms added. Click "Add Room" to create rooms with bed counts.
                </div>
              ) : (
                <div className="space-y-2">
                  {newLodge.rooms.map((room, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Room Name/Number</label>
                          <input
                            type="text"
                            value={room.roomNumber}
                            onChange={(e) => handleUpdateRoom(index, 'roomNumber', e.target.value)}
                            disabled={userRole === ROLES.REGISTRATION_ADMIN}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="e.g. 101, Room 1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Bed Count</label>
                <input
                  type="number"
                            min="1"
                            value={room.bedCount}
                            onChange={(e) => handleUpdateRoom(index, 'bedCount', parseInt(e.target.value) || 1)}
                            disabled={userRole === ROLES.REGISTRATION_ADMIN}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="Beds"
                />
              </div>
            </div>
                      {userRole !== ROLES.REGISTRATION_ADMIN && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRoom(index)}
                        className="text-red-600 hover:text-red-800 px-2 py-1 rounded transition"
                        title="Remove room"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleAddLodge}
                disabled={userRole === ROLES.REGISTRATION_ADMIN}
                className={`px-4 py-2 rounded-lg transition ${
                  userRole === ROLES.REGISTRATION_ADMIN
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                Save
              </button>
              <button
                onClick={() => {
                  setShowAddLodge(false);
                  setNewLodge({ name: '', lodgeMainCategory: '', camperCategory: '', camperCategories: [], gender: '', rooms: [] });
                }}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Edit Lodge Form */}
        {editingLodge && canCreateLodge && (
          <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
            <h3 className="font-semibold mb-4 text-blue-800">Edit Lodge: {editingLodge.name}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Lodge Name
                </label>
                <input
                  type="text"
                  value={editingLodge.name}
                  onChange={(e) => setEditingLodge({ ...editingLodge, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter lodge name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Lodge Main Category
                </label>
                <select
                  value={editingLodge.lodgeMainCategory}
                  onChange={(e) => {
                    const category = e.target.value;
                    setEditingLodge({ 
                      ...editingLodge, 
                      lodgeMainCategory: category,
                      // Clear restrictions if switching to Family Lodge
                      camperCategory: category === 'Family Lodge' ? '' : editingLodge.camperCategory,
                      gender: category === 'Family Lodge' ? '' : editingLodge.gender
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select Category</option>
                  <option value="Family Lodge">Family Lodge</option>
                  <option value="Student Lodge">Student Lodge</option>
                </select>
              </div>
              {editingLodge.lodgeMainCategory === 'Student Lodge' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> Camper Category
                    </label>
                    <select
                      value={editingLodge.camperCategory}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditingLodge({ 
                          ...editingLodge, 
                          camperCategory: val,
                          camperCategories: val === 'Multi-Category' ? (editingLodge.camperCategories || []) : []
                        });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select Category</option>
                      <option value="Multi-Category">Multi-Category (multiple student types)</option>
                      <option value="Elementary">Elementary</option>
                      <option value="Middle School">Middle School</option>
                      <option value="High School">High School</option>
                      <option value="College">College</option>
                      <option value="Adult">Adult</option>
                    </select>
                  </div>
                  {editingLodge.camperCategory === 'Multi-Category' && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select allowed categories</label>
                      <div className="flex flex-wrap gap-4">
                        {['Elementary', BELOW_ELEMENTARY_CAMPER_CATEGORY, 'Middle School', 'High School', 'College', 'Adult'].map(cat => (
                          <label key={cat} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(editingLodge.camperCategories || []).includes(cat)}
                              onChange={(e) => {
                                const arr = editingLodge.camperCategories || [];
                                const next = e.target.checked ? [...arr, cat] : arr.filter(c => c !== cat);
                                setEditingLodge({ ...editingLodge, camperCategories: next });
                              }}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm">{cat === BELOW_ELEMENTARY_CAMPER_CATEGORY ? belowElementaryDisplayLabel(campSettings) : cat}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gender (optional)
                    </label>
                    <select
                      value={editingLodge.gender}
                      onChange={(e) => setEditingLodge({ ...editingLodge, gender: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select Gender (optional)</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                </>
              )}
            </div>
            {editingLodge.lodgeMainCategory === 'Family Lodge' && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Family Lodge:</strong> Only Adults, Guest Speaker, Elementary, {belowElementaryDisplayLabel(campSettings)}, and Middle School students staying with parents. The registrant dropdown for this lodge will show only those campers.
                </p>
              </div>
            )}
            {editingLodge.lodgeMainCategory === 'Student Lodge' && (
              (editingLodge.camperCategory === 'Multi-Category' 
                ? (editingLodge.camperCategories || []).length > 0 
                : editingLodge.camperCategory
              ) && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Restrictions:</strong> This lodge will only accept {[editingLodge.camperCategory === 'Multi-Category' 
                    ? (editingLodge.camperCategories || []).join(', ') 
                    : editingLodge.camperCategory, editingLodge.gender].filter(Boolean).join(' ')} registrations.
                </p>
              </div>
            ))}
            
            {/* Rooms Management Section */}
            <div className="mt-6 border-t pt-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-semibold text-gray-700">
                  <span className="text-red-500">*</span> Rooms ({editingLodge.rooms.length})
                </h4>
                <button
                  type="button"
                  onClick={handleEditAddRoom}
                  disabled={userRole === ROLES.REGISTRATION_ADMIN}
                  className={`px-3 py-1 rounded text-sm transition flex items-center gap-1 ${
                    userRole === ROLES.REGISTRATION_ADMIN
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  <UserPlus className="w-4 h-4" />
                  Add Room
                </button>
              </div>
              
              {editingLodge.rooms.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No rooms added yet. Click "Add Room" to add rooms.</p>
              ) : (
                <div className="space-y-2">
                  {editingLodge.rooms.map((room, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Room Name/Number</label>
                          <input
                            type="text"
                            value={room.roomNumber}
                            onChange={(e) => handleEditUpdateRoom(index, 'roomNumber', e.target.value)}
                            disabled={userRole === ROLES.REGISTRATION_ADMIN}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="e.g. 101, Room 1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Beds</label>
                          <input
                            type="number"
                            min="1"
                            value={room.bedCount}
                            onChange={(e) => handleEditUpdateRoom(index, 'bedCount', parseInt(e.target.value) || 1)}
                            disabled={userRole === ROLES.REGISTRATION_ADMIN}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>
                      <div className="mt-2 col-span-2">
                        <label className="block text-xs text-gray-600 mb-1">Attendance code</label>
                        <input
                          type="text"
                          value={room.rosterCode || ''}
                          onChange={(e) => handleEditUpdateRoom(index, 'rosterCode', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12))}
                          disabled={!canSetRosterCode}
                          className="w-full max-w-xs px-2 py-1 text-sm border border-gray-300 rounded font-mono disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="e.g. ROOM101"
                        />
                        {canSetRosterCode && (
                          <p className="text-xs text-gray-500 mt-1">Set by admin. Room leaders use this code on the Room Attendance page.</p>
                        )}
                      </div>
                      {room.assignedBeds && room.assignedBeds.length > 0 && (
                        <span className="text-xs text-orange-600 ml-2">
                          ({room.assignedBeds.length} assigned)
                        </span>
                      )}
                      {userRole !== ROLES.REGISTRATION_ADMIN && (
                      <button
                        type="button"
                        onClick={() => handleEditRemoveRoom(index)}
                        className="ml-auto text-red-600 hover:text-red-800 text-sm"
                        disabled={room.assignedBeds && room.assignedBeds.length > 0}
                        title={room.assignedBeds && room.assignedBeds.length > 0 ? "Cannot remove room with assignments" : "Remove room"}
                      >
                        <X className="w-4 h-4" />
                      </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleUpdateLodge}
                disabled={userRole === ROLES.REGISTRATION_ADMIN}
                className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${
                  userRole === ROLES.REGISTRATION_ADMIN
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                <Save className="w-4 h-4" />
                Save Changes
              </button>
              <button
                onClick={() => {
                  setEditingLodge(null);
                }}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {lodges.map(lodge => {
            const bedStats = getLodgeBedStats(lodge);
            return (
            <div key={lodge.id} className="border rounded-lg p-4">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{lodge.name}</h3>
                  <div className="mt-1 space-y-1">
                  <p className="text-sm text-gray-600">
                      Rooms: {lodge.rooms?.length || lodge.totalRooms || 0} total
                    </p>
                    <p className="text-sm font-medium text-gray-700">
                      Beds: <span className="text-green-600">{bedStats.assignedBeds}</span> / <span className="text-gray-900">{bedStats.totalBeds}</span> assigned
                      {bedStats.availableBeds > 0 && (
                        <span className="text-blue-600 ml-2">({bedStats.availableBeds} available)</span>
                      )}
                    </p>
                  </div>
                  {lodge.lodgeMainCategory && (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs font-medium text-gray-700">
                        Category: <span className="text-blue-600">{lodge.lodgeMainCategory}</span>
                      </p>
                      {lodge.lodgeMainCategory === 'Student Lodge' && ((lodge.camperCategories && lodge.camperCategories.length > 0) || lodge.camperCategory || lodge.gender) && (
                        <p className="text-xs text-gray-600">
                          Restrictions: {[(lodge.camperCategories && lodge.camperCategories.length > 0) ? lodge.camperCategories.join(', ') : lodge.camperCategory, lodge.gender].filter(Boolean).join(' ')}
                        </p>
                      )}
                </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* View/Manage button - visible to Room Admin and Application Admin for managing, visible to Registration Admin for viewing only */}
                  {(canAssignRooms || userRole === ROLES.REGISTRATION_ADMIN) && (
                <button
                  onClick={() => setSelectedLodge(selectedLodge === lodge.id ? null : lodge.id)}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                      {selectedLodge === lodge.id ? 'Hide' : (canAssignRooms ? 'Manage' : 'View')}
                </button>
                  )}
                  {canCreateLodge && userRole !== ROLES.REGISTRATION_ADMIN && (
                    <button
                      onClick={() => handleStartEdit(lodge)}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                      title="Edit lodge"
                    >
                      <Pencil className="w-4 h-4" />
                      Edit
                </button>
                  )}
                  {canDeleteLodge && (
                    <button
                      onClick={async () => {
                        // Check if lodge has any assignments
                        let hasAssignments = false;
                        let assignmentCount = 0;
                        
                        // Check old structure
                        if (lodge.assignedRooms && Array.isArray(lodge.assignedRooms) && lodge.assignedRooms.length > 0) {
                          hasAssignments = true;
                          assignmentCount = lodge.assignedRooms.length;
                        }
                        
                        // Check new structure
                        if (lodge.rooms && Array.isArray(lodge.rooms)) {
                          const totalAssignedBeds = lodge.rooms.reduce((sum, room) => {
                            return sum + (room.assignedBeds?.length || 0);
                          }, 0);
                          if (totalAssignedBeds > 0) {
                            hasAssignments = true;
                            assignmentCount = totalAssignedBeds;
                          }
                        }
                        
                        if (hasAssignments) {
                          const confirmMessage = `This lodge has ${assignmentCount} assignment(s). Deleting it will remove all room assignments. Are you sure you want to delete "${lodge.name}"?`;
                          if (!window.confirm(confirmMessage)) {
                            return;
                          }
                        } else {
                          if (!window.confirm(`Are you sure you want to delete "${lodge.name}"?`)) {
                            return;
                          }
                        }
                        
                        try {
                          await lodgesAPI.delete(lodge.id);
                          // Reload lodges from API
                          const updatedLodges = await lodgesAPI.getAll();
                          setLodges(updatedLodges);
                          // If this was the selected lodge, clear selection
                          if (selectedLodge === lodge.id) {
                            setSelectedLodge(null);
                          }
                        } catch (error) {
                          console.error('Error deleting lodge:', error);
                          alert('Failed to delete lodge. Please try again.');
                        }
                      }}
                      className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                      title="Delete lodge"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
              </div>

              {selectedLodge === lodge.id && (canAssignRooms || canViewOnly) && (
                <div className="mt-4 border-t pt-4 space-y-4">
                  {/* Assignment Controls - ONLY visible to Room Admin and Application Admin */}
                  {canAssignRooms && userRole !== ROLES.REGISTRATION_ADMIN && (
                    <>
                      <h4 className="font-medium mb-2">Assign Registrants</h4>
                      
                      {/* Select Registrant */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Registrant
                        </label>
                  <select
                          value={selectedRoomForAssignment?.registrationId || ''}
                    onChange={(e) => {
                      // Prevent Registration Admin from assigning
                      if (!canAssignRooms || userRole === ROLES.REGISTRATION_ADMIN) {
                        alert('You do not have permission to assign rooms.');
                        return;
                      }
                      if (e.target.value) {
                              setSelectedRoomForAssignment({ 
                                ...selectedRoomForAssignment, 
                                lodgeId: lodge.id,
                                registrationId: e.target.value,
                                roomNumber: null,
                                bedNumber: null
                              });
                            } else {
                              setSelectedRoomForAssignment(null);
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-2"
                          disabled={!canAssignRooms || userRole === ROLES.REGISTRATION_ADMIN}
                  >
                    <option value="">Select a registrant...</option>
                          {(() => {
                            // Get registrations that match this lodge's restrictions, sorted alphabetically by name
                            const matchingRegistrations = getUnassignedRegistrationsForLodge(lodge.id)
                              .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                            return matchingRegistrations.length > 0 ? matchingRegistrations.map(reg => {
                              const camperCat = reg.camperCategory || reg.data?.camperCategory || '';
                              const gender = reg.gender || reg.data?.gender || '';
                              return (
                      <option key={reg.id} value={reg.id}>
                                  {reg.name} ({reg.type}{camperCat ? ` - ${camperCat}` : ''}{gender ? ` ${gender}` : ''})
                              </option>
                              );
                            }) : (
                              <option value="" disabled>
                                No matching registrants available for this lodge
                              </option>
                            );
                          })()}
                        </select>
                        {lodge.lodgeMainCategory === 'Family Lodge' && (
                          <p className="text-xs text-gray-500 mt-1">
                            Only Adults, Guest Speaker, Elementary, Below Elementary, and Middle School (staying with parents) can be assigned to this lodge.
                          </p>
                        )}
                        {lodge.lodgeMainCategory === 'Student Lodge' && ((lodge.camperCategories && lodge.camperCategories.length > 0) || lodge.camperCategory || lodge.gender) && (
                          <p className="text-xs text-gray-500 mt-1">
                            Only {[(lodge.camperCategories && lodge.camperCategories.length > 0) ? lodge.camperCategories.join(', ') : lodge.camperCategory, lodge.gender].filter(Boolean).join(' ')} registrations can be assigned to this lodge.
                          </p>
                        )}
                      </div>
                      
                      {/* Select Room and Bed (only if registrant is selected) */}
                      {selectedRoomForAssignment?.registrationId && selectedRoomForAssignment?.lodgeId === lodge.id && canAssignRooms && userRole !== ROLES.REGISTRATION_ADMIN && hasPermission(userRole, 'assign_rooms') && (
                        <>
                          {/* Get available rooms for this lodge */}
                          {(() => {
                            // Support both new structure (rooms array) and old structure (totalRooms)
                            const rooms = lodge.rooms || [];
                            const hasNewStructure = rooms.length > 0;
                            
                            // For old structure, create rooms array from totalRooms
                            const availableRooms = hasNewStructure ? rooms : 
                              Array.from({ length: lodge.totalRooms || 0 }, (_, i) => ({
                                roomNumber: (i + 1).toString(),
                                bedCount: 1, // Default to 1 bed for old structure
                                assignedBeds: (lodge.assignedRooms || [])
                                  .filter(a => a.roomNumber === (i + 1).toString())
                                  .map(a => ({ registrationId: a.registrationId, bedNumber: a.bedNumber || 1 }))
                              }));
                            
                            return (
                              <>
                                {/* Room Selection */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Room
                                  </label>
                                  <select
                                    value={selectedRoomForAssignment?.roomNumber || ''}
                                    onChange={(e) => {
                                      if (!canAssignRooms || userRole === ROLES.REGISTRATION_ADMIN) return;
                                      setSelectedRoomForAssignment({ 
                                        ...selectedRoomForAssignment, 
                                        roomNumber: e.target.value,
                                        bedNumber: null // Reset bed when room changes
                                      });
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-2"
                                    disabled={!canAssignRooms || userRole === ROLES.REGISTRATION_ADMIN}
                                  >
                                    <option value="">Select a room...</option>
                                    {availableRooms.map((room, idx) => {
                                      const assignedBeds = room.assignedBeds?.length || 0;
                                      const availableBeds = room.bedCount - assignedBeds;
                                      const isFull = availableBeds <= 0;
                                      
                                      return (
                                        <option 
                                          key={idx} 
                                          value={room.roomNumber}
                                          disabled={isFull}
                                        >
                                          Room {room.roomNumber} ({availableBeds} of {room.bedCount} beds available{isFull ? ' - FULL' : ''})
                                        </option>
                                      );
                                    })}
                                  </select>
                                </div>
                                
                                {/* Bed Selection (only if room is selected) */}
                                {selectedRoomForAssignment?.roomNumber && (() => {
                                  const selectedRoom = availableRooms.find(r => r.roomNumber === selectedRoomForAssignment.roomNumber);
                                  if (!selectedRoom) return null;
                                  
                                  const assignedBedNumbers = new Set(
                                    (selectedRoom.assignedBeds || []).map(b => b.bedNumber)
                                  );
                                  const availableBedNumbers = [];
                                  for (let i = 1; i <= selectedRoom.bedCount; i++) {
                                    if (!assignedBedNumbers.has(i)) {
                                      availableBedNumbers.push(i);
                                    }
                                  }
                                  
                                  return (
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Bed Number
                                      </label>
                                      <select
                                        value={selectedRoomForAssignment?.bedNumber || ''}
                                        onChange={(e) => {
                                          // Prevent Registration Admin from assigning
                                          if (!canAssignRooms || userRole === ROLES.REGISTRATION_ADMIN) {
                                            alert('You do not have permission to assign rooms.');
                                            return;
                                          }
                                          setSelectedRoomForAssignment({ 
                                            ...selectedRoomForAssignment, 
                                            bedNumber: e.target.value ? parseInt(e.target.value) : null
                                          });
                                        }}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-2"
                                        disabled={!canAssignRooms || userRole === ROLES.REGISTRATION_ADMIN}
                                      >
                                        <option value="">Select a bed...</option>
                                        {availableBedNumbers.map(bedNum => (
                                          <option key={bedNum} value={bedNum}>
                                            Bed {bedNum}
                      </option>
                    ))}
                  </select>
                                    </div>
                                  );
                                })()}
                                
                                {/* Assign Button - Only shown if onAssignRoom is available and user has assignment permissions */}
                                {onAssignRoom && canAssignRooms && selectedRoomForAssignment?.roomNumber && selectedRoomForAssignment?.bedNumber && (
                                  <button
                                    onClick={() => {
                                      // Double-check permission before assigning
                                      if (!canAssignRooms || userRole === ROLES.REGISTRATION_ADMIN) {
                                        alert('You do not have permission to assign rooms.');
                                        return;
                                      }
                                      if (selectedRoomForAssignment.registrationId && 
                                          selectedRoomForAssignment.roomNumber && 
                                          selectedRoomForAssignment.bedNumber) {
                                        onAssignRoom(
                                          selectedRoomForAssignment.registrationId,
                                          lodge.id,
                                          selectedRoomForAssignment.roomNumber,
                                          selectedRoomForAssignment.bedNumber
                                        );
                                        setSelectedRoomForAssignment(null); // Reset selection
                                      }
                                    }}
                                    className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition"
                                  >
                                    Assign to Room {selectedRoomForAssignment.roomNumber}, Bed {selectedRoomForAssignment.bedNumber}
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </>
                      )}
                    </>
                  )}
                  
                  {/* Assigned Rooms List - visible to all (Room Admin, Registration Admin, Application Admin) */}
                  {canViewOnly && (
                    <h4 className="font-medium mb-2">Lodge Assignments (View Only)</h4>
                  )}

                  {(() => {
                    // Support both new structure (rooms array) and old structure (assignedRooms)
                    const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
                    
                    if (hasNewStructure) {
                      // New structure: get assignments from rooms.assignedBeds
                      const allAssignments = [];
                      lodge.rooms.forEach(room => {
                        if (room.assignedBeds && room.assignedBeds.length > 0) {
                          room.assignedBeds.forEach(bed => {
                            allAssignments.push({
                              registrationId: bed.registrationId,
                              roomNumber: room.roomNumber,
                              bedNumber: bed.bedNumber
                            });
                          });
                        }
                      });
                      
                      if (allAssignments.length > 0) {
                        return (
                    <div className="mt-4">
                      <h5 className="text-sm font-medium mb-2">Assigned Rooms:</h5>
                      <div className="space-y-1">
                              {allAssignments.map((assignment, idx) => {
                          const reg = registrations.find(r => r.id === assignment.registrationId);
                          return (
                                  <div key={idx} className="text-sm bg-gray-50 p-2 rounded flex items-center justify-between gap-2">
                                    <span className="flex-1">
                                      Room {assignment.roomNumber}, Bed {assignment.bedNumber}: {reg?.name || 'Unknown'}
                                    </span>
                                    {canRemoveRoom && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          if (window.confirm(`Are you sure you want to remove ${reg?.name || 'this registrant'} from Room ${assignment.roomNumber}, Bed ${assignment.bedNumber}?`)) {
                                            onRemoveRoom(assignment.registrationId, lodge.id);
                                          }
                                        }}
                                        className="flex-shrink-0 text-red-600 hover:text-red-800 hover:bg-red-100 px-2 py-1 rounded transition border border-red-300 hover:border-red-500 flex items-center gap-1"
                                        title="Remove assignment"
                                        type="button"
                                      >
                                        <X className="w-4 h-4" />
                                        <span className="text-xs">Remove</span>
                                      </button>
                                    )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                        );
                      }
                    } else if (lodge.assignedRooms && lodge.assignedRooms.length > 0) {
                      // Old structure: use assignedRooms array
                      return (
                        <div className="mt-4">
                          <h5 className="text-sm font-medium mb-2">Assigned Rooms:</h5>
                          <div className="space-y-1">
                            {lodge.assignedRooms.map((assignment, idx) => {
                              const reg = registrations.find(r => r.id === assignment.registrationId);
                              return (
                                <div key={idx} className="text-sm bg-gray-50 p-2 rounded flex items-center justify-between gap-2">
                                  <span className="flex-1">
                                    Room {assignment.roomNumber}{assignment.bedNumber ? `, Bed ${assignment.bedNumber}` : ''}: {reg?.name || 'Unknown'}
                                  </span>
                                  {canRemoveRoom && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        if (window.confirm(`Are you sure you want to remove ${reg?.name || 'this registrant'} from Room ${assignment.roomNumber}?`)) {
                                          onRemoveRoom(assignment.registrationId, lodge.id);
                                        }
                                      }}
                                      className="flex-shrink-0 text-red-600 hover:text-red-800 hover:bg-red-100 px-2 py-1 rounded transition border border-red-300 hover:border-red-500 flex items-center gap-1"
                                      title="Remove assignment"
                                      type="button"
                                    >
                                      <X className="w-4 h-4" />
                                      <span className="text-xs">Remove</span>
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
            );
          })}
        </div>

        {lodges.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No lodges created yet. Click "Add Lodge" to get started.
          </div>
        )}
      </div>
    </div>
  );
}

// Badge Printing Component
function BadgePrinting({ registrations, lodges, userRole }) {
  const [selectedReg, setSelectedReg] = useState(null);
  const printRef = useRef(null);

  const confirmedRegistrations = registrations.filter(r => r.status === 'confirmed');

  const handlePrint = (registration) => {
    setSelectedReg(registration);
    setTimeout(() => {
      window.print();
    }, 100);
  };

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: 3.375in 2.125in;
            margin: 0;
          }
          * {
            visibility: hidden;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .printable-badge, .printable-badge * {
            visibility: visible;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .printable-badge {
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 3.375in !important;
            height: 2.125in !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .no-print, .no-print * {
            display: none !important;
            visibility: hidden !important;
          }
        }
      `}</style>

      <div className="space-y-6 no-print">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Print Badges</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {confirmedRegistrations.map(reg => {
              const lodge = lodges.find(l => l.id === reg.lodgeId);
              const hasAllergies = reg.allergies && 
                reg.allergies.toLowerCase() !== 'none' && 
                reg.allergies.trim() !== '';
              const city = reg.city || reg.data?.city || '';
              const camperCategory = reg.camperCategory || reg.data?.camperCategory || '';
              
              return (
                <div key={reg.id} className={`border-2 rounded-lg p-4 ${hasAllergies ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold">{reg.name}</h3>
                    {hasAllergies && (
                      <span className="px-2 py-1 text-xs font-bold bg-red-600 text-white rounded">ALLERGIES</span>
                    )}
                  </div>
                  {city && (
                    <p className="text-sm text-gray-600 mb-1">City: {city}</p>
                  )}
                  {camperCategory && (
                    <p className="text-sm text-gray-600 mb-1">Category: {camperCategory}</p>
                  )}
                  <p className="text-sm text-gray-600 mb-1">Type: {reg.type}</p>
                  {lodge && (
                    <p className="text-sm text-gray-600 mb-3">
                      {lodge.name} - Room {reg.roomNumber}
                    </p>
                  )}
                  <button
                    onClick={() => handlePrint(reg)}
                    disabled={userRole === ROLES.VIEWER}
                    className={`w-full px-4 py-2 rounded-lg transition flex items-center justify-center gap-2 ${
                      userRole === ROLES.VIEWER
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : hasAllergies
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    <Printer className="w-4 h-4" />
                    Print Badge
                  </button>
                </div>
              );
            })}
          </div>

          {confirmedRegistrations.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No confirmed registrations to print badges for
            </div>
          )}
        </div>
      </div>

      {/* Printable Badge (hidden on screen, visible when printing) */}
      {selectedReg && (() => {
        const hasAllergies = selectedReg.allergies && 
          selectedReg.allergies.toLowerCase() !== 'none' && 
          selectedReg.allergies.trim() !== '';
        const city = selectedReg.city || selectedReg.data?.city || '';
        const camperCategory = selectedReg.camperCategory || selectedReg.data?.camperCategory || '';
        const lodgeName = lodges.find(l => l.id === selectedReg.lodgeId)?.name || '';

        return (
          <div className="printable-badge" ref={printRef} style={{ position: 'absolute', left: '-9999px', width: '3.375in', height: '2.125in' }}>
            {/* Badge outer shell — kraft/parchment background, dark green border */}
            <div style={{
              width: '3.375in',
              height: '2.125in',
              boxSizing: 'border-box',
              border: '3px solid #1a3a1a',
              borderRadius: '6px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#f0ebe0',
              fontFamily: 'Georgia, serif',
            }}>

              {/* TOP BAND — dark forest green */}
              <div style={{
                backgroundColor: '#1a3a1a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '3px 7px',
                flexShrink: 0,
              }}>
                {/* Logo */}
                <img
                  src={process.env.PUBLIC_URL + '/logo.png'}
                  alt="ICPF Logo"
                  style={{ height: '22px', width: 'auto', objectFit: 'contain' }}
                />
                {/* Event title */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#a8c49a', fontSize: '6pt', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Arial, sans-serif' }}>
                    AWAKE CAMP 2026
                  </div>
                  <div style={{ color: '#ffffff', fontSize: '11pt', fontWeight: '900', letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: 'Arial Black, Arial, sans-serif', lineHeight: 1 }}>
                    UNSHAKEABLE
                  </div>
                  <div style={{ color: '#a8c49a', fontSize: '5.5pt', letterSpacing: '0.1em', fontFamily: 'Arial, sans-serif' }}>
                    HEB 12:28–29 · MAR 19–22, HOUSTON TX
                  </div>
                </div>
              </div>

              {/* MAIN BODY */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px 8px',
                textAlign: 'center',
              }}>
                {/* Name */}
                <div style={{
                  fontSize: '16pt',
                  fontWeight: '900',
                  color: '#1a3a1a',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  fontFamily: 'Arial Black, Arial, sans-serif',
                  lineHeight: 1.1,
                  wordBreak: 'break-word',
                }}>
                  {selectedReg.name.toUpperCase()}
                </div>

                {/* City */}
                {city && (
                  <div style={{ fontSize: '8pt', color: '#3d5a3d', marginTop: '2px', fontFamily: 'Arial, sans-serif', letterSpacing: '0.05em' }}>
                    {city}
                  </div>
                )}

                {/* Category / Type */}
                {(camperCategory || selectedReg.type) && (
                  <div style={{
                    marginTop: '3px',
                    backgroundColor: '#2d5a2d',
                    color: '#ffffff',
                    fontSize: '6.5pt',
                    fontWeight: 'bold',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    padding: '1px 8px',
                    borderRadius: '2px',
                    fontFamily: 'Arial, sans-serif',
                  }}>
                    {camperCategory || selectedReg.type}
                  </div>
                )}

                {/* Lodge / Room */}
                {lodgeName && (
                  <div style={{ fontSize: '7pt', color: '#4a5a4a', marginTop: '3px', fontFamily: 'Arial, sans-serif' }}>
                    {lodgeName}{selectedReg.roomNumber ? ` · Room ${selectedReg.roomNumber}` : ''}
                  </div>
                )}
              </div>

              {/* ALLERGY ALERT BAND */}
              {hasAllergies && (
                <div style={{
                  backgroundColor: '#b91c1c',
                  color: '#ffffff',
                  textAlign: 'center',
                  fontSize: '6.5pt',
                  fontWeight: '900',
                  letterSpacing: '0.15em',
                  padding: '2px 4px',
                  fontFamily: 'Arial, sans-serif',
                  flexShrink: 0,
                }}>
                  ⚠ ALLERGIES: {selectedReg.allergies.toUpperCase()}
                </div>
              )}

            </div>
          </div>
        );
      })()}
    </>
  );
}

// Export View Component
function ExportView({ registrations, lodges, userRole }) {
  const getAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const dob = new Date(dateOfBirth);
    if (isNaN(dob.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
    return age;
  };

  const getLodgeAssignment = (reg) => {
    if (reg.lodgeId) {
      const lodge = lodges.find(l => l.id === reg.lodgeId);
      return lodge ? { lodgeName: lodge.name, roomNumber: reg.data?.roomNumber || reg.roomNumber } : null;
    }
    if (reg.data?.lodgeId) {
      const lodge = lodges.find(l => l.id === reg.data.lodgeId);
      return lodge ? { lodgeName: lodge.name, roomNumber: reg.data.roomNumber || reg.roomNumber } : null;
    }
    for (const lodge of lodges) {
      const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
      if (hasNewStructure) {
        for (const room of lodge.rooms) {
          if (room.assignedBeds && room.assignedBeds.some(bed => bed.registrationId === reg.id)) {
            return { lodgeName: lodge.name, roomNumber: room.roomNumber };
          }
        }
      } else if (lodge.assignedRooms && Array.isArray(lodge.assignedRooms)) {
        const assignment = lodge.assignedRooms.find(a => a.registrationId === reg.id);
        if (assignment) return { lodgeName: lodge.name, roomNumber: assignment.roomNumber };
      }
    }
    return null;
  };

  const exportToCSV = () => {
    const headers = [
      'First Name',
      'Last Name',
      'Middle Name',
      'Gender',
      'Age',
      'Camper Category',
      'Email',
      'Mobile Number',
      'Home Phone',
      'Address Line 1',
      'Address Line 2',
      'City',
      'State',
      'Zip',
      'Country',
      'Church Name',
      'Pastor Name',
      'First Time Attending',
      'T-Shirt Size',
      'Allergies',
      'Allergy Description',
      'Emergency Contact Name',
      'Emergency Contact Relationship',
      'Emergency Contact Phone',
      'Parent/Guardian Name',
      'Parent/Guardian Email',
      'Parent/Guardian Phone',
      'Type',
      'Attendance Days',
      'Registration Status',
      'Payment Status',
      'Payment Method',
      'Amount',
      'Lodge',
      'Room Number',
      'Checked In',
      'Check-In Time',
      'QR Code',
      'Registered At'
    ];

    const rows = registrations.map(reg => {
      const lodgeAssignment = getLodgeAssignment(reg);
      const dob = reg.dateOfBirth || reg.data?.dateOfBirth;
      return [
        reg.firstName || '',
        reg.lastName || '',
        reg.middleName || '',
        reg.gender || '',
        getAge(dob) !== '' ? String(getAge(dob)) : (reg.age ?? ''),
        reg.camperCategory || '',
        reg.email,
        reg.mobileNumber || '',
        reg.homePhone || '',
        reg.addressLine1 || '',
        reg.addressLine2 || '',
        reg.city || '',
        reg.state || '',
        reg.zip || '',
        reg.country || '',
        (reg.churchName || reg.data?.churchName || ''),
        (reg.pastorName || reg.data?.pastorName || ''),
        reg.firstTimeAttending || '',
        reg.tshirtSize || '',
        reg.allergies || '',
        reg.allergyDescription || '',
        reg.emergencyContactName || '',
        reg.emergencyContactRelationship || '',
        reg.emergencyContactPhone || '',
        reg.parentGuardianName || '',
        reg.parentGuardianEmail || '',
        reg.parentGuardianPhone || '',
        reg.type,
        reg.attendanceDays || (reg.dayRegistration ? 'N/A' : ''),
        reg.status,
        reg.paymentStatus,
        reg.paymentMethod,
        reg.amount,
        lodgeAssignment?.lodgeName || 'Not Assigned',
        lodgeAssignment?.roomNumber || 'Not Assigned',
        reg.checkedIn ? 'Yes' : 'No',
        reg.checkInTime || 'N/A',
        reg.qrCode,
        new Date(reg.registeredAt).toLocaleString()
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `camp-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Export Data</h2>
      
      <div className="space-y-6">
        <div className="border rounded-lg p-6">
          <div className="flex items-start gap-4">
            <Download className="w-8 h-8 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-2">Export All Registrations</h3>
              <p className="text-gray-600 mb-4">
                Download a CSV file containing all registration data including personal information,
                payment status, room assignments, and check-in status.
              </p>
              <button
                onClick={exportToCSV}
                className="px-6 py-2 rounded-lg transition flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Download className="w-4 h-4" />
                Download CSV
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border rounded-lg p-4">
            <h4 className="font-semibold mb-2">Total Registrations</h4>
            <p className="text-3xl font-bold text-blue-600">{registrations.length}</p>
          </div>
          <div className="border rounded-lg p-4">
            <h4 className="font-semibold mb-2">Confirmed</h4>
            <p className="text-3xl font-bold text-green-600">
              {registrations.filter(r => r.status === 'confirmed').length}
            </p>
          </div>
          <div className="border rounded-lg p-4">
            <h4 className="font-semibold mb-2">Checked In</h4>
            <p className="text-3xl font-bold text-purple-600">
              {registrations.filter(r => r.checkedIn).length}
            </p>
          </div>
        </div>

        <div className="border rounded-lg p-4">
          <h3 className="font-semibold mb-4">Export Preview</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Email</th>
                  <th className="px-4 py-2 text-left">Age</th>
                  <th className="px-4 py-2 text-left">Church Name</th>
                  <th className="px-4 py-2 text-left">Mobile</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">T-Shirt</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {registrations.slice(0, 5).map(reg => {
                  const dob = reg.dateOfBirth || reg.data?.dateOfBirth;
                  const age = getAge(dob) !== '' ? getAge(dob) : (reg.age != null ? reg.age : 'N/A');
                  return (
                    <tr key={reg.id}>
                      <td className="px-4 py-2">{reg.name}</td>
                      <td className="px-4 py-2">{reg.email}</td>
                      <td className="px-4 py-2">{age}</td>
                      <td className="px-4 py-2">{reg.churchName || reg.data?.churchName || 'N/A'}</td>
                      <td className="px-4 py-2">{reg.mobileNumber || 'N/A'}</td>
                      <td className="px-4 py-2">{reg.type}</td>
                      <td className="px-4 py-2">
                        <StatusBadge status={reg.status} />
                      </td>
                      <td className="px-4 py-2">{reg.tshirtSize || 'N/A'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {registrations.length > 5 && (
            <p className="text-sm text-gray-500 mt-2">
              Showing 5 of {registrations.length} registrations
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// User Management Component
function UserManagement({ currentUserRole, onUserUpdate }) {
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: ROLES.VIEWER
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load users from API
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersList = await usersAPI.getAll();
        setUsers(usersList);
      } catch (error) {
        console.error('Error loading users:', error);
        setError('Failed to load users. Please try again.');
      }
    };
    loadUsers();
  }, []);

  // Reload users after save/delete operations
  const reloadUsers = async () => {
    try {
      const usersList = await usersAPI.getAll();
      setUsers(usersList);
    } catch (error) {
      console.error('Error reloading users:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const validateForm = () => {
    if (!formData.username.trim()) {
      setError('Username is required');
      return false;
    }
    if (!editingUser && !formData.password) {
      setError('Password is required for new users');
      return false;
    }
    if (formData.password && formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (formData.password && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!formData.role) {
      setError('Role is required');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const normalizedUsername = formData.username.toLowerCase().trim();
      const userData = {
        id: editingUser ? editingUser.id : 'user_' + Date.now(),
        username: normalizedUsername,
        email: formData.email || undefined,
        password: formData.password || (editingUser ? undefined : ''), // Only send password if provided or new user
        role: formData.role,
        temporaryPassword: !editingUser ? true : undefined, // Mark new user passwords as temporary
      };

      if (editingUser) {
        // Update existing user
        await usersAPI.update(editingUser.id, userData);
        setSuccess('User updated successfully');
      } else {
        // Create new user
        await usersAPI.create(userData);
        setSuccess('User created successfully');
      }

      await reloadUsers();
      setFormData({ username: '', email: '', password: '', confirmPassword: '', role: ROLES.VIEWER });
      setShowAddUser(false);
      setEditingUser(null);
      
      // Trigger parent update
      if (onUserUpdate) {
        onUserUpdate();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving user:', error);
      setError(error.message || 'Failed to save user. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email || '',
      password: '',
      confirmPassword: '',
      role: user.role
    });
    setShowAddUser(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    if (!window.confirm(`Are you sure you want to delete user "${user.username}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // Check if this is the last Application Admin
      const appAdmins = users.filter(u => u.role === ROLES.APPLICATION_ADMIN);
      if (user.role === ROLES.APPLICATION_ADMIN && appAdmins.length === 1) {
        setError('Cannot delete the last Application Admin');
        setTimeout(() => setError(''), 3000);
        return;
      }

      await usersAPI.delete(userId);
      await reloadUsers();
      setSuccess('User deleted successfully');
      
      // Trigger parent update
      if (onUserUpdate) {
        onUserUpdate();
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError(error.message || 'Failed to delete user. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleCancel = () => {
    setShowAddUser(false);
    setEditingUser(null);
    setFormData({ username: '', email: '', password: '', confirmPassword: '', role: ROLES.VIEWER });
    setError('');
    setSuccess('');
  };

  const usersList = users;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">User Management</h2>
          <button
            onClick={() => {
              setShowAddUser(true);
              setEditingUser(null);
              setFormData({ username: '', email: '', password: '', confirmPassword: '', role: ROLES.VIEWER });
              setError('');
              setSuccess('');
            }}
            disabled={currentUserRole === ROLES.VIEWER}
            className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${
              currentUserRole === ROLES.VIEWER
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            <UserPlus className="w-5 h-5" />
            Add New User
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        {showAddUser && (
          <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">
              {editingUser ? 'Edit User' : 'Add New User'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username *
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter username"
                  disabled={currentUserRole === ROLES.VIEWER || editingUser !== null}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter email (for password reset)"
                  disabled={currentUserRole === ROLES.VIEWER}
                />
                <p className="text-xs text-gray-500 mt-1">Required for password reset functionality</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  disabled={currentUserRole === ROLES.VIEWER}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value={ROLES.VIEWER}>Viewer</option>
                  <option value={ROLES.ROOM_ADMIN}>Room Admin</option>
                  <option value={ROLES.REGISTRATION_ADMIN}>Registration Admin</option>
                  <option value={ROLES.TRANSPORTATION_ADMIN}>Transportation Admin</option>
                  <option value={ROLES.APPLICATION_ADMIN}>Application Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={currentUserRole === ROLES.VIEWER}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder={editingUser ? 'Enter new password (optional)' : 'Enter password'}
                />
              </div>

              {formData.password && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    disabled={currentUserRole === ROLES.VIEWER}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Confirm password"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleSave}
                disabled={currentUserRole === ROLES.VIEWER}
                className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${
                  currentUserRole === ROLES.VIEWER
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                <Save className="w-4 h-4" />
                {editingUser ? 'Update User' : 'Create User'}
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y bg-white">
              {usersList.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    No users found. Click "Add New User" to create one.
                  </td>
                </tr>
              ) : (
                usersList.map((user) => {
                  const isLastAppAdmin = user.role === ROLES.APPLICATION_ADMIN && 
                    usersList.filter(u => u.role === ROLES.APPLICATION_ADMIN).length === 1;
                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{user.username}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          user.role === ROLES.APPLICATION_ADMIN ? 'bg-purple-100 text-purple-800' :
                          user.role === ROLES.REGISTRATION_ADMIN ? 'bg-blue-100 text-blue-800' :
                          user.role === ROLES.TRANSPORTATION_ADMIN ? 'bg-orange-100 text-orange-800' :
                          user.role === ROLES.ROOM_ADMIN ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {getRoleDisplayName(user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {currentUserRole !== ROLES.VIEWER && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(user)}
                            className="text-blue-600 hover:text-blue-700 p-1.5 rounded hover:bg-blue-50 transition"
                            title="Edit User"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {!isLastAppAdmin && (
                            <button
                              onClick={() => handleDelete(user.username)}
                              className="text-red-600 hover:text-red-700 p-1.5 rounded hover:bg-red-50 transition"
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Passwords are stored in plain text for demo purposes. In production, 
            use secure password hashing (e.g., bcrypt) and store user data in a secure database.
          </p>
        </div>
      </div>
    </div>
  );
}

// Schedule Builder Component
function ScheduleBuilder({ userRole }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    startTime: '',
    endTime: '',
    location: '',
    camperCategories: [],
    mc: '',
    worshipTeam: [],
    events: [],
    notes: '',
    status: 'draft' // draft, published, cancelled
  });

  const camperCategoryOptions = ['Middle School', 'High School', 'College', 'Adults'];

  useEffect(() => {
    loadSchedules();
  }, []);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const allSchedules = await schedulesAPI.getAll();
      setSchedules(allSchedules);
    } catch (error) {
      console.error('Error loading schedules:', error);
      alert('Failed to load schedules. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryToggle = (category) => {
    setFormData(prev => ({
      ...prev,
      camperCategories: prev.camperCategories.includes(category)
        ? prev.camperCategories.filter(c => c !== category)
        : [...prev.camperCategories, category]
    }));
  };

  const handleWorshipTeamChange = (e) => {
    const value = e.target.value;
    const members = value.split(',').map(m => m.trim()).filter(m => m);
    setFormData(prev => ({ ...prev, worshipTeam: members }));
  };

  const handleAddEvent = () => {
    setFormData(prev => ({
      ...prev,
      events: [...prev.events, {
        id: `event_${Date.now()}`,
        title: '',
        startTime: '',
        endTime: '',
        assignedTo: '',
        description: '',
        order: prev.events.length + 1
      }]
    }));
  };

  const handleUpdateEvent = (eventId, updates) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.map(e => 
        e.id === eventId ? { ...e, ...updates } : e
      )
    }));
  };

  const handleRemoveEvent = (eventId) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.filter(e => e.id !== eventId).map((e, idx) => ({
        ...e,
        order: idx + 1
      }))
    }));
  };

  const handleMoveEvent = (eventId, direction) => {
    setFormData(prev => {
      const events = [...prev.events];
      const index = events.findIndex(e => e.id === eventId);
      if (index === -1) return prev;
      
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= events.length) return prev;
      
      [events[index], events[newIndex]] = [events[newIndex], events[index]];
      events.forEach((e, idx) => { e.order = idx + 1; });
      
      return { ...prev, events };
    });
  };

  const handleSave = async () => {
    if (!formData.title || !formData.date || !formData.startTime) {
      alert('Please fill in required fields: Title, Date, and Start Time');
      return;
    }

    try {
      const scheduleData = {
        ...formData,
        id: editingSchedule ? editingSchedule.id : `schedule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        events: formData.events.sort((a, b) => a.order - b.order)
      };

      if (editingSchedule) {
        await schedulesAPI.update(editingSchedule.id, scheduleData);
        alert('Schedule updated successfully!');
      } else {
        await schedulesAPI.create(scheduleData);
        alert('Schedule created successfully!');
      }

      await loadSchedules();
      handleCancel();
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Failed to save schedule. Please try again.');
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      title: schedule.title || '',
      date: schedule.date || '',
      startTime: schedule.startTime || '',
      endTime: schedule.endTime || '',
      location: schedule.location || '',
      camperCategories: schedule.camperCategories || [],
      mc: schedule.mc || '',
      worshipTeam: schedule.worshipTeam || [],
      events: schedule.events || [],
      notes: schedule.notes || '',
      status: schedule.status || 'draft'
    });
    setShowAddModal(true);
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      await schedulesAPI.delete(scheduleId);
      await loadSchedules();
      alert('Schedule deleted successfully!');
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('Failed to delete schedule. Please try again.');
    }
  };

  const handleCancel = () => {
    setShowAddModal(false);
    setEditingSchedule(null);
    setFormData({
      title: '',
      date: '',
      startTime: '',
      endTime: '',
      location: '',
      camperCategories: [],
      mc: '',
      worshipTeam: [],
      events: [],
      notes: '',
      status: 'draft'
    });
  };

  const filteredSchedules = schedules.filter(schedule => {
    if (filterCategory !== 'all' && !schedule.camperCategories?.includes(filterCategory)) {
      return false;
    }
    if (filterDate && schedule.date !== filterDate) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading schedules...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-3 md:p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 md:gap-4">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">Schedule Builder</h2>
            <p className="text-xs md:text-sm text-gray-600 mt-1">Create and manage camp schedules for all camper categories</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            disabled={userRole === ROLES.VIEWER}
            className={`w-full sm:w-auto px-3 md:px-4 py-1.5 md:py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-xs md:text-sm font-medium ${
              userRole === ROLES.VIEWER
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <Calendar className="w-4 h-4 md:w-5 md:h-5" />
            <span className="whitespace-nowrap">Create Schedule</span>
          </button>
        </div>

        {/* Filters */}
        <div className="mt-4 md:mt-6 flex flex-col sm:flex-row gap-3 md:gap-4">
          <div className="flex-1 min-w-0">
            <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2">Filter by Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-3 md:px-4 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {camperCategoryOptions.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-0">
            <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2">Filter by Date</label>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="w-full px-3 md:px-4 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {(filterCategory !== 'all' || filterDate) && (
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilterCategory('all');
                  setFilterDate('');
                }}
                className="w-full sm:w-auto px-3 md:px-4 py-2 text-xs md:text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Schedules List */}
      <div className="p-3 md:p-6">
        {filteredSchedules.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No schedules found</p>
            <p className="text-gray-500 text-sm mt-2">
              {filterCategory !== 'all' || filterDate ? 'Try adjusting your filters' : 'Create your first schedule to get started'}
            </p>
          </div>
        ) : (
          <div className="space-y-3 md:space-y-4">
            {filteredSchedules.map(schedule => (
              <div key={schedule.id} className="border border-gray-200 rounded-lg p-3 md:p-6 hover:shadow-md transition-shadow">
                <div className="flex flex-col lg:flex-row justify-between gap-3 md:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <h3 className="text-base md:text-xl font-semibold text-gray-900 truncate flex-1">{schedule.title}</h3>
                      <span className={`px-2 md:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        schedule.status === 'published' ? 'bg-green-100 text-green-800' :
                        schedule.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {schedule.status || 'draft'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{schedule.date} {schedule.startTime}{schedule.endTime ? ` - ${schedule.endTime}` : ''}</span>
                      </div>
                      {schedule.location && (
                        <div className="flex items-center gap-2">
                          <Home className="w-4 h-4" />
                          <span>{schedule.location}</span>
                        </div>
                      )}
                      {schedule.camperCategories && schedule.camperCategories.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>{schedule.camperCategories.join(', ')}</span>
                        </div>
                      )}
                      {schedule.mc && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">MC:</span>
                          <span>{schedule.mc}</span>
                        </div>
                      )}
                    </div>
                    {schedule.events && schedule.events.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Events ({schedule.events.length}):</h4>
                        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                          {schedule.events.slice(0, 3).map((event, idx) => (
                            <li key={event.id || idx}>
                              {event.title || 'Untitled Event'}
                              {event.startTime && ` (${event.startTime}${event.endTime ? ` - ${event.endTime}` : ''})`}
                            </li>
                          ))}
                          {schedule.events.length > 3 && (
                            <li className="text-gray-500">... and {schedule.events.length - 3} more</li>
                          )}
                        </ol>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-row sm:flex-col gap-2 lg:items-end">
                    <button
                      onClick={() => handleEdit(schedule)}
                      disabled={userRole === ROLES.VIEWER}
                      className={`flex-1 sm:flex-none px-3 md:px-4 py-1.5 md:py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-xs md:text-sm font-medium ${
                        userRole === ROLES.VIEWER
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      <Pencil className="w-3 h-3 md:w-4 md:h-4" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(schedule.id)}
                      disabled={userRole === ROLES.VIEWER}
                      className={`flex-1 sm:flex-none px-3 md:px-4 py-1.5 md:py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-xs md:text-sm font-medium ${
                        userRole === ROLES.VIEWER
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
              <h3 className="text-xl font-semibold">
                {editingSchedule ? 'Edit Schedule' : 'Create New Schedule'}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    disabled={userRole === ROLES.VIEWER}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="e.g., Morning Session"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    disabled={userRole === ROLES.VIEWER}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> Start Time
                  </label>
                  <input
                    type="time"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                    disabled={userRole === ROLES.VIEWER}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Time
                  </label>
                  <input
                    type="time"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleInputChange}
                    disabled={userRole === ROLES.VIEWER}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location/Venue
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    disabled={userRole === ROLES.VIEWER}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="e.g., Main Hall, Auditorium"
                  />
                </div>
              </div>

              {/* Camper Categories */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Camper Categories
                </label>
                <div className="flex flex-wrap gap-3">
                  {camperCategoryOptions.map(category => (
                    <label key={category} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.camperCategories.includes(category)}
                        onChange={() => handleCategoryToggle(category)}
                        disabled={userRole === ROLES.VIEWER}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className="text-sm text-gray-700">{category}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* MC and Worship Team */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Master of Ceremonies (MC)
                  </label>
                  <input
                    type="text"
                    name="mc"
                    value={formData.mc}
                    onChange={handleInputChange}
                    disabled={userRole === ROLES.VIEWER}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Enter MC name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Worship Team (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={formData.worshipTeam.join(', ')}
                    onChange={handleWorshipTeamChange}
                    disabled={userRole === ROLES.VIEWER}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="e.g., John Doe, Jane Smith"
                  />
                </div>
              </div>

              {/* Events */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Events (Ordered)
                  </label>
                  <button
                    onClick={handleAddEvent}
                    disabled={userRole === ROLES.VIEWER}
                    className={`px-3 py-1 text-sm rounded-lg transition-colors flex items-center gap-2 ${
                      userRole === ROLES.VIEWER
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    <UserPlus className="w-4 h-4" />
                    Add Event
                  </button>
                </div>
                <div className="space-y-3">
                  {formData.events.map((event, index) => (
                    <div key={event.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-sm font-medium text-gray-700">Event #{event.order}</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleMoveEvent(event.id, 'up')}
                            disabled={userRole === ROLES.VIEWER || index === 0}
                            className={`px-2 py-1 text-xs rounded ${
                              userRole === ROLES.VIEWER
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed'
                            }`}
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => handleMoveEvent(event.id, 'down')}
                            disabled={userRole === ROLES.VIEWER || index === formData.events.length - 1}
                            className={`px-2 py-1 text-xs rounded ${
                              userRole === ROLES.VIEWER
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed'
                            }`}
                          >
                            ↓
                          </button>
                          {userRole !== ROLES.VIEWER && (
                          <button
                            onClick={() => handleRemoveEvent(event.id)}
                            className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Event Title</label>
                          <input
                            type="text"
                            value={event.title}
                            onChange={(e) => handleUpdateEvent(event.id, { title: e.target.value })}
                            disabled={userRole === ROLES.VIEWER}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="Event name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Assigned To</label>
                          <input
                            type="text"
                            value={event.assignedTo}
                            onChange={(e) => handleUpdateEvent(event.id, { assignedTo: e.target.value })}
                            disabled={userRole === ROLES.VIEWER}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="Person/Team name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Start Time</label>
                          <input
                            type="time"
                            value={event.startTime}
                            onChange={(e) => handleUpdateEvent(event.id, { startTime: e.target.value })}
                            disabled={userRole === ROLES.VIEWER}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">End Time</label>
                          <input
                            type="time"
                            value={event.endTime}
                            onChange={(e) => handleUpdateEvent(event.id, { endTime: e.target.value })}
                            disabled={userRole === ROLES.VIEWER}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                          <textarea
                            value={event.description}
                            onChange={(e) => handleUpdateEvent(event.id, { description: e.target.value })}
                            disabled={userRole === ROLES.VIEWER}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            rows="2"
                            placeholder="Event description or notes"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {formData.events.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">No events added yet. Click "Add Event" to get started.</p>
                  )}
                </div>
              </div>

              {/* Notes and Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="Additional notes or instructions"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={userRole === ROLES.VIEWER}
                  className={`px-6 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                    userRole === ROLES.VIEWER
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <Save className="w-4 h-4" />
                  {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Public Registration Details Component (accessed via QR code)
function PublicRegistrationDetails({ registrationCode, registrations, lodges, selfCheckInEnabled: propSelfCheckInEnabled = false, onCheckIn, onBack }) {
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [selfCheckInEnabled, setSelfCheckInEnabled] = useState(propSelfCheckInEnabled);

  // Always load self check-in setting from API (in case page accessed directly via QR code)
  useEffect(() => {
    const loadSelfCheckInSetting = async () => {
      try {
        console.log('🔍 [PublicRegistrationDetails] Attempting to load selfCheckInEnabled from API...');
        const setting = await settingsAPI.get('selfCheckInEnabled');
        console.log('📥 [PublicRegistrationDetails] Full API response:', JSON.stringify(setting, null, 2));
        console.log('📥 [PublicRegistrationDetails] Setting object:', setting);
        console.log('📥 [PublicRegistrationDetails] Setting.value:', setting?.value);
        console.log('📥 [PublicRegistrationDetails] Setting.value type:', typeof setting?.value);
        
        // Handle different possible formats
        let isEnabled = false;
        if (setting) {
          // Handle object with .value property
          const value = setting.value !== undefined ? setting.value : setting;
          isEnabled = value === 'true' || value === true || value === 'True' || value === 1 || value === '1' || value === 'TRUE';
          console.log('✅ [PublicRegistrationDetails] Parsed selfCheckInEnabled:', isEnabled, 'from value:', value);
        } else {
          console.warn('⚠️ [PublicRegistrationDetails] Setting is null or undefined');
        }
        
        setSelfCheckInEnabled(isEnabled);
      } catch (error) {
        // If setting not found or error, use the prop value as fallback
        console.error('❌ Could not load self check-in setting from API:', error);
        console.log('⚠️ Using prop value as fallback:', propSelfCheckInEnabled);
        setSelfCheckInEnabled(propSelfCheckInEnabled);
      }
    };
    
    // Always try to load from API first (most reliable), but also use prop as initial value
    setSelfCheckInEnabled(propSelfCheckInEnabled);
    loadSelfCheckInSetting();
    
    // Also reload periodically in case it changes (every 2 seconds for first 10 seconds)
    let reloadCount = 0;
    const reloadInterval = setInterval(() => {
      reloadCount++;
      if (reloadCount < 5) { // Reload 5 times (10 seconds total)
        loadSelfCheckInSetting();
      } else {
        clearInterval(reloadInterval);
      }
    }, 2000);
    
    return () => clearInterval(reloadInterval);
  }, [propSelfCheckInEnabled]);

  const loadRegistration = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to find registration by QR code or registration code
      const found = registrations.find(
        reg => reg.qrCode === registrationCode || 
               reg.registrationCode === registrationCode ||
               reg.qrCode === decodeURIComponent(registrationCode) ||
               reg.registrationCode === decodeURIComponent(registrationCode)
      );

      if (found) {
        console.log('📋 Found registration locally:', found.id, 'Status:', found.status, 'LodgeId:', found.lodgeId, 'RoomNumber:', found.roomNumber);
        setRegistration(found);
      } else {
        // If not found locally, try API search
        try {
          console.log('🔍 Registration not found locally, searching API for code:', registrationCode);
          const result = await registrationsAPI.search('', registrationCode);
          console.log('📥 API search result:', result);
          if (result && result.length > 0) {
            console.log('✅ Found registration via API:', result[0].id, 'Status:', result[0].status, 'LodgeId:', result[0].lodgeId, 'RoomNumber:', result[0].roomNumber);
            setRegistration(result[0]);
          } else {
            setError('Registration not found. Please check your QR code.');
          }
        } catch (apiError) {
          setError('Unable to load registration details. Please try again later.');
          console.error('❌ Error fetching registration:', apiError);
        }
      }
    } catch (err) {
      setError('An error occurred while loading registration details.');
      console.error('❌ Error loading registration:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (registrationCode) {
      loadRegistration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationCode, registrations]); // loadRegistration is stable, safe to omit

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    const statusText = {
      pending: 'Pending Approval',
      confirmed: 'Confirmed',
      rejected: 'Rejected',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusText[status] || status}
      </span>
    );
  };

  const getPaymentBadge = (paymentStatus) => {
    const paymentColors = {
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      linked: 'bg-gray-100 text-gray-800',
    };
    const paymentText = {
      paid: 'Paid',
      pending: 'Pending',
      processing: 'Processing',
      linked: 'Linked',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${paymentColors[paymentStatus] || 'bg-gray-100 text-gray-800'}`}>
        {paymentText[paymentStatus] || paymentStatus}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading registration details...</p>
        </div>
      </div>
    );
  }

  if (error || !registration) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Registration Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The registration could not be found. Please check your QR code and try again.'}</p>
          <button
            onClick={onBack}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  const lodge = lodges.find(l => l.id === registration.lodgeId);
  const hasRoom = !!(lodge && registration.roomNumber);

  // Debug logging to help troubleshoot
  console.log('PublicRegistrationDetails - Check-in visibility:', {
    selfCheckInEnabled,
    propSelfCheckInEnabled,
    registrationStatus: registration.status,
    isConfirmed: registration.status === 'confirmed',
    checkedIn: registration.checkedIn,
    lodgeId: registration.lodgeId,
    roomNumber: registration.roomNumber,
    lodge: lodge ? { id: lodge.id, name: lodge.name } : null,
    hasRoom,
    shouldShowCheckIn: selfCheckInEnabled && registration.status === 'confirmed',
    shouldShowRoom: selfCheckInEnabled && hasRoom,
    registrationId: registration.id
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">ICPF USA Camp Portal</h1>
            <button
              onClick={onBack}
              className="text-sm bg-blue-700 hover:bg-blue-800 px-4 py-2 rounded transition"
            >
              Home
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <h2 className="text-3xl font-bold mb-2">Registration Details</h2>
            <p className="text-blue-100">Registration Code: {registration.qrCode || registration.registrationCode}</p>
          </div>

          {/* Status Section */}
          <div className="p-6 border-b bg-gray-50">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="text-sm text-gray-600 block mb-1">Registration Status</label>
                {getStatusBadge(registration.status)}
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-1">Payment Status</label>
                {getPaymentBadge((() => {
                  // Check if this is a non-primary family member
                  // Method 1: Check familyCode and familyPosition
                  if (registration.familyCode && registration.familyPosition && registration.familyPosition !== 1) {
                    return 'linked';
                  }
                  // Method 2: Check registration code pattern (ICPFFAM001-1, ICPFFAM001-2, etc.)
                  const regCode = registration.qrCode || registration.registrationCode || '';
                  if (regCode && typeof regCode === 'string' && regCode.startsWith('ICPFFAM')) {
                    const match = regCode.match(/^ICPFFAM\d+-(\d+)$/);
                    if (match && parseInt(match[1], 10) !== 1) {
                      return 'linked';
                    }
                  }
                  // Method 3: Check old format (FAM_ prefix) - legacy support
                  if (registration.familyCode && typeof registration.familyCode === 'string' && registration.familyCode.startsWith('FAM_')) {
                    if (registration.familyPosition && registration.familyPosition !== 1) {
                      return 'linked';
                    }
                  }
                  return registration.paymentStatus;
                })())}
              </div>
              {registration.checkedIn && (
                <div>
                  <label className="text-sm text-gray-600 block mb-1">Check-In Status</label>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    Checked In
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Personal Information */}
          <div className="p-6 border-b">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Full Name</label>
                <p className="text-gray-800 font-semibold">{registration.name}</p>
              </div>
              {registration.email && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Email</label>
                  <p className="text-gray-800">{registration.email}</p>
                </div>
              )}
              {(registration.mobileNumber || registration.phone || registration.homePhone) && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Phone</label>
                  <p className="text-gray-800">{registration.mobileNumber || registration.phone || registration.homePhone}</p>
                </div>
              )}
              {registration.dateOfBirth && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Date of Birth</label>
                  <p className="text-gray-800">{new Date(registration.dateOfBirth).toLocaleDateString()}</p>
                </div>
              )}
              {registration.age && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Age</label>
                  <p className="text-gray-800">{registration.age} years</p>
                </div>
              )}
              {registration.gender && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Gender</label>
                  <p className="text-gray-800 capitalize">{registration.gender}</p>
                </div>
              )}
              {registration.camperCategory && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Camper Category</label>
                  <p className="text-gray-800">{registration.camperCategory}</p>
                </div>
              )}
              {registration.type && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Registration Type</label>
                  <p className="text-gray-800 capitalize">{registration.type}</p>
                </div>
              )}
            </div>
          </div>

          {/* Address Information */}
          {(registration.addressLine1 || registration.city || registration.state) && (
            <div className="p-6 border-b">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Address</h3>
              <div className="text-gray-800">
                {registration.addressLine1 && <p>{registration.addressLine1}</p>}
                {registration.addressLine2 && <p>{registration.addressLine2}</p>}
                {(registration.city || registration.state || registration.zip) && (
                  <p>
                    {registration.city}
                    {registration.city && registration.state ? ', ' : ''}
                    {registration.state} {registration.zip}
                  </p>
                )}
                {registration.country && <p>{registration.country}</p>}
              </div>
            </div>
          )}

          {/* Registration & Payment Details */}
          <div className="p-6 border-b">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Registration & Payment Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Amount</label>
                <p className="text-gray-800 font-semibold text-lg">${registration.amount || registration.totalAmount || '0.00'}</p>
              </div>
              {registration.paymentMethod && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Payment Method</label>
                  <p className="text-gray-800 capitalize">{registration.paymentMethod}</p>
                </div>
              )}
              {registration.transactionId && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Transaction ID</label>
                  <p className="text-gray-800 font-mono text-sm">{registration.transactionId}</p>
                </div>
              )}
              {registration.registeredAt && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Registered On</label>
                  <p className="text-gray-800">{new Date(registration.registeredAt).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</p>
                </div>
              )}
              {registration.paymentDate && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Payment Date</label>
                  <p className="text-gray-800">{new Date(registration.paymentDate).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric'
                  })}</p>
                </div>
              )}
            </div>
          </div>

          {/* Room Assignment - Only visible when check-in is enabled */}
          {selfCheckInEnabled && lodge && registration.roomNumber && (
            <div className="p-6 border-b bg-green-50">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Room Assignment</h3>
              <div className="flex items-center gap-2">
                <Bed className="w-5 h-5 text-green-600" />
                <p className="text-gray-800 font-semibold">
                  {lodge.name} - Room {registration.roomNumber}
                </p>
              </div>
            </div>
          )}

          {/* Self Check-In Section - Only visible when check-in is enabled */}
          {selfCheckInEnabled && registration && registration.status === 'confirmed' && (
            <div className="p-6 border-b bg-blue-50">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Check-In</h3>
              {registration.checkedIn ? (
                <div className="flex items-center gap-3 p-4 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800">You are checked in!</p>
                    {registration.checkInTime && (
                      <p className="text-sm text-green-700">
                        Checked in on: {new Date(registration.checkInTime).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-700 mb-4">
                    Click the button below to check yourself in for the camp.
                  </p>
                  <button
                    onClick={async () => {
                      if (window.confirm('Are you ready to check in? This action cannot be undone.')) {
                        setCheckingIn(true);
                        try {
                          // Update check-in status
                          await registrationsAPI.update(registration.id, { 
                            checkedIn: true, 
                            checkInTime: new Date().toISOString() 
                          });
                          
                          // Call onCheckIn callback if provided (to update parent state)
                          if (onCheckIn) {
                            await onCheckIn(registration.id);
                          }
                          
                          // Reload registration data from API to get updated status
                          const updated = await registrationsAPI.search('', registration.qrCode || registration.registrationCode);
                          if (updated && updated.length > 0) {
                            setRegistration(updated[0]);
                          } else {
                            // Fallback: reload from local registrations
                            loadRegistration();
                          }
                          
                          alert('Check-in successful! Welcome to ICPF Awake Camp 2026!');
                        } catch (error) {
                          console.error('Error checking in:', error);
                          alert('Failed to check in. Please try again or contact support.');
                        } finally {
                          setCheckingIn(false);
                        }
                      }
                    }}
                    disabled={checkingIn}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2 text-lg"
                  >
                    {checkingIn ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Checking In...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-6 h-6" />
                        <span>Check In Now</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Additional Information */}
          {(registration.tshirtSize || registration.allergies || registration.firstTimeAttending) && (
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {registration.tshirtSize && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">T-Shirt Size</label>
                    <p className="text-gray-800">{registration.tshirtSize}</p>
                  </div>
                )}
                {registration.firstTimeAttending && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">First Time Attending</label>
                    <p className="text-gray-800">{registration.firstTimeAttending}</p>
                  </div>
                )}
                {registration.allergies === 'yes' && registration.allergyDescription && (
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-600">Allergy Description</label>
                    <p className="text-gray-800">{registration.allergyDescription}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="p-6 bg-gray-50 border-t text-center">
            <p className="text-sm text-gray-600">
              If you have any questions about your registration, please contact us.
            </p>
            <button
              onClick={onBack}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Admin Registrations Component
function AdminRegistrations({
  registrations,
  lodges = [],
  userRole,
  onUpdateStatus,
  onUpdateRegistration,
  onDelete,
  cardPaymentEnabled = true,
  paypalEnabled = true,
  venmoEnabled = true,
  zelleEnabled = true,
  campSettings = DEFAULT_CAMP_SETTINGS,
  pricingSettings = DEFAULT_PRICING_SETTINGS,
  feeWaiverSettings = DEFAULT_FEE_WAIVER_SETTINGS
}) {
  const mergedCampForEmail = mergeCampSettings(campSettings);
  const emailEventName = (mergedCampForEmail.campTitle || '').trim() || DEFAULT_CAMP_SETTINGS.campTitle;
  const emailEventNameEsc = escapeHtmlForEmail(emailEventName);

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCamperCategory, setFilterCamperCategory] = useState('all');
  const [filterRegistrationCategory, setFilterRegistrationCategory] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all');
  const canApprove = hasPermission(userRole, 'approve_registrations');
  const canEdit = hasPermission(userRole, 'edit_registrations');
  const canReject = hasPermission(userRole, 'reject_registrations');
  const canDelete = hasPermission(userRole, 'approve_registrations') || hasPermission(userRole, 'manage_settings'); // Reg Admin or App Admin can delete
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRegistration, setExpandedRegistration] = useState(null);
  const [sortBy, setSortBy] = useState('registeredAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [originalFamilyCode, setOriginalFamilyCode] = useState(null);

  // Get unique camper categories from registrations
  const availableCamperCategories = Array.from(new Set(
    registrations
      .map(reg => reg.camperCategory || reg.data?.camperCategory)
      .filter(cat => cat && cat.trim() !== '')
  )).sort();

  // Helper function to get lodge assignment info for a registration
  const getLodgeAssignment = (reg) => {
    // Check direct lodgeId
    if (reg.lodgeId) {
      const lodge = lodges.find(l => l.id === reg.lodgeId);
      return lodge ? { lodgeName: lodge.name, roomNumber: reg.data?.roomNumber || reg.roomNumber } : null;
    }
    
    // Check data.lodgeId
    if (reg.data?.lodgeId) {
      const lodge = lodges.find(l => l.id === reg.data.lodgeId);
      return lodge ? { lodgeName: lodge.name, roomNumber: reg.data.roomNumber || reg.roomNumber } : null;
    }
    
    // Check lodges for assignments (both new and old structures)
    for (const lodge of lodges) {
      const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
      
      if (hasNewStructure) {
        // New structure: check rooms.assignedBeds
        for (const room of lodge.rooms) {
          if (room.assignedBeds && room.assignedBeds.some(bed => bed.registrationId === reg.id)) {
            const bed = room.assignedBeds.find(bed => bed.registrationId === reg.id);
            return { 
              lodgeName: lodge.name, 
              roomNumber: room.roomNumber,
              bedNumber: bed?.bedNumber 
            };
          }
        }
      } else {
        // Old structure: check assignedRooms
        if (lodge.assignedRooms && Array.isArray(lodge.assignedRooms)) {
          const assignment = lodge.assignedRooms.find(a => a.registrationId === reg.id);
          if (assignment) {
            return { 
              lodgeName: lodge.name, 
              roomNumber: assignment.roomNumber,
              bedNumber: assignment.bedNumber 
            };
          }
        }
      }
    }
    
    return null;
  };

  const filteredRegistrations = registrations
    .filter(reg => {
      const regType = reg.type || reg.data?.type || '';
      const matchesRegistrationCategory = filterRegistrationCategory === 'all' || (
        filterRegistrationCategory === 'student' && (regType === 'student' || regType === 'earlyBirdStudent' || regType === 'test')
      ) || (
        filterRegistrationCategory === 'adult' && regType === 'adult'
      ) || (
        filterRegistrationCategory === 'family' && regType && regType.startsWith('family')
      ) || (
        filterRegistrationCategory === 'day' && regType === 'day'
      );
      const matchesStatus = filterStatus === 'all' || reg.status === filterStatus;
      const matchesCamperCategory = filterCamperCategory === 'all' || 
        (reg.camperCategory || reg.data?.camperCategory || '') === filterCamperCategory;
      const matchesPaymentStatus = filterPaymentStatus === 'all' || 
        (filterPaymentStatus === 'partial' 
          ? (parseFloat(reg.partialPayment) || 0) > 0 && (parseFloat(reg.remainingPayment) || 0) > 0
          : reg.paymentStatus === filterPaymentStatus);
      const matchesSearch = 
        reg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reg.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reg.qrCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reg.mobileNumber?.includes(searchTerm);
      return matchesRegistrationCategory && matchesStatus && matchesCamperCategory && matchesPaymentStatus && matchesSearch;
    })
    .sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === 'registeredAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const handleApprove = async (registrationId) => {
    await onUpdateStatus(registrationId, 'confirmed');
    // Send confirmation email with payment details
    const registration = registrations.find(r => r.id === registrationId);
    if (registration) {
      // Check if this is a family registration
      const isFamilyRegistration = registration.familyCode && registration.familyPosition;
      let familyMembers = [];
      let totalFamilyAmount = 0;
      
      if (isFamilyRegistration) {
        // Find all family members
        familyMembers = registrations.filter(r => 
          r.familyCode === registration.familyCode && 
          r.status !== 'deleted' &&
          !r.deletedAt
        );
        // Calculate total family amount
        totalFamilyAmount = familyMembers.reduce((sum, member) => {
          return sum + (parseFloat(member.amount) || 0);
        }, 0);
      }

      const paymentMode = registration.paymentMethod || 'Not specified';
      const paymentDate = registration.paymentDate 
        ? new Date(registration.paymentDate).toLocaleDateString() 
        : registration.transactionId 
          ? new Date(registration.registeredAt).toLocaleDateString() 
          : 'Pending';
      
      const confirmationEmailItems = [
        { label: 'Registration Code', value: `<strong style="color: #3b82f6; font-family: monospace; font-size: 16px;">${registration.qrCode || registration.registrationCode}</strong>` },
        { label: 'Registration Type', value: isFamilyRegistration ? `Family (${familyMembers.length} members)` : registration.type },
        { label: 'Amount', value: isFamilyRegistration ? `<strong style="color: #ef4444; font-size: 18px;">$${totalFamilyAmount}</strong> (Total for ${familyMembers.length} family members)` : `$${registration.amount}` },
        { label: 'Payment Mode', value: escapeHtmlForEmail(paymentMethodDisplayLabel(paymentMode, campSettings)) },
        { label: 'Payment Date', value: paymentDate }
      ];
      
      if (registration.transactionId) {
        confirmationEmailItems.push({ label: 'Transaction ID', value: registration.transactionId });
      }

      // Add family members section if this is a family registration
      let familyMembersHtml = '';
      if (isFamilyRegistration && familyMembers.length > 0) {
        const familyMembersList = familyMembers.map(member => ({
          name: member.name,
          category: member.camperCategory || 'N/A',
          registrationCode: member.qrCode || member.registrationCode,
          amount: `$${member.amount || 0}`
        }));

        familyMembersHtml = `
          <div style="background-color: #f9fafb; border-radius: 8px; padding: 14px; margin: 12px 0;">
            <h3 style="margin: 0 0 15px 0; color: #374151; font-size: 18px; font-weight: 600;">Family Members Confirmed:</h3>
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background-color: #f3f4f6;">
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Name</th>
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Category</th>
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Registration Code</th>
                  <th style="padding: 10px; text-align: center; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">QR Code</th>
                  <th style="padding: 10px; text-align: right; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${familyMembersList.map((member, index) => {
                  const qrCodeUrl = buildPublicRegistrationViewUrl(member.registrationCode);
                  const qrCodeImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrCodeUrl)}`;
                  return `
                  <tr style="border-bottom: 1px solid #e5e7eb;">
                    <td style="padding: 10px; color: #374151; font-size: 14px;">${member.name}</td>
                    <td style="padding: 10px; color: #374151; font-size: 14px;">${member.category}</td>
                    <td style="padding: 10px; color: #3b82f6; font-size: 14px; font-family: monospace;">${member.registrationCode}</td>
                    <td style="padding: 10px; text-align: center;">
                      <img src="${qrCodeImageUrl}" alt="QR Code for ${member.registrationCode}" style="width: 150px; height: 150px; border: 2px solid #e5e7eb; border-radius: 4px;" />
                    </td>
                    <td style="padding: 10px; text-align: right; color: #374151; font-size: 14px; font-weight: 600;">${member.amount}</td>
                  </tr>
                `;
                }).join('')}
                <tr style="background-color: #d1fae5;">
                  <td colspan="4" style="padding: 10px; text-align: right; color: #065f46; font-size: 16px; font-weight: 600;">Total:</td>
                  <td style="padding: 10px; text-align: right; color: #065f46; font-size: 16px; font-weight: 600;">$${totalFamilyAmount}</td>
                </tr>
              </tbody>
            </table>
          </div>
        `;
      }

      const primaryAppr = isFamilyRegistration
        ? familyMembers.find((m) => m.familyPosition === 1) || registration
        : registration;
      const apprPayMethod = primaryAppr.paymentMethod || registration.paymentMethod;
      const apprPayStatus = primaryAppr.paymentStatus || registration.paymentStatus;
      const apprMemoCode = isFamilyRegistration
        ? primaryAppr.familyCode ||
          registration.familyCode ||
          primaryAppr.qrCode ||
          primaryAppr.registrationCode
        : registration.qrCode || registration.registrationCode;
      const apprNonCardPaymentMemoHtml = shouldAppendNonCardPaymentMemo(apprPayMethod, apprPayStatus)
        ? buildNonCardPaymentMemoInstructionsHtml(campSettings, apprPayMethod, apprMemoCode)
        : '';

      const confirmationEmailContent = `
        <p style="margin: 0 0 14px 0; color: #374151; font-size: 16px; line-height: 1.6;">
          Dear <strong>${registration.name}</strong>,
        </p>
        <div style="background-color: #d1fae5; border-left: 4px solid #10b981; padding: 15px; margin: 0 0 14px 0; border-radius: 4px;">
          <p style="margin: 0; color: #065f46; font-size: 16px; line-height: 1.6;">
            <strong>✅ Registration Confirmed!</strong> Your ${isFamilyRegistration ? 'family ' : ''}registration for ${emailEventNameEsc} has been confirmed!
          </p>
        </div>
        ${formatEmailSection('Registration Details', confirmationEmailItems)}
        ${familyMembersHtml}
        ${apprNonCardPaymentMemoHtml}
        <p style="margin: 12px 0 0 0; color: #374151; font-size: 16px; line-height: 1.6;">
We look forward to seeing you${isFamilyRegistration ? ' and your family' : ''} at the camp!
        </p>
      `;

      const emailBody = createEmailTemplate(confirmationEmailContent, 'Registration Confirmed');

      // Generate QR code URL for the registration (only for single registrations)
      const qrCodeText = registration.qrCode || registration.registrationCode;
      const qrCodeUrl = isFamilyRegistration ? null : buildPublicRegistrationViewUrl(qrCodeText);
      console.log('📧 Sending confirmation email for registration:', registration.id);
      console.log('   QR Code value:', qrCodeText);
      console.log('   QR Code URL:', qrCodeUrl);
      console.log('   Email:', registration.email);
      console.log('   Is family registration:', isFamilyRegistration);
      if (isFamilyRegistration) {
        console.log('   Family members count:', familyMembers.length);
        console.log('   Total family amount:', totalFamilyAmount);
        console.log('   Skipping single QR code - all QR codes are in the email table');
      }
      
      try {
        const result = await sendEmail(
        registration.email,
        `${emailEventName} Registration Confirmed`,
          emailBody,
          qrCodeUrl // null for family registrations (QR codes are in table), URL for single registrations
        );
        
        if (result.success) {
          console.log('✅ Confirmation email sent successfully');
        } else {
          console.error('❌ Failed to send confirmation email:', result.error);
          alert('Registration approved, but failed to send confirmation email. Please check server logs.');
        }
      } catch (error) {
        console.error('❌ Error sending confirmation email:', error);
        alert('Registration approved, but failed to send confirmation email. Error: ' + error.message);
      }
    }
  };

  const handleSendPaymentReminder = async (registrationId) => {
    const registration = registrations.find(r => r.id === registrationId);
    if (registration && registration.paymentStatus !== 'paid') {
      // Check if this is a family registration
      const isFamilyRegistration = registration.familyCode && registration.familyPosition;
      let familyMembers = [];
      let totalFamilyAmount = 0;
      let remainingFamilyAmount = 0;
      
      if (isFamilyRegistration) {
        // Find all family members
        familyMembers = registrations.filter(r => 
          r.familyCode === registration.familyCode && 
          r.status !== 'deleted' &&
          !r.deletedAt
        );
        // Calculate total family amount and remaining amount
        totalFamilyAmount = familyMembers.reduce((sum, member) => {
          return sum + (parseFloat(member.amount) || 0);
        }, 0);
        
        // For family registrations, check if primary member has partial payment info
        const primaryMember = familyMembers.find(m => m.familyPosition === 1) || registration;
        if (primaryMember.partialPayment !== undefined && primaryMember.remainingPayment !== undefined) {
          remainingFamilyAmount = parseFloat(primaryMember.remainingPayment) || totalFamilyAmount;
        } else {
          remainingFamilyAmount = totalFamilyAmount;
        }
      } else {
        remainingFamilyAmount = parseFloat(registration.remainingPayment) || parseFloat(registration.amount) || 0;
      }

      const paymentMethodLabel = paymentMethodDisplayLabel(registration.paymentMethod, campSettings);
      const reminderEmailItems = [
        { label: 'Registration Code', value: `<strong style="color: #3b82f6; font-family: monospace; font-size: 16px;">${registration.qrCode || registration.registrationCode}</strong>` },
        { label: 'Amount Due', value: `<strong style="color: #ef4444; font-size: 18px;">$${isFamilyRegistration ? totalFamilyAmount : registration.amount}</strong>${isFamilyRegistration ? ` (Total for ${familyMembers.length} family members)` : ''}` },
        { label: 'Payment Method', value: escapeHtmlForEmail(paymentMethodLabel) }
      ];

      if (isFamilyRegistration && remainingFamilyAmount < totalFamilyAmount) {
        reminderEmailItems.push(
          { label: 'Amount Paid', value: `$${(totalFamilyAmount - remainingFamilyAmount).toFixed(2)}` },
          { label: 'Remaining Balance', value: `<strong style="color: #ef4444; font-size: 18px;">$${remainingFamilyAmount.toFixed(2)}</strong>` }
        );
      }

      // Add family members section if this is a family registration
      let familyMembersHtml = '';
      if (isFamilyRegistration && familyMembers.length > 0) {
        const familyMembersList = familyMembers.map(member => ({
          name: member.name,
          category: member.camperCategory || 'N/A',
          registrationCode: member.qrCode || member.registrationCode,
          amount: `$${member.amount || 0}`
        }));

        familyMembersHtml = `
          <div style="background-color: #f9fafb; border-radius: 8px; padding: 14px; margin: 12px 0;">
            <h3 style="margin: 0 0 15px 0; color: #374151; font-size: 18px; font-weight: 600;">Family Members:</h3>
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background-color: #f3f4f6;">
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Name</th>
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Category</th>
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Registration Code</th>
                  <th style="padding: 10px; text-align: center; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">QR Code</th>
                  <th style="padding: 10px; text-align: right; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${familyMembersList.map((member, index) => {
                  const qrCodeUrl = buildPublicRegistrationViewUrl(member.registrationCode);
                  const qrCodeImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrCodeUrl)}`;
                  return `
                  <tr style="border-bottom: 1px solid #e5e7eb;">
                    <td style="padding: 10px; color: #374151; font-size: 14px;">${member.name}</td>
                    <td style="padding: 10px; color: #374151; font-size: 14px;">${member.category}</td>
                    <td style="padding: 10px; color: #3b82f6; font-size: 14px; font-family: monospace;">${member.registrationCode}</td>
                    <td style="padding: 10px; text-align: center;">
                      <img src="${qrCodeImageUrl}" alt="QR Code for ${member.registrationCode}" style="width: 150px; height: 150px; border: 2px solid #e5e7eb; border-radius: 4px;" />
                    </td>
                    <td style="padding: 10px; text-align: right; color: #374151; font-size: 14px; font-weight: 600;">${member.amount}</td>
                  </tr>
                `;
                }).join('')}
                <tr style="background-color: #fee2e2;">
                  <td colspan="3" style="padding: 10px; text-align: right; color: #991b1b; font-size: 16px; font-weight: 600;">Total Due:</td>
                  <td style="padding: 10px; text-align: right; color: #991b1b; font-size: 16px; font-weight: 600;">$${totalFamilyAmount}</td>
                </tr>
              </tbody>
            </table>
          </div>
        `;
      }
      
      const primaryForPay = isFamilyRegistration ? familyMembers.find((m) => m.familyPosition === 1) || registration : registration;
      const payMethodForMemo = primaryForPay.paymentMethod || registration.paymentMethod;
      const payStatusForMemo = primaryForPay.paymentStatus || registration.paymentStatus;
      const memoRegistrationCode = isFamilyRegistration
        ? primaryForPay.familyCode || registration.familyCode || primaryForPay.qrCode || primaryForPay.registrationCode
        : registration.qrCode || registration.registrationCode;
      const nonCardPaymentMemoHtml = shouldAppendNonCardPaymentMemo(payMethodForMemo, payStatusForMemo)
        ? buildNonCardPaymentMemoInstructionsHtml(campSettings, payMethodForMemo, memoRegistrationCode)
        : '';

      const reminderEmailContent = `
        <p style="margin: 0 0 14px 0; color: #374151; font-size: 16px; line-height: 1.6;">
          Dear <strong>${registration.name}</strong>,
        </p>
        <div style="background-color: #fee2e2; border-left: 4px solid #ef4444; padding: 15px; margin: 0 0 14px 0; border-radius: 4px;">
          <p style="margin: 0; color: #991b1b; font-size: 16px; line-height: 1.6;">
            <strong>⏰ Payment Reminder:</strong> This is a friendly reminder that your ${isFamilyRegistration ? 'family ' : ''}payment for ${emailEventNameEsc} is pending.
          </p>
        </div>
        ${formatEmailSection('Registration Details', reminderEmailItems)}
        ${familyMembersHtml}
        ${nonCardPaymentMemoHtml}
        <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 12px 0; border-radius: 4px;">
          <p style="margin: 0; color: #92400e; font-size: 14px; line-height: 1.6;">
            <strong>📅 Payment Deadline:</strong> February 15th<br>
Please complete your payment to confirm your ${isFamilyRegistration ? 'family ' : ''}registration. If you have already made the payment, please contact us.
          </p>
        </div>
        <div style="background-color: #dbeafe; border-left: 4px solid #3b82f6; padding: 15px; margin: 12px 0; border-radius: 4px;">
          <p style="margin: 0; color: #1e40af; font-size: 14px; line-height: 1.6;">
            <strong>💳 Important:</strong> Your registration will only be confirmed after payment is received. Please complete your payment to secure your spot.
          </p>
        </div>
      `;

      const emailBody = createEmailTemplate(reminderEmailContent, 'Payment Reminder');

      // Generate QR code URL for the registration (only for single registrations)
      const qrCodeText = registration.qrCode || registration.registrationCode;
      const qrCodeUrl = isFamilyRegistration ? null : buildPublicRegistrationViewUrl(qrCodeText);

      await sendEmail(
        registration.email,
        `Payment Reminder - ${emailEventName}`,
        emailBody,
        qrCodeUrl // null for family registrations (QR codes are in table), URL for single registrations
      );
      alert('Payment reminder email sent!');
    }
  };

  const handleResendRegistrationReceived = async (registrationId) => {
    const registration = registrations.find(r => r.id === registrationId);
    if (!registration) {
      alert('Registration not found');
      return;
    }

    // Check if this is a family registration
    const isFamilyRegistration = registration.familyCode && registration.familyPosition;
    let familyMembers = [];
    let totalFamilyAmount = 0;
    let primaryMember = registration;

    if (isFamilyRegistration) {
      // Find all family members
      familyMembers = registrations.filter(r => 
        r.familyCode === registration.familyCode && 
        r.status !== 'deleted' &&
        !r.deletedAt
      );
      // Get primary member (familyPosition === 1)
      primaryMember = familyMembers.find(m => m.familyPosition === 1) || registration;
      // Calculate total family amount
      totalFamilyAmount = familyMembers.reduce((sum, member) => {
        return sum + (parseFloat(member.amount) || 0);
      }, 0);
    }

    try {
      if (isFamilyRegistration && familyMembers.length > 0) {
        // Build family members list for email
        const familyMembersList = familyMembers.map((reg) => {
          const memberAmount = parseFloat(reg.amount);
          const displayAmt = Number.isFinite(memberAmount) ? memberAmount : 0;
          return {
            name: reg.name,
            category: reg.camperCategory || 'N/A',
            registrationCode: reg.qrCode || reg.registrationCode,
            amount: `$${displayAmt.toFixed(2)}`
          };
        });

        const familyCode = primaryMember.familyCode || registration.familyCode;
        const paymentMethod = primaryMember.paymentMethod || registration.paymentMethod || 'Not specified';
        const paymentMethodForMemo = primaryMember.paymentMethod || registration.paymentMethod || '';
        const familyFullyCovered =
          totalFamilyAmount <= 0.005 || registrationHasNoPaymentDue(primaryMember);

        const familyEmailItems = [
          { label: 'Family Code', value: `<strong style="color: #3b82f6; font-family: monospace; font-size: 16px;">${familyCode}</strong>` },
          { label: 'Number of Family Members', value: familyMembers.length },
          {
            label: 'Total Amount',
            value: `<strong style="color: #ef4444; font-size: 18px;">$${totalFamilyAmount.toFixed(2)}</strong>`
          }
        ];

        if (familyFullyCovered) {
          familyEmailItems.push(
            {
              label: 'Payment',
              value:
                '<span style="color: #059669; font-weight: 600;">You are all set — no payment is required.</span> Sponsor waivers/discounts cover your family&apos;s fees as shown.'
            },
            {
              label: 'Payment status',
              value: '<span style="color: #059669; font-weight: 600;">Paid (fully covered)</span>'
            }
          );
        } else {
          familyEmailItems.push({
            label: 'Payment Method',
            value: escapeHtmlForEmail(paymentMethodDisplayLabel(paymentMethod, campSettings))
          });

          // Check for installment plan
          if (primaryMember.installmentPlan) {
            const plan = primaryMember.installmentPlan;
            familyEmailItems.push(
              { label: 'Payment Plan', value: `${plan.totalInstallments} Installments` },
              { label: 'Current Installment', value: `Installment ${plan.currentInstallment} of ${plan.totalInstallments}` },
              { label: 'Amount Paid', value: `$${plan.paidAmount || 0}` },
              { label: 'Remaining Balance', value: `$${plan.remainingAmount || totalFamilyAmount}` }
            );
          }

          // Payment status
          if (primaryMember.paymentStatus === 'paid') {
            familyEmailItems.push(
              { label: 'Payment Status', value: '<span style="color: #10b981; font-weight: 600;">Paid</span>' },
              ...(primaryMember.transactionId ? [{ label: 'Transaction ID', value: primaryMember.transactionId }] : [])
            );
          } else if (primaryMember.paymentStatus === 'partial') {
            const paid = primaryMember.installmentPlan?.paidAmount || primaryMember.partialPayment || 0;
            const remaining =
              primaryMember.installmentPlan?.remainingAmount ||
              primaryMember.remainingPayment ||
              totalFamilyAmount - paid;
            familyEmailItems.push(
              { label: 'Payment Status', value: '<span style="color: #f59e0b; font-weight: 600;">Partial Payment</span>' },
              { label: 'Amount Paid', value: `$${paid}` },
              { label: 'Remaining', value: `$${remaining}` },
              ...(primaryMember.transactionId ? [{ label: 'Transaction ID', value: primaryMember.transactionId }] : [])
            );
          } else {
            familyEmailItems.push({
              label: 'Payment Status',
              value: '<span style="color: #ef4444; font-weight: 600;">Pending</span>'
            });
          }
        }

        // Add family members section with QR codes
        const familyMembersHtml = `
          <div style="background-color: #f9fafb; border-radius: 8px; padding: 14px; margin: 12px 0;">
            <h3 style="margin: 0 0 15px 0; color: #374151; font-size: 18px; font-weight: 600;">Family Members Registered:</h3>
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background-color: #f3f4f6;">
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Name</th>
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Category</th>
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Registration Code</th>
                  <th style="padding: 10px; text-align: center; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">QR Code</th>
                  <th style="padding: 10px; text-align: right; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${familyMembersList.map((member, index) => {
                  const qrCodeUrl = buildPublicRegistrationViewUrl(member.registrationCode);
                  const qrCodeImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrCodeUrl)}`;
                  return `
                  <tr style="border-bottom: 1px solid #e5e7eb;">
                    <td style="padding: 10px; color: #374151; font-size: 14px;">${member.name}</td>
                    <td style="padding: 10px; color: #374151; font-size: 14px;">${member.category}</td>
                    <td style="padding: 10px; color: #3b82f6; font-size: 14px; font-family: monospace;">${member.registrationCode}</td>
                    <td style="padding: 10px; text-align: center;">
                      <img src="${qrCodeImageUrl}" alt="QR Code for ${member.registrationCode}" style="width: 150px; height: 150px; border: 2px solid #e5e7eb; border-radius: 4px;" />
                    </td>
                    <td style="padding: 10px; text-align: right; color: #374151; font-size: 14px; font-weight: 600;">${member.amount}</td>
                  </tr>
                `;
                }).join('')}
                <tr style="background-color: #fef3c7;">
                  <td colspan="4" style="padding: 10px; text-align: right; color: #92400e; font-size: 16px; font-weight: 600;">Total:</td>
                  <td style="padding: 10px; text-align: right; color: #92400e; font-size: 16px; font-weight: 600;">$${Number(totalFamilyAmount).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        `;
        
        const familyEmailContent = `
          <p style="margin: 0 0 14px 0; color: #374151; font-size: 16px; line-height: 1.6;">
            Dear <strong>${primaryMember.name}</strong>,
          </p>
          <p style="margin: 0 0 14px 0; color: #374151; font-size: 16px; line-height: 1.6;">
            Thank you for registering your family for <strong>${emailEventNameEsc}</strong>!
          </p>
          ${formatEmailSection('Registration Details', familyEmailItems)}
          ${familyMembersHtml}
          <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 12px 0; border-radius: 4px;">
            <p style="margin: 0; color: #92400e; font-size: 14px; line-height: 1.6;">
              <strong>⏳ Pending Approval:</strong> Your registration is now pending admin approval. You will receive a confirmation email once your registration has been reviewed and approved.
            </p>
          </div>
          ${
            familyFullyCovered
              ? `<div style="background-color: #ecfdf5; border-left: 4px solid #059669; padding: 15px; margin: 12px 0; border-radius: 4px;">
            <p style="margin: 0; color: #065f46; font-size: 14px; line-height: 1.6;">
              <strong>Payment complete:</strong> We recorded your family&apos;s discounts and/or sponsor waivers. You do not need to send a separate payment for the total shown above.
            </p>
          </div>`
              : `<div style="background-color: #dbeafe; border-left: 4px solid #3b82f6; padding: 15px; margin: 12px 0; border-radius: 4px;">
            <p style="margin: 0; color: #1e40af; font-size: 14px; line-height: 1.6;">
              <strong>💳 Important:</strong> Your registration will only be confirmed after payment is received. Please complete your payment to secure your spot.
            </p>
          </div>`
          }
          ${
            !familyFullyCovered &&
            shouldAppendNonCardPaymentMemo(paymentMethodForMemo, primaryMember.paymentStatus)
              ? buildNonCardPaymentMemoInstructionsHtml(
                  campSettings,
                  paymentMethodForMemo,
                  familyCode || primaryMember.qrCode || primaryMember.registrationCode || ''
                )
              : ''
          }
        `;
        
        const familyEmailBody = createEmailTemplate(familyEmailContent, 'Family Registration Received');

        // For family registrations, don't send a single QR code since all QR codes are already in the email table
        const result = await sendEmail(
          primaryMember.email || registration.email,
          `${emailEventName} Family Registration Received`,
          familyEmailBody,
          null // No single QR code needed - all QR codes are in the email table
        );
        
        if (result.success) {
          console.log('✅ Family registration received email resent successfully');
          alert('Family registration received email resent successfully!');
        } else {
          console.error('❌ Failed to resend family registration received email:', result.error);
          alert('Failed to resend registration received email: ' + (result.error || 'Unknown error'));
        }
      } else {
        // Single registration
        const registrationType = registration.type || 'Registration';
        const isGuestSpeaker = registration.camperCategory === 'Guest Speaker';
        
        const noPaymentDue = !isGuestSpeaker && registrationHasNoPaymentDue(registration);

        const registrationEmailItems = [
          { label: 'Registration Code', value: `<strong style="color: #3b82f6; font-family: monospace; font-size: 16px;">${registration.qrCode || registration.registrationCode}</strong>` },
          { label: 'Registration Type', value: registrationType === 'test' ? 'Test Registration' : registrationType === 'student' ? 'Student' : registrationType === 'earlyBirdStudent' ? 'Early Bird Student' : registrationType === 'adult' ? 'Adult' : registrationType }
        ];
        
        if (isGuestSpeaker) {
          registrationEmailItems.push(
            { label: 'Camper Category', value: 'Guest Speaker' },
            { label: 'Amount', value: '$0 (Fee Waived)' },
            { label: 'Payment Status', value: '<span style="color: #10b981; font-weight: 600;">Waived (No payment required)</span>' }
          );
        } else if (noPaymentDue) {
          const amt = Number.isFinite(parseFloat(registration.amount)) ? parseFloat(registration.amount) : 0;
          registrationEmailItems.push(
            { label: 'Amount', value: `$${amt.toFixed(2)}` },
            {
              label: 'Payment',
              value:
                '<span style="color: #059669; font-weight: 600;">You are all set — no payment is required.</span> Your sponsor waiver or discount covers your registration fee.'
            },
            {
              label: 'Payment status',
              value: '<span style="color: #059669; font-weight: 600;">Paid (fee covered by waiver or discount)</span>'
            }
          );
          const sponsor = (registration.feeWaiverSponsorName || '').trim();
          if (sponsor) {
            registrationEmailItems.push({ label: 'Sponsor', value: escapeHtmlForEmail(sponsor) });
          }
        } else {
          const amt = Number.isFinite(parseFloat(registration.amount)) ? parseFloat(registration.amount) : 0;
          registrationEmailItems.push(
            { label: 'Amount', value: `$${amt.toFixed(2)}` },
            { label: 'Payment Method', value: escapeHtmlForEmail(paymentMethodDisplayLabel(registration.paymentMethod, campSettings)) }
          );
          
          if (registration.paymentMethod === 'card' && registration.paymentStatus === 'paid') {
            registrationEmailItems.push(
              { label: 'Payment Status', value: '<span style="color: #10b981; font-weight: 600;">Paid</span>' },
              ...(registration.transactionId ? [{ label: 'Transaction ID', value: registration.transactionId }] : [])
            );
          } else if (registration.paymentStatus === 'paid') {
            registrationEmailItems.push({
              label: 'Payment Status',
              value: '<span style="color: #10b981; font-weight: 600;">Paid</span>'
            });
          } else if (registration.paymentStatus === 'partial') {
            registrationEmailItems.push({
              label: 'Payment Status',
              value: '<span style="color: #f59e0b; font-weight: 600;">Partial payment</span>'
            });
          } else {
            registrationEmailItems.push({ label: 'Payment Status', value: '<span style="color: #ef4444; font-weight: 600;">Pending</span>' });
          }
        }
        
        const resendPaymentFollowUp = isGuestSpeaker
          ? ''
          : noPaymentDue
            ? `<div style="background-color: #ecfdf5; border-left: 4px solid #059669; padding: 15px; margin: 12px 0; border-radius: 4px;">
          <p style="margin: 0; color: #065f46; font-size: 14px; line-height: 1.6;">
            <strong>Payment complete:</strong> We recorded your discount or sponsor waiver. You do not need to send a separate payment for the amount shown above.
          </p>
        </div>`
            : `<div style="background-color: #dbeafe; border-left: 4px solid #3b82f6; padding: 15px; margin: 12px 0; border-radius: 4px;">
          <p style="margin: 0; color: #1e40af; font-size: 14px; line-height: 1.6;">
            <strong>💳 Important:</strong> Your registration will only be confirmed after payment is received. Please complete your payment to secure your spot.
          </p>
        </div>`;
        
        const registrationEmailContent = `
          <p style="margin: 0 0 14px 0; color: #374151; font-size: 16px; line-height: 1.6;">
            Dear <strong>${registration.name}</strong>,
          </p>
          <p style="margin: 0 0 14px 0; color: #374151; font-size: 16px; line-height: 1.6;">
            Thank you for registering for <strong>${emailEventNameEsc}</strong>!
          </p>
          ${formatEmailSection('Registration Details', registrationEmailItems)}
          <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 12px 0; border-radius: 4px;">
            <p style="margin: 0; color: #92400e; font-size: 14px; line-height: 1.6;">
              <strong>⏳ Pending Approval:</strong> Your registration is now pending admin approval. You will receive a confirmation email once your registration has been reviewed and approved.
            </p>
          </div>
          ${resendPaymentFollowUp}
          ${
            !isGuestSpeaker &&
            !noPaymentDue &&
            shouldAppendNonCardPaymentMemo(registration.paymentMethod || '', registration.paymentStatus)
              ? buildNonCardPaymentMemoInstructionsHtml(
                  campSettings,
                  registration.paymentMethod || '',
                  registration.qrCode || registration.registrationCode || ''
                )
              : ''
          }
        `;
        
        const registrationEmailBody = createEmailTemplate(registrationEmailContent, 'Registration Received');

        // Generate QR code URL for the registration
        const qrCodeText = registration.qrCode || registration.registrationCode;
        const qrCodeUrl = buildPublicRegistrationViewUrl(qrCodeText);

        const result = await sendEmail(
          registration.email,
          `${emailEventName} Registration Received`,
          registrationEmailBody,
          qrCodeUrl
        );
        
        if (result.success) {
          console.log('✅ Registration received email resent successfully');
          alert('Registration received email resent successfully!');
        } else {
          console.error('❌ Failed to resend registration received email:', result.error);
          alert('Failed to resend registration received email: ' + (result.error || 'Unknown error'));
        }
      }
    } catch (error) {
      console.error('❌ Error resending registration received email:', error);
      alert('Failed to resend registration received email. Error: ' + error.message);
    }
  };

  const handleResendConfirmation = async (registrationId) => {
    const registration = registrations.find(r => r.id === registrationId);
    if (!registration) {
      alert('Registration not found');
      return;
    }
    
    if (registration.status !== 'confirmed') {
      alert('Can only resend confirmation email for confirmed registrations');
      return;
    }
    
    // Check if this is a family registration
    const isFamilyRegistration = registration.familyCode && registration.familyPosition;
    let familyMembers = [];
    let totalFamilyAmount = 0;
    
    if (isFamilyRegistration) {
      // Find all family members
      familyMembers = registrations.filter(r => 
        r.familyCode === registration.familyCode && 
        r.status !== 'deleted' &&
        !r.deletedAt
      );
      // Calculate total family amount
      totalFamilyAmount = familyMembers.reduce((sum, member) => {
        return sum + (parseFloat(member.amount) || 0);
      }, 0);
    }
    
      const paymentMode = registration.paymentMethod || 'Not specified';
      const paymentDate = registration.paymentDate 
        ? new Date(registration.paymentDate).toLocaleDateString() 
        : registration.transactionId 
          ? new Date(registration.registeredAt).toLocaleDateString() 
          : 'Pending';
      
      const resendConfirmationEmailItems = [
        { label: 'Registration Code', value: `<strong style="color: #3b82f6; font-family: monospace; font-size: 16px;">${registration.qrCode || registration.registrationCode}</strong>` },
        { label: 'Registration Type', value: isFamilyRegistration ? `Family (${familyMembers.length} members)` : registration.type },
        { label: 'Amount', value: isFamilyRegistration ? `<strong style="color: #ef4444; font-size: 18px;">$${totalFamilyAmount}</strong> (Total for ${familyMembers.length} family members)` : `$${registration.amount}` },
        { label: 'Payment Mode', value: escapeHtmlForEmail(paymentMethodDisplayLabel(paymentMode, campSettings)) },
        { label: 'Payment Date', value: paymentDate }
      ];
      
      if (registration.transactionId) {
        resendConfirmationEmailItems.push({ label: 'Transaction ID', value: registration.transactionId });
      }

      // Add family members section if this is a family registration
      let familyMembersHtml = '';
      if (isFamilyRegistration && familyMembers.length > 0) {
        const familyMembersList = familyMembers.map(member => ({
          name: member.name,
          category: member.camperCategory || 'N/A',
          registrationCode: member.qrCode || member.registrationCode,
          amount: `$${member.amount || 0}`
        }));

        familyMembersHtml = `
          <div style="background-color: #f9fafb; border-radius: 8px; padding: 14px; margin: 12px 0;">
            <h3 style="margin: 0 0 15px 0; color: #374151; font-size: 18px; font-weight: 600;">Family Members Confirmed:</h3>
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background-color: #f3f4f6;">
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Name</th>
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Category</th>
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Registration Code</th>
                  <th style="padding: 10px; text-align: center; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">QR Code</th>
                  <th style="padding: 10px; text-align: right; border-bottom: 2px solid #e5e7eb; color: #374151; font-size: 14px; font-weight: 600;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${familyMembersList.map((member, index) => {
                  const qrCodeUrl = buildPublicRegistrationViewUrl(member.registrationCode);
                  const qrCodeImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrCodeUrl)}`;
                  return `
                  <tr style="border-bottom: 1px solid #e5e7eb;">
                    <td style="padding: 10px; color: #374151; font-size: 14px;">${member.name}</td>
                    <td style="padding: 10px; color: #374151; font-size: 14px;">${member.category}</td>
                    <td style="padding: 10px; color: #3b82f6; font-size: 14px; font-family: monospace;">${member.registrationCode}</td>
                    <td style="padding: 10px; text-align: center;">
                      <img src="${qrCodeImageUrl}" alt="QR Code for ${member.registrationCode}" style="width: 150px; height: 150px; border: 2px solid #e5e7eb; border-radius: 4px;" />
                    </td>
                    <td style="padding: 10px; text-align: right; color: #374151; font-size: 14px; font-weight: 600;">${member.amount}</td>
                  </tr>
                `;
                }).join('')}
                <tr style="background-color: #d1fae5;">
                  <td colspan="4" style="padding: 10px; text-align: right; color: #065f46; font-size: 16px; font-weight: 600;">Total:</td>
                  <td style="padding: 10px; text-align: right; color: #065f46; font-size: 16px; font-weight: 600;">$${totalFamilyAmount}</td>
                </tr>
              </tbody>
            </table>
          </div>
        `;
      }

      const primaryResend = isFamilyRegistration
        ? familyMembers.find((m) => m.familyPosition === 1) || registration
        : registration;
      const resendPayMethod = primaryResend.paymentMethod || registration.paymentMethod;
      const resendPayStatus = primaryResend.paymentStatus || registration.paymentStatus;
      const resendMemoCode = isFamilyRegistration
        ? primaryResend.familyCode ||
          registration.familyCode ||
          primaryResend.qrCode ||
          primaryResend.registrationCode
        : registration.qrCode || registration.registrationCode;
      const resendNonCardPaymentMemoHtml = shouldAppendNonCardPaymentMemo(resendPayMethod, resendPayStatus)
        ? buildNonCardPaymentMemoInstructionsHtml(campSettings, resendPayMethod, resendMemoCode)
        : '';

      const resendConfirmationEmailContent = `
        <p style="margin: 0 0 14px 0; color: #374151; font-size: 16px; line-height: 1.6;">
          Dear <strong>${registration.name}</strong>,
        </p>
        <div style="background-color: #d1fae5; border-left: 4px solid #10b981; padding: 15px; margin: 0 0 14px 0; border-radius: 4px;">
          <p style="margin: 0; color: #065f46; font-size: 16px; line-height: 1.6;">
            <strong>✅ Registration Confirmed!</strong> Your ${isFamilyRegistration ? 'family ' : ''}registration for ${emailEventNameEsc} has been confirmed!
          </p>
        </div>
        ${formatEmailSection('Registration Details', resendConfirmationEmailItems)}
        ${familyMembersHtml}
        ${resendNonCardPaymentMemoHtml}
        <p style="margin: 12px 0 0 0; color: #374151; font-size: 16px; line-height: 1.6;">
We look forward to seeing you${isFamilyRegistration ? ' and your family' : ''} at the camp!
        </p>
      `;

      const emailBody = createEmailTemplate(resendConfirmationEmailContent, 'Registration Confirmed');

    // Generate QR code URL for the registration (only for single registrations)
    const qrCodeText = registration.qrCode || registration.registrationCode;
    const qrCodeUrl = isFamilyRegistration ? null : buildPublicRegistrationViewUrl(qrCodeText);
    console.log('📧 Resending confirmation email for registration:', registration.id);
    console.log('   QR Code value:', qrCodeText);
    console.log('   QR Code URL:', qrCodeUrl);
    console.log('   Email:', registration.email);
    console.log('   Is family registration:', isFamilyRegistration);
    if (isFamilyRegistration) {
      console.log('   Family members count:', familyMembers.length);
      console.log('   Total family amount:', totalFamilyAmount);
      console.log('   Skipping single QR code - all QR codes are in the email table');
    }
    
    try {
      const result = await sendEmail(
        registration.email,
        `${emailEventName} Registration Confirmed`,
        emailBody,
        qrCodeUrl // null for family registrations (QR codes are in table), URL for single registrations
      );
      
      if (result.success) {
        console.log('✅ Confirmation email resent successfully');
        alert('Confirmation email resent successfully!');
      } else {
        console.error('❌ Failed to resend confirmation email:', result.error);
        alert('Failed to resend confirmation email: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error resending confirmation email:', error);
      alert('Failed to resend confirmation email. Error: ' + error.message);
    }
  };

  const handleReject = (registrationId) => {
    if (window.confirm('Are you sure you want to reject this registration?')) {
      onUpdateStatus(registrationId, 'rejected');
      const registration = registrations.find(r => r.id === registrationId);
      if (registration) {
        const rejectionEmailContent = `
          <p style="margin: 0 0 20px 0; color: #374151; font-size: 16px; line-height: 1.6;">
            Dear ${registration.name},
          </p>
          <p style="margin: 0 0 20px 0; color: #374151; font-size: 16px; line-height: 1.6;">
            Your registration has been reviewed. Please contact us for more information.
          </p>
        `;
        const emailBody = createEmailTemplate(rejectionEmailContent, 'Registration Update');
        sendEmail(
          registration.email,
          `${emailEventName} Registration Update`,
          emailBody
        );
      }
    }
  };

  const handleDelete = async (registrationId) => {
    const registration = registrations.find(r => r.id === registrationId);
    if (window.confirm(`Are you sure you want to delete the registration for ${registration?.name || 'this person'}? This will move it to the deleted container.`)) {
      if (onDelete) {
        await onDelete(registrationId);
      }
    }
  };

  const handleEdit = (registration) => {
    // If this is a non-primary family member, load payment info from primary member
    let paymentInfo = {
      paymentMethod: registration.paymentMethod || '',
      paymentDate: registration.paymentDate || '',
      paymentConfirmation: registration.paymentConfirmation || '',
      paymentStatus: registration.paymentStatus || 'pending',
      partialPayment: (registration.partialPayment !== undefined && registration.partialPayment !== null && registration.partialPayment !== '') 
        ? (isNaN(parseFloat(registration.partialPayment)) ? 0 : parseFloat(registration.partialPayment)) 
        : 0,
      remainingPayment: registration.remainingPayment || (registration.amount ? Math.max(0, (parseFloat(registration.amount) || 0) - (parseFloat(registration.partialPayment) || 0)) : 0),
    };
    
    if (registration.familyCode && registration.familyPosition !== 1) {
      // Find the primary member (familyPosition === 1)
      const primaryMember = registrations.find(r => 
        r.familyCode === registration.familyCode && 
        r.familyPosition === 1
      );
      
      if (primaryMember) {
        // Use primary member's payment info
        paymentInfo = {
          paymentMethod: primaryMember.paymentMethod || '',
          paymentDate: primaryMember.paymentDate || '',
          paymentConfirmation: primaryMember.paymentConfirmation || '',
          paymentStatus: primaryMember.paymentStatus || 'pending',
          partialPayment: (primaryMember.partialPayment !== undefined && primaryMember.partialPayment !== null && primaryMember.partialPayment !== '') 
            ? (isNaN(parseFloat(primaryMember.partialPayment)) ? 0 : parseFloat(primaryMember.partialPayment)) 
            : 0,
          remainingPayment: registration.remainingPayment || (registration.amount ? Math.max(0, (parseFloat(registration.amount) || 0)) : 0),
        };
      }
    }
    
    setEditFormData({
      firstName: registration.firstName || registration.data?.firstName || '',
      lastName: registration.lastName || registration.data?.lastName || '',
      middleName: registration.middleName || registration.data?.middleName || '',
      gender: registration.gender || registration.data?.gender || '',
      camperCategory: registration.camperCategory || registration.data?.camperCategory || '',
      email: registration.email || registration.data?.email || '',
      mobileNumber: registration.mobileNumber || registration.data?.mobileNumber || '',
      homePhone: registration.homePhone || registration.data?.homePhone || '',
      churchName: registration.churchName || registration.data?.churchName || '',
      pastorName: registration.pastorName || registration.data?.pastorName || '',
      addressLine1: registration.addressLine1 || registration.data?.addressLine1 || '',
      addressLine2: registration.addressLine2 || registration.data?.addressLine2 || '',
      city: registration.city || registration.data?.city || '',
      state: registration.state || registration.data?.state || '',
      zip: registration.zip || registration.data?.zip || '',
      country: registration.country || registration.data?.country || 'United States',
      countryCode: registration.countryCode || registration.data?.countryCode || 'United States +1',
      firstTimeAttending: registration.firstTimeAttending || registration.data?.firstTimeAttending || '',
      dateOfBirth: registration.dateOfBirth || registration.data?.dateOfBirth || '',
      age: registration.age || registration.data?.age || '',
      tshirtSize: registration.tshirtSize || registration.data?.tshirtSize || '',
      allergies: registration.allergies || registration.data?.allergies || '',
      allergyDescription: registration.allergyDescription || registration.data?.allergyDescription || '',
      emergencyContactName: registration.emergencyContactName || registration.data?.emergencyContactName || '',
      emergencyContactRelationship: registration.emergencyContactRelationship || registration.data?.emergencyContactRelationship || '',
      emergencyContactPhone: registration.emergencyContactPhone || registration.data?.emergencyContactPhone || '',
      parentGuardianName: registration.parentGuardianName || registration.data?.parentGuardianName || '',
      parentGuardianEmail: registration.parentGuardianEmail || registration.data?.parentGuardianEmail || '',
      parentGuardianPhone: registration.parentGuardianPhone || registration.data?.parentGuardianPhone || '',
      stayPreference: registration.stayPreference || registration.data?.stayPreference || '',
      attendanceDays: registration.attendanceDays || registration.data?.attendanceDays || '',
      type: registration.type || registration.data?.type || '',
      amount: registration.amount || registration.data?.amount || 0,
      ...paymentInfo,
      status: registration.status || registration.data?.status || 'pending',
      specialRequests: registration.specialRequests || registration.data?.specialRequests || '',
      roomAssignmentNotes: registration.roomAssignmentNotes || registration.data?.roomAssignmentNotes || '',
      // Store registration code for editing (only for admins)
      qrCode: registration.qrCode || registration.registrationCode || '',
      registrationCode: registration.registrationCode || registration.qrCode || '',
      // Store family info for EditRegistrationModal
      familyCode: registration.familyCode,
      familyPosition: registration.familyPosition,
      feeWaiverCode: registration.feeWaiverCode || '',
      discountCode: registration.discountCode || '',
      feeWaiverSponsorName: registration.feeWaiverSponsorName || '',
      amountBeforeCredits:
        registration.amountBeforeCredits != null && registration.amountBeforeCredits !== ''
          ? registration.amountBeforeCredits
          : ''
    });
    setEditingRegistration(registration.id);
    // Store original family code to detect changes
    setOriginalFamilyCode(registration.familyCode);
  };

  const handleSaveEdit = async () => {
    if (editingRegistration && editFormData && registrations) {
      const registration = registrations.find(r => r && r.id === editingRegistration);
      if (!registration) {
        alert('Registration not found');
        return;
      }
      
      // Validate registration code for duplicates
      const newRegistrationCode = editFormData.qrCode || editFormData.registrationCode;
      if (newRegistrationCode) {
        const isDuplicate = isRegistrationCodeDuplicate(newRegistrationCode, registrations, editingRegistration);
        if (isDuplicate) {
          alert(`Error: Registration code "${newRegistrationCode}" is already in use by another registration. Please use a unique code.`);
          return;
        }
      }
      
      // Family code is allowed to be shared by multiple registrations (linkage); no uniqueness check on family code when editing.
      const isNonPrimaryFamilyMember = registration?.familyCode && registration?.familyPosition !== 1;
      const isFamilyRegistration = registration?.familyCode && registration?.familyPosition;
      const oldFamilyCode = originalFamilyCode || registration?.familyCode;
      const newFamilyCode = editFormData.familyCode;
      const familyCodeChanged = oldFamilyCode && newFamilyCode && oldFamilyCode !== newFamilyCode;
      
      // Read the latest values directly from editFormData
      // Ensure partialPayment and remainingPayment are explicitly included and properly typed
      const partialPaymentValue = editFormData.partialPayment !== undefined && editFormData.partialPayment !== null 
        ? (typeof editFormData.partialPayment === 'string' ? parseFloat(editFormData.partialPayment) : Number(editFormData.partialPayment)) || 0 
        : 0;
      const remainingPaymentValue = editFormData.remainingPayment !== undefined && editFormData.remainingPayment !== null 
        ? (typeof editFormData.remainingPayment === 'string' ? parseFloat(editFormData.remainingPayment) : Number(editFormData.remainingPayment)) || 0 
        : 0;
      
      // For non-primary family members, exclude payment fields from save
      // Payment info is managed through the primary member
      const dataToSave = isNonPrimaryFamilyMember ? {
        ...editFormData,
        // Exclude payment-related fields
        paymentMethod: undefined,
        paymentDate: undefined,
        paymentConfirmation: undefined,
        paymentStatus: undefined,
        partialPayment: undefined,
        remainingPayment: undefined,
      } : {
        ...editFormData,
        partialPayment: partialPaymentValue,
        remainingPayment: remainingPaymentValue,
      };
      
      // If family code changed, ask admin if they want to update all family members or just this one
      if (familyCodeChanged && isFamilyRegistration && registrations && Array.isArray(registrations)) {
        // Find all family members
        const familyMembers = registrations.filter(r => 
          r && 
          r.familyCode === oldFamilyCode && 
          r.status !== 'deleted' &&
          !r.deletedAt
        );
        
        if (familyMembers.length > 1) {
          // Ask admin if they want to update all family members or just this one
          const updateAll = window.confirm(
            `Family code changed from ${oldFamilyCode} to ${newFamilyCode}.\n\n` +
            `Found ${familyMembers.length} family members with this family code.\n\n` +
            `Click OK to update ALL ${familyMembers.length} family members' registration codes.\n` +
            `Click Cancel to update ONLY this registration (${registration.name}).`
          );
          
          if (updateAll) {
            // Update all family members
            try {
              console.log(`🔄 Family code changed from ${oldFamilyCode} to ${newFamilyCode}`);
              console.log(`   Updating all ${familyMembers.length} family members`);
              
              // First, validate that none of the new registration codes would be duplicates
              const duplicateCodes = [];
              for (const member of familyMembers) {
                const memberPosition = member.familyPosition || 1;
                const newRegistrationCode = `${newFamilyCode}-${memberPosition}`;
                
                // Check if this code would be a duplicate (excluding current family members)
                const wouldBeDuplicate = registrations.some(r => {
                  if (!r || r.status === 'deleted' || r.deletedAt) return false;
                  // Exclude current family members from duplicate check
                  if (r.familyCode === oldFamilyCode) return false;
                  
                  const regCode = (r.qrCode || r.registrationCode || '').toUpperCase().trim();
                  return regCode === newRegistrationCode.toUpperCase().trim() && regCode !== '';
                });
                
                if (wouldBeDuplicate) {
                  duplicateCodes.push({ member: member.name, code: newRegistrationCode });
                }
              }
              
              if (duplicateCodes.length > 0) {
                const duplicateList = duplicateCodes.map(d => `  - ${d.member}: ${d.code}`).join('\n');
                alert(`Error: The new family code "${newFamilyCode}" would create duplicate registration codes:\n\n${duplicateList}\n\nPlease use a different family code.`);
                return; // Don't save
              }
              
              // Update each family member's registration code
              for (const member of familyMembers) {
                const memberPosition = member.familyPosition || 1;
                const newRegistrationCode = `${newFamilyCode}-${memberPosition}`;
                
                const memberUpdate = {
                  familyCode: newFamilyCode,
                  qrCode: newRegistrationCode,
                  registrationCode: newRegistrationCode,
                };
                
                console.log(`   Updating member ${member.name} (${member.id}): ${member.qrCode || member.registrationCode} → ${newRegistrationCode}`);
                
                // Update the member directly via API (bypass onUpdateRegistration to avoid reloading)
                try {
                  if (typeof registrationsAPI !== 'undefined' && registrationsAPI.update) {
                    await registrationsAPI.update(member.id, memberUpdate);
                  } else {
                    console.error('registrationsAPI is not available');
                  }
                } catch (error) {
                  console.error(`Error updating family member ${member.id}:`, error);
                }
              }
              
              // Update the current registration's registration code based on its position
              const currentPosition = registration.familyPosition || 1;
              const newRegistrationCode = `${newFamilyCode}-${currentPosition}`;
              dataToSave.qrCode = newRegistrationCode;
              dataToSave.registrationCode = newRegistrationCode;
              
              // Show confirmation message
              alert(`Family code updated from ${oldFamilyCode} to ${newFamilyCode}. All ${familyMembers.length} family members' registration codes have been updated.`);
            } catch (error) {
              console.error('Error updating family code:', error);
              alert('Error updating family code. Please try again or contact support.');
            }
          } else {
            // Update only this registration - keep the new family code but don't update other members
            // Update the current registration's registration code based on its position
            const currentPosition = registration.familyPosition || 1;
            const newRegistrationCode = `${newFamilyCode}-${currentPosition}`;
            dataToSave.qrCode = newRegistrationCode;
            dataToSave.registrationCode = newRegistrationCode;
            
            // Note: Other family members will still have the old family code
            console.log(`🔄 Family code updated for single registration only: ${oldFamilyCode} → ${newFamilyCode}`);
            console.log(`   Other family members still have family code: ${oldFamilyCode}`);
          }
        } else {
          // Only one family member, just update it
          const currentPosition = registration.familyPosition || 1;
          const newRegistrationCode = `${newFamilyCode}-${currentPosition}`;
          dataToSave.qrCode = newRegistrationCode;
          dataToSave.registrationCode = newRegistrationCode;
        }
      } else {
        // Ensure registration code is saved (use qrCode if registrationCode is empty, or vice versa)
        if (dataToSave.qrCode && !dataToSave.registrationCode) {
          dataToSave.registrationCode = dataToSave.qrCode;
        } else if (dataToSave.registrationCode && !dataToSave.qrCode) {
          dataToSave.qrCode = dataToSave.registrationCode;
        }
      }
      
      // Ensure roomAssignmentNotes is persisted in data object (backend deep-merges data; display reads from both)
      const notesValue = (editFormData.roomAssignmentNotes !== undefined && editFormData.roomAssignmentNotes !== null)
        ? String(editFormData.roomAssignmentNotes)
        : '';
      dataToSave.data = { ...(registration.data || {}), roomAssignmentNotes: notesValue };
      if (dataToSave.roomAssignmentNotes === undefined) {
        dataToSave.roomAssignmentNotes = notesValue;
      }
      
      // Remove undefined fields
      Object.keys(dataToSave).forEach(key => {
        if (dataToSave[key] === undefined) {
          delete dataToSave[key];
        }
      });
      
      console.log('💾 Saving registration - editFormData.partialPayment:', editFormData.partialPayment);
      console.log('💾 Saving registration with partialPayment:', partialPaymentValue, 'remainingPayment:', remainingPaymentValue);
      console.log('💾 Full dataToSave:', JSON.stringify(dataToSave, null, 2));
      
      await onUpdateRegistration(editingRegistration, dataToSave);
      setEditingRegistration(null);
      setEditFormData(null);
      setOriginalFamilyCode(null);
      setExpandedRegistration(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingRegistration(null);
    setEditFormData(null);
    setOriginalFamilyCode(null);
  };

  const stats = {
    total: registrations.length,
    pending: registrations.filter(r => r.status === 'pending').length,
    confirmed: registrations.filter(r => r.status === 'confirmed').length,
    rejected: registrations.filter(r => r.status === 'rejected').length,
  };

  return (
    <div className="space-y-4 md:space-y-6 px-2 md:px-0">
      <div className="bg-white rounded-lg shadow p-3 md:p-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6">Manage Registrations</h2>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
          <div className="bg-blue-50 rounded-lg p-3 md:p-4">
            <p className="text-xs md:text-sm text-gray-600 mb-1">Total</p>
            <p className="text-xl md:text-2xl font-bold text-blue-600">{stats.total}</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 md:p-4">
            <p className="text-xs md:text-sm text-gray-600 mb-1">Pending</p>
            <p className="text-xl md:text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 md:p-4">
            <p className="text-xs md:text-sm text-gray-600 mb-1">Confirmed</p>
            <p className="text-xl md:text-2xl font-bold text-green-600">{stats.confirmed}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3 md:p-4">
            <p className="text-xs md:text-sm text-gray-600 mb-1">Rejected</p>
            <p className="text-xl md:text-2xl font-bold text-red-600">{stats.rejected}</p>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col gap-2 md:gap-4 mb-4 md:mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name, email, phone, or QR code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="flex-1 min-w-[120px] px-3 md:px-4 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              value={filterCamperCategory}
              onChange={(e) => setFilterCamperCategory(e.target.value)}
              className="flex-1 min-w-[120px] px-3 md:px-4 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {availableCamperCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <select
              value={filterPaymentStatus}
              onChange={(e) => setFilterPaymentStatus(e.target.value)}
              className="flex-1 min-w-[120px] px-3 md:px-4 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Payment Status</option>
              <option value="paid">Paid</option>
              <option value="partial">Partial Payment</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="failed">Failed</option>
            </select>
            <select
              value={filterRegistrationCategory}
              onChange={(e) => setFilterRegistrationCategory(e.target.value)}
              className="flex-1 min-w-[120px] px-3 md:px-4 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Registration Types</option>
              <option value="student">Student</option>
              <option value="adult">Adult</option>
              <option value="family">Family</option>
              <option value="day">Day</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="flex-1 min-w-[120px] px-3 md:px-4 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="registeredAt">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="status">Sort by Status</option>
              <option value="type">Sort by Type</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 md:px-4 py-2 text-xs md:text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>

        {/* Registrations Grid Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredRegistrations.map(reg => {
                const isGuestSpeaker = (reg.camperCategory === 'Guest Speaker' || reg.data?.camperCategory === 'Guest Speaker');
                const hasSpecialRequests = reg.specialRequests && reg.specialRequests.trim();
                return (
            <div
              key={reg.id}
              className={`bg-white rounded-lg shadow-md border-2 transition-all hover:shadow-lg ${
                isGuestSpeaker
                  ? 'border-orange-400 bg-orange-50'
                  : hasSpecialRequests
                    ? 'border-purple-300 bg-purple-50' 
                    : 'border-gray-200'
              } ${expandedRegistration === reg.id ? 'ring-2 ring-blue-500' : ''}`}
            >
              {/* Card Header */}
              <div className="p-3 md:p-4 border-b border-gray-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-base md:text-lg mb-1 truncate">{reg.name}</h3>
                    <p className="text-xs md:text-sm text-gray-600 truncate">{reg.email}</p>
                    {reg.mobileNumber && (
                      <p className="text-xs text-gray-500 mt-1">{reg.mobileNumber}</p>
                    )}
                  </div>
                  <div className="ml-2 flex gap-1">
                    {isGuestSpeaker && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800" title="Guest Speaker">
                        🎤
                      </span>
                    )}
                    {reg.specialRequests && reg.specialRequests.trim() && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800" title="Has Special Requests">
                        ⭐
                      </span>
                    )}
                    {(() => {
                      const allergies = reg.allergies || reg.data?.allergies || '';
                      const allergyDescription = reg.allergyDescription || reg.data?.allergyDescription || '';
                      if (allergies && allergies.toLowerCase() !== 'none' && allergies.trim()) {
                        return (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" title={`Allergies: ${allergies}${allergyDescription ? ' - ' + allergyDescription : ''}`}>
                            ⚠️
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
                
                {/* Status and Payment Badges */}
                <div className="flex flex-wrap gap-2 mt-2">
                      <StatusBadge status={reg.status} />
                      {(parseFloat(reg.partialPayment) || 0) > 0 && (parseFloat(reg.remainingPayment) || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200" title={`Partial Payment: $${(parseFloat(reg.partialPayment) || 0).toFixed(2)} received, $${(parseFloat(reg.remainingPayment) || 0).toFixed(2)} remaining`}>
                          <DollarSign className="w-3 h-3" />
                          Partial
                        </span>
                      )}
                      <PaymentBadge status={(() => {
                        // Check if this is a non-primary family member
                        // Method 1: Check familyCode and familyPosition
                        if (reg.familyCode && reg.familyPosition && reg.familyPosition !== 1) {
                          return 'linked';
                        }
                        // Method 2: Check registration code pattern (ICPFFAM001-1, ICPFFAM001-2, etc.)
                        const regCode = reg.qrCode || reg.registrationCode || '';
                        if (regCode && typeof regCode === 'string' && regCode.startsWith('ICPFFAM')) {
                          const match = regCode.match(/^ICPFFAM\d+-(\d+)$/);
                          if (match && parseInt(match[1], 10) !== 1) {
                            return 'linked';
                          }
                        }
                        // Method 3: Check old format (FAM_ prefix) - legacy support
                        if (reg.familyCode && typeof reg.familyCode === 'string' && reg.familyCode.startsWith('FAM_')) {
                          if (reg.familyPosition && reg.familyPosition !== 1) {
                            return 'linked';
                          }
                        }
                        return reg.paymentStatus;
                      })()} />
                  <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                    {reg.type}
                  </span>
                  {(reg.camperCategory || reg.data?.camperCategory) && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      {reg.camperCategory || reg.data?.camperCategory}
                    </span>
                  )}
                  {(() => {
                    const lodgeAssignment = getLodgeAssignment(reg);
                    if (lodgeAssignment) {
                      return (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-700 flex items-center gap-1" title={`Room ${lodgeAssignment.roomNumber}${lodgeAssignment.bedNumber ? `, Bed ${lodgeAssignment.bedNumber}` : ''}`}>
                          <Bed className="w-3 h-3" />
                          {lodgeAssignment.lodgeName}
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>

              {/* Card Body */}
              <div className="p-3 md:p-4">
                <div className="space-y-2 mb-3 md:mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Amount:</span>
                    <span className="font-semibold text-gray-900">${reg.amount || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Registered:</span>
                    <span className="text-sm text-gray-700">
                      {new Date(reg.registeredAt).toLocaleDateString()}
                    </span>
                  </div>
                  {reg.qrCode && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">QR Code:</span>
                      <span className="text-xs font-mono text-gray-700">{reg.qrCode}</span>
                    </div>
                  )}
                  {reg.lodgeId && (() => {
                    const lodge = lodges.find(l => l.id === reg.lodgeId);
                    if (!lodge && !reg.roomNumber) return null;
                    return (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Room:</span>
                        <span className="text-sm font-medium text-blue-600">
                          {lodge ? `${lodge.name} - ` : ''}Room {reg.roomNumber || 'N/A'}
                        </span>
                      </div>
                    );
                  })()}
                  {reg.checkedIn && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Checked In:</span>
                      <span className="text-xs text-green-600 font-medium">
                        {reg.checkInTime ? new Date(reg.checkInTime).toLocaleString() : 'Yes'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Quick Info Toggle */}
                {expandedRegistration === reg.id && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2 text-xs">
                    {reg.gender && (
                      <div><span className="text-gray-600">Gender:</span> <span className="font-medium capitalize">{reg.gender}</span></div>
                    )}
                    {reg.camperCategory && (
                      <div><span className="text-gray-600">Category:</span> <span className="font-medium">{reg.camperCategory}</span></div>
                    )}
                    {reg.age && (
                      <div><span className="text-gray-600">Age:</span> <span className="font-medium">{reg.age} years</span></div>
                    )}
                    {reg.tshirtSize && (
                      <div><span className="text-gray-600">T-Shirt:</span> <span className="font-medium">{reg.tshirtSize}</span></div>
                    )}
                    {reg.allergies && reg.allergies !== 'none' && (
                      <div className="pt-2 border-t border-gray-300">
                        <span className="text-gray-600">Allergies:</span> <span className="font-medium capitalize">{reg.allergies}</span>
                      </div>
                    )}
                    {canEdit && reg.specialRequests && reg.specialRequests.trim() && (
                      <div className="pt-2 border-t border-purple-300">
                        <span className="text-purple-700 font-semibold">Special Requests:</span>
                        <p className="text-purple-700 mt-1 bg-purple-50 p-2 rounded">{reg.specialRequests}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                        {reg.status === 'pending' && (
                          <>
                      {canApprove && (
                            <button
                              onClick={() => handleApprove(reg.id)}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-3 rounded-lg transition flex items-center justify-center gap-1"
                              title="Approve/Confirm"
                            >
                              <CheckCircle className="w-4 h-4" />
                          <span>Approve</span>
                            </button>
                      )}
                      {canReject && (
                            <button
                              onClick={() => handleReject(reg.id)}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-3 rounded-lg transition flex items-center justify-center gap-1"
                              title="Reject"
                            >
                              <XCircle className="w-4 h-4" />
                          <span>Reject</span>
                            </button>
                      )}
                          </>
                        )}
                  
                        {reg.status === 'confirmed' && (
                    <span className="flex-1 bg-green-100 text-green-800 text-sm font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-1" title="Confirmed">
                            <CheckCircle className="w-4 h-4" />
                      <span>Confirmed</span>
                          </span>
                        )}

                        {reg.status === 'rejected' && (
                    <span className="flex-1 bg-red-100 text-red-800 text-sm font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-1" title="Rejected">
                            <XCircle className="w-4 h-4" />
                      <span>Rejected</span>
                          </span>
                        )}

                        <button
                          onClick={() => setExpandedRegistration(expandedRegistration === reg.id ? null : reg.id)}
                    className={`flex-1 text-xs md:text-sm font-medium py-1.5 md:py-2 px-2 md:px-3 rounded-lg transition flex items-center justify-center gap-1 ${
                      expandedRegistration === reg.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                          title={expandedRegistration === reg.id ? 'Hide Details' : 'Show Details'}
                        >
                          <ListChecks className="w-3 h-3 md:w-4 md:h-4" />
                    <span>{expandedRegistration === reg.id ? 'Hide' : 'Details'}</span>
                        </button>

                  {canEdit && (
                        <button
                          onClick={() => handleEdit(reg)}
                      className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium py-2 px-3 rounded-lg transition flex items-center justify-center gap-1"
                          title="Edit Registration"
                        >
                          <Pencil className="w-4 h-4" />
                      <span>Edit</span>
                        </button>
                  )}

                  {canDelete && (
                        <button
                          onClick={() => handleDelete(reg.id)}
                      className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-3 rounded-lg transition flex items-center justify-center gap-1"
                          title="Delete Registration (Moves to deleted container)"
                        >
                          <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                        </button>
                  )}

                  {reg.status === 'confirmed' && canApprove && (
                          <button
                            onClick={() => handleResendConfirmation(reg.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-3 rounded-lg transition flex items-center justify-center gap-1"
                            title="Re-send Confirmation Email"
                          >
                            <Mail className="w-4 h-4" />
                      <span>Resend</span>
                          </button>
                        )}

                  {canApprove && (
                          <button
                            onClick={() => handleResendRegistrationReceived(reg.id)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium py-2 px-3 rounded-lg transition flex items-center justify-center gap-1"
                            title="Re-send Registration Received Email"
                          >
                            <Mail className="w-4 h-4" />
                      <span>Resend Received</span>
                          </button>
                        )}

                  {reg.paymentStatus !== 'paid' && reg.status !== 'rejected' && canApprove && (
                          <button
                            onClick={() => handleSendPaymentReminder(reg.id)}
                      className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium py-2 px-3 rounded-lg transition flex items-center justify-center gap-1"
                            title="Send Payment Reminder"
                          >
                            <Mail className="w-4 h-4" />
                      <span>Remind</span>
                          </button>
                        )}
                      </div>
                          </div>
                          </div>
                );
              })}
        </div>
          
          {filteredRegistrations.length === 0 && (
          <div className="text-center py-12 text-gray-500 col-span-full">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg">No registrations found</p>
              <p className="text-sm">Try adjusting your filters or search terms</p>
            </div>
          )}
      </div>

      {/* Edit Registration Modal */}
      {editingRegistration && editFormData && (
        <EditRegistrationModal
          formData={editFormData}
          setFormData={setEditFormData}
          userRole={userRole}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
          cardPaymentEnabled={cardPaymentEnabled}
          paypalEnabled={paypalEnabled}
          venmoEnabled={venmoEnabled}
          zelleEnabled={zelleEnabled}
          campSettings={campSettings}
          pricingSettings={pricingSettings}
          feeWaiverSettings={feeWaiverSettings}
        />
      )}
    </div>
  );
}

// Edit Registration Modal Component
function EditRegistrationModal({
  formData,
  setFormData,
  userRole,
  onSave,
  onCancel,
  cardPaymentEnabled = true,
  paypalEnabled = true,
  venmoEnabled = true,
  zelleEnabled = true,
  campSettings = null,
  pricingSettings = null,
  feeWaiverSettings = DEFAULT_FEE_WAIVER_SETTINGS
}) {
  const canEditSpecialRequests = hasPermission(userRole, 'edit_registrations');
  const canEditRegistrationCode = hasPermission(userRole, 'approve_registrations'); // Registration admins and app admins
  const fwMerged = mergeFeeWaiverSettings(feeWaiverSettings || DEFAULT_FEE_WAIVER_SETTINGS);
  
  // Local state for partial payment input (to allow free typing)
  const [partialPaymentInput, setPartialPaymentInput] = useState(() => {
    const partial = formData?.partialPayment;
    if (partial === undefined || partial === null) {
      return '';
    }
    const partialNum = parseFloat(partial);
    if (isNaN(partialNum)) {
      return '';
    }
    if (partialNum === 0) {
      return '0';
    }
    return partialNum % 1 === 0 ? String(partialNum) : partialNum.toFixed(2);
  });
  
  // Initialize remaining payment when form loads (only once on mount)
  useEffect(() => {
    if (formData && formData.amount !== undefined && formData.amount !== null && formData.remainingPayment === undefined) {
      const totalAmount = parseFloat(formData.amount) || 0;
      const partialPaid = parseFloat(formData.partialPayment) || 0;
      const calculatedRemaining = Math.max(0, totalAmount - partialPaid);
      setFormData(prev => ({ ...prev, remainingPayment: calculatedRemaining }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount
  
  // Sync local input state when formData changes externally (e.g., when editing a different registration)
  // Only update if the value actually changed to avoid interfering with user typing
  useEffect(() => {
    const partialPayment = formData?.partialPayment;
    const currentInputValue = parseFloat(partialPaymentInput) || 0;
    
    if (partialPayment === undefined || partialPayment === null) {
      if (currentInputValue !== 0 && partialPaymentInput !== '') {
        setPartialPaymentInput('');
      }
    } else {
      const partialPaymentNum = parseFloat(partialPayment);
      if (isNaN(partialPaymentNum)) {
        if (partialPaymentInput !== '') {
          setPartialPaymentInput('');
        }
      } else {
        // Only update if the value is significantly different (more than 0.01 difference)
        // This prevents the input from being reset while the user is typing
        if (Math.abs(currentInputValue - partialPaymentNum) > 0.01) {
          if (partialPaymentNum === 0) {
            setPartialPaymentInput('0');
          } else {
            // Format to 2 decimal places if needed
            const formattedValue = partialPaymentNum % 1 === 0 ? String(partialPaymentNum) : partialPaymentNum.toFixed(2);
            setPartialPaymentInput(formattedValue);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.partialPayment]);

  const editDayRegistrationOptions = useMemo(() => {
    const pr = { ...DEFAULT_PRICING_SETTINGS, ...(pricingSettings || {}) };
    return getDayRegistrationOptions(mergeCampSettings(campSettings), pr);
  }, [campSettings, pricingSettings]);
  
  // Helper function to calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };
  const usStates = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
    'Wisconsin', 'Wyoming'
  ];

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      console.log(`🔄 handleChange: ${field} = ${value}, updated formData:`, updated);
      return updated;
    });
  };

  // Guard clause - don't render if formData is not available (must be after all hooks)
  if (!formData) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">Edit Registration</h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Participant Information */}
          <div>
            <h3 className="text-lg font-semibold bg-gray-600 text-white px-4 py-2 rounded mb-4">Participant Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {canEditRegistrationCode && formData && (
                <>
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Family Code
                      <span className="ml-2 text-xs text-orange-600 font-normal">(Admin Only - Same code links family members)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.familyCode || ''}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        handleChange('familyCode', value);
                      }}
                      className="w-full px-4 py-2 border-2 border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-orange-50 font-mono"
                      placeholder="e.g., ICPFFAM001 (leave same on linked members)"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Multiple registrations can share the same family code to establish linkage. Changing the family code here only affects this registration unless you choose to update all members when saving.
                    </p>
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Registration Code / QR Code
                      <span className="ml-2 text-xs text-orange-600 font-normal">(Admin Only - Must be unique)</span>
                    </label>
                    <input
                      type="text"
                      value={(formData && (formData.qrCode || formData.registrationCode)) || ''}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        handleChange('qrCode', value);
                        handleChange('registrationCode', value);
                      }}
                      className="w-full px-4 py-2 border-2 border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-orange-50 font-mono"
                      placeholder="e.g., ICPF001 or ICPFFAM001-1"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Each registration must have a unique code. Family members typically use codes like ICPFFAM001-1, ICPFFAM001-2, etc.
                    </p>
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Middle Name</label>
                <input
                  type="text"
                  value={formData.middleName}
                  onChange={(e) => handleChange('middleName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleChange('gender', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Camper Category</label>
                <select
                  value={formData.camperCategory}
                  onChange={(e) => handleChange('camperCategory', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select</option>
                  <option value={BELOW_ELEMENTARY_CAMPER_CATEGORY}>{belowElementaryDisplayLabel(campSettings)}</option>
                  <option value="Elementary">Elementary</option>
                  <option value="Middle School">Middle School</option>
                  <option value="High School">High School</option>
                  <option value="College">College</option>
                  <option value="Adults">Adults</option>
                  {hasPermission(userRole, 'edit_registrations') && <option value="Guest Speaker">Guest Speaker</option>}
                  <option value="Test">Test</option>
                </select>
              </div>
              {(formData.type === 'day') && (
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Attendance Days</label>
                  <select
                    value={formData.attendanceDays || ''}
                    onChange={(e) => handleChange('attendanceDays', e.target.value)}
                    className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select attendance days</option>
                    {editDayRegistrationOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                    {formData.attendanceDays &&
                      !editDayRegistrationOptions.some((o) => o.value === formData.attendanceDays) && (
                      <option value={formData.attendanceDays}>
                        {formData.attendanceDays} (saved)
                      </option>
                    )}
                  </select>
                  {!editDayRegistrationOptions.length ? (
                    <p className="text-xs text-amber-700 mt-1">
                      Camp start/end dates are not set, the range is invalid, or no days are enabled for day passes (Camp Settings → Day passes). The saved attendance value can stay as-is.
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">Meals included. No accommodations.</p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stay Preference</label>
                {hasPermission(userRole, 'edit_registrations') ? (
                  <>
                    <select
                      value={formData.camperCategory === 'Middle School' ? (formData.stayPreference || 'withGroup') : ''}
                      onChange={(e) => handleChange('stayPreference', e.target.value)}
                      disabled={formData.camperCategory !== 'Middle School'}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">{formData.camperCategory === 'Middle School' ? 'Select...' : 'N/A (Middle School only)'}</option>
                      <option value="withGroup">Stay with Middle School Group</option>
                      <option value="withParents">Stay with Parents (Family Room)</option>
                    </select>
                    {formData.camperCategory !== 'Middle School' && (
                      <p className="mt-1 text-xs text-gray-500">Only applies to Middle School campers.</p>
                    )}
                  </>
                ) : (
                  <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                    {formData.camperCategory === 'Middle School'
                      ? (formData.stayPreference === 'withParents' ? 'Stay with Parents (Family Room)' : 'Stay with Middle School Group')
                      : 'N/A (Middle School only)'}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                <input
                  type="date"
                  value={formData.dateOfBirth || ''}
                  onChange={(e) => {
                    const age = calculateAge(e.target.value);
                    handleChange('dateOfBirth', e.target.value);
                    handleChange('age', age);
                  }}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 11)).toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                <input
                  type="text"
                  readOnly
                  value={formData.age || calculateAge(formData.dateOfBirth) || ''}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                  placeholder="Auto-calculated"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Church Name</label>
                <input
                  type="text"
                  value={formData.churchName || ''}
                  onChange={(e) => handleChange('churchName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pastor Name</label>
                <input
                  type="text"
                  value={formData.pastorName || ''}
                  onChange={(e) => handleChange('pastorName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
                <input
                  type="tel"
                  value={formData.mobileNumber}
                  onChange={(e) => handleChange('mobileNumber', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Home Phone</label>
                <input
                  type="tel"
                  value={formData.homePhone}
                  onChange={(e) => handleChange('homePhone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">T-Shirt Size</label>
                <select
                  value={formData.tshirtSize}
                  onChange={(e) => handleChange('tshirtSize', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select</option>
                  <option value="Youth small">Youth small</option>
                  <option value="Youth Medium">Youth Medium</option>
                  <option value="Youth Large">Youth Large</option>
                  <option value="Adult small">Adult small</option>
                  <option value="Adult Medium">Adult Medium</option>
                  <option value="Adult Large">Adult Large</option>
                  <option value="Adult XL">Adult XL</option>
                  <option value="Adult XXL">Adult XXL</option>
                </select>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div>
            <h3 className="text-lg font-semibold bg-gray-600 text-white px-4 py-2 rounded mb-4">Address Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 1</label>
                <input
                  type="text"
                  value={formData.addressLine1}
                  onChange={(e) => handleChange('addressLine1', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 2</label>
                <input
                  type="text"
                  value={formData.addressLine2}
                  onChange={(e) => handleChange('addressLine2', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <select
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select</option>
                  {usStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Zip</label>
                <input
                  type="text"
                  value={formData.zip}
                  onChange={(e) => handleChange('zip', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <select
                  value={formData.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div>
            <h3 className="text-lg font-semibold bg-gray-600 text-white px-4 py-2 rounded mb-4">Emergency Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                <input
                  type="text"
                  value={formData.emergencyContactRelationship}
                  onChange={(e) => handleChange('emergencyContactRelationship', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.emergencyContactName}
                  onChange={(e) => handleChange('emergencyContactName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.emergencyContactPhone}
                  onChange={(e) => handleChange('emergencyContactPhone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Parent/Guardian Information */}
          <div>
            <h3 className="text-lg font-semibold bg-gray-600 text-white px-4 py-2 rounded mb-4">Parent/Guardian Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.parentGuardianName}
                  onChange={(e) => handleChange('parentGuardianName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.parentGuardianEmail}
                  onChange={(e) => handleChange('parentGuardianEmail', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.parentGuardianPhone}
                  onChange={(e) => handleChange('parentGuardianPhone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Medical Information */}
          <div>
            <h3 className="text-lg font-semibold bg-gray-600 text-white px-4 py-2 rounded mb-4">Medical Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Allergies</label>
                <select
                  value={formData.allergies}
                  onChange={(e) => handleChange('allergies', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select</option>
                  <option value="none">None</option>
                  <option value="food">Food Allergies</option>
                  <option value="medication">Medication Allergies</option>
                  <option value="environmental">Environmental Allergies</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Allergy Description</label>
                <input
                  type="text"
                  value={formData.allergyDescription}
                  onChange={(e) => handleChange('allergyDescription', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Registration & Payment Information */}
          <div>
            <h3 className="text-lg font-semibold bg-gray-600 text-white px-4 py-2 rounded mb-4">Registration & Payment Information</h3>
            {/* Show notice for non-primary family members */}
            {formData.familyCode && formData.familyPosition !== 1 && (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Payment information for this family member is managed through the primary family member (first registration). Changes to payment status, method, or partial payments should be made on the primary member's registration.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Registration Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="student">Student</option>
                  <option value="adult">Adult</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                <select
                  value={formData.paymentMethod}
                  onChange={(e) => handleChange('paymentMethod', e.target.value)}
                  disabled={formData.familyCode && formData.familyPosition !== 1}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    formData.familyCode && formData.familyPosition !== 1 ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                >
                  {cardPaymentEnabled && <option value="card">Credit/Debit Card</option>}
                  {(zelleEnabled || formData.paymentMethod === 'zelle') && (
                    <option value="zelle">{paymentMethodDisplayLabel('zelle', campSettings)}</option>
                  )}
                  {(venmoEnabled || formData.paymentMethod === 'venmo') && (
                    <option value="venmo">{paymentMethodDisplayLabel('venmo', campSettings)}</option>
                  )}
                  {(paypalEnabled || formData.paymentMethod === 'paypal') && (
                    <option value="paypal">{paymentMethodDisplayLabel('paypal', campSettings)}</option>
                  )}
                  <option value="waived">Waived (sponsor / no charge)</option>
                </select>
              </div>
              {formData.paymentMethod && formData.paymentMethod !== 'card' && formData.paymentMethod !== 'waived' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date</label>
                    <input
                      type="date"
                      value={formData.paymentDate ? formData.paymentDate.split('T')[0] : ''}
                      onChange={(e) => handleChange('paymentDate', e.target.value ? new Date(e.target.value).toISOString() : '')}
                      disabled={formData.familyCode && formData.familyPosition !== 1}
                      className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formData.familyCode && formData.familyPosition !== 1 ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Confirmation</label>
                    <input
                      type="text"
                      value={formData.paymentConfirmation || ''}
                      onChange={(e) => handleChange('paymentConfirmation', e.target.value)}
                      placeholder="Check number, receipt number, etc."
                      disabled={formData.familyCode && formData.familyPosition !== 1}
                      className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formData.familyCode && formData.familyPosition !== 1 ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
                <select
                  value={formData.paymentStatus}
                  onChange={(e) => handleChange('paymentStatus', e.target.value)}
                  disabled={formData.familyCode && formData.familyPosition !== 1}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    formData.familyCode && formData.familyPosition !== 1 ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="partial">Partial Payment</option>
                  <option value="processing">Processing</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div className="md:col-span-4 border-t border-gray-200 pt-4 mt-1 space-y-3">
                <p className="text-sm font-semibold text-gray-800">Discount &amp; sponsor fee waiver</p>
                <p className="text-xs text-gray-600">
                  Choose active codes from Camp Settings. Saving recalculates this member&apos;s amount and updates global use counts / sponsor balances. Configure codes under Camp Settings → Fee waivers &amp; discounts.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Discount code</label>
                    <select
                      value={formData.discountCode || ''}
                      onChange={(e) => handleChange('discountCode', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">No discount</option>
                      {(fwMerged.discounts || [])
                        .filter((d) => d.active !== false)
                        .map((d) => (
                          <option key={d.id} value={d.code}>
                            {d.code}
                            {d.discountType === 'percent' ? ` (${d.value}%)` : ` ($${d.value})`}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sponsor fee waiver code</label>
                    <select
                      value={formData.feeWaiverCode || ''}
                      onChange={(e) => handleChange('feeWaiverCode', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">No sponsor waiver</option>
                      {(fwMerged.feeWaivers || [])
                        .filter((w) => w.active !== false)
                        .map((w) => (
                          <option key={w.id} value={w.code}>
                            {w.code} —{' '}
                            {w.sponsorType === 'individual'
                              ? w.individualName || 'Individual sponsor'
                              : w.churchName || 'Church sponsor'}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sponsor / waiver display name (saved on registration)
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={formData.feeWaiverSponsorName || ''}
                    placeholder="—"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-800"
                  />
                </div>
                {formData.amountBeforeCredits !== '' &&
                  formData.amountBeforeCredits != null &&
                  Number.isFinite(parseFloat(formData.amountBeforeCredits)) && (
                  <p className="text-xs text-gray-500">
                    Pre-credit list price for this line:{' '}
                    <strong>${parseFloat(formData.amountBeforeCredits).toFixed(2)}</strong> (amount due may be lower after codes)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Total Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount !== undefined && formData.amount !== null ? formData.amount : ''}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (inputValue === '') {
                      handleChange('amount', '');
                      handleChange('remainingPayment', '');
                      return;
                    }
                    const totalAmount = parseFloat(inputValue);
                    if (!isNaN(totalAmount)) {
                      handleChange('amount', totalAmount);
                      // Auto-calculate remaining payment
                      const partialPaid = parseFloat(formData.partialPayment) || 0;
                      const remaining = Math.max(0, totalAmount - partialPaid);
                      handleChange('remainingPayment', remaining);
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Partial Payment Received ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={partialPaymentInput}
                  onChange={(e) => {
                    // Prevent changes for non-primary family members
                    if (formData.familyCode && formData.familyPosition !== 1) return;
                    
                    const inputValue = e.target.value;
                    setPartialPaymentInput(inputValue);
                    
                    // Update formData and calculate remaining
                    if (inputValue === '' || inputValue === '-') {
                      handleChange('partialPayment', 0);
                      const totalAmount = parseFloat(formData.amount) || 0;
                      handleChange('remainingPayment', totalAmount);
                    } else {
                      const partialPaid = parseFloat(inputValue);
                      if (!isNaN(partialPaid) && partialPaid >= 0) {
                        handleChange('partialPayment', partialPaid);
                        // Auto-calculate remaining payment
                        const totalAmount = parseFloat(formData.amount) || 0;
                        const remaining = Math.max(0, totalAmount - partialPaid);
                        handleChange('remainingPayment', remaining);
                      }
                    }
                  }}
                  onBlur={(e) => {
                    // Prevent changes for non-primary family members
                    if (formData.familyCode && formData.familyPosition !== 1) return;
                    
                    // On blur, normalize the value
                    const inputValue = e.target.value.trim();
                    if (inputValue === '' || isNaN(parseFloat(inputValue))) {
                      setPartialPaymentInput('');
                      handleChange('partialPayment', 0);
                      const totalAmount = parseFloat(formData.amount) || 0;
                      handleChange('remainingPayment', totalAmount);
                    } else {
                      const partialPaid = parseFloat(inputValue);
                      if (!isNaN(partialPaid) && partialPaid >= 0) {
                        setPartialPaymentInput(String(partialPaid));
                        handleChange('partialPayment', partialPaid);
                        const totalAmount = parseFloat(formData.amount) || 0;
                        const remaining = Math.max(0, totalAmount - partialPaid);
                        handleChange('remainingPayment', remaining);
                      }
                    }
                  }}
                  disabled={formData.familyCode && formData.familyPosition !== 1}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    formData.familyCode && formData.familyPosition !== 1 ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Remaining Payment ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={(() => {
                    if (formData.remainingPayment !== undefined && formData.remainingPayment !== null) {
                      return formData.remainingPayment;
                    }
                    const totalAmount = parseFloat(formData.amount) || 0;
                    const partialPaid = parseFloat(formData.partialPayment) || 0;
                    return Math.max(0, totalAmount - partialPaid);
                  })()}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                  placeholder="Auto-calculated"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Time Attending</label>
                <select
                  value={formData.firstTimeAttending}
                  onChange={(e) => handleChange('firstTimeAttending', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
          </div>

          {/* Special Requests - Only visible to Registration Admin */}
          {canEditSpecialRequests && (
            <div>
              <h3 className="text-lg font-semibold bg-purple-600 text-white px-4 py-2 rounded mb-4">Special Requests (Admin Only)</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Requests
                </label>
                <textarea
                  value={formData.specialRequests || ''}
                  onChange={(e) => handleChange('specialRequests', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter any special requests or notes for this registration..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  This field is only visible to Registration Admin. Registrations with special requests will be highlighted in light purple in the list.
                </p>
              </div>
            </div>
          )}

          {/* Room Assignment Notes - Only visible to Registration Admin */}
          {canEditSpecialRequests && (
            <div>
              <h3 className="text-lg font-semibold bg-teal-600 text-white px-4 py-2 rounded mb-4">Room Assignment Notes (Admin Only)</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Room Assignment Notes
                </label>
                <textarea
                  value={formData.roomAssignmentNotes || ''}
                  onChange={(e) => handleChange('roomAssignmentNotes', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Notes for room admin (e.g. accessibility, room near family, etc.). Shown on Room Assignment page when not empty."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Visible only to Room Admin on the Room Assignment page. Registrations with notes will appear in the &quot;Registrations with Room Assignment Notes&quot; table.
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <button
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={onSave}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper Components
function StatCard({ title, value, icon: Icon, color }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-100 text-purple-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    orange: 'bg-orange-100 text-orange-600'
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function PaymentBadge({ status }) {
  const colors = {
    paid: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    failed: 'bg-red-100 text-red-800',
    partial: 'bg-orange-100 text-orange-800',
    linked: 'bg-gray-100 text-gray-800'
  };

  const displayText = {
    paid: 'Paid',
    pending: 'Pending',
    processing: 'Processing',
    failed: 'Failed',
    partial: 'Partial Payment',
    linked: 'Linked'
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {displayText[status] || status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// Bulk Email Component
function BulkEmail({ registrations, userRole }) {
  const [selectedRegistrants, setSelectedRegistrants] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [additionalRecipients, setAdditionalRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0, failed: 0 });
  const [sendResults, setSendResults] = useState(null);

  // Filter registrations
  const filteredRegistrations = registrations.filter(reg => {
    const matchesSearch = 
      reg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.registrationCode?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || reg.status === filterStatus;
    
    return matchesSearch && matchesStatus && reg.email; // Only show registrations with email
  });

  // Toggle select all
  const handleSelectAll = () => {
    if (selectedRegistrants.size === filteredRegistrations.length) {
      setSelectedRegistrants(new Set());
    } else {
      setSelectedRegistrants(new Set(filteredRegistrations.map(r => r.id)));
    }
  };

  // Toggle individual selection
  const handleToggleSelection = (id) => {
    const newSelected = new Set(selectedRegistrants);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRegistrants(newSelected);
  };

  // Parse additional recipients (comma or newline separated)
  const parseAdditionalRecipients = () => {
    if (!additionalRecipients.trim()) return [];
    return additionalRecipients
      .split(/[,\n]/)
      .map(email => email.trim())
      .filter(email => email && email.includes('@'));
  };

  // Send bulk emails
  const handleSendBulkEmail = async () => {
    if (selectedRegistrants.size === 0 && parseAdditionalRecipients().length === 0) {
      alert('Please select at least one registrant or add additional recipients.');
      return;
    }

    if (!subject.trim()) {
      alert('Please enter a subject for the email.');
      return;
    }

    const bodyTrimmed = (body || '').replace(/<[^>]*>/g, '').trim();
    if (!bodyTrimmed) {
      alert('Please enter email body content.');
      return;
    }

    const selectedEmails = Array.from(selectedRegistrants)
      .map(id => {
        const reg = registrations.find(r => r.id === id);
        return reg?.email;
      })
      .filter(email => email);

    const additionalEmails = parseAdditionalRecipients();
    const allRecipients = [...selectedEmails, ...additionalEmails];

    if (allRecipients.length === 0) {
      alert('No valid email addresses found.');
      return;
    }

    // Confirm before sending
    const confirmed = window.confirm(
      `Are you sure you want to send this email to ${allRecipients.length} recipient(s)?\n\n` +
      `Selected registrants: ${selectedEmails.length}\n` +
      `Additional recipients: ${additionalEmails.length}`
    );

    if (!confirmed) return;

    setIsSending(true);
    setSendProgress({ sent: 0, total: allRecipients.length, failed: 0 });
    setSendResults(null);

    const results = {
      successful: [],
      failed: []
    };

    // Send emails one by one (to avoid overwhelming the email server)
    for (let i = 0; i < allRecipients.length; i++) {
      const email = allRecipients[i];
      try {
        const result = await sendEmail(email, subject, body);
        if (result.success) {
          results.successful.push(email);
          setSendProgress(prev => ({ ...prev, sent: prev.sent + 1 }));
        } else {
          results.failed.push({ email, error: result.error || 'Unknown error' });
          setSendProgress(prev => ({ ...prev, failed: prev.failed + 1 }));
        }
      } catch (error) {
        results.failed.push({ email, error: error.message || 'Failed to send' });
        setSendProgress(prev => ({ ...prev, failed: prev.failed + 1 }));
      }

      // Small delay between emails to avoid rate limiting
      if (i < allRecipients.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    setIsSending(false);
    setSendResults(results);

    // Show summary
    alert(
      `Bulk email sending completed!\n\n` +
      `✅ Successful: ${results.successful.length}\n` +
      `❌ Failed: ${results.failed.length}\n\n` +
      (results.failed.length > 0 ? 'Check the results below for failed emails.' : '')
    );
  };

  return (
    <div className="space-y-4 md:space-y-6 px-2 md:px-0">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Bulk Email</h2>
        <div className="text-xs md:text-sm text-gray-600">
          {selectedRegistrants.size} of {filteredRegistrations.length} selected
        </div>
      </div>

      {/* Email Composition Form */}
      <div className="bg-white rounded-lg shadow p-3 md:p-6 space-y-3 md:space-y-4">
        <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">Compose Email</h3>
        
        {/* Additional Recipients */}
        <div>
          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2">
            Additional Recipients (Optional)
          </label>
          <textarea
            value={additionalRecipients}
            onChange={(e) => setAdditionalRecipients(e.target.value)}
            placeholder="Enter email addresses separated by commas or new lines..."
            className="w-full px-3 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="3"
            disabled={isSending}
          />
          <p className="text-xs text-gray-500 mt-1">
            {parseAdditionalRecipients().length > 0 && `${parseAdditionalRecipients().length} additional recipient(s) will be added`}
          </p>
        </div>

        {/* Subject */}
        <div>
          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2">
            Subject <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
            className="w-full px-3 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isSending}
          />
        </div>

        {/* Body - Rich text editor */}
        <div>
          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2">
            Email Body <span className="text-red-500">*</span>
          </label>
          <div className={isSending ? 'opacity-60 pointer-events-none' : ''}>
            <ReactQuill
              theme="snow"
              value={body}
              onChange={setBody}
              placeholder="Enter your email message here..."
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['link'],
                  [{ color: [] }, { background: [] }],
                  ['clean']
                ]
              }}
              className="bg-white rounded-lg border border-gray-300 overflow-hidden"
              style={{ minHeight: '200px' }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Use the toolbar to format text (bold, lists, links, etc.). Emails are sent as HTML.</p>
        </div>
      </div>

      {/* Recipient Selection */}
      <div className="bg-white rounded-lg shadow p-3 md:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 md:gap-4 mb-3 md:mb-4">
          <h3 className="text-base md:text-lg font-semibold text-gray-800">Select Recipients</h3>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            {/* Search */}
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or code..."
              className="flex-1 sm:flex-none sm:min-w-[200px] px-3 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isSending}
            />
            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="flex-1 sm:flex-none px-3 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isSending}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="rejected">Rejected</option>
            </select>
            {/* Select All */}
            <button
              onClick={handleSelectAll}
              disabled={isSending || filteredRegistrations.length === 0}
              className="px-3 md:px-4 py-2 text-xs md:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {selectedRegistrants.size === filteredRegistrations.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>

        {/* Registrations List */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="max-h-96 overflow-y-auto">
            {filteredRegistrations.length === 0 ? (
              <div className="p-4 md:p-8 text-center text-xs md:text-sm text-gray-500">
                No registrations found with email addresses.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                        <input
                          type="checkbox"
                          checked={selectedRegistrants.size === filteredRegistrations.length && filteredRegistrations.length > 0}
                          onChange={handleSelectAll}
                          disabled={isSending}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredRegistrations.map(reg => (
                      <tr key={reg.id} className="hover:bg-gray-50">
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <input
                            type="checkbox"
                            checked={selectedRegistrants.has(reg.id)}
                            onChange={() => handleToggleSelection(reg.id)}
                            disabled={isSending}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-gray-900">{reg.name || 'N/A'}</td>
                        <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-gray-600 truncate max-w-[150px] md:max-w-none">{reg.email}</td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <StatusBadge status={reg.status} />
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-gray-600 capitalize">{reg.type || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Send Button and Progress */}
      <div className="bg-white rounded-lg shadow p-3 md:p-6">
        {isSending && (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Sending emails...</span>
              <span className="text-sm text-gray-600">
                {sendProgress.sent} / {sendProgress.total} sent
                {sendProgress.failed > 0 && ` (${sendProgress.failed} failed)`}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(sendProgress.sent / sendProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {sendResults && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-800 mb-2">Send Results:</h4>
            <div className="space-y-2 text-sm">
              <div className="text-green-600">
                ✅ Successful: {sendResults.successful.length} email(s)
              </div>
              {sendResults.failed.length > 0 && (
                <div className="text-red-600">
                  ❌ Failed: {sendResults.failed.length} email(s)
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs">View failed emails</summary>
                    <ul className="mt-2 space-y-1 text-xs">
                      {sendResults.failed.map((item, idx) => (
                        <li key={idx} className="pl-4">
                          {item.email}: {item.error}
                        </li>
                      ))}
                    </ul>
                  </details>
                </div>
              )}
            </div>
          </div>
        )}

        <button
          onClick={handleSendBulkEmail}
          disabled={userRole === ROLES.VIEWER || isSending || (selectedRegistrants.size === 0 && parseAdditionalRecipients().length === 0) || !subject.trim() || !(body || '').replace(/<[^>]*>/g, '').trim()}
          className={`w-full py-2 md:py-3 px-4 md:px-6 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm md:text-base ${
            userRole === ROLES.VIEWER
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed'
          }`}
        >
          {isSending ? (
            <>
              <Clock className="w-5 h-5 animate-spin" />
              Sending Emails...
            </>
          ) : (
            <>
              <Mail className="w-5 h-5" />
              Send Email to {selectedRegistrants.size + parseAdditionalRecipients().length} Recipient(s)
            </>
          )}
        </button>
      </div>
    </div>
  );
}
// Room Assignment Engine Component
function RoomAssignmentEngine({ registrations, lodges, onUpdateRegistrations, onUpdateLodges, userRole }) {
  const [includeTestRegistrations, setIncludeTestRegistrations] = useState(false);
  const [roomAssignments, setRoomAssignments] = useState([]);
  const [unresolvedFamilies, setUnresolvedFamilies] = useState([]);
  const [roomsWithCapacityInfo, setRoomsWithCapacityInfo] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [assignmentResults, setAssignmentResults] = useState(null);
  const [hasAutoRun, setHasAutoRun] = useState(false);
  const [skippedRegistrations, setSkippedRegistrations] = useState([]);
  const [expandedLodges, setExpandedLodges] = useState(new Set());
  const [skippedRegistrationsExpanded, setSkippedRegistrationsExpanded] = useState(false);
  
  // Toggle lodge expansion
  const toggleLodgeExpansion = (lodgeId) => {
    setExpandedLodges(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lodgeId)) {
        newSet.delete(lodgeId);
      } else {
        newSet.add(lodgeId);
      }
      return newSet;
    });
  };
  
  // Get current assignments for a specific lodge
  const getLodgeAssignments = (lodgeId) => {
    const assignments = [];
    const lodge = lodges.find(l => l.id === lodgeId);
    if (!lodge) return assignments;
    
    const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
    
    if (hasNewStructure) {
      // New structure: get from rooms.assignedBeds
      lodge.rooms.forEach(room => {
        if (room.assignedBeds && Array.isArray(room.assignedBeds)) {
          room.assignedBeds.forEach(bed => {
            const reg = registrations.find(r => r.id === bed.registrationId);
            if (reg) {
              assignments.push({
                registrationId: reg.id,
                registrationName: reg.name,
                roomNumber: room.roomNumber,
                bedNumber: bed.bedNumber,
                type: reg.type,
                camperCategory: reg.camperCategory || reg.data?.camperCategory || 'N/A',
                churchName: reg.data?.churchName || reg.churchName || 'N/A',
                city: reg.data?.city || reg.city || 'N/A'
              });
            }
          });
        }
      });
    } else {
      // Old structure: get from assignedRooms
      if (lodge.assignedRooms && Array.isArray(lodge.assignedRooms)) {
        lodge.assignedRooms.forEach(assignment => {
          const reg = registrations.find(r => r.id === assignment.registrationId);
          if (reg) {
            assignments.push({
              registrationId: reg.id,
              registrationName: reg.name,
              roomNumber: assignment.roomNumber,
              bedNumber: assignment.bedNumber || 'N/A',
              type: reg.type,
              camperCategory: reg.camperCategory || reg.data?.camperCategory || 'N/A',
              churchName: reg.data?.churchName || reg.churchName || 'N/A',
              city: reg.data?.city || reg.city || 'N/A'
            });
          }
        });
      }
    }
    
    return assignments;
  };
  
  // Get all current assignments from database
  const getCurrentAssignments = () => {
    const currentAssignments = [];
    
    lodges.forEach(lodge => {
      const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
      
      if (hasNewStructure) {
        // New structure: get from rooms.assignedBeds
        lodge.rooms.forEach(room => {
          if (room.assignedBeds && room.assignedBeds.length > 0) {
            room.assignedBeds.forEach(bed => {
              const reg = registrations.find(r => r.id === bed.registrationId);
              if (reg) {
                currentAssignments.push({
                  registrationId: bed.registrationId,
                  registrationName: reg.name,
                  lodgeId: lodge.id,
                  lodgeName: lodge.name,
                  roomNumber: room.roomNumber,
                  bedNumber: bed.bedNumber
                });
              }
            });
          }
        });
      } else {
        // Old structure: get from assignedRooms
        if (lodge.assignedRooms && Array.isArray(lodge.assignedRooms)) {
          lodge.assignedRooms.forEach(assignment => {
            const reg = registrations.find(r => r.id === assignment.registrationId);
            if (reg) {
              currentAssignments.push({
                registrationId: assignment.registrationId,
                registrationName: reg.name,
                lodgeId: lodge.id,
                lodgeName: lodge.name,
                roomNumber: assignment.roomNumber,
                bedNumber: assignment.bedNumber || 1
              });
            }
          });
        }
      }
    });
    
    return currentAssignments;
  };
  
  const currentAssignments = getCurrentAssignments();

  // Helper function to get age from date of birth
  const getAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Detect family registrations
  const getFamilyGroups = (regsToUse = registrations) => {
    const familyMap = new Map();
    
    regsToUse.forEach(reg => {
      if (reg.familyCode) {
        if (!familyMap.has(reg.familyCode)) {
          familyMap.set(reg.familyCode, []);
        }
        familyMap.get(reg.familyCode).push(reg);
      }
    });
    
    return Array.from(familyMap.values());
  };

  // Categorize registrations
  const categorizeRegistration = (reg) => {
    const age = getAge(reg.dateOfBirth) || parseInt(reg.age) || 0;
    // Check both registration.camperCategory and registration.data.camperCategory
    const camperCategory = reg.camperCategory || reg.data?.camperCategory || '';
    const stayPreference = reg.stayPreference || reg.data?.stayPreference || 'withGroup'; // Default to withGroup
    
    // Determine if adult (25+ or camperCategory is Adults)
    const isAdult = age >= 25 || camperCategory === 'Adults' || camperCategory === 'Adult';
    
    // Determine student category
    let studentCategory = null;
    if (!isAdult) {
      if (camperCategory === 'Middle School' || (age >= 11 && age <= 13)) {
        studentCategory = 'middleSchool';
      } else if (camperCategory === 'High School' || (age >= 14 && age <= 17)) {
        studentCategory = 'highSchool';
      } else if (camperCategory === 'College' || (age >= 18 && age < 25)) {
        studentCategory = 'college';
      }
    }
    
    // Check both registration.gender and registration.data.gender
    const gender = reg.gender || reg.data?.gender || 'Unknown';
    
    return {
      isAdult,
      studentCategory,
      stayPreference,
      age,
      gender: gender
    };
  };

  // Helper function to find available room and bed in a lodge
  const findAvailableRoomAndBed = (lodge) => {
    // Support both new structure (rooms array) and old structure (totalRooms)
    if (lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0) {
      // New structure: find room with available bed
      for (const room of lodge.rooms) {
        const assignedBeds = room.assignedBeds || [];
        const bedCount = room.bedCount || 1;
        if (assignedBeds.length < bedCount) {
          // Find first available bed number
          const assignedBedNumbers = new Set(assignedBeds.map(b => b.bedNumber));
          let bedNumber = 1;
          while (assignedBedNumbers.has(bedNumber) && bedNumber <= bedCount) {
            bedNumber++;
          }
          if (bedNumber <= bedCount) {
            return { roomNumber: room.roomNumber, bedNumber: bedNumber };
          }
        }
      }
      return null; // No available beds
    } else {
      // Old structure: use sequential room assignment
      const used = lodge.assignedRooms?.length || 0;
      const capacity = lodge.totalRooms || 0;
      if (used < capacity) {
        return { roomNumber: (used + 1).toString(), bedNumber: 1 };
      }
      return null;
    }
  };

  // Family lodges: one family per room (no mixing two families in the same room).
  // assignmentsSoFar = array of assignments already made in this run; familyCode = this group's code (null = single adults).
  // Returns { roomNumber, bedNumbers } or null. bedNumbers is array [1], [1,2], etc. for requiredBeds.
  const findAvailableRoomInFamilyLodge = (lodge, assignmentsSoFar, familyCode, requiredBeds) => {
    const inThisLodge = (a) => a.lodgeId === lodge.id;
    if (lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0) {
      for (const room of lodge.rooms) {
        const bedCount = room.bedCount || 1;
        const inThisRoom = (a) => inThisLodge(a) && (a.roomNumber === room.roomNumber || a.roomNumber === room.roomNumber.toString());
        const existing = assignmentsSoFar.filter(inThisRoom);
        const existingCount = existing.length;
        if (existingCount >= bedCount) continue;
        const existingFamilyCodes = [...new Set(existing.map(a => a.familyCode ?? null))];
        const sameFamily = existingFamilyCodes.length === 0 ||
          (existingFamilyCodes.length === 1 && (existingFamilyCodes[0] === familyCode || (existingFamilyCodes[0] == null && familyCode == null)));
        if (!sameFamily) continue;
        if (existingCount + requiredBeds > bedCount) continue;
        const bedNumbers = [];
        const usedBedNumbers = new Set(existing.map(a => a.bedNumber));
        for (let b = 1; b <= bedCount && bedNumbers.length < requiredBeds; b++) {
          if (!usedBedNumbers.has(b)) bedNumbers.push(b);
        }
        if (bedNumbers.length >= requiredBeds) {
          return { roomNumber: room.roomNumber, bedNumbers: bedNumbers.slice(0, requiredBeds) };
        }
      }
      return null;
    } else {
      // Old structure: one registration per "room" (slot). To avoid mixing families, use one slot per family or one per single.
      const used = assignmentsSoFar.filter(inThisLodge).length;
      const capacity = lodge.totalRooms || 0;
      if (requiredBeds > 1) {
        // Family needs multiple slots - take consecutive "rooms" (slots). Only allow if we have requiredBeds free and we don't mix.
        if (used + requiredBeds > capacity) return null;
        const roomNumbers = [];
        for (let i = 1; i <= requiredBeds; i++) roomNumbers.push((used + i).toString());
        return { roomNumber: roomNumbers[0], bedNumbers: [1], extraSlots: roomNumbers.slice(1) };
      }
      if (used < capacity) {
        return { roomNumber: (used + 1).toString(), bedNumbers: [1] };
      }
      return null;
    }
  };

  // Process room assignments
  const processRoomAssignments = async () => {
    setIsProcessing(true);
    setAssignmentResults(null);

    try {
      // Fetch fresh data to ensure we're working with the latest state
      console.log('Fetching fresh registrations and lodges data...');
      const freshRegistrations = await registrationsAPI.getAll();
      const freshLodges = await lodgesAPI.getAll();
      console.log(`Fetched ${freshRegistrations.length} registrations and ${freshLodges.length} lodges`);
      
      // Use fresh data instead of props
      const currentRegistrations = freshRegistrations;
      const currentLodges = freshLodges;
      
      // Get confirmed registrations only
      let confirmedRegs = currentRegistrations.filter(r => r.status === 'confirmed');
      
      // Track skipped registrations for reporting
      const skipped = [];
      const alreadyAssigned = [];
      
      // Optionally exclude test registrations (they're typically just for payment testing)
      const testRegistrations = confirmedRegs.filter(r => r.type === 'test');
      if (!includeTestRegistrations && testRegistrations.length > 0) {
        const beforeCount = confirmedRegs.length;
        confirmedRegs = confirmedRegs.filter(r => r.type !== 'test');
        const testCount = beforeCount - confirmedRegs.length;
        console.log(`Excluding ${testCount} test registration(s) from assignment engine`);
        
        // Add test registrations to skipped list
        testRegistrations.forEach(reg => {
          skipped.push({
            registrationId: reg.id,
            registrationName: reg.name,
            reason: 'Test registration (excluded from assignment engine)',
            type: reg.type,
            status: reg.status
          });
        });
      }
      
      // Track non-confirmed registrations
      const nonConfirmed = currentRegistrations.filter(r => r.status !== 'confirmed');
      nonConfirmed.forEach(reg => {
        skipped.push({
          registrationId: reg.id,
          registrationName: reg.name,
          reason: `Status: ${reg.status} (not confirmed)`,
          type: reg.type,
          status: reg.status
        });
      });
      
      // Track already assigned registrations from existing data
      const alreadyAssignedRegIds = new Set();
      console.log('Checking for already assigned registrations...');
      currentRegistrations.forEach(reg => {
        // Check if registration already has a room assignment
        // Only consider it assigned if lodgeId is truthy (not null, undefined, or empty string)
        const regLodgeId = reg.data?.lodgeId || reg.lodgeId;
        console.log(`Checking ${reg.name} (${reg.id}): lodgeId=${reg.lodgeId}, data.lodgeId=${reg.data?.lodgeId}, combined=${regLodgeId}`);
        if (regLodgeId && regLodgeId !== null && regLodgeId !== 'null' && regLodgeId !== '') {
          console.log(`✓ Found registration with lodgeId: ${reg.name} (${reg.id}), lodgeId: ${regLodgeId}`);
          
          // Verify the lodge still exists
          const lodge = currentLodges.find(l => l.id === regLodgeId);
          if (lodge) {
            // Lodge exists, check if assignment is in lodge data
            const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
            let assignmentFound = false;
            
            if (hasNewStructure) {
              // Check new structure
              assignmentFound = lodge.rooms.some(room => 
                room.assignedBeds?.some(bed => bed.registrationId === reg.id)
              );
            } else {
              // Check old structure
              assignmentFound = (lodge.assignedRooms || []).some(
                assignment => assignment.registrationId === reg.id
              );
            }
            
            // Only mark as assigned if assignment exists in lodge OR if we can't verify (for safety)
            if (assignmentFound || (!hasNewStructure && !lodge.assignedRooms)) {
          alreadyAssignedRegIds.add(reg.id);
              alreadyAssigned.push({
                registrationId: reg.id,
                registrationName: reg.name,
                reason: `Already assigned to ${lodge.name}${hasNewStructure ? '' : ' (Room ' + (lodge.assignedRooms?.find(a => a.registrationId === reg.id)?.roomNumber || '?') + ')'}`,
                type: reg.type,
                lodgeId: regLodgeId
              });
              console.log(`Marked as already assigned: ${reg.name} - ${assignmentFound ? 'found in lodge' : 'lodge exists but assignment not verified'}`);
            } else {
              console.log(`Registration ${reg.name} has lodgeId ${regLodgeId} but assignment not found in lodge - treating as orphaned`);
              // Orphaned assignment - still mark it but indicate it needs clearing
              alreadyAssignedRegIds.add(reg.id);
              alreadyAssigned.push({
                registrationId: reg.id,
                registrationName: reg.name,
                reason: `Orphaned assignment to ${lodge.name} (ID: ${regLodgeId}) - assignment not found in lodge data`,
                type: reg.type,
                lodgeId: regLodgeId
              });
            }
          } else {
            // Lodge doesn't exist - this is an orphaned assignment, mark it for clearing
            console.log(`Registration ${reg.name} has lodgeId ${regLodgeId} but lodge doesn't exist - orphaned assignment`);
            alreadyAssignedRegIds.add(reg.id);
            alreadyAssigned.push({
              registrationId: reg.id,
              registrationName: reg.name,
              reason: `Already assigned to deleted/unknown lodge (ID: ${regLodgeId}) - needs clearing`,
              type: reg.type,
              lodgeId: regLodgeId
            });
          }
        }
      });
      
      // Also check lodges for existing assignments (both new and old structure)
      console.log('Checking lodges for existing assignments...');
      currentLodges.forEach(lodge => {
        // Check old structure (assignedRooms)
        if (lodge.assignedRooms && Array.isArray(lodge.assignedRooms)) {
          console.log(`Checking lodge ${lodge.name} (old structure): ${lodge.assignedRooms.length} assigned rooms`);
          lodge.assignedRooms.forEach(assignment => {
            if (assignment.registrationId) {
              console.log(`  Found assignment in ${lodge.name}: registrationId=${assignment.registrationId}`);
              if (!alreadyAssignedRegIds.has(assignment.registrationId)) {
              alreadyAssignedRegIds.add(assignment.registrationId);
                const reg = currentRegistrations.find(r => r.id === assignment.registrationId);
                if (reg) {
                  console.log(`  → Marking ${reg.name} as already assigned (found in lodge ${lodge.name})`);
                  alreadyAssigned.push({
                    registrationId: reg.id,
                    registrationName: reg.name,
                    reason: `Already assigned to ${lodge.name} (Room ${assignment.roomNumber})`,
                    type: reg.type,
                    lodgeId: lodge.id
                  });
                }
              }
            }
          });
        }
        // Check new structure (rooms.assignedBeds)
        if (lodge.rooms && Array.isArray(lodge.rooms)) {
          lodge.rooms.forEach(room => {
            if (room.assignedBeds && Array.isArray(room.assignedBeds)) {
              room.assignedBeds.forEach(bed => {
                if (bed.registrationId) {
                  if (!alreadyAssignedRegIds.has(bed.registrationId)) {
                    alreadyAssignedRegIds.add(bed.registrationId);
                    const reg = currentRegistrations.find(r => r.id === bed.registrationId);
                    if (reg) {
                      alreadyAssigned.push({
                        registrationId: reg.id,
                        registrationName: reg.name,
                        reason: `Already assigned to ${lodge.name} (Room ${room.roomNumber}, Bed ${bed.bedNumber})`,
                        type: reg.type,
                        lodgeId: lodge.id
                      });
                    }
                  }
                }
              });
            }
          });
        }
      });
      
      // Add already assigned to skipped list
      skipped.push(...alreadyAssigned);
      
      // Helper: registration eligible for Family Lodge (adults, guest speaker, elementary, below elementary, middle school staying with parents only)
      const isFamilyLodgeEligibleInEngine = (registration) => {
        const regData = registration.data || {};
        const category = (regData.camperCategory || registration.camperCategory || '').trim();
        const stayPreference = regData.stayPreference || registration.stayPreference || '';
        const isAdult = category === 'Adults' || category === 'Adult' || (category && category.includes('Adult'));
        const isGuestSpeaker = category === 'Guest Speaker';
        const isElementary = category === 'Elementary';
        const isBelowElementary = category === BELOW_ELEMENTARY_CAMPER_CATEGORY;
        const isMiddleWithParents = category === 'Middle School' && stayPreference === 'withParents';
        return isAdult || isGuestSpeaker || isElementary || isBelowElementary || isMiddleWithParents;
      };

      // Helper function to check if a registration matches lodge restrictions
      // This should match the logic in LodgeManagement component for consistency
      const matchesLodgeRestrictions = (lodge, registration, category) => {
        // If lodge has no restrictions, it's open to all (backward compatibility)
        if (!lodge.lodgeMainCategory) {
          return true;
        }
        
        // Family lodges: only adults, guest speaker, elementary, below elementary, and middle school staying with parents
        if (lodge.lodgeMainCategory === 'Family Lodge') {
          return isFamilyLodgeEligibleInEngine(registration);
        }
        
        // Student lodges have restrictions (category and gender are optional)
        if (lodge.lodgeMainCategory === 'Student Lodge') {
          const hasCategoryRestriction = (lodge.camperCategories && lodge.camperCategories.length > 0) || lodge.camperCategory;
          if (!hasCategoryRestriction && !lodge.gender) {
            return true; // No restrictions defined, allow all
          }

          const regData = registration.data || {};
          const regType = registration.type || '';

          // Exclude family-type only when lodge does NOT allow Adults (students-only)
          const lodgeAllowsAdults = (lodge.camperCategories || []).some(c => {
            const cc = (c || '').trim().toLowerCase();
            return cc === 'adult' || cc === 'adults' || cc.includes('adult');
          });
          if (regType && regType.toLowerCase().includes('family') && !lodgeAllowsAdults) {
            return false;
          }

          const regCamperCategory = (regData.camperCategory || registration.camperCategory || '').trim();
          const regGender = (regData.gender || registration.gender || '').trim();
          const regCategory = categorizeRegistration(registration);

          if (lodge.gender && regGender && regGender.toLowerCase() !== lodge.gender.trim().toLowerCase()) {
            return false;
          }

          // Multi-category lodge: check if reg's category is in the allowed list
          if (lodge.camperCategories && lodge.camperCategories.length > 0) {
            const belowElem = BELOW_ELEMENTARY_CAMPER_CATEGORY.toLowerCase();
            const inList = lodge.camperCategories.some(c => {
              const cc = (c || '').trim().toLowerCase();
              const rcc = regCamperCategory.toLowerCase();
              if (cc === rcc) return true;
              if (cc === 'adult' && (rcc === 'adults' || rcc.includes('adult'))) return true;
              if (cc === 'elementary' && rcc === belowElem) return true;
              if (cc === belowElem && rcc === belowElem) return true;
              return false;
            });
            const studentCatMatch = lodge.camperCategories.some(c => {
              const cc = (c || '').trim();
              return (cc === 'Middle School' && regCategory.studentCategory === 'middleSchool') ||
                (cc === 'High School' && regCategory.studentCategory === 'highSchool') ||
                (cc === 'College' && regCategory.studentCategory === 'college') ||
                (cc === 'Adult' && regCategory.isAdult) ||
                (cc === 'Elementary' && (regCamperCategory.toLowerCase().includes('elementary') || regCategory.studentCategory === 'elementary')) ||
                (cc === BELOW_ELEMENTARY_CAMPER_CATEGORY && regCamperCategory.toLowerCase() === belowElem);
            });
            return inList || studentCatMatch;
          }

          // Single category lodge - original logic
          if (lodge.camperCategory) {
            const categoryMatch = regCamperCategory.toLowerCase() === lodge.camperCategory.trim().toLowerCase();
            let studentCategoryMatch = false;
            if (lodge.camperCategory === 'Middle School' && regCategory.studentCategory === 'middleSchool') studentCategoryMatch = true;
            else if (lodge.camperCategory === 'High School' && regCategory.studentCategory === 'highSchool') studentCategoryMatch = true;
            else if (lodge.camperCategory === 'College' && regCategory.studentCategory === 'college') studentCategoryMatch = true;
            else if (lodge.camperCategory === 'Adult' && regCategory.isAdult) studentCategoryMatch = true;
            if (!categoryMatch && !studentCategoryMatch) return false;
          }

          return true;
        }
        
        return false;
      };
      
      // Group lodges by category (using new field or fallback to old logic)
      const familyLodges = currentLodges.filter(l => 
        l.lodgeMainCategory === 'Family Lodge' || 
        l.type === 'family' || 
        (!l.lodgeMainCategory && !l.type && l.name?.toLowerCase().includes('family'))
      );
      
      // Group student lodges by gender and category
      const maleStudentLodges = currentLodges.filter(l => {
        if (l.lodgeMainCategory === 'Student Lodge' && l.gender === 'Male') return true;
        // Fallback to old logic
        return !l.lodgeMainCategory && (l.type === 'male_student' || 
          l.name?.toLowerCase().includes('male') || 
          l.name?.toLowerCase().includes('boys'));
      });
      
      const femaleStudentLodges = currentLodges.filter(l => {
        if (l.lodgeMainCategory === 'Student Lodge' && l.gender === 'Female') return true;
        // Fallback to old logic
        return !l.lodgeMainCategory && (l.type === 'female_student' || 
          l.name?.toLowerCase().includes('female') || 
          l.name?.toLowerCase().includes('girls'));
      });

      // If no type-based lodges, use all lodges and categorize by name
      const allLodges = currentLodges.length > 0 ? currentLodges : [];
      
      const assignments = [];
      const unresolved = [];
      const capacityInfo = [];
      const assignedRegIds = new Set(alreadyAssignedRegIds); // Start with already assigned registrations

      // Track room usage - support both new structure (rooms array) and old structure (totalRooms)
      const roomUsage = new Map(); // lodgeId -> { used: number, capacity: number }
      currentLodges.forEach(lodge => {
        let used = 0;
        let capacity = 0;
        
        // Check new structure (rooms array with bedCount)
        if (lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0) {
          capacity = lodge.rooms.reduce((sum, room) => sum + (room.bedCount || 1), 0);
          used = lodge.rooms.reduce((sum, room) => sum + (room.assignedBeds?.length || 0), 0);
        } else {
          // Old structure
          used = lodge.assignedRooms?.length || 0;
          capacity = lodge.totalRooms || 0;
        }
        
        roomUsage.set(lodge.id, {
          used: used,
          capacity: capacity,
          lodge: lodge
        });
      });

      // Filter out already assigned registrations from processing
      const unassignedConfirmedRegs = confirmedRegs.filter(reg => !alreadyAssignedRegIds.has(reg.id));
      
      // Step 1: Process family registrations (only unassigned ones)
      const familyGroups = getFamilyGroups(currentRegistrations).map(family => 
        family.filter(reg => !alreadyAssignedRegIds.has(reg.id))
      ).filter(family => family.length > 0);
      
      for (const family of familyGroups) {
        const familyMembers = family.map(reg => ({
          registration: reg,
          category: categorizeRegistration(reg)
        }));

        // Check if any child wants to stay with parents
        const childrenWithParents = familyMembers.filter(m => 
          !m.category.isAdult && 
          m.category.stayPreference === 'withParents'
        );
        
        const adults = familyMembers.filter(m => m.category.isAdult);
        const childrenWithGroup = familyMembers.filter(m => 
          !m.category.isAdult && 
          m.category.stayPreference === 'withGroup'
        );

        // If at least one child wants to stay with parents, assign family room
        if (childrenWithParents.length > 0 && adults.length > 0) {
          // Find available family room (must be Family Lodge)
          let assigned = false;
          const availableFamilyLodges = familyLodges.filter(lodge => {
            const usage = roomUsage.get(lodge.id);
            return usage && usage.used < usage.capacity;
          });
          
          const familyCodeForRoom = familyMembers[0]?.registration?.familyCode ?? null;
          for (const lodge of availableFamilyLodges.length > 0 ? availableFamilyLodges : allLodges.filter(l => !l.lodgeMainCategory || l.lodgeMainCategory === 'Family Lodge')) {
            const usage = roomUsage.get(lodge.id);
            if (!usage || usage.used >= usage.capacity) continue;
            const roomResult = findAvailableRoomInFamilyLodge(lodge, assignments, familyCodeForRoom, familyMembers.length);
            if (roomResult) {
              const { roomNumber, bedNumbers, extraSlots } = roomResult;
              familyMembers.forEach((member, idx) => {
                const roomNum = (extraSlots && idx > 0 && extraSlots[idx - 1] != null) ? extraSlots[idx - 1] : (roomNumber?.toString?.() ?? roomNumber);
                const bedNum = bedNumbers?.[idx] ?? idx + 1;
                assignments.push({
                  registrationId: member.registration.id,
                  registrationName: member.registration.name,
                  lodgeId: lodge.id,
                  lodgeName: lodge.name,
                  roomNumber: roomNum?.toString?.() ?? roomNum,
                  bedNumber: bedNum,
                  assignmentType: 'family',
                  familyCode: member.registration.familyCode,
                  camperCategory: member.registration.camperCategory || member.registration.data?.camperCategory || 'N/A',
                  churchName: member.registration.data?.churchName || member.registration.churchName || 'N/A',
                  city: member.registration.data?.city || member.registration.city || 'N/A'
                });
                assignedRegIds.add(member.registration.id);
              });
              usage.used += familyMembers.length;
              assigned = true;
              break;
            }
          }
          
          if (!assigned) {
            unresolved.push({
              familyCode: family[0].familyCode,
              reason: 'No available family rooms',
              members: family.map(f => ({ name: f.name, category: categorizeRegistration(f) }))
            });
          }
        } else {
          // All children want to stay with group - split family
          // Adults go to family lodges (no separate adult lodges)
          adults.forEach(adult => {
            if (!assignedRegIds.has(adult.registration.id)) {
              const availableFamilyLodges = familyLodges.filter(lodge => {
                const usage = roomUsage.get(lodge.id);
                return usage && usage.used < usage.capacity;
              });
              let assigned = false;
              
              for (const lodge of availableFamilyLodges) {
                const usage = roomUsage.get(lodge.id);
                if (!usage || usage.used >= usage.capacity) continue;
                const roomResult = findAvailableRoomInFamilyLodge(lodge, assignments, adult.registration.familyCode ?? null, 1);
                if (roomResult) {
                  const bedNumber = roomResult.bedNumbers?.[0] ?? 1;
                  assignments.push({
                    registrationId: adult.registration.id,
                    registrationName: adult.registration.name,
                    lodgeId: lodge.id,
                    lodgeName: lodge.name,
                    roomNumber: roomResult.roomNumber?.toString?.() ?? roomResult.roomNumber,
                    bedNumber,
                    assignmentType: 'adult',
                    familyCode: adult.registration.familyCode,
                    camperCategory: adult.registration.camperCategory || adult.registration.data?.camperCategory || 'N/A',
                    churchName: adult.registration.data?.churchName || adult.registration.churchName || 'N/A',
                    city: adult.registration.data?.city || adult.registration.city || 'N/A'
                  });
                  assignedRegIds.add(adult.registration.id);
                  usage.used += 1;
                  assigned = true;
                  break;
                }
              }
              
              if (!assigned) {
                unresolved.push({
                  familyCode: adult.registration.familyCode,
                  reason: 'No available family rooms',
                  members: [{ name: adult.registration.name, category: adult.category }]
                });
              }
            }
          });

          // Children go to gender-based student dorms
          [...childrenWithParents, ...childrenWithGroup].forEach(child => {
            if (!assignedRegIds.has(child.registration.id)) {
              // First filter by gender
              const genderFilteredLodges = child.category.gender === 'Male' || child.category.gender === 'male' 
                ? (maleStudentLodges.length > 0 ? maleStudentLodges : allLodges)
                : (femaleStudentLodges.length > 0 ? femaleStudentLodges : allLodges);
              
              // Then filter by lodge restrictions (camper category and gender)
              const targetLodges = genderFilteredLodges.filter(l => 
                matchesLodgeRestrictions(l, child.registration, child.category)
              );
              
              let assigned = false;
              for (const lodge of targetLodges) {
                const usage = roomUsage.get(lodge.id);
                if (usage && usage.used < usage.capacity) {
                  const roomBed = findAvailableRoomAndBed(lodge);
                  if (roomBed) {
                  assignments.push({
                    registrationId: child.registration.id,
                    registrationName: child.registration.name,
                    lodgeId: lodge.id,
                    lodgeName: lodge.name,
                      roomNumber: roomBed.roomNumber,
                      bedNumber: roomBed.bedNumber,
                    assignmentType: child.category.studentCategory || 'student',
                    familyCode: child.registration.familyCode,
                    camperCategory: child.registration.camperCategory || child.registration.data?.camperCategory || 'N/A',
                    churchName: child.registration.data?.churchName || child.registration.churchName || 'N/A',
                    city: child.registration.data?.city || child.registration.city || 'N/A'
                  });
                  assignedRegIds.add(child.registration.id);
                  usage.used += 1;
                  assigned = true;
                  break;
                  }
                }
              }
              
              if (!assigned) {
                unresolved.push({
                  familyCode: child.registration.familyCode,
                  reason: `No available ${child.category.gender} student rooms`,
                  members: [{ name: child.registration.name, category: child.category }]
                });
              }
            }
          });
        }
      }

      // Step 2: Process single students (not in families, not already assigned)
      const singleStudents = unassignedConfirmedRegs.filter(reg => 
        !reg.familyCode && 
        !assignedRegIds.has(reg.id) &&
        (reg.type === 'student' || reg.type === 'earlyBirdStudent')
      );

      // Debug: Log single students found
      console.log('Room Assignment Engine - Single Students:', {
        totalUnassignedConfirmed: unassignedConfirmedRegs.length,
        singleStudentsCount: singleStudents.length,
        singleStudents: singleStudents.map(r => ({
          id: r.id,
          name: r.name,
          type: r.type,
          camperCategory: r.camperCategory || r.data?.camperCategory || 'N/A',
          gender: r.gender || r.data?.gender || 'N/A',
          status: r.status
        }))
      });

      singleStudents.forEach(reg => {
        const category = categorizeRegistration(reg);
        if (!category.isAdult) {
          // First filter by gender
          const genderFilteredLodges = category.gender === 'Male' || category.gender === 'male'
            ? (maleStudentLodges.length > 0 ? maleStudentLodges : allLodges)
            : (femaleStudentLodges.length > 0 ? femaleStudentLodges : allLodges);
          
          // Then filter by lodge restrictions (camper category and gender)
          const targetLodges = genderFilteredLodges.filter(lodge => 
            matchesLodgeRestrictions(lodge, reg, category)
          );
          
          // Debug: Log matching lodges for this student
          if (process.env.NODE_ENV === 'development') {
            console.log(`Matching lodges for ${reg.name}:`, {
              regCamperCategory: reg.camperCategory || reg.data?.camperCategory,
              regGender: reg.gender || reg.data?.gender,
              studentCategory: category.studentCategory,
              genderFilteredCount: genderFilteredLodges.length,
              matchingLodgesCount: targetLodges.length,
              matchingLodges: targetLodges.map(l => ({
                id: l.id,
                name: l.name,
                camperCategory: l.camperCategory,
                gender: l.gender
              }))
            });
          }
          
          let assigned = false;
          for (const lodge of targetLodges) {
            const usage = roomUsage.get(lodge.id);
            if (usage && usage.used < usage.capacity) {
              const roomBed = findAvailableRoomAndBed(lodge);
              if (roomBed) {
              assignments.push({
                registrationId: reg.id,
                registrationName: reg.name,
                lodgeId: lodge.id,
                lodgeName: lodge.name,
                  roomNumber: roomBed.roomNumber,
                  bedNumber: roomBed.bedNumber,
                assignmentType: category.studentCategory || 'student',
                familyCode: null,
                camperCategory: reg.camperCategory || reg.data?.camperCategory || 'N/A',
                churchName: reg.data?.churchName || reg.churchName || 'N/A',
                city: reg.data?.city || reg.city || 'N/A'
              });
              assignedRegIds.add(reg.id);
              usage.used += 1;
              assigned = true;
              break;
              }
            }
          }
          
          if (!assigned) {
            unresolved.push({
              familyCode: null,
              reason: `No available ${category.gender} student rooms`,
              members: [{ name: reg.name, category: category }]
            });
          }
        }
      });

      // Step 3: Process single adults (not in families, not already assigned) — assign to family lodges only
      const singleAdults = unassignedConfirmedRegs.filter(reg => 
        !reg.familyCode && 
        !assignedRegIds.has(reg.id) &&
        (reg.type === 'adult' || categorizeRegistration(reg).isAdult)
      );

      singleAdults.forEach(reg => {
        const availableFamilyLodges = familyLodges.filter(lodge => {
          const usage = roomUsage.get(lodge.id);
          return usage && usage.used < usage.capacity;
        });
        let assigned = false;
        
        for (const lodge of availableFamilyLodges) {
          const usage = roomUsage.get(lodge.id);
          if (usage && usage.used < usage.capacity) {
            const roomResult = findAvailableRoomInFamilyLodge(lodge, assignments, null, 1);
            if (roomResult) {
            assignments.push({
              registrationId: reg.id,
              registrationName: reg.name,
              lodgeId: lodge.id,
              lodgeName: lodge.name,
              roomNumber: roomResult.roomNumber,
              bedNumber: (roomResult.bedNumbers && roomResult.bedNumbers[0] != null) ? roomResult.bedNumbers[0] : 1,
              assignmentType: 'adult',
              familyCode: null,
              camperCategory: reg.camperCategory || reg.data?.camperCategory || 'N/A',
              churchName: reg.data?.churchName || reg.churchName || 'N/A',
              city: reg.data?.city || reg.city || 'N/A'
            });
            assignedRegIds.add(reg.id);
            usage.used += 1;
            assigned = true;
            break;
            }
          }
        }
        
        if (!assigned) {
          unresolved.push({
            familyCode: null,
            reason: 'No available family rooms',
            members: [{ name: reg.name, category: categorizeRegistration(reg) }]
          });
        }
      });

      // Step 4: Add any remaining unassigned registrations to unresolved (no Family Lodge fallback)
      const remainingUnassigned = unassignedConfirmedRegs.filter(reg => 
        !assignedRegIds.has(reg.id)
      );

      remainingUnassigned.forEach(reg => {
        const regCategory = categorizeRegistration(reg);
        unresolved.push({
          registrationId: reg.id,
          registrationName: reg.name,
          familyCode: reg.familyCode || null,
          reason: 'No available rooms in matching lodge type',
          members: [{ name: reg.name, category: regCategory.studentCategory || (regCategory.isAdult ? 'adult' : 'unknown') }]
        });
        skipped.push({
          registrationId: reg.id,
          registrationName: reg.name,
          reason: 'No available rooms in matching lodge type (students need student lodges; adults need adult lodges; families need family lodges)',
          type: reg.type,
          status: reg.status
        });
      });

      // Build capacity info
      roomUsage.forEach((usage, lodgeId) => {
        const lodge = usage.lodge;
        
        // Calculate current utilization from actual assignments in database
        let currentUsed = 0;
        const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
        
        if (hasNewStructure) {
          // New structure: count assigned beds
          currentUsed = lodge.rooms.reduce((sum, room) => {
            return sum + (room.assignedBeds?.length || 0);
          }, 0);
        } else {
          // Old structure: count assigned rooms
          currentUsed = lodge.assignedRooms?.length || 0;
        }
        
        const currentUtilizationRate = usage.capacity > 0 ? (currentUsed / usage.capacity * 100).toFixed(1) : 0;
        const projectedUtilizationRate = usage.capacity > 0 ? (usage.used / usage.capacity * 100).toFixed(1) : 0;
        
        capacityInfo.push({
          lodgeId: lodgeId,
          lodgeName: usage.lodge.name,
          used: usage.used, // Projected (from suggested assignments)
          currentUsed: currentUsed, // Current (from database)
          capacity: usage.capacity,
          available: usage.capacity - usage.used,
          currentUtilizationRate: currentUtilizationRate,
          utilizationRate: projectedUtilizationRate // Projected utilization
        });
      });

      // Rebalancing: If any lodges are over capacity, redistribute
      const overCapacityLodges = capacityInfo.filter(r => r.used > r.capacity);
      if (overCapacityLodges.length > 0) {
        // Find lodges with available space
        const availableLodges = capacityInfo.filter(r => r.available > 0);
        
        for (const overLodge of overCapacityLodges) {
          // eslint-disable-next-line no-unused-vars
          const excess = overLodge.used - overLodge.capacity;
          // Find assignments for this lodge that can be moved
          const assignmentsToMove = assignments
            .filter(a => a.lodgeId === overLodge.lodgeId)
            .slice(overLodge.capacity); // Get excess assignments
          
          // Try to redistribute
          for (const assignment of assignmentsToMove) {
            const targetLodge = availableLodges.find(l => 
              l.lodgeName.toLowerCase().includes(assignment.assignmentType) ||
              l.available > 0
            );
            
            if (targetLodge) {
              assignment.lodgeId = targetLodge.lodgeId;
              assignment.lodgeName = targetLodge.lodgeName;
              assignment.roomNumber = targetLodge.capacity - targetLodge.available + 1;
              
              // Update capacity info
              const overUsage = roomUsage.get(overLodge.lodgeId);
              const targetUsage = roomUsage.get(targetLodge.lodgeId);
              if (overUsage) overUsage.used--;
              if (targetUsage) targetUsage.used++;
              
              targetLodge.used++;
              targetLodge.available--;
              overLodge.used--;
            }
          }
        }
      }

      setRoomAssignments(assignments);
      setUnresolvedFamilies(unresolved);
      setRoomsWithCapacityInfo(capacityInfo);
      setSkippedRegistrations(skipped);

      setAssignmentResults({
        totalAssignments: assignments.length,
        unresolvedCount: unresolved.length,
        totalRegistrations: confirmedRegs.length,
        skippedCount: skipped.length,
        totalRegistrationsAll: registrations.length
      });

    } catch (error) {
      console.error('Error processing room assignments:', error);
      alert('Error processing room assignments: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Remove all current room assignments
  const removeAllAssignments = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to remove ALL current room assignments? This will clear all assignments from all lodges and registrations. This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setIsProcessing(true);

      // Get all current assignments
      const allAssignments = getCurrentAssignments();
      
      if (allAssignments.length === 0) {
        alert('No assignments to remove.');
        setIsProcessing(false);
        return;
      }

      // Clear assignments from all lodges
      for (const lodge of lodges) {
        const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
        
        if (hasNewStructure) {
          // New structure: clear all assignedBeds from all rooms
          const updatedRooms = lodge.rooms.map(room => ({
            ...room,
            assignedBeds: []
          }));
          await lodgesAPI.update(lodge.id, { rooms: updatedRooms });
        } else {
          // Old structure: clear assignedRooms
          await lodgesAPI.update(lodge.id, { assignedRooms: [] });
        }
      }

      // Clear lodgeId and roomNumber from all registrations
      const updatePromises = allAssignments.map(assignment =>
        registrationsAPI.update(assignment.registrationId, {
          data: {
            lodgeId: null,
            roomNumber: null
          }
        })
      );

      await Promise.all(updatePromises);

      // Reload data
      await onUpdateRegistrations();
      await onUpdateLodges();

      alert(`Successfully removed all ${allAssignments.length} room assignments!`);
      
      // Reload data and re-run assignment engine
      await onUpdateRegistrations();
      await onUpdateLodges();
      await processRoomAssignments();
    } catch (error) {
      console.error('Error removing all assignments:', error);
      alert('Error removing assignments: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const clearSkippedAssignments = async () => {
    // Filter skipped registrations that are marked as "Already assigned"
    const alreadyAssignedSkipped = skippedRegistrations.filter(
      skipped => skipped.reason && (skipped.reason.includes('Already assigned') || skipped.reason.includes('Orphaned assignment'))
    );

    if (alreadyAssignedSkipped.length === 0) {
      alert('No assigned or orphaned registrations to clear.');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to clear assignments from ${alreadyAssignedSkipped.length} registration(s)? This will remove their lodge assignments so they can be reassigned.`
    );

    if (!confirmed) return;

    try {
      setIsProcessing(true);

      // Clear assignments from registrations - clear both top-level and data.lodgeId
      console.log('Clearing assignments from registrations:', alreadyAssignedSkipped.map(s => s.registrationName));
      const updatePromises = alreadyAssignedSkipped.map(async (skipped) => {
        const updateData = {
          lodgeId: null,
          roomNumber: null,
          data: {
            lodgeId: null,
            roomNumber: null
          }
        };
        console.log(`Clearing assignment for ${skipped.registrationName} (${skipped.registrationId}):`, updateData);
        return registrationsAPI.update(skipped.registrationId, updateData);
      });

      await Promise.all(updatePromises);
      console.log('All registration updates completed');

      // Clear assignments from lodges (both old and new structure)
      console.log('Clearing assignments from lodges...');
      const clearedRegIds = alreadyAssignedSkipped.map(s => s.registrationId);
      console.log('Registration IDs to clear from lodges:', clearedRegIds);
      
      // Fetch fresh lodges to ensure we have the latest data
      console.log('Fetching fresh lodges data...');
      const freshLodgesForClear = await lodgesAPI.getAll();
      console.log(`Fetched ${freshLodgesForClear.length} lodges`);
      
      const foundAssignments = []; // Track assignments found in lodges
      
      console.log('Inspecting all lodges for assignments:');
      freshLodgesForClear.forEach(lodge => {
        console.log(`Lodge ${lodge.name} (${lodge.id}):`, {
          hasNewStructure: !!(lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0),
          assignedRooms: lodge.assignedRooms,
          assignedRoomsCount: (lodge.assignedRooms || []).length,
          rooms: lodge.rooms ? lodge.rooms.map(r => ({
            roomNumber: r.roomNumber,
            assignedBeds: r.assignedBeds,
            assignedBedsCount: (r.assignedBeds || []).length
          })) : null
        });
      });
      
      for (const lodge of freshLodgesForClear) {
        const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
        let needsUpdateNew = false;
        let needsUpdateOld = false;
        let updatedRooms = [];

        // Check BOTH structures - lodges can have both old and new structures
        // First, check new structure (rooms.assignedBeds)
        if (hasNewStructure) {
          // New structure: remove assigned beds for these registrations
          updatedRooms = lodge.rooms.map(room => {
            const beforeCount = (room.assignedBeds || []).length;
            const filteredBeds = (room.assignedBeds || []).filter(bed => {
              const shouldRemove = clearedRegIds.includes(bed.registrationId);
              if (shouldRemove) {
                foundAssignments.push({ lodge: lodge.name, room: room.roomNumber, bed: bed.bedNumber, regId: bed.registrationId });
                console.log(`Found assignment in ${lodge.name} Room ${room.roomNumber} (new structure): removing bed for ${bed.registrationId}`);
              }
              return !shouldRemove;
            });
            if (filteredBeds.length !== beforeCount) {
              needsUpdateNew = true;
              console.log(`  Will remove ${beforeCount - filteredBeds.length} bed assignment(s) from ${lodge.name} Room ${room.roomNumber}`);
            }
            return {
              ...room,
              assignedBeds: filteredBeds
            };
          });
        }
        
        // Also check old structure (assignedRooms) - even if new structure exists
        const beforeCountOld = (lodge.assignedRooms || []).length;
        if (beforeCountOld > 0) {
          console.log(`Checking lodge ${lodge.name} (old structure): ${beforeCountOld} assigned rooms`);
          console.log(`  Assigned rooms:`, lodge.assignedRooms);
          console.log(`  Looking for registration IDs:`, clearedRegIds);
          const filteredAssignments = (lodge.assignedRooms || []).filter(assignment => {
            const assignmentRegId = assignment.registrationId;
            const shouldRemove = clearedRegIds.includes(assignmentRegId);
            console.log(`  Assignment in ${lodge.name}: registrationId=${assignmentRegId}, shouldRemove=${shouldRemove}`);
            if (shouldRemove) {
              foundAssignments.push({ lodge: lodge.name, room: assignment.roomNumber, regId: assignmentRegId });
              console.log(`  ✓ Will remove assignment for ${assignmentRegId} from ${lodge.name} (old structure)`);
            }
            return !shouldRemove;
          });
          if (filteredAssignments.length !== beforeCountOld) {
            needsUpdateOld = true;
            console.log(`Found assignment in ${lodge.name} (old structure): removing ${beforeCountOld - filteredAssignments.length} assignment(s)`);
            console.log(`  Before: ${beforeCountOld} assignments, After: ${filteredAssignments.length} assignments`);
            await lodgesAPI.update(lodge.id, { assignedRooms: filteredAssignments });
            console.log(`  ✓ Updated lodge ${lodge.name} (old structure), verifying update...`);
            
            // Verify the update worked by fetching the lodge again
            const verifyLodge = await lodgesAPI.getById(lodge.id);
            const verifyCount = (verifyLodge.assignedRooms || []).length;
            console.log(`  Verification: lodge ${lodge.name} now has ${verifyCount} assigned rooms (expected ${filteredAssignments.length})`);
            if (verifyCount !== filteredAssignments.length) {
              console.error(`  ⚠️ WARNING: Update verification failed! Expected ${filteredAssignments.length}, got ${verifyCount}`);
            }
          } else {
            console.log(`  No assignments to remove from ${lodge.name} old structure (${beforeCountOld} assignments, none match cleared IDs)`);
          }
        }

        // Update new structure if needed
        if (hasNewStructure && needsUpdateNew) {
          console.log(`Updating lodge ${lodge.name} with cleared assignments (new structure)`);
          await lodgesAPI.update(lodge.id, { rooms: updatedRooms });
          console.log(`  ✓ Updated lodge ${lodge.name} (new structure)`);
        }
      }
      
      if (foundAssignments.length > 0) {
        console.log('Found and cleared assignments from lodges:', foundAssignments);
      } else {
        console.log('No assignments found in lodges to clear');
      }

      alert(`Successfully cleared assignments from ${alreadyAssignedSkipped.length} registration(s)!`);
      
      // Reload data and wait a bit for database to sync
      console.log('Reloading registrations and lodges...');
      await onUpdateRegistrations();
      await onUpdateLodges();
      
      // Verify the cleared registrations actually have null lodgeId
      console.log('Verifying cleared registrations after reload...');
      const freshRegistrations = await registrationsAPI.getAll();
      const freshLodges = await lodgesAPI.getAll();
      
      // Helper function to check if registration is still in any lodge
      const checkRegistrationInLodges = (regId, regName) => {
          let foundInLodge = false;
          for (const lodge of freshLodges) {
            const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
            if (hasNewStructure) {
              const found = lodge.rooms.some(room => 
                room.assignedBeds?.some(bed => bed.registrationId === regId)
              );
              if (found) {
              console.log(`⚠️ WARNING: Registration ${regName} still found in lodge ${lodge.name} (new structure)`);
                foundInLodge = true;
              }
            } else {
              const found = (lodge.assignedRooms || []).some(a => a.registrationId === regId);
              if (found) {
              console.log(`⚠️ WARNING: Registration ${regName} still found in lodge ${lodge.name} (old structure)`);
                foundInLodge = true;
              }
            }
          }
        return foundInLodge;
      };
      
      clearedRegIds.forEach(regId => {
        const reg = freshRegistrations.find(r => r.id === regId);
        if (reg) {
          const regLodgeId = reg.data?.lodgeId || reg.lodgeId;
          console.log(`Registration ${reg.name} (${regId}):`, {
            lodgeId: reg.lodgeId,
            dataLodgeId: reg.data?.lodgeId,
            roomNumber: reg.roomNumber,
            dataRoomNumber: reg.data?.roomNumber,
            hasLodgeId: !!regLodgeId,
            lodgeIdValue: regLodgeId
          });
          
          // Also check if assignment still exists in any lodge
          const foundInLodge = checkRegistrationInLodges(regId, reg.name);
          if (!foundInLodge) {
            console.log(`✓ Registration ${reg.name} not found in any lodge - cleared successfully`);
          }
        }
      });
      
      // Small delay to ensure data is fully refreshed
      console.log('Waiting for data to sync...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Re-run assignment engine
      console.log('Re-running assignment engine...');
      await processRoomAssignments();
    } catch (error) {
      console.error('Error clearing skipped assignments:', error);
      alert('Error clearing assignments: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const clearSingleAssignment = async (registrationId, registrationName) => {
    const confirmed = window.confirm(
      `Are you sure you want to clear the assignment for "${registrationName}"? This will remove their lodge assignment so they can be reassigned.`
    );

    if (!confirmed) return;

    try {
      setIsProcessing(true);

      // Clear assignment from registration - clear both top-level and data.lodgeId
      const updateData = {
        lodgeId: null,
        roomNumber: null,
        data: {
          lodgeId: null,
          roomNumber: null
        }
      };
      console.log(`Clearing assignment for ${registrationName} (${registrationId}):`, updateData);
      await registrationsAPI.update(registrationId, updateData);
      console.log('Registration update completed');

      // Clear assignment from lodges (both old and new structure)
      // Fetch fresh lodges to ensure we have the latest data
      console.log('Fetching fresh lodges data for single assignment clear...');
      const freshLodgesForSingle = await lodgesAPI.getAll();
      console.log(`Fetched ${freshLodgesForSingle.length} lodges`);
      
      console.log(`Looking for registration ID: ${registrationId} in lodges...`);
      let foundInLodge = false;
      
      for (const lodge of freshLodgesForSingle) {
        const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
        let needsUpdateNew = false;
        let needsUpdateOld = false;

        // Check BOTH structures - lodges can have both old and new structures
        // First, check new structure (rooms.assignedBeds)
        if (hasNewStructure) {
          // New structure: remove assigned bed for this registration
          console.log(`Checking lodge ${lodge.name} (new structure)...`);
          const updatedRooms = lodge.rooms.map(room => {
            const beforeCount = (room.assignedBeds || []).length;
            let bedFound = false;
            const filteredBeds = (room.assignedBeds || []).filter(bed => {
              const matches = bed.registrationId === registrationId;
              if (matches) {
                console.log(`  Found bed assignment in ${lodge.name} Room ${room.roomNumber} for ${registrationId}`);
                bedFound = true;
              }
              return !matches;
            });
            if (bedFound) {
              foundInLodge = true;
            }
            if (filteredBeds.length !== beforeCount) {
              needsUpdateNew = true;
              console.log(`  Will remove ${beforeCount - filteredBeds.length} bed assignment(s) from ${lodge.name} Room ${room.roomNumber}`);
            }
            return {
              ...room,
              assignedBeds: filteredBeds
            };
          });

          if (needsUpdateNew) {
            console.log(`Updating lodge ${lodge.name} (new structure) to remove assignment...`);
            await lodgesAPI.update(lodge.id, { rooms: updatedRooms });
            console.log(`  ✓ Updated lodge ${lodge.name} (new structure)`);
          }
        }
        
        // Also check old structure (assignedRooms) - even if new structure exists
        const beforeCountOld = (lodge.assignedRooms || []).length;
        if (beforeCountOld > 0) {
          console.log(`Checking lodge ${lodge.name} (old structure): ${beforeCountOld} assigned rooms`);
          console.log(`  Assigned rooms:`, lodge.assignedRooms);
          let assignmentFound = false;
          const filteredAssignments = (lodge.assignedRooms || []).filter(assignment => {
            const matches = assignment.registrationId === registrationId;
            if (matches) {
              console.log(`  ✓ Found assignment in ${lodge.name} for ${registrationId} (Room ${assignment.roomNumber})`);
              assignmentFound = true;
            }
            return !matches;
          });
          if (assignmentFound) {
            foundInLodge = true;
          }
          if (filteredAssignments.length !== beforeCountOld) {
            needsUpdateOld = true;
            console.log(`Updating lodge ${lodge.name} (old structure): removing ${beforeCountOld - filteredAssignments.length} assignment(s)`);
            await lodgesAPI.update(lodge.id, { assignedRooms: filteredAssignments });
            console.log(`  ✓ Updated lodge ${lodge.name} (old structure) with ${filteredAssignments.length} remaining assignments`);
            
            // Verify the update
            const verifyLodge = await lodgesAPI.getById(lodge.id);
            const verifyCount = (verifyLodge.assignedRooms || []).length;
            console.log(`  Verification: lodge ${lodge.name} now has ${verifyCount} assigned rooms (expected ${filteredAssignments.length})`);
            if (verifyCount !== filteredAssignments.length) {
              console.error(`  ⚠️ WARNING: Update verification failed! Expected ${filteredAssignments.length}, got ${verifyCount}`);
            }
          } else {
            console.log(`  No assignments to remove from ${lodge.name} old structure`);
          }
        }
      }
      
      if (!foundInLodge) {
        console.log(`⚠️ WARNING: Registration ${registrationId} not found in any lodge's assignments`);
      } else {
        console.log(`✓ Found and cleared assignment from lodge(s)`);
      }

      alert(`Successfully cleared assignment for "${registrationName}"!`);
      
      // Reload data and wait a bit for database to sync
      console.log('Reloading registrations and lodges...');
      await onUpdateRegistrations();
      await onUpdateLodges();
      
      // Small delay to ensure data is fully refreshed
      console.log('Waiting for data to sync...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Re-run assignment engine
      console.log('Re-running assignment engine...');
      await processRoomAssignments();
    } catch (error) {
      console.error('Error clearing single assignment:', error);
      alert('Error clearing assignment: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Auto-run assignment engine when component loads
  useEffect(() => {
    if (!hasAutoRun && registrations.length > 0 && lodges.length > 0 && !isProcessing) {
      setHasAutoRun(true);
      // Use setTimeout to avoid calling during render
      setTimeout(() => {
        processRoomAssignments();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrations.length, lodges.length]);

  // Apply a single assignment
  const applySingleAssignment = async (assignment) => {
    try {
      setIsProcessing(true);

      // Update registration
      await registrationsAPI.update(assignment.registrationId, {
        data: {
          lodgeId: assignment.lodgeId,
          roomNumber: assignment.roomNumber
        }
      });

      // Find the lodge
      const lodge = lodges.find(l => l.id === assignment.lodgeId);
      if (!lodge) {
        throw new Error('Lodge not found');
      }

      // Support both new structure (rooms array) and old structure (assignedRooms)
      const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
      
      if (hasNewStructure) {
        // New structure: add to rooms.assignedBeds
        const updatedRooms = lodge.rooms.map(room => {
          if (room.roomNumber === assignment.roomNumber.toString()) {
            const assignedBeds = room.assignedBeds || [];
            // Check if already assigned
            if (!assignedBeds.some(bed => bed.registrationId === assignment.registrationId)) {
              // Use bedNumber from assignment if available, otherwise find next available
              let bedNumber = assignment.bedNumber;
              if (!bedNumber) {
                const assignedBedNumbers = new Set(assignedBeds.map(b => b.bedNumber));
                bedNumber = 1;
                while (assignedBedNumbers.has(bedNumber) && bedNumber <= room.bedCount) {
                  bedNumber++;
                }
              }
              if (bedNumber && bedNumber <= room.bedCount) {
                return {
                  ...room,
                  assignedBeds: [...assignedBeds, { 
                    registrationId: assignment.registrationId, 
                    bedNumber: bedNumber 
                  }]
                };
              }
            }
            return room;
          }
          return room;
        });
        await lodgesAPI.update(assignment.lodgeId, { rooms: updatedRooms });
      } else {
        // Old structure: add to assignedRooms
        const existingAssignments = lodge.assignedRooms || [];
        const isDuplicate = existingAssignments.some(a => a.registrationId === assignment.registrationId);
        
        if (!isDuplicate) {
          const assignedRooms = [...existingAssignments, { 
            registrationId: assignment.registrationId, 
            roomNumber: assignment.roomNumber,
            bedNumber: 1
          }];
          await lodgesAPI.update(assignment.lodgeId, { assignedRooms });
        }
      }

      // Remove from suggested assignments
      setRoomAssignments(prev => prev.filter(a => 
        !(a.registrationId === assignment.registrationId && a.lodgeId === assignment.lodgeId)
      ));

      // Reload data
      await onUpdateRegistrations();
      await onUpdateLodges();

      alert(`Successfully assigned ${assignment.registrationName} to ${assignment.lodgeName}, Room ${assignment.roomNumber}!`);

    } catch (error) {
      console.error('Error applying single assignment:', error);
      alert('Error applying assignment: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Apply assignments to database
  const applyAssignments = async () => {
    if (roomAssignments.length === 0) {
      alert('No assignments to apply. Please run the assignment engine first.');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to apply ${roomAssignments.length} room assignments? This will update the database.`
    );

    if (!confirmed) return;

    try {
      setIsProcessing(true);

      // Group assignments by lodge for batch updates
      const lodgeUpdates = new Map();
      
      for (const assignment of roomAssignments) {
        // Update registration
        await registrationsAPI.update(assignment.registrationId, {
          data: {
            lodgeId: assignment.lodgeId,
            roomNumber: assignment.roomNumber
          }
        });

        // Track lodge updates
        if (!lodgeUpdates.has(assignment.lodgeId)) {
          const lodge = lodges.find(l => l.id === assignment.lodgeId);
          lodgeUpdates.set(assignment.lodgeId, {
            lodge: lodge,
            newAssignments: []
          });
        }
        
        lodgeUpdates.get(assignment.lodgeId).newAssignments.push({
          registrationId: assignment.registrationId,
          roomNumber: assignment.roomNumber,
          bedNumber: assignment.bedNumber || 1
        });
      }

      // Update lodges - remove duplicate assignments first and add new ones
      // First, collect all registration IDs that are being assigned
      const assignedRegistrationIds = new Set(roomAssignments.map(a => a.registrationId));
      
      // Remove these registrations from all lodges first (to prevent duplicates)
      for (const lodge of lodges) {
        const hasNewStructure = lodge.rooms && Array.isArray(lodge.rooms) && lodge.rooms.length > 0;
        
        if (hasNewStructure) {
          // New structure: remove from rooms.assignedBeds
          let needsUpdate = false;
          const updatedRooms = lodge.rooms.map(room => {
            if (room.assignedBeds && room.assignedBeds.length > 0) {
              const filteredBeds = room.assignedBeds.filter(bed => 
                !assignedRegistrationIds.has(bed.registrationId)
              );
              if (filteredBeds.length !== room.assignedBeds.length) {
                needsUpdate = true;
                return { ...room, assignedBeds: filteredBeds };
              }
            }
            return room;
          });
          
          if (needsUpdate) {
            await lodgesAPI.update(lodge.id, { rooms: updatedRooms });
          }
        } else {
          // Old structure: remove from assignedRooms
        if (lodge.assignedRooms && Array.isArray(lodge.assignedRooms)) {
          const filteredAssignments = lodge.assignedRooms.filter(assignment => 
            !assignedRegistrationIds.has(assignment.registrationId)
          );
          
          // Only update if assignments were removed
          if (filteredAssignments.length !== lodge.assignedRooms.length) {
            await lodgesAPI.update(lodge.id, {
              assignedRooms: filteredAssignments
            });
            }
          }
        }
      }
      
      // Now add new assignments to target lodges
      for (const [lodgeId, update] of lodgeUpdates) {
        // Get the latest lodge data from the database
        let latestLodge;
        try {
          latestLodge = await lodgesAPI.getById(lodgeId);
        } catch (error) {
          console.warn(`Could not fetch latest lodge data for ${lodgeId}, using cached:`, error);
          latestLodge = update.lodge;
        }
        
        const hasNewStructure = latestLodge.rooms && Array.isArray(latestLodge.rooms) && latestLodge.rooms.length > 0;
        
        if (hasNewStructure) {
          // New structure: add to rooms.assignedBeds
          const updatedRooms = (latestLodge.rooms || []).map(room => {
            // Find assignments for this room
            const roomAssignments = update.newAssignments.filter(a => {
              const assignmentRoomNum = a.roomNumber?.toString();
              const roomNum = room.roomNumber?.toString();
              return assignmentRoomNum === roomNum;
            });
            
            if (roomAssignments.length > 0) {
              // Start with existing assigned beds
              let assignedBeds = [...(room.assignedBeds || [])];
              
              roomAssignments.forEach(assignment => {
                // Check if already assigned
                if (!assignedBeds.some(bed => bed.registrationId === assignment.registrationId)) {
                  // Find the bedNumber from the assignment object
                  let bedNumber = assignment.bedNumber;
                  
                  // If no bedNumber or bed is already taken, find next available
                  if (!bedNumber || assignedBeds.some(bed => bed.bedNumber === bedNumber)) {
                    const assignedBedNumbers = new Set(assignedBeds.map(b => b.bedNumber));
                    bedNumber = 1;
                    while (assignedBedNumbers.has(bedNumber) && bedNumber <= room.bedCount) {
                      bedNumber++;
                    }
                  }
                  
                  // Only add if we found a valid bed number
                  if (bedNumber && bedNumber <= room.bedCount) {
                    assignedBeds.push({
                      registrationId: assignment.registrationId,
                      bedNumber: bedNumber
                    });
                  }
                }
              });
              
              return { ...room, assignedBeds };
            }
            return room;
          });
          
          console.log(`Updating lodge ${lodgeId} with ${update.newAssignments.length} assignments:`, {
            lodgeName: latestLodge.name,
            updatedRooms: updatedRooms.map(r => ({
              roomNumber: r.roomNumber,
              bedCount: r.bedCount,
              assignedBedsCount: r.assignedBeds?.length || 0
            }))
          });
          
          await lodgesAPI.update(lodgeId, { rooms: updatedRooms });
        } else {
          // Old structure: add to assignedRooms
          const existingAssignments = latestLodge.assignedRooms || [];
        
        // Filter out any assignments that are in the new assignments list (to avoid duplicates)
        const filteredExisting = existingAssignments.filter(existing => 
          !update.newAssignments.some(newAssign => newAssign.registrationId === existing.registrationId)
        );
          
          // Convert new assignments to old format
          const newAssignmentsOldFormat = update.newAssignments.map(a => ({
            registrationId: a.registrationId,
            roomNumber: a.roomNumber,
            bedNumber: a.bedNumber || 1
          }));
        
        // Combine filtered existing with new assignments
          const combinedAssignments = [...filteredExisting, ...newAssignmentsOldFormat];
        
        await lodgesAPI.update(lodgeId, {
          assignedRooms: combinedAssignments
        });
        }
      }

      // Reload data to get the latest from database
      console.log('Reloading data after applying assignments...');
      await onUpdateRegistrations();
      await onUpdateLodges();
      
      // Wait a moment for state to update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Debug: Log what was updated
      console.log('Applied assignments summary:', {
        totalAssignments: roomAssignments.length,
        lodgesUpdated: Array.from(lodgeUpdates.keys()),
        assignmentsByLodge: Array.from(lodgeUpdates.entries()).map(([lodgeId, update]) => ({
          lodgeId,
          assignmentsCount: update.newAssignments.length,
          assignments: update.newAssignments
        }))
      });

      alert(`Successfully applied ${roomAssignments.length} room assignments!`);
      
      // Clear assignments
      setRoomAssignments([]);
      setUnresolvedFamilies([]);
      setRoomsWithCapacityInfo([]);
      setAssignmentResults(null);
      setSkippedRegistrations([]);

    } catch (error) {
      console.error('Error applying assignments:', error);
      alert('Error applying assignments: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6 px-2 md:px-0">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-4">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Room Assignment Engine</h2>
        <div className="flex flex-col sm:flex-row gap-2 md:gap-4 items-stretch sm:items-center">
          <label className="flex items-center gap-2 text-xs md:text-sm text-gray-700 whitespace-nowrap">
            <input
              type="checkbox"
              checked={includeTestRegistrations}
              onChange={(e) => setIncludeTestRegistrations(e.target.checked)}
              disabled={userRole === ROLES.REGISTRATION_ADMIN || userRole === ROLES.VIEWER}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="whitespace-nowrap">Include Test Registrations</span>
          </label>
          {currentAssignments.length > 0 && userRole !== ROLES.VIEWER && userRole !== ROLES.REGISTRATION_ADMIN && (
            <button
              onClick={removeAllAssignments}
              disabled={isProcessing}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 md:px-6 md:py-2 rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center justify-center gap-2 text-xs md:text-sm font-medium"
              title="Remove all current room assignments"
            >
              <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
              <span className="whitespace-nowrap">Remove All Assignments</span>
            </button>
          )}
          <button
            onClick={processRoomAssignments}
            disabled={userRole === ROLES.VIEWER || userRole === ROLES.REGISTRATION_ADMIN || isProcessing || registrations.length === 0 || lodges.length === 0}
            className={`px-3 py-1.5 md:px-6 md:py-2 rounded-lg transition text-xs md:text-sm font-medium whitespace-nowrap ${
              userRole === ROLES.VIEWER || userRole === ROLES.REGISTRATION_ADMIN
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed'
            }`}
          >
            {isProcessing ? 'Processing...' : 'Run Assignment Engine'}
          </button>
          {roomAssignments.length > 0 && userRole !== ROLES.VIEWER && userRole !== ROLES.REGISTRATION_ADMIN && (
            <button
              onClick={applyAssignments}
              disabled={isProcessing}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 md:px-6 md:py-2 rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition text-xs md:text-sm font-medium whitespace-nowrap"
            >
              Apply Assignments
            </button>
          )}
        </div>
      </div>

      {/* Registrations with Room Assignment Notes - visible to room admin */}
      {(() => {
        const withNotes = registrations.filter(r => {
          const notes = (r && (r.roomAssignmentNotes || r.data?.roomAssignmentNotes)) || '';
          return typeof notes === 'string' && notes.trim() !== '';
        });
        if (withNotes.length === 0) return null;
        return (
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 md:p-6">
            <h3 className="text-base md:text-lg font-semibold text-teal-800 mb-3 md:mb-4">
              Registrations with Room Assignment Notes ({withNotes.length})
            </h3>
            <div className="overflow-x-auto -mx-3 md:mx-0">
              <table className="w-full min-w-[700px]">
                <thead className="bg-teal-100">
                  <tr>
                    <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-teal-700 uppercase">Name</th>
                    <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-teal-700 uppercase">Registration Code</th>
                    <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-teal-700 uppercase">Family Code</th>
                    <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-teal-700 uppercase">Room Assignment Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-teal-200 bg-white">
                  {withNotes.map(reg => (
                    <tr key={reg.id} className="hover:bg-teal-50/50">
                      <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm font-medium text-gray-800">{reg.name}</td>
                      <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-gray-700">{reg.registrationCode || reg.qrCode || '-'}</td>
                      <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-gray-600">{reg.familyCode || '-'}</td>
                      <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-gray-700 whitespace-pre-wrap">{(reg.roomAssignmentNotes || reg.data?.roomAssignmentNotes || '').trim()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {assignmentResults && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
          <h3 className="text-sm md:text-base font-semibold text-blue-800 mb-2">Assignment Results</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4 text-xs md:text-sm">
            <div>
              <span className="text-blue-600 font-medium">Total Assignments:</span> {assignmentResults.totalAssignments}
            </div>
            <div>
              <span className="text-yellow-600 font-medium">Unresolved:</span> {assignmentResults.unresolvedCount}
            </div>
            <div>
              <span className="text-gray-600 font-medium">Confirmed:</span> {assignmentResults.totalRegistrations}
            </div>
            <div>
              <span className="text-orange-600 font-medium">Skipped:</span> {assignmentResults.skippedCount || 0}
            </div>
            <div>
              <span className="text-gray-600 font-medium">Total All:</span> {assignmentResults.totalRegistrationsAll || assignmentResults.totalRegistrations}
            </div>
          </div>
        </div>
      )}

      {/* Skipped Registrations */}
      {skippedRegistrations.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 md:p-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 md:gap-4">
            <div 
              className="flex-1 cursor-pointer"
              onClick={() => setSkippedRegistrationsExpanded(!skippedRegistrationsExpanded)}
            >
              <div className="flex items-center gap-2">
                {skippedRegistrationsExpanded ? (
                  <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-orange-800" />
                ) : (
                  <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-orange-800" />
                )}
                <h3 className="text-base md:text-lg font-semibold text-orange-800">
                  Skipped Registrations ({skippedRegistrations.length})
                </h3>
              </div>
              <p className="text-xs md:text-sm text-orange-700 mt-1 ml-6 md:ml-7">
                These registrations were not included in the assignment engine
              </p>
            </div>
            <div className="flex items-center gap-2">
              {skippedRegistrations.some(s => s.reason && (s.reason.includes('Already assigned') || s.reason.includes('Orphaned assignment'))) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSkippedAssignments();
                  }}
                  disabled={userRole === ROLES.VIEWER || userRole === ROLES.REGISTRATION_ADMIN || isProcessing}
                  className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg transition flex items-center gap-2 text-xs md:text-sm font-medium whitespace-nowrap ${
                    userRole === ROLES.VIEWER || userRole === ROLES.REGISTRATION_ADMIN
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-orange-600 hover:bg-orange-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed'
                  }`}
                  title="Clear assignments from all assigned and orphaned registrations"
                >
                  <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="hidden sm:inline">Clear All "Already Assigned"</span>
                  <span className="sm:hidden">Clear All</span>
                </button>
              )}
            </div>
          </div>
          {skippedRegistrationsExpanded && (
            <div className="mt-3 md:mt-4 overflow-x-auto -mx-3 md:mx-0">
            <table className="w-full min-w-[600px]">
              <thead className="bg-orange-100">
                <tr>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-orange-800 uppercase">Name</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-orange-800 uppercase">Type</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-orange-800 uppercase">Status</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-orange-800 uppercase">Reason</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-orange-800 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-orange-200">
                {[...skippedRegistrations]
                  .sort((a, b) => (a.registrationName || '').localeCompare(b.registrationName || '', undefined, { sensitivity: 'base' }))
                  .map((skipped, idx) => (
                  <tr key={idx} className="hover:bg-orange-100">
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm font-medium text-gray-900">{skipped.registrationName}</td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-gray-700 capitalize">{skipped.type || 'N/A'}</td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm">
                      <span className={`px-1.5 md:px-2 py-0.5 md:py-1 rounded text-xs font-medium ${
                        skipped.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                        skipped.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {skipped.status || 'N/A'}
                      </span>
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-orange-700">{skipped.reason}</td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm">
                      {skipped.reason && (skipped.reason.includes('Already assigned') || skipped.reason.includes('Orphaned assignment')) ? (
                        <button
                          onClick={() => clearSingleAssignment(skipped.registrationId, skipped.registrationName)}
                          disabled={isProcessing}
                          className="bg-orange-600 hover:bg-orange-700 text-white px-2 md:px-3 py-1 rounded text-xs disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center gap-1"
                          title="Clear assignment for this registration"
                        >
                          <Trash2 className="w-3 h-3" />
                          Clear
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {/* Room Assignments */}
      {roomAssignments.length > 0 && (
        <div className="bg-white rounded-lg shadow p-3 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">
            Room Assignments ({roomAssignments.length})
          </h3>
          <div className="overflow-x-auto -mx-3 md:mx-0">
            <table className="w-full min-w-[1100px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Lodge</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Room</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Camper Category</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Church Name</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Family Code</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Room Assignment Notes</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {[...roomAssignments]
                  .sort((a, b) => (a.registrationName || '').localeCompare(b.registrationName || '', undefined, { sensitivity: 'base' }))
                  .map((assignment, idx) => {
                  const reg = registrations.find(r => r && r.id === assignment.registrationId);
                  const notes = (reg && (reg.roomAssignmentNotes || reg.data?.roomAssignmentNotes)) || '';
                  const notesDisplay = typeof notes === 'string' ? notes.trim() : '';
                  return (
                  <tr key={assignment.registrationId ?? idx} className="hover:bg-gray-50">
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm">{assignment.registrationName}</td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm">{assignment.lodgeName}</td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm font-medium">{assignment.roomNumber}</td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm">
                      <span className="px-1.5 md:px-2 py-0.5 md:py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        {assignment.assignmentType}
                      </span>
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-gray-700 capitalize">
                      {assignment.camperCategory || 'N/A'}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-gray-700">
                      {assignment.churchName || 'N/A'}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-gray-700">
                      {assignment.city || 'N/A'}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-gray-500">
                      {assignment.familyCode || '-'}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-gray-700 max-w-[200px] md:max-w-[280px] whitespace-pre-wrap" title={notesDisplay || undefined}>
                      {notesDisplay || '-'}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm">
                      <button
                        onClick={() => applySingleAssignment(assignment)}
                        disabled={isProcessing}
                        className="bg-green-600 hover:bg-green-700 text-white px-2 md:px-3 py-1 rounded text-xs font-medium disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                        title="Apply this assignment"
                      >
                        Apply
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Unresolved Families */}
      {unresolvedFamilies.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-800 mb-4">
            Unresolved Families/Registrations ({unresolvedFamilies.length})
          </h3>
          <div className="space-y-4">
            {unresolvedFamilies.map((unresolved, idx) => (
              <div key={idx} className="bg-white rounded p-4 border border-yellow-300">
                <div className="font-medium text-yellow-800 mb-2">
                  {unresolved.familyCode ? `Family: ${unresolved.familyCode}` : 'Single Registration'}
                </div>
                <div className="text-sm text-yellow-700 mb-2">Reason: {unresolved.reason}</div>
                <div className="text-sm">
                  <span className="font-medium">Members:</span>
                  <ul className="list-disc list-inside mt-1">
                    {unresolved.members.map((member, mIdx) => (
                      <li key={mIdx}>
                        {member.name} ({member.category.studentCategory || member.category.isAdult ? 'Adult' : 'Student'}, {member.category.gender})
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rooms with Capacity Info */}
      {roomsWithCapacityInfo.length > 0 && (
        <div className="bg-white rounded-lg shadow p-3 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">Room Capacity Information</h3>
          <div className="overflow-x-auto -mx-3 md:mx-0">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Lodge Name</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Used</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Projected Used</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Capacity</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Available</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Utilization</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Projected Utilization</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {roomsWithCapacityInfo.map((info, idx) => {
                  const isExpanded = expandedLodges.has(info.lodgeId);
                  const lodgeAssignments = getLodgeAssignments(info.lodgeId);
                  const lodge = lodges.find(l => l.id === info.lodgeId);
                  // Determine row background color for student lodges
                  const isStudentLodge = lodge && lodge.lodgeMainCategory === 'Student Lodge';
                  const rowBgColor = isStudentLodge && lodge.gender === 'Male' 
                    ? 'bg-blue-50 hover:bg-blue-100' 
                    : isStudentLodge && lodge.gender === 'Female' 
                    ? 'bg-pink-50 hover:bg-pink-100' 
                    : 'hover:bg-gray-50';
                  
                  return (
                    <React.Fragment key={idx}>
                      <tr className={`${rowBgColor} cursor-pointer`} onClick={() => toggleLodgeExpansion(info.lodgeId)}>
                        <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm font-medium">
                          <div className="flex items-center gap-1 md:gap-2">
                            {isExpanded ? (
                              <ChevronDown className="w-3 h-3 md:w-4 md:h-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-gray-500" />
                            )}
                            <span>{info.lodgeName}</span>
                            {lodgeAssignments.length > 0 && (
                              <span className="text-xs text-gray-500">({lodgeAssignments.length})</span>
                            )}
                          </div>
                        </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm font-medium text-gray-700">{info.currentUsed || 0}</td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm font-medium text-blue-600">{info.used}</td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm">{info.capacity}</td>
                    <td className={`px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm font-medium ${
                      info.available < 5 ? 'text-red-600' : info.available < 10 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {info.available}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm">
                      <div className="flex items-center gap-1 md:gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5 md:h-2">
                          <div
                            className={`h-1.5 md:h-2 rounded-full ${
                              parseFloat(info.currentUtilizationRate) > 90 ? 'bg-red-500' :
                              parseFloat(info.currentUtilizationRate) > 75 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(parseFloat(info.currentUtilizationRate), 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600">{info.currentUtilizationRate}%</span>
                      </div>
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm">
                      <div className="flex items-center gap-1 md:gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5 md:h-2">
                          <div
                            className={`h-1.5 md:h-2 rounded-full ${
                              parseFloat(info.utilizationRate) > 90 ? 'bg-red-500' :
                              parseFloat(info.utilizationRate) > 75 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(parseFloat(info.utilizationRate), 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-blue-600 font-medium">{info.utilizationRate}%</span>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && lodgeAssignments.length > 0 && (
                    <tr>
                      <td colSpan="7" className="px-2 md:px-4 py-3 md:py-4 bg-gray-50">
                        <div className="ml-3 md:ml-6">
                          <h4 className="text-xs md:text-sm font-semibold text-gray-700 mb-2 md:mb-3">Current Assignments ({lodgeAssignments.length}):</h4>
                            <div className="overflow-x-auto -mx-2 md:mx-0">
                            <table className="w-full text-xs md:text-sm min-w-[700px]">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-2 md:px-3 py-1.5 md:py-2 text-left text-xs font-medium text-gray-600 uppercase">Name</th>
                                  <th className="px-2 md:px-3 py-1.5 md:py-2 text-left text-xs font-medium text-gray-600 uppercase">Room</th>
                                  <th className="px-2 md:px-3 py-1.5 md:py-2 text-left text-xs font-medium text-gray-600 uppercase">Bed</th>
                                  <th className="px-2 md:px-3 py-1.5 md:py-2 text-left text-xs font-medium text-gray-600 uppercase">Type</th>
                                  <th className="px-2 md:px-3 py-1.5 md:py-2 text-left text-xs font-medium text-gray-600 uppercase">Category</th>
                                  <th className="px-2 md:px-3 py-1.5 md:py-2 text-left text-xs font-medium text-gray-600 uppercase">Church</th>
                                  <th className="px-2 md:px-3 py-1.5 md:py-2 text-left text-xs font-medium text-gray-600 uppercase">City</th>
                                  <th className="px-2 md:px-3 py-1.5 md:py-2 text-left text-xs font-medium text-gray-600 uppercase">Room Assignment Notes</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {lodgeAssignments.map((assignment, aIdx) => {
                                  const regForNotes = registrations.find(r => r && r.id === assignment.registrationId);
                                  const notes = (regForNotes && (regForNotes.roomAssignmentNotes || regForNotes.data?.roomAssignmentNotes)) || '';
                                  const notesDisplay = typeof notes === 'string' ? notes.trim() : '';
                                  return (
                                  <tr key={aIdx} className="hover:bg-gray-100">
                                    <td className="px-2 md:px-3 py-1.5 md:py-2 text-gray-900">{assignment.registrationName}</td>
                                    <td className="px-2 md:px-3 py-1.5 md:py-2 text-gray-700">{assignment.roomNumber}</td>
                                    <td className="px-2 md:px-3 py-1.5 md:py-2 text-gray-700">{assignment.bedNumber}</td>
                                    <td className="px-2 md:px-3 py-1.5 md:py-2">
                                      <span className="px-1.5 md:px-2 py-0.5 md:py-1 bg-blue-100 text-blue-800 rounded text-xs capitalize">
                                        {assignment.type || 'N/A'}
                                      </span>
                                    </td>
                                    <td className="px-2 md:px-3 py-1.5 md:py-2 text-gray-700 capitalize">{assignment.camperCategory}</td>
                                    <td className="px-2 md:px-3 py-1.5 md:py-2 text-gray-700">{assignment.churchName}</td>
                                    <td className="px-2 md:px-3 py-1.5 md:py-2 text-gray-700">{assignment.city}</td>
                                    <td className="px-2 md:px-3 py-1.5 md:py-2 text-gray-700 max-w-[180px] md:max-w-[240px] whitespace-pre-wrap" title={notesDisplay || undefined}>{notesDisplay || '-'}</td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {isExpanded && lodgeAssignments.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-2 md:px-4 py-3 md:py-4 bg-gray-50">
                        <div className="ml-3 md:ml-6 text-xs md:text-sm text-gray-500 italic">No current assignments</div>
                      </td>
                    </tr>
                  )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {roomAssignments.length === 0 && !isProcessing && currentAssignments.length === 0 && !assignmentResults && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-600">Click "Run Assignment Engine" to process room assignments for all confirmed registrations.</p>
        </div>
      )}
    </div>
  );
}

// Deleted Registrations Component
function DeletedRegistrations({ userRole, onReload }) {
  const [deletedRegistrations, setDeletedRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const canRestore = hasPermission(userRole, 'approve_registrations') || hasPermission(userRole, 'manage_settings');
  const canPermanentDelete = hasPermission(userRole, 'approve_registrations') || hasPermission(userRole, 'manage_settings');

  useEffect(() => {
    loadDeletedRegistrations();
  }, []);

  const loadDeletedRegistrations = async () => {
    try {
      setLoading(true);
      const deleted = await registrationsAPI.getDeleted();
      setDeletedRegistrations(deleted);
    } catch (error) {
      console.error('Error loading deleted registrations:', error);
      alert('Failed to load deleted registrations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (registrationId) => {
    const registration = deletedRegistrations.find(r => r.id === registrationId);
    if (window.confirm(`Are you sure you want to restore the registration for ${registration?.name || 'this person'}?`)) {
      try {
        await registrationsAPI.restore(registrationId);
        alert('Registration restored successfully!');
        await loadDeletedRegistrations();
        if (onReload) {
          onReload();
        }
      } catch (error) {
        console.error('Error restoring registration:', error);
        alert('Failed to restore registration. Please try again.');
      }
    }
  };

  const handlePermanentDelete = async (registrationId) => {
    const registration = deletedRegistrations.find(r => r.id === registrationId);
    if (window.confirm(`⚠️ WARNING: Are you sure you want to PERMANENTLY DELETE the registration for ${registration?.name || 'this person'}? This action cannot be undone!`)) {
      const doubleConfirm = window.confirm('This will permanently remove the registration from the system. Are you absolutely sure?');
      if (doubleConfirm) {
        try {
          await registrationsAPI.permanentDelete(registrationId);
          alert('Registration permanently deleted.');
          await loadDeletedRegistrations();
        } catch (error) {
          console.error('Error permanently deleting registration:', error);
          alert('Failed to permanently delete registration. Please try again.');
        }
      }
    }
  };

  const filteredRegistrations = deletedRegistrations.filter(reg => {
    const matchesSearch = 
      reg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.registrationCode?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Deleted Registrations</h2>
          <p className="text-sm text-gray-600 mt-1">
            View and manage deleted registrations. You can restore them or permanently delete them.
          </p>
        </div>
        <button
          onClick={loadDeletedRegistrations}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition flex items-center gap-2"
        >
          <CheckCircle className="w-5 h-5" />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading deleted registrations...</p>
        </div>
      ) : (
        <>
          {/* Search */}
          <div className="bg-white rounded-lg shadow p-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or registration code..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Deleted Registrations List */}
          {filteredRegistrations.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Trash2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Deleted Registrations</h3>
              <p className="text-gray-500">There are no deleted registrations to display.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deleted At</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredRegistrations.map(reg => (
                      <tr key={reg.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {reg.name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {reg.email || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">
                          {reg.type || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={reg.status || 'pending'} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(reg.deletedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            {canRestore && (
                              <button
                                onClick={() => handleRestore(reg.id)}
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg transition flex items-center gap-1 text-xs"
                                title="Restore Registration"
                              >
                                <CheckCircle className="w-4 h-4" />
                                Restore
                              </button>
                            )}
                            {canPermanentDelete && (
                              <button
                                onClick={() => handlePermanentDelete(reg.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-lg transition flex items-center gap-1 text-xs"
                                title="Permanently Delete (Cannot be undone)"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete Forever
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-gray-50 px-6 py-3 border-t">
                <p className="text-sm text-gray-600">
                  Showing {filteredRegistrations.length} of {deletedRegistrations.length} deleted registration(s)
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
